import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import { motion, AnimatePresence } from "motion/react";
import * as lucide from "lucide-react";
import UploadPropertiesModal from "./components/UploadPropertiesModal";
import ManageSecurityModal from "./components/ManageSecurityModal";
import FolderSecurityModal from "./components/FolderSecurityModal";
import FolderCreateModal from "./components/FolderCreateModal";
import DocumentEffectiveSecurityView from "./components/DocumentEffectiveSecurityView";
import MfaSettingsSection from "./components/MfaSettingsSection";
import AccessDeniedModal from "./components/AccessDeniedModal";

// iManage-style palette: light canvas, deep-navy sidebar, cyan accent.
// Light surfaces (bg/surface/raised/hover/deep), dark text tokens
// (text/ink2/textMuted/textDim/muted2), iManage-cyan accent family,
// plus sidebar-on-navy tokens used exclusively inside the Sidebar
// component so white text reads on the dark nav.
const THEME = {
  bg:"#F4F6F9", surface:"#FFFFFF", surfaceRaised:"#FAFBFC", surfaceHover:"#ECEFF3",
  surfaceDeep:"#E6E8EE", sidebar:"#2D2D5F",
  border:"#E0E3E8", borderSubtle:"#EBEDF0", line2:"#D6DAE0", line3:"#C6CCD3",
  text:"#1A1F2B", ink2:"#3A4150", textMuted:"#6B7280", textDim:"#9AA0A8", muted2:"#B5BAC2",
  accent:"#00A8E1", accentHover:"#1FB8E8", accent2:"#0095CC", accentDark:"#0078A6",
  accentDim:"rgba(0,168,225,0.12)", accentGlow:"rgba(0,168,225,0.06)", accentLine:"rgba(0,168,225,0.35)",
  success:"#16A34A", successDim:"rgba(22,163,74,0.10)", successLine:"rgba(22,163,74,0.30)",
  danger:"#DC2626", dangerDim:"rgba(220,38,38,0.10)",
  warning:"#F59E0B", warningDim:"rgba(245,158,11,0.12)",
  info:"#2563EB", infoDim:"rgba(37,99,235,0.10)", infoLine:"rgba(37,99,235,0.30)",
  sidebarText:"#FFFFFF", sidebarInk:"#D8DCE6", sidebarMuted:"#A8AEC0", sidebarDim:"#7E8499",
  sidebarBorder:"rgba(255,255,255,0.10)", sidebarHover:"rgba(255,255,255,0.06)",
  sidebarActiveBg:"rgba(0,168,225,0.18)", sidebarActiveLine:"rgba(0,168,225,0.45)",
};
const FONT = `'Geist','Inter','DM Sans',-apple-system,sans-serif`;
const FONT_MONO = `'Geist Mono','JetBrains Mono',ui-monospace,monospace`;
const FONT_SERIF = `'Instrument Serif',Georgia,serif`;

// Sprint 11.1a: country list for the Add Organization dropdown. Curated
// pragmatically — DR Congo + DRC neighbors + key francophone Africa
// markets surface first ("popular" for KlassDoc's launch market), then
// the full ISO 3166-1 list alphabetically. Keeps the default selection
// instant for the 90% case while still covering everywhere a real
// customer could be.
const COUNTRIES_POPULAR = [
  "DR Congo",
  "Congo (Republic)",
  "Rwanda", "Burundi", "Uganda", "Tanzania", "Kenya", "Zambia", "Angola", "Central African Republic", "South Sudan",
  "France", "Belgium", "Canada", "United States",
  "Côte d'Ivoire", "Senegal", "Cameroon", "Gabon", "Mali", "Burkina Faso", "Togo", "Benin", "Niger", "Chad",
];
const COUNTRIES_ALL = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia",
  "Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin",
  "Bhutan","Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi",
  "Cabo Verde","Cambodia","Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia",
  "Comoros","Congo (Republic)","Costa Rica","Côte d'Ivoire","Croatia","Cuba","Cyprus","Czech Republic",
  "DR Congo","Denmark","Djibouti","Dominica","Dominican Republic","Ecuador","Egypt","El Salvador",
  "Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France","Gabon","Gambia",
  "Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guyana","Haiti",
  "Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Jamaica",
  "Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon",
  "Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg","Madagascar","Malawi","Malaysia",
  "Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia","Moldova",
  "Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nauru","Nepal","Netherlands",
  "New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway","Oman","Pakistan",
  "Palau","Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal",
  "Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines",
  "Samoa","San Marino","São Tomé and Príncipe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone",
  "Singapore","Slovakia","Slovenia","Solomon Islands","Somalia","South Africa","South Korea","South Sudan",
  "Spain","Sri Lanka","Sudan","Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania",
  "Thailand","Timor-Leste","Togo","Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu",
  "Uganda","Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan",
  "Vanuatu","Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe",
];

// ─── i18n ───
const TR = {
  fr: {
    dashboard:"Tableau de bord",documents:"Documents",folders:"Dossiers",search:"Recherche",
    users:"Utilisateurs",audit:"Journal d'audit",dashTitle:"Tableau de bord",
    dashSub:"Vue d'ensemble de vos archives",docsLabel:"Documents",foldersLabel:"Dossiers",
    teamMembers:"Membres",storageUsed:"Stockage",recentDocs:"Documents récents",
    recentActivity:"Activité récente",quickUpload:"Téléverser",selectFolder:"Parcourir",
    rootFolder:"Racine (aucun dossier)",uploadToFolder:"Téléverser vers le dossier",
    docsInArchive:"documents dans l'archive",upload:"Téléverser",
    dropFiles:"Glissez des fichiers ici ou cliquez pour parcourir",
    dropLimit:"PDF, DOCX, XLSX, images — jusqu'à 50 Mo",uploading:"Téléversement...",
    sortBy:"Trier par",date:"Date",name:"Nom",size:"Taille",
    clickPreview:"Cliquez pour l'aperçu",folder:"Dossier",uploadedBy:"Par",
    modified:"Modifié",created:"Créé",type:"Type",rootFolders:"dossiers racine",
    insideFolder:"Dans :",allFolders:"Tous les dossiers",newFolder:"Nouveau dossier",
    folderName:"Nom du dossier...",create:"Créer",cancel:"Annuler",
    emptyFolder:"Dossier vide",noFolders:"Aucun dossier",
    createHint:"Créez un dossier pour organiser vos documents",
    docs:"docs",subfolders:"sous-dossiers",
    searchTitle:"Recherche",searchSub:"Rechercher dans toute votre archive",
    searchPH:"Rechercher par nom, contenu, tags...",searchBtn:"Rechercher",
    typeF:"Type :",all:"Tous",pdf:"PDF",word:"Word",images:"Images",sheets:"Tableurs",
    dateF:"Date :",anyTime:"Toute période",pastWeek:"Semaine",pastMonth:"Mois",pastQ:"Trimestre",
    noResults:"Aucun résultat",noMatch:"Aucun document correspondant à",
    result:"résultat",results:"résultats",searchArchive:"Rechercher dans vos archives",
    fullText:"Recherche en texte intégral",quickSearch:"Recherche rapide...",
    userMgmt:"Gestion des utilisateurs",membersCount:"membres",inviteUser:"Inviter",
    emailAddr:"Adresse e-mail",role:"Rôle",sendInvite:"Envoyer",user:"Utilisateur",
    email:"E-mail",status:"Statut",lastActive:"Dernière activité",
    viewer:"Lecteur",editor:"Éditeur",admin:"Administrateur",
    inviteSent:"Invitation envoyée à",managePerms:"Permissions",permissions:"Permissions",
    addDocs:"Ajouter",saveDocs:"Sauvegarder",modifyDocs:"Modifier",deleteDocs:"Supprimer",
    viewOnly:"Lecture seule",savePerms:"Enregistrer",removeUser:"Retirer",
    confirmRemove:"Confirmer la suppression ?",yesRemove:"Oui, retirer",
    userRemoved:"Utilisateur retiré",permsSaved:"Permissions enregistrées",
    auditLog:"Journal d'audit",eventsRecorded:"événements",exportCsv:"Exporter CSV",
    allEvents:"Tous",moveToFolder:"Déplacer vers :",move:"Déplacer",delete:"Supprimer",
    download:"Télécharger",confirmDel:"Supprimer définitivement",
    docMoved:"Document déplacé",docDeleted:"Document supprimé",
    failMove:"Échec du déplacement",failDel:"Échec de la suppression",
    noPreview:"Aperçu non disponible (données test)",
    previewFor:"Aperçu pour images et PDF. Téléversez un fichier.",
    noFile:"Aucun fichier (données test)",document:"Document",root:"Racine",
    pdfPreview:"Aperçu PDF",signIn:"Se connecter",password:"Mot de passe",
    forgotPassword:"Mot de passe oublié ?",
    resetPassword:"Réinitialiser le mot de passe",
    resetEmailLabel:"Adresse e-mail",
    resetEmailHint:"Entrez l'adresse e-mail de votre compte KlassDoc. Nous vous enverrons un lien pour choisir un nouveau mot de passe.",
    resetEmailSent:"Si votre compte existe, un lien de réinitialisation a été envoyé à votre e-mail. Vérifiez également votre dossier de courrier indésirable.",
    sendResetLink:"Envoyer le lien",
    backToLogin:"Retour à la connexion",
    newPassword:"Nouveau mot de passe",
    confirmPassword:"Confirmer le mot de passe",
    passwordRequirements:"Minimum 8 caractères, avec majuscule, minuscule et chiffre.",
    passwordsDontMatch:"Les mots de passe ne correspondent pas",
    passwordUpdated:"Mot de passe mis à jour. Vous pouvez maintenant vous connecter.",
    linkInvalid:"Ce lien est invalide ou a expiré.",
    linkExpired:"Ce lien a expiré. Demandez-en un nouveau depuis la page de connexion.",
    requestNewLink:"Demander un nouveau lien",
    verifyingLink:"Vérification du lien...",
    resetForAccount:"Réinitialisation pour ",
    mfa:"Double authentification",
    mfaSubtitle:"Protégez votre compte avec un second facteur",
    mfaEnable:"Activer la double authentification",
    mfaDisable:"Désactiver",
    mfaEnabled:"Double authentification activée",
    mfaNotEnabled:"Non configurée",
    mfaEnrolledOn:"Activée le",
    mfaScanQR:"Scannez ce QR code avec Google Authenticator, Authy ou toute autre application compatible.",
    mfaManualEntry:"Ou saisissez cette clé manuellement :",
    mfaEnterCode:"Entrez le code à 6 chiffres",
    mfaCode:"Code d'authentification",
    mfaVerify:"Vérifier et activer",
    mfaSaveBackupCodes:"Sauvegardez ces codes de secours",
    mfaBackupWarning:"Chaque code ne peut être utilisé qu'une seule fois. Gardez-les en lieu sûr — ils vous permettront de vous connecter si vous perdez votre téléphone.",
    mfaDownloadCodes:"Télécharger les codes",
    mfaConfirmSaved:"J'ai sauvegardé mes codes",
    mfaBackupRemaining:"codes de secours restants",
    mfaRegenerateCodes:"Régénérer les codes",
    mfaChallengeTitle:"Vérification en deux étapes",
    mfaChallengeSubtitle:"Entrez le code généré par votre application d'authentification.",
    mfaUseBackup:"Utiliser un code de secours",
    mfaUseTotp:"Utiliser mon application",
    mfaInvalidCode:"Code incorrect. Vérifiez l'heure sur votre téléphone puis réessayez.",
    mfaBackupUsed:"Code de secours utilisé. Pensez à en régénérer quand vous récupérerez votre appareil.",
    mfaDisableTitle:"Désactiver la double authentification",
    mfaDisableWarning:"Votre compte sera moins protégé sans ce second facteur.",
    mfaCurrentPassword:"Mot de passe actuel",
    docSecurity:"Sécurité du document",
    docSecurityMode:"Mode de sécurité",
    docSecInherit:"Hériter du dossier",
    docSecInheritDesc:"Ce document utilise les permissions du dossier. Tous les utilisateurs ayant accès au dossier le verront.",
    docSecRestrict:"Restreindre",
    docSecRestrictDesc:"Seuls les utilisateurs listés ci-dessous peuvent voir ce document, même si d'autres ont accès au dossier.",
    docSecCustom:"Personnalisé",
    docSecCustomDesc:"Ce document utilise ses propres permissions. L'ACL du dossier ne s'applique pas.",
    docSecSwitchRestrictWarn:"Vous devrez choisir qui conserve l'accès. Les utilisateurs non listés perdront l'accès à ce document.",
    docSecSwitchInheritWarn:"Les permissions personnalisées seront supprimées. Tous les utilisateurs du dossier retrouveront l'accès.",
    docSecSwitchCustomWarn:"Les utilisateurs hors du dossier peuvent maintenant recevoir l'accès. L'ACL du dossier ne s'appliquera plus.",
    docSecRestrictedBadge:"Accès restreint",
    docSecCustomBadge:"Accès personnalisé",
    docSecSaveChanges:"Enregistrer",
    docSecFolderLock:"Le créateur et le groupe Administrateurs conservent toujours l'accès",
    noAccountPrompt:"Pas encore de compte ?",
    requestAccount:"Demander un compte",
    requestAccountTitle:"Demander un compte KlassDoc",
    requestAccountLead:"Les comptes sont créés par Seven Co Success après un court appel de démo.",
    requestAccountBody:"L'inscription libre est désactivée. Pour ouvrir un compte KlassDoc pour votre cabinet, votre ministère ou votre entreprise, contactez notre équipe — nous planifierons une démo, validerons votre plan et configurerons votre organisation avec les bons utilisateurs.",
    contactUs:"Contactez-nous",
    demoCreds:"Démo : eva@klassdoc.com / admin123",
    console:"Console de gestion documentaire",collapse:"Réduire",logout:"Déconnexion",
    filesUp:"fichier(s) téléversé(s)",folderCreated:"Dossier créé",
    pressEnter:"Appuyez sur Entrée pour rechercher",
    loading:"Chargement...",error:"Erreur de connexion au serveur",retry:"Réessayer",
    noDocsYet:"Aucun document",uploadFirst:"Téléversez votre premier document",
    subscription:"Abonnement",adminPortal:"Portail Admin",
    // Sprint 6
    courrier:"Courrier",reports:"Rapports",
    courrierSub:"Gestion du courrier entrant et sortant",
    incoming:"Entrant",outgoing:"Sortant",received:"Reçu",processing:"En traitement",
    processed:"Traité",sent:"Envoyé",archived:"Archivé",draft:"Brouillon",
    newCourrier:"Nouveau courrier",subject:"Objet",sender:"Expéditeur",
    recipient:"Destinataire",department:"Département",departments:"Départements",addDepartment:"Ajouter un département",departmentName:"Nom du département",departmentNameFr:"Nom (français)",departmentNameEn:"Nom (anglais)",departmentDescription:"Description",disableDepartment:"Désactiver",enableDepartment:"Réactiver",confirmDisableDept:"Désactiver ce département ? Les documents existants conservent leur libellé.",noDepartments:"Aucun département. Ajoutez-en un pour structurer vos documents.",priority:"Priorité",
    low:"Basse",normal:"Normale",high:"Haute",urgent:"Urgent",
    reference:"Référence",assignedTo:"Assigné à",dueDate:"Date limite",
    notes:"Notes",save:"Enregistrer",
    reportsSub:"Analyses et statistiques",
    docsPerFolder:"Documents par dossier",uploadActivity:"Activité de téléversement",
    storageTrend:"Tendance stockage",activeUsers:"Utilisateurs actifs",
    docTypes:"Types de documents",
    metadata:"Métadonnées",author:"Auteur",keywords:"Mots-clés",
    docClass:"Classe/Catégorie",confidentiality:"Confidentialité",
    retentionDate:"Date de rétention",public:"Public",internal:"Interne",
    confidential:"Confidentiel",secret:"Secret",
    versions:"Versions",comments:"Commentaires",addComment:"Ajouter un commentaire",
    reply:"Répondre",versionHistory:"Historique des versions",
    uploadNewVersion:"Nouvelle version",changeNotes:"Notes de modification",
    version:"Version",
    // Sprint 6.7 — Checkout / Locking
    checkedOutBy:"Réservé par",docLocked:"Ce document est réservé par",
    openReadOnly:"Ouvrir en lecture seule",forceRelease:"Forcer la libération",
    readOnlyMode:"Lecture seule",editingMode:"En cours d'édition",
    docCheckedOutDialog:"a été réservé par",openReadOnlyQ:"Voulez-vous l'ouvrir en lecture seule ?",
    myCheckedOut:"Vos documents réservés",saveChanges:"Enregistrer les modifications",
    saveAsNewVersion:"Enregistrer comme nouvelle version…",
    checkInNoChanges:"Libérer sans modifier",dropToSave:"Déposez le fichier modifié ici",
    savedBack:"Modifications enregistrées",checkedIn:"Document libéré",
    openFile:"Ouvrir",details:"Détails",
    // Sprint 8A — WebDAV / Open in Office
    openInOffice:"Ouvrir dans Office",openInWord:"Ouvrir dans Word",openInExcel:"Ouvrir dans Excel",
    openInPowerPoint:"Ouvrir dans PowerPoint",notOfficeType:"Ce type de fichier ne peut pas être ouvert dans Office",
    webdavOpening:"Ouverture dans Office...",
    // Sprint 10C — Central 401 handler (Priority 8.1)
    sessionExpired:"Votre session a expiré. Veuillez vous reconnecter.",
  },
  en: {
    dashboard:"Dashboard",documents:"Documents",folders:"Folders",search:"Search",
    users:"Users",audit:"Audit Log",dashTitle:"Dashboard",
    dashSub:"Overview of your document archive",docsLabel:"Documents",foldersLabel:"Folders",
    teamMembers:"Team Members",storageUsed:"Storage Used",recentDocs:"Recent Documents",
    recentActivity:"Recent Activity",quickUpload:"Upload",selectFolder:"Browse",
    rootFolder:"Root (no folder)",uploadToFolder:"Upload to folder",
    docsInArchive:"documents in archive",upload:"Upload",
    dropFiles:"Drop files here or click to browse",
    dropLimit:"PDF, DOCX, XLSX, images — up to 50 MB",uploading:"Uploading...",
    sortBy:"Sort by",date:"Date",name:"Name",size:"Size",
    clickPreview:"Click to preview",folder:"Folder",uploadedBy:"Uploaded By",
    modified:"Modified",created:"Created",type:"Type",rootFolders:"root folders",
    insideFolder:"Inside:",allFolders:"All Folders",newFolder:"New Folder",
    folderName:"Folder name...",create:"Create",cancel:"Cancel",
    emptyFolder:"Empty folder",noFolders:"No folders yet",
    createHint:"Create a folder to organize your documents",
    docs:"docs",subfolders:"subfolders",
    searchTitle:"Search",searchSub:"Find documents across your entire archive",
    searchPH:"Search by filename, content, tags...",searchBtn:"Search",
    typeF:"Type:",all:"All",pdf:"PDF",word:"Word",images:"Images",sheets:"Sheets",
    dateF:"Date:",anyTime:"Any time",pastWeek:"Past week",pastMonth:"Past month",pastQ:"Past quarter",
    noResults:"No results found",noMatch:"No documents matching",
    result:"result",results:"results",searchArchive:"Search your archive",
    fullText:"Full-text search across all content",quickSearch:"Quick search...",
    userMgmt:"User Management",membersCount:"team members",inviteUser:"Invite User",
    emailAddr:"Email Address",role:"Role",sendInvite:"Send Invite",user:"User",
    email:"Email",status:"Status",lastActive:"Last Active",
    viewer:"Viewer",editor:"Editor",admin:"Admin",
    inviteSent:"Invitation sent to",managePerms:"Permissions",permissions:"Permissions",
    addDocs:"Add docs",saveDocs:"Save",modifyDocs:"Modify",deleteDocs:"Delete",
    viewOnly:"View only",savePerms:"Save",removeUser:"Remove User",
    confirmRemove:"Confirm removal?",yesRemove:"Yes, Remove",
    userRemoved:"User removed",permsSaved:"Permissions saved",
    auditLog:"Audit Log",eventsRecorded:"events recorded",exportCsv:"Export CSV",
    allEvents:"All Events",moveToFolder:"Move to folder:",move:"Move",delete:"Delete",
    download:"Download",confirmDel:"Permanently delete",
    docMoved:"Document moved",docDeleted:"Document deleted",
    failMove:"Failed to move",failDel:"Failed to delete",
    noPreview:"Preview not available (seeded data)",
    previewFor:"Preview available for images and PDFs.",
    noFile:"No file to download (seeded data)",document:"Document",root:"Root",
    pdfPreview:"PDF Preview",signIn:"Sign In",password:"Password",
    forgotPassword:"Forgot password?",
    resetPassword:"Reset password",
    resetEmailLabel:"Email address",
    resetEmailHint:"Enter your KlassDoc account email. We'll send you a link to choose a new password.",
    resetEmailSent:"If your account exists, a reset link has been sent to your email. Please also check your spam folder.",
    sendResetLink:"Send reset link",
    backToLogin:"Back to sign in",
    newPassword:"New password",
    confirmPassword:"Confirm password",
    passwordRequirements:"Minimum 8 characters, with uppercase, lowercase, and a number.",
    passwordsDontMatch:"Passwords don't match",
    passwordUpdated:"Password updated. You can now sign in.",
    linkInvalid:"This link is invalid or expired.",
    linkExpired:"This link has expired. Request a new one from the sign-in page.",
    requestNewLink:"Request a new link",
    verifyingLink:"Verifying link...",
    resetForAccount:"Resetting password for ",
    mfa:"Two-factor authentication",
    mfaSubtitle:"Protect your account with a second factor",
    mfaEnable:"Enable two-factor authentication",
    mfaDisable:"Disable",
    mfaEnabled:"Two-factor authentication is on",
    mfaNotEnabled:"Not configured",
    mfaEnrolledOn:"Enabled on",
    mfaScanQR:"Scan this QR code with Google Authenticator, Authy, or any compatible app.",
    mfaManualEntry:"Or enter this key manually:",
    mfaEnterCode:"Enter the 6-digit code",
    mfaCode:"Authentication code",
    mfaVerify:"Verify and enable",
    mfaSaveBackupCodes:"Save these backup codes",
    mfaBackupWarning:"Each code works once. Keep them somewhere safe — they let you sign in if you lose your phone.",
    mfaDownloadCodes:"Download codes",
    mfaConfirmSaved:"I've saved my codes",
    mfaBackupRemaining:"backup codes remaining",
    mfaRegenerateCodes:"Regenerate codes",
    mfaChallengeTitle:"Two-step verification",
    mfaChallengeSubtitle:"Enter the code generated by your authenticator app.",
    mfaUseBackup:"Use a backup code",
    mfaUseTotp:"Use my authenticator app",
    mfaInvalidCode:"Wrong code. Check your phone's time and try again.",
    mfaBackupUsed:"Backup code used. Remember to regenerate your codes once your device is back.",
    mfaDisableTitle:"Disable two-factor authentication",
    mfaDisableWarning:"Your account will be less secure without this second factor.",
    mfaCurrentPassword:"Current password",
    docSecurity:"Document security",
    docSecurityMode:"Security mode",
    docSecInherit:"Inherit from folder",
    docSecInheritDesc:"This document uses the folder's permissions. Every user with folder access can see it.",
    docSecRestrict:"Restrict",
    docSecRestrictDesc:"Only the users listed below can see this document, even if others have folder access.",
    docSecCustom:"Custom",
    docSecCustomDesc:"This document uses its own permissions. Folder access does not apply.",
    docSecSwitchRestrictWarn:"You'll need to pick who keeps access. Users not listed will lose access to this document.",
    docSecSwitchInheritWarn:"Any custom permissions will be discarded. All folder users will regain access.",
    docSecSwitchCustomWarn:"Users outside the folder can now be granted access. Folder ACL will no longer apply.",
    docSecRestrictedBadge:"Restricted access",
    docSecCustomBadge:"Custom access",
    docSecSaveChanges:"Save changes",
    docSecFolderLock:"Author and Administrators group always retain access",
    noAccountPrompt:"No account yet?",
    requestAccount:"Request an account",
    requestAccountTitle:"Request a KlassDoc account",
    requestAccountLead:"Accounts are provisioned by Seven Co Success after a short onboarding call.",
    requestAccountBody:"Self-service signup is closed. To open a KlassDoc account for your law firm, ministry, or enterprise, contact our team — we'll schedule a demo, review your plan, and set up your organization with the correct users.",
    contactUs:"Contact us",
    demoCreds:"Demo: eva@klassdoc.com / admin123",
    console:"Document Management Console",collapse:"Collapse",logout:"Sign Out",
    filesUp:"file(s) uploaded",folderCreated:"Folder created",
    pressEnter:"Press Enter to search",
    loading:"Loading...",error:"Cannot connect to server",retry:"Retry",
    noDocsYet:"No documents yet",uploadFirst:"Upload your first document",
    subscription:"Subscription",adminPortal:"Admin Portal",
    // Sprint 6
    courrier:"Courrier",reports:"Reports",
    courrierSub:"Manage incoming and outgoing correspondence",
    incoming:"Incoming",outgoing:"Outgoing",received:"Received",processing:"Processing",
    processed:"Processed",sent:"Sent",archived:"Archived",draft:"Draft",
    newCourrier:"New Courrier",subject:"Subject",sender:"Sender",
    recipient:"Recipient",department:"Department",departments:"Departments",addDepartment:"Add department",departmentName:"Department name",departmentNameFr:"Name (French)",departmentNameEn:"Name (English)",departmentDescription:"Description",disableDepartment:"Disable",enableDepartment:"Re-enable",confirmDisableDept:"Disable this department? Existing documents keep their label.",noDepartments:"No departments yet. Add one to organize your documents.",priority:"Priority",
    low:"Low",normal:"Normal",high:"High",urgent:"Urgent",
    reference:"Reference",assignedTo:"Assigned To",dueDate:"Due Date",
    notes:"Notes",save:"Save",
    reportsSub:"Analytics and statistics",
    docsPerFolder:"Documents per Folder",uploadActivity:"Upload Activity",
    storageTrend:"Storage Trend",activeUsers:"Active Users",
    docTypes:"Document Types",
    metadata:"Metadata",author:"Author",keywords:"Keywords",
    docClass:"Class/Category",confidentiality:"Confidentiality",
    retentionDate:"Retention Date",public:"Public",internal:"Internal",
    confidential:"Confidential",secret:"Secret",
    versions:"Versions",comments:"Comments",addComment:"Add Comment",
    reply:"Reply",versionHistory:"Version History",
    uploadNewVersion:"New Version",changeNotes:"Change Notes",
    version:"Version",
    // Sprint 6.7 — Checkout / Locking
    checkedOutBy:"Checked out by",docLocked:"This document is checked out by",
    openReadOnly:"Open Read-Only",forceRelease:"Force Release",
    readOnlyMode:"Read-only",editingMode:"Editing",
    docCheckedOutDialog:"has been checked out by",openReadOnlyQ:"Would you like to open it as read-only?",
    myCheckedOut:"Your checked-out documents",saveChanges:"Save Changes",
    saveAsNewVersion:"Save as new version…",
    checkInNoChanges:"Check in without changes",dropToSave:"Drop the edited file here",
    savedBack:"Changes saved",checkedIn:"Document checked in",
    openFile:"Open",details:"Details",
    // Sprint 8A — WebDAV / Open in Office
    openInOffice:"Open in Office",openInWord:"Open in Word",openInExcel:"Open in Excel",
    openInPowerPoint:"Open in PowerPoint",notOfficeType:"This file type cannot be opened in Office",
    webdavOpening:"Opening in Office...",
    // Sprint 10C — Central 401 handler (Priority 8.1)
    sessionExpired:"Your session has expired. Please log in again.",
  },
};
const LangContext = createContext();
function useLang(){return useContext(LangContext);}
function T(lang,key){return TR[lang]?.[key]||TR.en[key]||key;}

// ─── API CLIENT — Sprint 10: Refresh token support ───
const API_BASE = "/api";
class ApiClient {
  constructor(){
    this.token=localStorage.getItem("af_token")||null;
    this._refreshing=null;
    // Sprint 10C / Priority 8.1 — auth-event bridge across the React boundary.
    // AppShell subscribes via onAuthEvent(); ApiClient emits "session-expired"
    // when a 401 proves unrecoverable. _sessionExpiredFired de-dupes bursts
    // of parallel 401s so we only toast once per expiry, not once per request.
    this._authListeners=new Set();
    this._sessionExpiredFired=false;
  }
  setToken(t){
    this.token=t;
    if(t){localStorage.setItem("af_token",t);this._sessionExpiredFired=false;}
    else{localStorage.removeItem("af_token");}
  }
  // Subscribe to auth events. Returns an unsubscribe fn.
  // Events: "session-expired" — emitted when a 401 is unrecoverable.
  onAuthEvent(cb){this._authListeners.add(cb);return()=>this._authListeners.delete(cb);}
  _emitAuth(name){for(const cb of this._authListeners){try{cb(name);}catch(e){console.error("[ApiClient] auth listener error:",e);}}}
  // Centralized unrecoverable-401 handling. Clears tokens, emits event ONCE,
  // and returns an UNAUTHORIZED error for the caller to throw. React-state
  // transition happens via the subscriber (AppShell) — no window.location
  // reload, no href change.
  _handleSessionExpired(){
    this.setToken(null);
    localStorage.removeItem("kd_refresh_token");
    if(!this._sessionExpiredFired){
      this._sessionExpiredFired=true;
      this._emitAuth("session-expired");
    }
    const err=new Error("Session expired");
    err.status=401;
    err.code="UNAUTHORIZED";
    return err;
  }
  async req(method,path,body,isFormData){
    const headers={};
    if(this.token)headers["Authorization"]=`Bearer ${this.token}`;
    if(!isFormData)headers["Content-Type"]="application/json";
    const opts={method,headers};
    if(body)opts.body=isFormData?body:JSON.stringify(body);
    let res=await fetch(`${API_BASE}${path}`,opts);
    // Sprint 10: auto-refresh on token expiry.
    // Sprint 10C / Priority 8.1: unrecoverable 401 → emit session-expired,
    // throw UNAUTHORIZED error (don't return undefined — callers were rendering
    // empty state on the silent return, making expired sessions look like data loss).
    if(res.status===401&&path!=="/auth/login"&&path!=="/auth/refresh"){
      const data=await res.json().catch(()=>({}));
      if(data.expired){
        const refreshed=await this._tryRefresh();
        if(refreshed){
          headers["Authorization"]=`Bearer ${this.token}`;
          opts.headers=headers;
          res=await fetch(`${API_BASE}${path}`,opts);
        }else{throw this._handleSessionExpired();}
      }else{throw this._handleSessionExpired();}
    }
    const data=await res.json().catch(()=>({}));
    if(!res.ok){const err=new Error(data.message||`Request failed (${res.status})`);err.status=res.status;err.attempts_remaining=data.attempts_remaining;throw err;}
    return data;
  }
  async _tryRefresh(){
    if(this._refreshing)return this._refreshing;
    const rt=localStorage.getItem("kd_refresh_token");
    if(!rt)return false;
    this._refreshing=(async()=>{
      try{
        const res=await fetch(`${API_BASE}/auth/refresh`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({refreshToken:rt})});
        if(!res.ok)return false;
        const data=await res.json();
        this.setToken(data.token);
        if(data.refreshToken)localStorage.setItem("kd_refresh_token",data.refreshToken);
        return true;
      }catch{return false;}
      finally{this._refreshing=null;}
    })();
    return this._refreshing;
  }
  get(p){return this.req("GET",p);}
  post(p,b){return this.req("POST",p,b);}
  put(p,b){return this.req("PUT",p,b);}
  del(p,b){return this.req("DELETE",p,b);}
  upload(p,fd){return this.req("POST",p,fd,true);}

  // ─── Sprint 10B-P2: signed URL minting ───
  // Mints a short-lived signed URL for a resource. The JWT stays in the
  // Authorization header (via req()); the resulting URL carries an opaque
  // random token instead. Download tokens are single-use; preview tokens
  // are reusable within the 5-min TTL (so iframe PDF viewers survive
  // reload / print / open-in-new-tab).
  //
  // Usage:
  //   await api.signedUrl("document", docId)                     // download
  //   await api.signedUrl("document", docId, {purpose:"preview"})
  //   await api.signedUrl("version", docId, {versionNumber:3})
  //   await api.signedUrl("attachment", attId)
  //
  // Returns the URL string, or throws with err.status === 404 when the
  // resource is missing or the file isn't on disk (seeded data, etc.).
  async signedUrl(type, id, opts){
    const purpose = opts?.purpose === "preview" ? "preview" : "download";
    let path;
    if (type === "document") path = `/documents/${id}/signed-url`;
    else if (type === "version") path = `/versions/${id}/${opts?.versionNumber}/signed-url`;
    else if (type === "attachment") path = `/courrier/attachments/${id}/signed-url`;
    else throw new Error(`Unknown signed-url type: ${type}`);
    const res = await this.req("POST", path, {purpose});
    return res?.url;
  }
}
const api = new ApiClient();

// Sprint 10B-P2: Open file in browser using a short-lived signed URL.
// JWT never touches the URL. The generator endpoint verifies the file
// exists on disk before minting, so seeded/missing files fail cleanly
// with a toast rather than a broken tab.
async function openFileInBrowser(doc,toast,lang){
  if(!doc.file_path){toast(lang==="fr"?"Aucun fichier (données test)":"No file (seeded data)","warning");return false;}
  let url;
  try{
    url=await api.signedUrl("document",doc.id,{purpose:"download"});
  }catch(e){
    if(e?.status===404){toast(lang==="fr"?"Fichier introuvable sur le serveur (données test)":"File not found on server (seeded data)","warning");}
    else{toast(e?.message||(lang==="fr"?"Échec du téléchargement":"Download failed"),"error");}
    return false;
  }
  if(!url){toast(lang==="fr"?"Échec du téléchargement":"Download failed","error");return false;}
  window.open(url,"_blank");
  return true;
}

const AppContext = createContext();
function useApp(){return useContext(AppContext);}

// ─── UTILITIES ───
function formatBytes(b){if(!b)return"0 B";const k=1024,s=["B","KB","MB","GB"],i=Math.floor(Math.log(b)/Math.log(k));return parseFloat((b/Math.pow(k,i)).toFixed(1))+" "+s[i];}
function formatDocId(id){if(!id)return"—";return"#"+String(id).replace(/\B(?=(\d{3})+(?!\d))/g,",");}
function formatDate(iso,lang="fr"){if(!iso)return"—";const d=new Date(iso);if(isNaN(d.getTime()))return"—";const now=new Date(),diff=now-d;if(diff<60000)return lang==="fr"?"À l'instant":"Just now";if(diff<3600000)return Math.floor(diff/60000)+(lang==="fr"?" min":"m ago");if(diff<86400000)return Math.floor(diff/3600000)+(lang==="fr"?" h":"h ago");if(diff<604800000)return Math.floor(diff/86400000)+(lang==="fr"?" j":"d ago");return d.toLocaleDateString(lang==="fr"?"fr-FR":"en-US",{month:"short",day:"numeric",year:"numeric"});}
function formatFullDate(iso,lang="fr"){if(!iso)return"—";const d=new Date(iso);if(isNaN(d.getTime()))return"—";return d.toLocaleDateString(lang==="fr"?"fr-FR":"en-US",{month:"long",day:"numeric",year:"numeric"});}
// Sprint 11.4 follow-on: iManage-style compact metadata helpers for
// document-list rows ("v1 | #135,242,987 | EK | 5/14/2026 | 13.3 KB").
// formatNumericDate produces "5/14/2026" (en) / "14/05/2026" (fr) — the
// short numeric form iManage uses; trades verbosity for density.
// authorInitials reduces an uploader's full name to up to 4 uppercase
// initials (e.g., "Evariste Kabongo" → "EK", "Chris J. Hagstrom" → "CJH").
// iManage encodes user IDs ("HAGCJ"); we don't have such a code field
// in our schema so initials are the closest pilot equivalent.
function formatNumericDate(iso,lang="fr"){if(!iso)return"—";const d=new Date(iso);if(isNaN(d.getTime()))return"—";return d.toLocaleDateString(lang==="fr"?"fr-FR":"en-US");}
function authorInitials(name){if(!name)return"";return String(name).split(/\s+/).filter(Boolean).map(w=>w[0]).join("").slice(0,4).toUpperCase();}
// ─── File-type badge (iManage-style) ───
// Each document row shows a small colored chip with its extension to the
// left of the name, so the type reads at a glance: green XLS, blue DOC,
// red PDF, etc. Replaces the old generic single-color icon. Extension is
// taken from the filename when present (most accurate) and falls back to
// doc.type. Capped at 4 chars so XLSX/DOCX fit the chip.
function fileExtLabel(doc){
  const fromName=(doc?.name||"").includes(".")?doc.name.split(".").pop():"";
  const raw=String(fromName||doc?.type||"").toLowerCase();
  if(!raw)return doc?.type==="image"?"IMG":"FILE";
  return raw.slice(0,4).toUpperCase();
}
function fileTypeColor(label){
  const e=String(label||"").toLowerCase();
  if(e.startsWith("pdf"))return THEME.danger;
  if(["doc","docx","docm","rtf","txt","odt"].some(x=>e.startsWith(x)))return THEME.info;
  if(["xls","xlsx","xlsm","csv","ods"].some(x=>e.startsWith(x)))return THEME.success;
  if(["ppt","pptx","odp"].some(x=>e.startsWith(x)))return THEME.warning;
  if(["png","jpg","jpeg","gif","bmp","svg","webp","img"].some(x=>e.startsWith(x)))return "#9B6AD4";
  if(["zip","rar","tar"].some(x=>e.startsWith(x))||e==="7z"||e.startsWith("gz"))return THEME.textMuted;
  return THEME.accent;
}
function FileTypeBadge({doc,size=34,radius=7}){
  const label=fileExtLabel(doc);
  const color=fileTypeColor(label);
  const fs=label.length>=4?8:9.5;
  return(
    <div style={{width:size,height:size,borderRadius:radius,background:color,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:`0 1px 2px ${color}55`}}>
      <span style={{fontSize:fs,fontWeight:800,color:"#fff",letterSpacing:"0.02em",fontFamily:FONT_MONO}}>{label}</span>
    </div>
  );
}
function getActionColor(a){if(a.includes("upload")||a.includes("create"))return THEME.success;if(a.includes("delete")||a.includes("remove"))return THEME.danger;if(a.includes("login")||a.includes("view"))return THEME.info;if(a.includes("checkout")||a.includes("checkin"))return THEME.warning;if(a.includes("role")||a.includes("rename")||a.includes("perm"))return THEME.warning;return THEME.accent;}
function getActionIcon(a){if(a.includes("upload"))return lucide.Upload;if(a.includes("create"))return lucide.FolderPlus;if(a.includes("delete")||a.includes("remove"))return lucide.Trash2;if(a.includes("login"))return lucide.LogIn;if(a.includes("view"))return lucide.Eye;if(a.includes("download"))return lucide.Download;if(a.includes("checkout"))return lucide.Lock;if(a.includes("checkin"))return lucide.Unlock;if(a.includes("role")||a.includes("perm"))return lucide.Shield;if(a.includes("rename"))return lucide.Pencil;if(a.includes("invite"))return lucide.UserPlus;if(a.includes("move"))return lucide.FolderInput;if(a.includes("comment"))return lucide.MessageSquare;if(a.includes("version"))return lucide.GitBranch;if(a.includes("courrier"))return lucide.Mail;return lucide.Activity;}
// Sprint 11.x: richer per-action metadata for the dashboard activity feed.
// Each row gets its own dot color + natural-language verb (FR/EN) + a
// stable "entity kind" (document, folder, user, etc.) used to pick an
// icon. Falls back gracefully on unknown actions.
function getActionMeta(action, lang){
  const a=(action||"").toLowerCase();
  // Verb-by-verb mapping. Color tone + label_en + label_fr.
  // Tones: "create" (green), "update" (amber), "delete" (red), "info" (blue),
  // "view" (cyan-ish blue), "auth" (info), "security" (orange/accent).
  const table=[
    // ── Document lifecycle ──
    ["document.upload",        {tone:"create", en:"uploaded",                 fr:"a téléversé"}],
    ["document.create",        {tone:"create", en:"created",                  fr:"a créé"}],
    ["document.replace",       {tone:"update", en:"replaced",                 fr:"a remplacé"}],
    ["document.metadata_update",{tone:"update",en:"updated metadata for",    fr:"a mis à jour les métadonnées de"}],
    ["document.permissions_update",{tone:"security",en:"updated permissions on",fr:"a mis à jour les permissions de"}],
    ["document.security_mode_change",{tone:"security",en:"changed security mode on",fr:"a changé le mode de sécurité de"}],
    ["document.delete",        {tone:"delete", en:"deleted",                  fr:"a supprimé"}],
    ["document.move",          {tone:"update", en:"moved",                    fr:"a déplacé"}],
    // ── Checkout flow ──
    ["document.checkout",      {tone:"update", en:"checked out",              fr:"a réservé"}],
    ["document.checkin",       {tone:"create", en:"checked in",               fr:"a remis"}],
    ["checkout",               {tone:"update", en:"checked out",              fr:"a réservé"}],
    ["checkin",                {tone:"create", en:"checked in",               fr:"a remis"}],
    // ── Downloads / previews (signed-URL mint+consume) ──
    ["download.download.mint",   {tone:"info", en:"requested a download of",  fr:"a demandé le téléchargement de"}],
    ["download.download.consume",{tone:"info", en:"downloaded",               fr:"a téléchargé"}],
    ["download.preview.mint",    {tone:"view", en:"opened a preview of",     fr:"a ouvert un aperçu de"}],
    ["download.preview.consume", {tone:"view", en:"previewed",               fr:"a prévisualisé"}],
    // ── Folder ──
    ["folder.create",          {tone:"create", en:"created folder",           fr:"a créé le dossier"}],
    ["folder.update",          {tone:"update", en:"updated folder",           fr:"a mis à jour le dossier"}],
    ["folder.delete",          {tone:"delete", en:"deleted folder",           fr:"a supprimé le dossier"}],
    ["folder.permissions_update",{tone:"security",en:"updated folder permissions on",fr:"a mis à jour les permissions du dossier"}],
    // ── User ──
    ["user.invite",            {tone:"create", en:"invited",                  fr:"a invité"}],
    ["user.update",            {tone:"update", en:"updated",                  fr:"a mis à jour"}],
    ["user.permissions_update",{tone:"security",en:"updated permissions for", fr:"a mis à jour les permissions de"}],
    ["user.remove",            {tone:"delete", en:"removed",                  fr:"a retiré"}],
    ["user.login",             {tone:"auth",   en:"signed in",                fr:"s'est connecté"}],
    ["user.logout",            {tone:"auth",   en:"signed out",               fr:"s'est déconnecté"}],
    ["login",                  {tone:"auth",   en:"signed in",                fr:"s'est connecté"}],
    // ── MFA ──
    ["mfa.enroll",             {tone:"create", en:"enrolled in 2FA",          fr:"a activé la 2FA"}],
    ["mfa.disable",            {tone:"delete", en:"disabled 2FA",             fr:"a désactivé la 2FA"}],
    ["mfa.challenge.success",  {tone:"auth",   en:"passed 2FA",               fr:"a validé la 2FA"}],
    ["mfa.backup.used",        {tone:"update", en:"used a 2FA backup code",   fr:"a utilisé un code de secours 2FA"}],
    // ── Department ──
    ["department.create",      {tone:"create", en:"created department",       fr:"a créé le département"}],
    ["department.update",      {tone:"update", en:"updated department",       fr:"a mis à jour le département"}],
    ["department.delete",      {tone:"delete", en:"removed department",       fr:"a retiré le département"}],
    // ── Courrier ──
    ["courrier.create",        {tone:"create", en:"logged courrier",          fr:"a enregistré le courrier"}],
    ["courrier.update",        {tone:"update", en:"updated courrier",         fr:"a mis à jour le courrier"}],
    ["courrier.delete",        {tone:"delete", en:"removed courrier",         fr:"a retiré le courrier"}],
    // ── Admin ──
    ["admin.org_create",       {tone:"create", en:"provisioned a tenant",     fr:"a provisionné un tenant"}],
    ["admin.org_delete",       {tone:"delete", en:"offboarded a tenant",      fr:"a retiré un tenant"}],
    ["password.reset",         {tone:"security",en:"reset a password",        fr:"a réinitialisé un mot de passe"}],
  ];
  let row=table.find(([k])=>k===a);
  // Soft match on verb suffix if exact match missed (e.g., new action variants).
  if(!row){
    if(a.includes("delete")||a.includes("remove"))         row=["",{tone:"delete", en:"deleted",         fr:"a supprimé"}];
    else if(a.includes("create")||a.includes("upload"))     row=["",{tone:"create", en:"created",         fr:"a créé"}];
    else if(a.includes("login"))                            row=["",{tone:"auth",   en:"signed in",       fr:"s'est connecté"}];
    else if(a.includes("download"))                         row=["",{tone:"info",   en:"downloaded",      fr:"a téléchargé"}];
    else if(a.includes("view")||a.includes("preview"))      row=["",{tone:"view",   en:"viewed",          fr:"a consulté"}];
    else if(a.includes("permission")||a.includes("security")||a.includes("role"))
                                                            row=["",{tone:"security",en:"changed permissions on",fr:"a changé les permissions de"}];
    else                                                    row=["",{tone:"update", en:"updated",         fr:"a mis à jour"}];
  }
  const m=row[1];
  const colorMap={
    create:  {fg:THEME.success,  bg:THEME.successDim,  bd:THEME.successLine},
    update:  {fg:THEME.accent,   bg:THEME.accentDim,   bd:THEME.accentLine},
    delete:  {fg:THEME.danger,   bg:THEME.dangerDim,   bd:"rgba(224,122,122,0.3)"},
    info:    {fg:THEME.info,     bg:THEME.infoDim,     bd:THEME.infoLine},
    view:    {fg:"#7dd3fc",      bg:"rgba(125,211,252,0.12)", bd:"rgba(125,211,252,0.3)"},
    auth:    {fg:THEME.info,     bg:THEME.infoDim,     bd:THEME.infoLine},
    security:{fg:"#c084fc",      bg:"rgba(192,132,252,0.13)", bd:"rgba(192,132,252,0.35)"},
  };
  return {
    label: lang==="fr" ? m.fr : m.en,
    ...colorMap[m.tone],
    tone: m.tone,
  };
}

// Sprint 9: iManage-style time grouping
function getTimeGroup(iso,lang){if(!iso)return lang==="fr"?"Plus ancien":"Older";const d=new Date(iso),now=new Date(),today=new Date(now.getFullYear(),now.getMonth(),now.getDate()),docDay=new Date(d.getFullYear(),d.getMonth(),d.getDate()),diff=Math.floor((today-docDay)/86400000);if(diff===0)return lang==="fr"?"Aujourd'hui":"Today";if(diff>0&&diff<=6)return lang==="fr"?"Plus tôt cette semaine":"Earlier this week";if(diff>6&&diff<=13)return lang==="fr"?"Semaine dernière":"Last week";return lang==="fr"?"Plus ancien":"Older";}

const S={
  input:{width:"100%",padding:"10px 14px",background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:8,color:THEME.text,fontFamily:FONT,fontSize:14,outline:"none",transition:"border-color 0.2s"},
  btn:{display:"inline-flex",alignItems:"center",gap:8,padding:"10px 20px",borderRadius:8,border:"none",fontFamily:FONT,fontSize:14,fontWeight:600,cursor:"pointer",transition:"all 0.2s"},
  card:{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:12,padding:24},
};
function Avatar({name,size=36}){const ini=(name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();const hue=[...(name||"")].reduce((a,c)=>a+c.charCodeAt(0),0)%360;return(<div style={{width:size,height:size,borderRadius:"50%",background:`hsl(${hue},40%,30%)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.38,fontWeight:700,color:`hsl(${hue},50%,75%)`,flexShrink:0}}>{ini}</div>);}
function Badge({children,color=THEME.accent}){return(<span style={{display:"inline-block",padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,background:color+"1A",color}}>{children}</span>);}
function Spinner({size=20}){return(<motion.div animate={{rotate:360}} transition={{repeat:Infinity,duration:1,ease:"linear"}} style={{width:size,height:size,borderRadius:"50%",border:`2px solid ${THEME.border}`,borderTopColor:THEME.accent}}/>);}
function EmptyState({icon:Icon,title,subtitle}){return(<div style={{textAlign:"center",padding:"60px 20px"}}><Icon size={48} color={THEME.textDim} strokeWidth={1}/><div style={{marginTop:16,fontSize:16,fontWeight:600,color:THEME.textMuted}}>{title}</div>{subtitle&&<div style={{marginTop:6,fontSize:13,color:THEME.textDim}}>{subtitle}</div>}</div>);}
function Toast({message,type="info",onClose}){const colors={success:THEME.success,error:THEME.danger,info:THEME.accent,warning:THEME.warning};const c=colors[type]||THEME.accent;return(<motion.div initial={{opacity:0,y:30,scale:0.95}} animate={{opacity:1,y:0,scale:1}} exit={{opacity:0,y:20,scale:0.95}} style={{position:"fixed",bottom:24,right:24,zIndex:9999,background:THEME.surfaceRaised,border:`1px solid ${c}40`,borderRadius:12,padding:"14px 20px",display:"flex",alignItems:"center",gap:12,boxShadow:`0 8px 32px rgba(0,0,0,0.4)`,maxWidth:400}}><div style={{width:4,height:28,borderRadius:2,background:c,flexShrink:0}}/><span style={{fontSize:13,color:THEME.text,flex:1}}>{message}</span><lucide.X size={14} color={THEME.textDim} style={{cursor:"pointer",flexShrink:0}} onClick={onClose}/></motion.div>);}
function LangToggle({lang,setLang}){return(<button onClick={()=>setLang(lang==="fr"?"en":"fr")} style={{...S.btn,padding:"5px 10px",fontSize:11,fontWeight:700,background:THEME.surfaceRaised,color:THEME.accent,border:`1px solid ${THEME.border}`,letterSpacing:0.5,textTransform:"uppercase",borderRadius:6}}><lucide.Globe size={13}/>{lang==="fr"?"EN":"FR"}</button>);}

// ─── Sprint 6.7: Checkout UI Components ───

// Small lock badge for document list rows
function CheckoutBadge({doc,lang}){
  if(!doc?.checked_out_by_name)return null;
  return(<span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"2px 8px",borderRadius:12,fontSize:10,fontWeight:600,background:THEME.warningDim,color:THEME.warning,whiteSpace:"nowrap",flexShrink:0}}><lucide.Lock size={10}/>{doc.checked_out_by_name}</span>);
}

// Sprint 10C H10 Phase 2 — document-row badge. Renders only when the
// doc has a non-inherit security mode, so 99% of docs get no extra
// chrome. Tooltip tells the user what the badge means.
function SecurityBadge({doc,lang}){
  const mode=doc?.security_mode;
  if(!mode||mode==="inherit")return null;
  const isRestrict=mode==="restrict";
  const color=isRestrict?THEME.warning:THEME.accent;
  const bg=isRestrict?THEME.warningDim:THEME.accentDim;
  const label=isRestrict
    ?(lang==="fr"?"Accès restreint":"Restricted access")
    :(lang==="fr"?"Accès personnalisé":"Custom access");
  return(<span title={label} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"2px 6px",borderRadius:10,fontSize:10,fontWeight:600,background:bg,color:color,whiteSpace:"nowrap",flexShrink:0}}><lucide.Shield size={9}/>{label}</span>);
}

// iManage-style conflict dialog
function CheckoutConflictDialog({doc,checkoutStatus,lang,onOpenReadOnly,onCancel}){
  if(!checkoutStatus)return null;
  return(
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)onCancel();}}>
      <motion.div initial={{opacity:0,scale:0.92,y:16}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.92}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:480,overflow:"hidden",boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
        <div style={{padding:"22px 24px 16px",display:"flex",alignItems:"flex-start",gap:14}}>
          <div style={{width:40,height:40,borderRadius:10,background:THEME.infoDim,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}><lucide.Info size={20} color={THEME.info}/></div>
          <div style={{flex:1}}>
            <div style={{fontSize:15,fontWeight:600,color:THEME.text,marginBottom:8}}>KlassDoc</div>
            <div style={{fontSize:13,color:THEME.textMuted,lineHeight:1.6}}>
              {lang==="fr"?"Le document":"Document"} <span style={{fontWeight:600,color:THEME.text}}>'{doc?.name}'</span> {T(lang,"docCheckedOutDialog")} <span style={{fontWeight:600,color:THEME.warning}}>{checkoutStatus.user_name}</span>.
              <br/>{T(lang,"openReadOnlyQ")}
            </div>
          </div>
        </div>
        <div style={{padding:"12px 24px 20px",display:"flex",gap:10,justifyContent:"flex-end"}}>
          <button onClick={onOpenReadOnly} style={{...S.btn,padding:"9px 20px",fontSize:13,background:THEME.accent,color:"#fff"}}>{T(lang,"openReadOnly")}</button>
          <button onClick={onCancel} style={{...S.btn,padding:"9px 20px",fontSize:13,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}>{T(lang,"cancel")}</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// "Save changes back" modal — drag & drop or file picker.
// P7: Default is in-place save (/documents/:id/replace-file) for iManage parity.
// "Save as new version…" link opens NewVersionModal for explicit version bumps.
// NOTE: this component is currently unreferenced in App.jsx but kept wired
// correctly so any future re-integration gets the right defaults.
function CheckoutSaveModal({doc,lang,onSaved,onClose}){
  const [dragOver,setDragOver]=useState(false);const [saving,setSaving]=useState(false);const fileRef=useRef();
  const [showNewVersion,setShowNewVersion]=useState(false);
  async function saveInPlace(file){
    if(!file)return;setSaving(true);
    try{
      const fd=new FormData();fd.append("file",file);
      // P7: in-place by default — no version bump, audit as document.replace
      await api.upload(`/documents/${doc.id}/replace-file`,fd);
      onSaved?.();
    }catch(e){console.error("Save back error:",e);}
    setSaving(false);
  }
  return(
    <>
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:460,padding:28,boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <div style={{width:36,height:36,borderRadius:9,background:THEME.accentDim,display:"flex",alignItems:"center",justifyContent:"center"}}><lucide.Save size={18} color={THEME.accent}/></div>
          <div><div style={{fontSize:15,fontWeight:600,color:THEME.text}}>{T(lang,"saveChanges")}</div><div style={{fontSize:12,color:THEME.textDim,marginTop:2}}>{doc?.name}</div></div>
        </div>
        <motion.div onDragOver={e=>{e.preventDefault();setDragOver(true);}} onDragLeave={()=>setDragOver(false)} onDrop={e=>{e.preventDefault();setDragOver(false);saveInPlace(e.dataTransfer.files[0]);}}
          animate={{borderColor:dragOver?THEME.accent:THEME.border,background:dragOver?THEME.accentDim:"transparent"}}
          style={{border:`2px dashed ${THEME.border}`,borderRadius:12,padding:"36px 20px",textAlign:"center",cursor:"pointer",marginBottom:12}} onClick={()=>fileRef.current?.click()}>
          {saving?<div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10}}><Spinner size={20}/><span style={{color:THEME.textMuted,fontSize:13}}>{lang==="fr"?"Enregistrement...":"Saving..."}</span></div>
          :<><lucide.Upload size={28} color={dragOver?THEME.accent:THEME.textDim} strokeWidth={1.2}/><div style={{marginTop:10,fontSize:13,color:dragOver?THEME.accent:THEME.textMuted}}>{T(lang,"dropToSave")}</div><div style={{marginTop:4,fontSize:11,color:THEME.textDim}}>{lang==="fr"?"ou cliquez pour parcourir":"or click to browse"}</div></>}
          <input ref={fileRef} type="file" hidden onChange={e=>saveInPlace(e.target.files[0])}/>
        </motion.div>
        {/* P7: escape hatch — user can explicitly opt into a new version */}
        <div style={{textAlign:"center",marginBottom:12}}>
          <button onClick={()=>setShowNewVersion(true)} disabled={saving}
            style={{background:"transparent",border:"none",fontSize:12,color:THEME.info,cursor:"pointer",padding:"4px 8px",display:"inline-flex",alignItems:"center",gap:6}}>
            <lucide.GitBranch size={12}/>{T(lang,"saveAsNewVersion")}
          </button>
        </div>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <button onClick={onClose} style={{...S.btn,padding:"8px 18px",fontSize:13,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}>{T(lang,"cancel")}</button>
        </div>
      </motion.div>
    </motion.div>
    <AnimatePresence>{showNewVersion&&(
      <NewVersionModal doc={doc} lang={lang}
        onClose={()=>setShowNewVersion(false)}
        onUploaded={()=>{setShowNewVersion(false);onSaved?.();}}
        onError={e=>console.error("New version upload error:",e)}/>
    )}</AnimatePresence>
    </>
  );
}

// ─── P7: Split-button "Save Changes" — iManage parity ───
// Primary click → in-place save (overwrites current version via /documents/:id/replace-file).
// Dropdown arrow → "Save as new version…" (opens NewVersionModal → /versions/:id/upload).
// Both paths auto-checkin on success.
function SaveChangesSplitButton({lang,disabled,busy,onSaveInPlace,onSaveAsNewVersion,size="sm"}){
  const [open,setOpen]=useState(false);
  const wrapRef=useRef(null);
  useEffect(()=>{
    if(!open)return;
    function onDoc(e){if(wrapRef.current&&!wrapRef.current.contains(e.target))setOpen(false);}
    function onKey(e){if(e.key==="Escape")setOpen(false);}
    document.addEventListener("mousedown",onDoc);
    document.addEventListener("keydown",onKey);
    return()=>{document.removeEventListener("mousedown",onDoc);document.removeEventListener("keydown",onKey);};
  },[open]);
  // Two sizes: "sm" for CheckedOutBar (tight, 11px font), "md" for CheckedOutPage (12px)
  // P7 polish: chevron widened + visible separator + stronger icon so the dropdown
  // is discoverable on first look. 'More save options' tooltip on hover.
  const isSm=size==="sm";
  const padY=isSm?5:6,padXPrim=isSm?12:14,padXArrow=isSm?10:12,fs=isSm?11:12,iconSz=isSm?11:12,chevronSz=isSm?14:15;
  return(
    <div ref={wrapRef} style={{position:"relative",display:"inline-flex"}}>
      <button
        onClick={()=>{if(!disabled&&!busy)onSaveInPlace?.();}}
        disabled={disabled||busy}
        title={T(lang,"saveChanges")}
        style={{...S.btn,padding:`${padY}px ${padXPrim}px`,fontSize:fs,background:THEME.accent,color:"#fff",borderRadius:"6px 0 0 6px"}}>
        {busy?<Spinner size={iconSz}/>:<lucide.Upload size={iconSz}/>} {T(lang,"saveChanges")}
      </button>
      {/* Visible separator between the two halves — makes the split-button shape obvious */}
      <div style={{width:1,background:"rgba(0,0,0,0.25)",opacity:disabled||busy?0.3:1}} aria-hidden="true"/>
      <button
        onClick={()=>{if(!disabled&&!busy)setOpen(o=>!o);}}
        disabled={disabled||busy}
        title={lang==="fr"?"Plus d'options d'enregistrement":"More save options"}
        aria-label={T(lang,"saveAsNewVersion")}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{...S.btn,padding:`${padY}px ${padXArrow}px`,fontSize:fs,background:open?THEME.accentDark||THEME.accent:THEME.accent,color:"#fff",borderRadius:"0 6px 6px 0",filter:open?"brightness(0.9)":"none"}}>
        <lucide.ChevronDown size={chevronSz} strokeWidth={2.5}/>
      </button>
      <AnimatePresence>{open&&(
        <motion.div
          initial={{opacity:0,y:-4}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-4}}
          role="menu"
          style={{position:"absolute",top:"calc(100% + 4px)",right:0,minWidth:240,background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.4)",zIndex:100,overflow:"hidden"}}>
          <button
            onClick={()=>{setOpen(false);onSaveAsNewVersion?.();}}
            role="menuitem"
            style={{width:"100%",padding:"10px 14px",display:"flex",alignItems:"center",gap:10,fontSize:12,color:THEME.text,background:"transparent",border:"none",cursor:"pointer",textAlign:"left"}}
            onMouseEnter={e=>e.currentTarget.style.background=THEME.surfaceHover}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <lucide.GitBranch size={14} color={THEME.info}/>
            <span>{T(lang,"saveAsNewVersion")}</span>
          </button>
        </motion.div>
      )}</AnimatePresence>
    </div>
  );
}

// ─── P7: "Save as new version" modal — prompts for change notes, then uploads ───
// Fires POST /versions/:id/upload with file + change_notes, then triggers onUploaded
// which is expected to also auto-checkin and refresh state (parallels the in-place path).
function NewVersionModal({doc,lang,onClose,onUploaded,onError}){
  const [changeNotes,setChangeNotes]=useState("");
  const [file,setFile]=useState(null);
  const [dragOver,setDragOver]=useState(false);
  const [uploading,setUploading]=useState(false);
  const fileRef=useRef();
  async function doUpload(){
    if(!file||uploading)return;
    setUploading(true);
    try{
      const fd=new FormData();
      fd.append("file",file);
      fd.append("change_notes",changeNotes||"");
      await api.upload(`/versions/${doc.id}/upload`,fd);
      onUploaded?.();
    }catch(e){onError?.(e);setUploading(false);}
  }
  return(
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      style={{position:"fixed",inset:0,zIndex:2100,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}
      onClick={e=>{if(e.target===e.currentTarget&&!uploading)onClose();}}>
      <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}}
        style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:480,padding:28,boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <div style={{width:36,height:36,borderRadius:9,background:THEME.infoDim,display:"flex",alignItems:"center",justifyContent:"center"}}><lucide.GitBranch size={18} color={THEME.info}/></div>
          <div><div style={{fontSize:15,fontWeight:600,color:THEME.text}}>{T(lang,"saveAsNewVersion")}</div><div style={{fontSize:12,color:THEME.textDim,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:400}}>{doc?.name}</div></div>
        </div>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:12,color:THEME.textMuted,marginBottom:6}}>{T(lang,"changeNotes")} ({lang==="fr"?"optionnel":"optional"})</div>
          <textarea
            value={changeNotes}
            onChange={e=>setChangeNotes(e.target.value)}
            disabled={uploading}
            rows={3}
            placeholder={lang==="fr"?"ex. v2 pour revue partenaire":"e.g. v2 for partner review"}
            style={{...S.input,width:"100%",padding:"8px 10px",fontSize:12,resize:"vertical",fontFamily:"inherit"}}/>
        </div>
        <motion.div
          onDragOver={e=>{e.preventDefault();setDragOver(true);}}
          onDragLeave={()=>setDragOver(false)}
          onDrop={e=>{e.preventDefault();setDragOver(false);if(e.dataTransfer.files[0])setFile(e.dataTransfer.files[0]);}}
          animate={{borderColor:dragOver?THEME.accent:THEME.border,background:dragOver?THEME.accentDim:"transparent"}}
          style={{border:`2px dashed ${THEME.border}`,borderRadius:10,padding:"22px 16px",textAlign:"center",cursor:uploading?"not-allowed":"pointer",marginBottom:16}}
          onClick={()=>{if(!uploading)fileRef.current?.click();}}>
          {file?(
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
              <lucide.FileText size={18} color={THEME.accent}/>
              <div style={{textAlign:"left"}}>
                <div style={{fontSize:12,color:THEME.text,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:300}}>{file.name}</div>
                <div style={{fontSize:11,color:THEME.textDim}}>{formatBytes(file.size)}</div>
              </div>
            </div>
          ):(
            <>
              <lucide.Upload size={22} color={dragOver?THEME.accent:THEME.textDim} strokeWidth={1.2}/>
              <div style={{marginTop:8,fontSize:12,color:dragOver?THEME.accent:THEME.textMuted}}>{lang==="fr"?"Déposez le fichier modifié, ou cliquez pour parcourir":"Drop the edited file, or click to browse"}</div>
            </>
          )}
          <input ref={fileRef} type="file" hidden onChange={e=>{if(e.target.files[0])setFile(e.target.files[0]);}}/>
        </motion.div>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <button onClick={onClose} disabled={uploading}
            style={{...S.btn,padding:"8px 18px",fontSize:13,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}>{T(lang,"cancel")}</button>
          <button onClick={doUpload} disabled={!file||uploading}
            style={{...S.btn,padding:"8px 20px",fontSize:13,background:file?THEME.info:THEME.surface,color:file?"#fff":THEME.textDim,opacity:!file||uploading?0.6:1}}>
            {uploading?<><Spinner size={12}/> {lang==="fr"?"Téléversement…":"Uploading…"}</>:<><lucide.Upload size={13}/> {T(lang,"upload")}</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Persistent bar showing user's active checkouts — inline save via drag-drop or file picker
function CheckedOutBar({lang,onCheckin,onShowDetails,onRefresh}){
  const {toast}=useApp();
  const [myCheckouts,setMyCheckouts]=useState([]);
  const [dragOverId,setDragOverId]=useState(null);
  const [savingId,setSavingId]=useState(null);
  // P7: when user picks "Save as new version…", we stash the checkout here and open the modal
  const [newVersionFor,setNewVersionFor]=useState(null); // checkout row or null
  const fileRefs=useRef({});
  const load=useCallback(()=>{api.get("/checkouts/my").then(r=>setMyCheckouts(r.data||[])).catch(()=>{});},[]);
  useEffect(()=>{load();const iv=setInterval(load,15000);return()=>clearInterval(iv);},[load]);

  // P7: saveFile now defaults to in-place (iManage parity). saveMode='new-version' path
  // is used by the NewVersionModal (it handles its own upload internally; this function
  // is invoked from the modal's onUploaded callback with no file — just to do the
  // auto-checkin/refresh common tail).
  async function saveFile(co,file,saveMode="in-place"){
    if(savingId)return;
    if(saveMode==="in-place"&&!file)return;
    setSavingId(co.document_id);
    try{
      if(saveMode==="in-place"){
        // Replace the file on the document (overwrites file_path on disk + DB, no version bump)
        const fd=new FormData();fd.append("file",file);
        await api.upload(`/documents/${co.document_id}/replace-file`,fd);
      }
      // Auto-checkin after save (common tail for both paths)
      try{await api.del(`/checkouts/${co.document_id}`);}catch{}
      toast(T(lang,"savedBack"),"success");
      // Force clear from local state immediately
      setMyCheckouts(prev=>prev.filter(c=>c.document_id!==co.document_id));
      onRefresh?.();
    }catch(e){toast(e.message||"Save failed","error");}
    setSavingId(null);
  }

  async function handleCheckinLocal(co){
    try{
      await api.del(`/checkouts/${co.document_id}`);
      toast(T(lang,"checkedIn"),"success");
      setMyCheckouts(prev=>prev.filter(c=>c.document_id!==co.document_id));
      onCheckin?.(co);
    }catch(e){toast(e.message,"error");}
  }

  if(myCheckouts.length===0)return null;
  return(
    <>
    <motion.div initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}} style={{marginBottom:20,background:THEME.warningDim,border:`1px solid ${THEME.warning}25`,borderRadius:12,overflow:"hidden"}}>
      <div style={{padding:"10px 18px",display:"flex",alignItems:"center",gap:8,borderBottom:`1px solid ${THEME.warning}15`}}>
        <lucide.Lock size={14} color={THEME.warning}/>
        <span style={{fontSize:12,fontWeight:600,color:THEME.warning}}>{T(lang,"myCheckedOut")} ({myCheckouts.length})</span>
      </div>
      {myCheckouts.map(co=>(
        <div key={co.id}>
          <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 18px",borderBottom:`1px solid ${THEME.warning}10`}}>
            <div style={{width:30,height:30,borderRadius:7,background:THEME.accentDim,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{savingId===co.document_id?<Spinner size={14}/>:<lucide.FileText size={14} color={THEME.accent}/>}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:500,color:THEME.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{co.document_name}</div>
              <div style={{fontSize:11,color:THEME.textDim}}>{co.folder_name||"Root"} · {formatBytes(co.document_size)}</div>
            </div>
            {/* P7: split-button — primary=in-place, dropdown=new version */}
            <SaveChangesSplitButton
              lang={lang}
              size="sm"
              busy={savingId===co.document_id}
              disabled={!!savingId&&savingId!==co.document_id}
              onSaveInPlace={()=>fileRefs.current[co.document_id]?.click()}
              onSaveAsNewVersion={()=>setNewVersionFor(co)}/>
            <input ref={el=>{fileRefs.current[co.document_id]=el;}} type="file" hidden onChange={e=>{if(e.target.files[0])saveFile(co,e.target.files[0],"in-place");e.target.value="";}}/>
            <button onClick={()=>onShowDetails?.(co)} style={{...S.btn,padding:"5px 10px",fontSize:11,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}><lucide.Eye size={11}/></button>
            <button onClick={()=>handleCheckinLocal(co)} disabled={!!savingId} style={{...S.btn,padding:"5px 12px",fontSize:11,background:THEME.surface,color:THEME.textDim,border:`1px solid ${THEME.border}`}}><lucide.Unlock size={11}/> {T(lang,"checkInNoChanges")}</button>
          </div>
          {/* Inline drop zone — in-place save only (matches primary button) */}
          <div
            onDragOver={e=>{e.preventDefault();e.stopPropagation();setDragOverId(co.document_id);}}
            onDragLeave={e=>{e.stopPropagation();setDragOverId(null);}}
            onDrop={e=>{e.preventDefault();e.stopPropagation();setDragOverId(null);if(e.dataTransfer.files[0])saveFile(co,e.dataTransfer.files[0],"in-place");}}
            style={{margin:"0 18px 10px",padding:dragOverId===co.document_id?"16px 14px":"10px 14px",borderRadius:8,border:`1.5px dashed ${dragOverId===co.document_id?THEME.accent:THEME.warning}40`,background:dragOverId===co.document_id?THEME.accentDim:"transparent",textAlign:"center",transition:"all 0.2s",cursor:"pointer"}}
            onClick={()=>{fileRefs.current[co.document_id]?.click();}}>
            <div style={{fontSize:11,color:dragOverId===co.document_id?THEME.accent:THEME.textDim,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
              <lucide.Upload size={12}/>{T(lang,"dropToSave")}
            </div>
          </div>
        </div>
      ))}
    </motion.div>
    {/* P7: new-version modal — when user selects "Save as new version…" from split button */}
    <AnimatePresence>{newVersionFor&&(
      <NewVersionModal
        doc={{id:newVersionFor.document_id,name:newVersionFor.document_name}}
        lang={lang}
        onClose={()=>setNewVersionFor(null)}
        onUploaded={()=>{const co=newVersionFor;setNewVersionFor(null);saveFile(co,null,"new-version");}}
        onError={e=>toast(e.message||"Upload failed","error")}/>
    )}</AnimatePresence>
    </>
  );
}

// ─── Tabs component ───
function TabBar({tabs,active,onChange}){return(<div style={{display:"flex",gap:4,marginBottom:24,borderBottom:`1px solid ${THEME.border}`,paddingBottom:0}}>{tabs.map(t=>{const isA=active===t.id;return(<button key={t.id} onClick={()=>onChange(t.id)} style={{...S.btn,padding:"10px 20px",borderRadius:"8px 8px 0 0",fontSize:13,background:isA?THEME.surfaceRaised:"transparent",color:isA?THEME.accent:THEME.textDim,borderBottom:isA?`2px solid ${THEME.accent}`:"2px solid transparent"}}>{t.icon&&<t.icon size={15}/>}{t.label}</button>);})}</div>);}

// ─── LOGIN ───
function LoginPage({onLogin}){
  const [email,setEmail]=useState("eva@klassdoc.com");
  const [password,setPassword]=useState("");
  const [showPw,setShowPw]=useState(false);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState(null);
  const [lockout,setLockout]=useState(null);
  const [attemptsLeft,setAttemptsLeft]=useState(null);
  // Sprint 10C Session D1 — forgot-password mode. Keeps the same screen
  // (branding + language state) and just swaps the form body. Avoids
  // building a whole separate page for a single-field form.
  // Sprint 10C D2 — added 'mfa' mode: after a successful password step
  // against an MFA-enabled account, /auth/login returns a challenge
  // token instead of a JWT. We switch to the challenge screen and
  // collect the TOTP or backup code.
  const [mode,setMode]=useState("login"); // 'login' | 'forgot' | 'mfa'
  const [forgotSent,setForgotSent]=useState(false);
  const [mfaChallengeToken,setMfaChallengeToken]=useState(null);
  const [mfaCode,setMfaCode]=useState("");
  const [mfaIsBackup,setMfaIsBackup]=useState(false);
  const [mfaHasBackup,setMfaHasBackup]=useState(false);
  // Sprint 10C — Read persisted language so users who selected EN inside the app
  // don't get bounced back to FR on the login screen after session expiry or sign-out.
  // Mirror AppShell's init + persist pattern (see function AppShell below).
  const [lang,setLang]=useState(()=>localStorage.getItem("af_lang")||"fr");
  useEffect(()=>{localStorage.setItem("af_lang",lang);},[lang]);
  async function handleSubmit(e){
    e.preventDefault();setLoading(true);setError(null);setLockout(null);setAttemptsLeft(null);
    try{
      const res=await api.post("/auth/login",{email,password});
      // Sprint 10C D2: MFA gate. If backend says mfa_required, switch
      // modes and collect the TOTP code before finishing the login.
      if(res?.mfa_required&&res.challenge_token){
        setMfaChallengeToken(res.challenge_token);
        setMfaHasBackup(!!res.has_backup_codes);
        setMfaIsBackup(false);
        setMfaCode("");
        setMode("mfa");
        setLoading(false);
        return;
      }
      api.setToken(res.token);
      if(res.refreshToken)localStorage.setItem("kd_refresh_token",res.refreshToken);
      onLogin(res.user);
    }catch(err){
      const msg=err.message||T(lang,"error");
      if(msg.includes("locked")||msg.includes("verrouillé")){setLockout(msg);}
      else{setError(msg);if(err.attempts_remaining!=null)setAttemptsLeft(err.attempts_remaining);}
    }finally{setLoading(false);}
  }
  async function handleMfaSubmit(e){
    e.preventDefault();
    if(!mfaCode.trim()||loading)return;
    setLoading(true);setError(null);
    try{
      const res=await api.post("/auth/mfa/challenge",{
        challenge_token:mfaChallengeToken,
        code:mfaCode.trim(),
        is_backup:mfaIsBackup,
      });
      api.setToken(res.token);
      if(res.refreshToken)localStorage.setItem("kd_refresh_token",res.refreshToken);
      onLogin(res.user);
    }catch(err){
      setError(err?.message||T(lang,"mfaInvalidCode"));
    }finally{setLoading(false);}
  }
  // Sprint 10C D1 — password-reset request. Backend always returns 200
  // regardless of whether the email exists (enumeration guard), so we
  // always show the same generic success state.
  async function handleForgotSubmit(e){
    e.preventDefault();
    if(!email.trim()||loading)return;
    setLoading(true);setError(null);
    try{
      await api.post("/auth/password-reset/request",{email:email.trim(),lang});
      setForgotSent(true);
    }catch(err){
      // Even on error, surface the generic success — never leak
      // account state through error messages on this endpoint.
      setForgotSent(true);
    }finally{setLoading(false);}
  }
  function switchToForgot(){setMode("forgot");setError(null);setLockout(null);setForgotSent(false);}
  function switchToLogin(){setMode("login");setError(null);setForgotSent(false);}
  function switchToRequest(){setMode("request");setError(null);setLockout(null);}
  return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:THEME.bg,fontFamily:FONT,backgroundImage:`radial-gradient(ellipse 80% 60% at 50% 0%,${THEME.accentGlow} 0%,transparent 60%)`}}>
      <div style={{position:"fixed",top:20,right:20}}><LangToggle lang={lang} setLang={setLang}/></div>
      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} style={{width:400,padding:40}}>
        {/* Sprint 10C D1 follow-up: centered logo block rebuilt as a flex
            layout. Previous SVG had left-aligned content inside a 280-unit
            viewBox, so even with the parent flex centered the visible
            block appeared shifted left. This version centers the whole
            icon + wordmark group as one unit. */}
        <div style={{textAlign:"center",marginBottom:48}}>
          <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:14,marginBottom:14}}>
            {/* Folder icon with green checkmark — extracted from the old SVG */}
            <svg width="52" height="52" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" style={{flexShrink:0}}>
              <rect x="8" y="0" width="32" height="40" rx="4" fill="#2D2D5F" opacity="0.25"/>
              <rect x="2" y="6" width="32" height="40" rx="4" fill="#2D2D5F"/>
              <line x1="10" y1="20" x2="26" y2="20" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
              <line x1="10" y1="26" x2="22" y2="26" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
              <line x1="10" y1="32" x2="18" y2="32" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
              <circle cx="30" cy="12" r="10" fill="#00A8E1"/>
              <polyline points="25,12 28,15 35,8" stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-start",lineHeight:1.1}}>
              <div style={{fontSize:32,fontWeight:700,letterSpacing:"-0.5px"}}>
                <span style={{color:THEME.accent}}>Klass</span><span style={{color:THEME.text}}>Doc</span>
              </div>
              <div style={{fontSize:9.5,color:THEME.textMuted,letterSpacing:"2.5px",fontWeight:500,marginTop:5}}>DOCUMENT MANAGEMENT</div>
              <div style={{fontSize:9,color:THEME.textDim,letterSpacing:"0.8px",marginTop:3}}>by <span style={{fontWeight:700,color:"#8B9AB0"}}>SEVEN CO SUCCESS</span></div>
            </div>
          </div>
          <p style={{fontSize:13,color:THEME.textDim,marginTop:8}}>{T(lang,"console")}</p>
        </div>
        {/* Sprint 10C post-F: mode dispatch cleaned up from a ternary
            (which silently rendered forgot form on top of mfa form) to
            explicit mode checks. Adds 'request' mode for the sales-
            assisted signup flow. */}
        {mode==="login" && (
          <form onSubmit={handleSubmit}>
            <div style={{marginBottom:20}}><label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase"}}>{T(lang,"email")}</label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} style={{...S.input,background:THEME.surfaceRaised}} onFocus={e=>e.target.style.borderColor=THEME.accent} onBlur={e=>e.target.style.borderColor=THEME.border}/></div>
            <div style={{marginBottom:10}}><label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase"}}>{T(lang,"password")}</label><div style={{position:"relative"}}><input type={showPw?"text":"password"} value={password} onChange={e=>setPassword(e.target.value)} style={{...S.input,background:THEME.surfaceRaised,paddingRight:40}} onFocus={e=>e.target.style.borderColor=THEME.accent} onBlur={e=>e.target.style.borderColor=THEME.border}/><button type="button" onClick={()=>setShowPw(!showPw)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",border:"none",background:"transparent",cursor:"pointer",padding:4}}>{showPw?<lucide.EyeOff size={16} color={THEME.textDim}/>:<lucide.Eye size={16} color={THEME.textDim}/>}</button></div></div>
            <div style={{display:"flex",justifyContent:"center",marginBottom:20}}>
              <button type="button" onClick={switchToForgot} style={{background:"transparent",border:"none",padding:0,color:THEME.accent,fontSize:12,cursor:"pointer",fontFamily:"inherit",textDecoration:"none"}} onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"} onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>{T(lang,"forgotPassword")}</button>
            </div>
            {lockout&&<div style={{marginBottom:16,padding:"10px 14px",borderRadius:8,background:THEME.warningDim,color:THEME.warning,fontSize:13,display:"flex",alignItems:"center",gap:8}}><lucide.Lock size={14}/>{lockout}</div>}
            {error&&<div style={{marginBottom:16,padding:"10px 14px",borderRadius:8,background:THEME.dangerDim,color:THEME.danger,fontSize:13}}>{error}{attemptsLeft!=null&&<span style={{display:"block",fontSize:11,marginTop:4,opacity:0.8}}>{lang==="fr"?`${attemptsLeft} tentative(s) restante(s)`:`${attemptsLeft} attempt(s) remaining`}</span>}</div>}
            <button type="submit" disabled={loading||!!lockout} style={{...S.btn,width:"100%",justifyContent:"center",background:`linear-gradient(135deg,${THEME.accent},${THEME.accentHover})`,color:"#fff",padding:"12px 20px",fontSize:15,opacity:loading||lockout?0.7:1,boxShadow:`0 4px 16px ${THEME.accent}30`}}>{loading?<Spinner size={18}/>:T(lang,"signIn")}</button>
            {/* Request-an-account prompt — sales-assisted signup path */}
            <div style={{textAlign:"center",marginTop:18,fontSize:12,color:THEME.textDim}}>
              {T(lang,"noAccountPrompt")}{" "}
              <button type="button" onClick={switchToRequest} style={{background:"transparent",border:"none",padding:0,color:THEME.accent,fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:600}} onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"} onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>{T(lang,"requestAccount")}</button>
            </div>
          </form>
        )}
        {mode==="forgot" && (
          <form onSubmit={handleForgotSubmit}>
            <h2 style={{fontSize:17,fontWeight:700,color:THEME.text,margin:"0 0 10px 0"}}>{T(lang,"resetPassword")}</h2>
            <p style={{fontSize:12,color:THEME.textDim,margin:"0 0 20px 0",lineHeight:1.55}}>{T(lang,"resetEmailHint")}</p>
            {!forgotSent ? (
              <>
                <div style={{marginBottom:20}}>
                  <label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase"}}>{T(lang,"resetEmailLabel")}</label>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)} style={{...S.input,background:THEME.surfaceRaised}} autoFocus onFocus={e=>e.target.style.borderColor=THEME.accent} onBlur={e=>e.target.style.borderColor=THEME.border}/>
                </div>
                <button type="submit" disabled={loading||!email.trim()} style={{...S.btn,width:"100%",justifyContent:"center",background:`linear-gradient(135deg,${THEME.accent},${THEME.accentHover})`,color:"#fff",padding:"12px 20px",fontSize:15,opacity:(loading||!email.trim())?0.7:1,boxShadow:`0 4px 16px ${THEME.accent}30`,marginBottom:14}}>
                  {loading?<Spinner size={18}/>:<><lucide.Mail size={16}/>{T(lang,"sendResetLink")}</>}
                </button>
              </>
            ) : (
              <div style={{padding:"14px 16px",borderRadius:10,background:THEME.successDim,border:`1px solid ${THEME.success}40`,color:THEME.text,fontSize:13,lineHeight:1.55,marginBottom:14,display:"flex",gap:10}}>
                <lucide.MailCheck size={18} color={THEME.success} style={{flexShrink:0,marginTop:1}}/>
                <span>{T(lang,"resetEmailSent")}</span>
              </div>
            )}
            <button type="button" onClick={switchToLogin} style={{width:"100%",justifyContent:"center",display:"flex",alignItems:"center",gap:6,background:"transparent",border:"none",padding:"10px",color:THEME.textDim,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
              <lucide.ArrowLeft size={14}/>{T(lang,"backToLogin")}
            </button>
          </form>
        )}
        {mode==="request" && (
          <div>
            <div style={{textAlign:"center",marginBottom:22}}>
              <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:52,height:52,borderRadius:14,background:`linear-gradient(135deg,${THEME.accent}33,${THEME.accent}11)`,border:`1px solid ${THEME.accent}40`,marginBottom:14}}>
                <lucide.Building2 size={26} color={THEME.accent}/>
              </div>
              <h2 style={{fontSize:17,fontWeight:700,color:THEME.text,margin:"0 0 8px 0"}}>{T(lang,"requestAccountTitle")}</h2>
              <p style={{fontSize:12,color:THEME.textDim,margin:0,lineHeight:1.55}}>{T(lang,"requestAccountLead")}</p>
            </div>
            <p style={{fontSize:12,color:THEME.textDim,lineHeight:1.65,marginBottom:18}}>
              {T(lang,"requestAccountBody")}
            </p>
            <div style={{padding:14,borderRadius:10,background:THEME.surface,border:`1px solid ${THEME.borderSubtle}`,marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:36,height:36,borderRadius:9,background:THEME.accentDim,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <lucide.Mail size={17} color={THEME.accent}/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,color:THEME.textMuted,textTransform:"uppercase",letterSpacing:0.5,fontWeight:600,marginBottom:2}}>{T(lang,"contactUs")}</div>
                  <a href="mailto:contact@klassdoc.com" style={{fontSize:13,fontWeight:600,color:THEME.accent,textDecoration:"none",wordBreak:"break-all"}}>contact@klassdoc.com</a>
                </div>
              </div>
            </div>
            <button type="button" onClick={switchToLogin} style={{width:"100%",justifyContent:"center",display:"flex",alignItems:"center",gap:6,background:"transparent",border:`1px solid ${THEME.border}`,padding:"10px",borderRadius:8,color:THEME.textMuted,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
              <lucide.ArrowLeft size={14}/>{T(lang,"backToLogin")}
            </button>
          </div>
        )}
        {mode==="mfa" && (
          <form onSubmit={handleMfaSubmit}>
            <div style={{textAlign:"center",marginBottom:20}}>
              <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:52,height:52,borderRadius:14,background:`linear-gradient(135deg,${THEME.accent}33,${THEME.accent}11)`,border:`1px solid ${THEME.accent}40`,marginBottom:14}}>
                <lucide.ShieldCheck size={26} color={THEME.accent}/>
              </div>
              <h2 style={{fontSize:17,fontWeight:700,color:THEME.text,margin:"0 0 6px 0"}}>{T(lang,"mfaChallengeTitle")}</h2>
              <p style={{fontSize:12,color:THEME.textDim,margin:0,lineHeight:1.55}}>
                {mfaIsBackup
                  ? (lang==="fr"?"Entrez un de vos codes de secours (10 chiffres, tiret optionnel).":"Enter one of your backup codes (10 digits, optional dash).")
                  : T(lang,"mfaChallengeSubtitle")}
              </p>
            </div>
            <div style={{marginBottom:18}}>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase"}}>{T(lang,"mfaCode")}</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={e=>setMfaCode(e.target.value)}
                placeholder={mfaIsBackup?"00000-00000":"000000"}
                autoFocus
                maxLength={mfaIsBackup?11:6}
                style={{...S.input,background:THEME.surfaceRaised,textAlign:"center",fontSize:20,letterSpacing:3,fontFamily:"'JetBrains Mono',Consolas,monospace"}}
              />
            </div>
            {error && (
              <div style={{marginBottom:14,padding:"10px 14px",borderRadius:8,background:THEME.dangerDim,color:THEME.danger,fontSize:13,display:"flex",alignItems:"flex-start",gap:8}}>
                <lucide.AlertCircle size={14} style={{flexShrink:0,marginTop:1}}/>
                <span>{error}</span>
              </div>
            )}
            <button type="submit" disabled={loading||!mfaCode.trim()} style={{...S.btn,width:"100%",justifyContent:"center",background:`linear-gradient(135deg,${THEME.accent},${THEME.accentHover})`,color:"#fff",padding:"12px 20px",fontSize:15,opacity:(loading||!mfaCode.trim())?0.6:1,marginBottom:12,boxShadow:`0 4px 16px ${THEME.accent}30`}}>
              {loading?<Spinner size={18}/>:<><lucide.LogIn size={16}/>{T(lang,"signIn")}</>}
            </button>
            {mfaHasBackup && (
              <button type="button" onClick={()=>{setMfaIsBackup(!mfaIsBackup);setMfaCode("");setError(null);}} style={{width:"100%",justifyContent:"center",display:"flex",alignItems:"center",gap:6,background:"transparent",border:"none",padding:"8px",color:THEME.info,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                <lucide.Key size={13}/>{mfaIsBackup?T(lang,"mfaUseTotp"):T(lang,"mfaUseBackup")}
              </button>
            )}
            <button type="button" onClick={()=>{setMode("login");setMfaCode("");setError(null);setPassword("");}} style={{width:"100%",justifyContent:"center",display:"flex",alignItems:"center",gap:6,background:"transparent",border:"none",padding:"8px",color:THEME.textDim,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
              <lucide.ArrowLeft size={13}/>{T(lang,"backToLogin")}
            </button>
          </form>
        )}
        {/* Trust signal on every build — subtle encryption + tenant
            isolation note. Demo-credential hint removed (was previously
            shown in dev; cleaner login screen without it). */}
        <p style={{textAlign:"center",marginTop:24,fontSize:11,color:THEME.textDim,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
          <lucide.ShieldCheck size={12} color={THEME.success}/>
          {lang==="fr" ? "Connexion chiffrée · Vos documents restent dans votre organisation" : "Encrypted connection · Your documents stay in your organization"}
        </p>
      </motion.div>
    </div>
  );
}

// ─── Sprint 11.x SIDEBAR — mockup-aligned editorial design ───
// Four sections (WORKSPACE / FOLDERS / BROWSE / ADMIN) with mono
// uppercase labels, an inline folder tree under FOLDERS with chevron
// + filled-folder icon + child rows (dot prefix + connector line),
// active-state amber gradient + left bar indicator, count chips on
// the right of each nav row, and a user card at the bottom with
// the online dot. The collapse toggle lives below the user card.
function SidebarFolderTreeRow({folder,allFolders,depth,onSelect,expanded,toggleExpand,activeFolderId}){
  const children=allFolders.filter(f=>f.parent_id===folder.id);
  const hasChildren=children.length>0;
  const isExp=expanded[folder.id];
  const isActive=activeFolderId===folder.id;
  const childDocCount=folder._access_level==null?undefined:(folder.doc_count||0);
  if(depth===0){
    // Top-level folder — visually aligned with the other nav items in
    // WORKSPACE/BROWSE/ADMIN. Same padding ("7px 10px"), same 16px icon,
    // same 11px gap → the folder NAME starts at the same horizontal x
    // as Documents / Dashboard / Checked Out / etc.
    //
    // The expand chevron lives on the FAR RIGHT (after the count) so it
    // doesn't push the icon or label out of column. Tap the row to
    // select the folder; tap the chevron to expand/collapse.
    return(
      <div>
        <div style={{position:"relative",display:"flex",alignItems:"center",gap:11,padding:"7px 10px",margin:"0 0 1px",borderRadius:8,fontSize:13,fontWeight:500,cursor:"pointer",transition:"background 0.12s, color 0.12s",color:isActive?THEME.sidebarText:THEME.sidebarInk,background:isActive?THEME.sidebarActiveBg:"transparent"}}
          onClick={()=>onSelect(folder)}
          onMouseEnter={e=>{if(!isActive){e.currentTarget.style.background=THEME.sidebarHover;e.currentTarget.style.color=THEME.sidebarText;}}}
          onMouseLeave={e=>{if(!isActive){e.currentTarget.style.background="transparent";e.currentTarget.style.color=THEME.sidebarInk;}}}>
          {isActive&&<span style={{position:"absolute",left:-12,top:7,bottom:7,width:2,background:THEME.accent,borderRadius:"0 2px 2px 0"}}/>}
          <lucide.Folder size={16} fill="currentColor" color={THEME.accent} style={{flexShrink:0,opacity:isActive?1:0.85}}/>
          <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{folder.name}</span>
          {hasChildren&&<lucide.ChevronRight size={12} color={THEME.sidebarDim} style={{transform:isExp?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.15s",flexShrink:0,cursor:"pointer",marginLeft:2}} onClick={e=>{e.stopPropagation();toggleExpand(folder.id);}}/>}
        </div>
        {isExp&&children.map(child=>(
          <SidebarFolderTreeRow key={child.id} folder={child} allFolders={allFolders} depth={depth+1} onSelect={onSelect} expanded={expanded} toggleExpand={toggleExpand} activeFolderId={activeFolderId}/>
        ))}
      </div>
    );
  }
  // Sub-folder — indented BEYOND the top-level row so the hierarchy is
  // visible at a glance. Uses a small folder icon (12px, outline-only).
  // Indentation alone signals nesting (no connector lines). Increasing
  // depth adds further indent via `marginLeft: 18 * (depth + 1)`.
  return(
    <div>
      <div style={{position:"relative",display:"flex",alignItems:"center",gap:8,padding:"5px 10px",marginLeft:18*(depth+1),fontSize:12.5,color:isActive?THEME.sidebarText:THEME.sidebarMuted,borderRadius:8,cursor:"pointer",marginBottom:1}}
        onClick={()=>onSelect(folder)}
        onMouseEnter={e=>{if(!isActive)e.currentTarget.style.color=THEME.sidebarInk;}}
        onMouseLeave={e=>{if(!isActive)e.currentTarget.style.color=THEME.sidebarMuted;}}>
        <lucide.Folder size={12} color={THEME.sidebarDim} style={{flexShrink:0,opacity:0.75}}/>
        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{folder.name}</span>
        {hasChildren&&<lucide.ChevronRight size={10} color={THEME.sidebarDim} style={{transform:isExp?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.15s",flexShrink:0,cursor:"pointer"}} onClick={e=>{e.stopPropagation();toggleExpand(folder.id);}}/>}
      </div>
      {isExp&&children.map(child=>(
        <SidebarFolderTreeRow key={child.id} folder={child} allFolders={allFolders} depth={depth+1} onSelect={onSelect} expanded={expanded} toggleExpand={toggleExpand} activeFolderId={activeFolderId}/>
      ))}
    </div>
  );
}

function Sidebar({active,onNav,user,onLogout,onCreateFolder}){
  const [collapsed,setCollapsed]=useState(false);const {lang}=useLang();
  // 2026-06-11: drag-resizable width. A 5px col-resize handle sits on the
  // sidebar's right edge; dragging clamps to [180, 420] px, double-click
  // resets to the 248px default, and the chosen width persists across
  // sessions in localStorage. While dragging, the motion transition is
  // zeroed so the sidebar tracks the cursor 1:1 instead of springing.
  const SIDEBAR_MIN=180, SIDEBAR_MAX=420, SIDEBAR_DEFAULT=248;
  const [sbWidth,setSbWidth]=useState(()=>{
    const v=parseInt(localStorage.getItem("af_sidebar_w"),10);
    return v>=SIDEBAR_MIN&&v<=SIDEBAR_MAX?v:SIDEBAR_DEFAULT;
  });
  const [resizing,setResizing]=useState(false);
  function startSidebarResize(e){
    e.preventDefault();
    setResizing(true);
    const clamp=x=>Math.min(SIDEBAR_MAX,Math.max(SIDEBAR_MIN,x));
    const onMove=ev=>setSbWidth(clamp(ev.clientX));
    const onUp=ev=>{
      window.removeEventListener("mousemove",onMove);
      window.removeEventListener("mouseup",onUp);
      document.body.style.userSelect="";
      document.body.style.cursor="";
      setResizing(false);
      localStorage.setItem("af_sidebar_w",String(clamp(ev.clientX)));
    };
    document.body.style.userSelect="none";
    document.body.style.cursor="col-resize";
    window.addEventListener("mousemove",onMove);
    window.addEventListener("mouseup",onUp);
  }
  const [folders,setFolders]=useState([]);const [treeExpanded,setTreeExpanded]=useState({});
  const [activeSidebarFolderId,setActiveSidebarFolderId]=useState(null);
  // Sprint 11.x: light counts for the WORKSPACE / BROWSE nav chips.
  // Documents + checkouts + courrier come from existing endpoints;
  // emails + clients + favorites stay null (no feature yet).
  const [counts,setCounts]=useState({documents:null,checkouts:null,courrier:null});
  // Sprint 11.x: auto-detect the user's country via a free public GeoIP
  // lookup (ipapi.co — 1k free requests/day, HTTPS, no key needed).
  // Used purely for the user-card display ("ADMIN · OWNER · USA").
  // Cached in sessionStorage so we ping the service once per session;
  // falls back to "DRC" if the fetch fails or the user is offline.
  const [country,setCountry]=useState(()=>sessionStorage.getItem("klassdoc_country")||null);
  const {refreshSignal,setSelectedFolderId}=useApp();

  useEffect(()=>{
    api.get("/folders").then(r=>setFolders(r.data||[])).catch(()=>{});
    // Sprint 11.x bugfix: Documents count must respect access, not show
    // the org-wide stat. /api/documents goes through filterListResponse
    // (middleware/aclFilter.js) which:
    //   - For admins: bypasses filtering, returns org-wide total
    //   - For non-admins: filters the page to only docs the user can read
    //     and overwrites total with filtered.length
    // The middleware filters JS-side after the SQL select, so we need a
    // limit high enough to cover all docs the user could see. limit=500
    // is plenty for pilot scale; revisit if any tenant grows past that.
    api.get("/documents?limit=500&sort=updated_at").then(r=>setCounts(c=>({...c,documents:r?.total??(r?.data||[]).length??null}))).catch(()=>{});
    api.get("/checkouts/my").then(r=>setCounts(c=>({...c,checkouts:(r?.data||[]).length}))).catch(()=>{});
    api.get("/courrier?limit=1").then(r=>setCounts(c=>({...c,courrier:r?.total??(r?.data||[]).length}))).catch(()=>{});
  },[refreshSignal]);

  // Country detection — runs once on mount unless already cached.
  useEffect(()=>{
    if(country)return;
    fetch("https://ipapi.co/json/").then(r=>r.ok?r.json():null).then(d=>{
      // Prefer ISO-3 (e.g., "USA", "FRA", "COD") so it reads like a passport.
      // ipapi.co returns: country_code (ISO-2), country_code_iso3, country_name.
      let cc = d?.country_code_iso3 || d?.country_code || null;
      // The official ISO-3 for the Democratic Republic of the Congo is "COD",
      // but "DRC" is what locals and the product brand use — translate.
      const COLLOQUIAL = { COD: "DRC" };
      if (cc && COLLOQUIAL[cc]) cc = COLLOQUIAL[cc];
      if(cc){sessionStorage.setItem("klassdoc_country",cc);setCountry(cc);}
    }).catch(()=>{});
  },[country]);

  function toggleTreeNode(id){setTreeExpanded(p=>({...p,[id]:!p[id]}));}
  function selectFolder(folder){setSelectedFolderId(folder.id);setActiveSidebarFolderId(folder.id);onNav("folders");}
  const rootFolders=folders.filter(f=>!f.parent_id);
  const isAdmin=user?.role==="admin"||user?.is_super_admin===true;
  const isSuper=user?.is_super_admin===true;

  // Nav-item render helper. Renders a single row with optional left
  // active-bar, icon, label, and right-aligned count chip.
  function navItem({id,Icon,label,count,onClick,disabled,active:isActive}){
    return(
      <div key={id} onClick={()=>{if(!disabled)(onClick||(()=>onNav(id)))();}}
        style={{display:"flex",alignItems:"center",gap:11,padding:collapsed?"8px":"7px 10px",margin:collapsed?"0 6px 1px":"0 0 1px",borderRadius:8,fontSize:13,fontWeight:500,cursor:disabled?"default":"pointer",transition:"background 0.12s, color 0.12s",position:"relative",color:isActive?THEME.sidebarText:disabled?THEME.sidebarDim:THEME.sidebarInk,background:isActive?THEME.sidebarActiveBg:"transparent",justifyContent:collapsed?"center":"flex-start",opacity:disabled?0.55:1}}
        onMouseEnter={e=>{if(!isActive&&!disabled){e.currentTarget.style.background=THEME.sidebarHover;e.currentTarget.style.color=THEME.sidebarText;}}}
        onMouseLeave={e=>{if(!isActive&&!disabled){e.currentTarget.style.background="transparent";e.currentTarget.style.color=THEME.sidebarInk;}}}>
        {isActive&&!collapsed&&<span style={{position:"absolute",left:-12,top:7,bottom:7,width:2,background:THEME.accent,borderRadius:"0 2px 2px 0"}}/>}
        <Icon size={16} style={{flexShrink:0,opacity:isActive?1:0.85}}/>
        {!collapsed&&<span style={{whiteSpace:"nowrap",flex:1,overflow:"hidden",textOverflow:"ellipsis"}}>{label}</span>}
      </div>
    );
  }

  // Section-label render helper. Mono, uppercase, letter-spaced.
  function sectionLabel(text,rightAccessory){
    if(collapsed)return <div style={{height:10}}/>;
    return(
      <div style={{fontFamily:FONT_MONO,fontSize:10,letterSpacing:"0.14em",textTransform:"uppercase",color:THEME.sidebarDim,padding:"0 10px 8px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span>{text}</span>
        {rightAccessory}
      </div>
    );
  }

  // ── User initials for the bottom card avatar ──
  const userInitials=(user?.name||"?").split(" ").map(s=>s[0]).join("").slice(0,2).toUpperCase();

  return(
    <motion.div animate={{width:collapsed?68:sbWidth}} transition={{duration:resizing?0:0.25}}
      style={{position:"relative",height:"100vh",background:THEME.sidebar,borderRight:`1px solid ${THEME.sidebarBorder}`,display:"flex",flexDirection:"column",overflow:"hidden",flexShrink:0}}>

      {/* ── Brand mark + name ── */}
      <div style={{height:64,display:"flex",alignItems:"center",gap:10,padding:collapsed?"0 19px":"0 18px",borderBottom:`1px solid ${THEME.sidebarBorder}`,flexShrink:0}}>
        <div style={{width:30,height:30,borderRadius:8,background:`linear-gradient(135deg,${THEME.accent},${THEME.accentDark})`,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",boxShadow:`0 6px 16px ${THEME.accentDim}, 0 1px 0 rgba(255,255,255,0.2) inset`,flexShrink:0}}>
          <lucide.FileText size={16} strokeWidth={2.4}/>
        </div>
        {!collapsed&&<div style={{fontWeight:600,fontSize:15,letterSpacing:"-0.01em",color:THEME.sidebarText}}>Klass<span style={{color:THEME.accent,fontWeight:600}}>Doc</span></div>}
      </div>

      {/* ── Scrollable nav ── */}
      <div style={{flex:1,overflowY:"auto",padding:"14px 12px 10px"}}>

        {/* WORKSPACE */}
        <div style={{marginBottom:18}}>
          {sectionLabel(lang==="fr"?"Espace":"Workspace")}
          {navItem({id:"documents",Icon:lucide.FileText,label:T(lang,"documents"),count:counts.documents,active:active==="documents"})}
          {navItem({id:"emails",Icon:lucide.Mail,label:lang==="fr"?"E-mails":"Emails",count:null,active:active==="emails"})}
          {navItem({id:"clients",Icon:lucide.Users,label:lang==="fr"?"Clients":"Clients",count:null,disabled:true})}
        </div>

        {/* FOLDERS */}
        {!collapsed&&(
          <div style={{marginBottom:18}}>
            {sectionLabel(
              lang==="fr"?"Dossiers":"Folders",
              <span onClick={()=>{onCreateFolder?.();}} style={{cursor:"pointer",color:THEME.sidebarMuted,display:"inline-flex"}} title={lang==="fr"?"Nouveau dossier":"New folder"} onMouseEnter={e=>e.currentTarget.style.color=THEME.sidebarText} onMouseLeave={e=>e.currentTarget.style.color=THEME.sidebarMuted}>
                <lucide.Plus size={11}/>
              </span>
            )}
            {rootFolders.length===0
              ?<div style={{padding:"5px 10px",fontSize:11,color:THEME.sidebarDim,fontStyle:"italic"}}>{lang==="fr"?"Aucun dossier":"No folders yet"}</div>
              :rootFolders.map(f=>(
                <SidebarFolderTreeRow key={f.id} folder={f} allFolders={folders} depth={0} onSelect={selectFolder} expanded={treeExpanded} toggleExpand={toggleTreeNode} activeFolderId={active==="folders"?activeSidebarFolderId:null}/>
              ))}
          </div>
        )}

        {/* BROWSE */}
        <div style={{marginBottom:18}}>
          {sectionLabel(lang==="fr"?"Naviguer":"Browse")}
          {navItem({id:"favorites",Icon:lucide.Star,label:lang==="fr"?"Favoris":"Favorites",count:null,disabled:true})}
          {navItem({id:"dashboard",Icon:lucide.LayoutDashboard,label:T(lang,"dashboard"),active:active==="dashboard"})}
          {navItem({id:"search",Icon:lucide.Search,label:T(lang,"search"),active:active==="search"})}
          {navItem({id:"checkedout",Icon:lucide.Lock,label:lang==="fr"?"Réservations":"Checked Out",count:counts.checkouts||null,active:active==="checkedout"})}
          {navItem({id:"courrier",Icon:lucide.Truck,label:T(lang,"courrier"),count:counts.courrier||null,active:active==="courrier"})}
        </div>

        {/* ADMIN — only if user has any admin tier */}
        {(isAdmin||isSuper)&&(
          <div style={{marginBottom:18}}>
            {sectionLabel("Admin")}
            {isAdmin&&navItem({id:"reports",Icon:lucide.BarChart3,label:T(lang,"reports"),active:active==="reports"})}
            {isAdmin&&navItem({id:"users",Icon:lucide.Users,label:T(lang,"users"),active:active==="users"})}
            {isAdmin&&navItem({id:"departments",Icon:lucide.Building2,label:T(lang,"departments"),active:active==="departments"})}
            {isAdmin&&navItem({id:"audit",Icon:lucide.Clock,label:T(lang,"audit"),active:active==="audit"})}
            {isAdmin&&navItem({id:"subscription",Icon:lucide.CreditCard,label:T(lang,"subscription"),active:active==="subscription"})}
            {isSuper&&navItem({id:"admin",Icon:lucide.Shield,label:T(lang,"adminPortal"),active:active==="admin"})}
          </div>
        )}

      </div>

      {/* ── User card ── */}
      <div style={{borderTop:`1px solid ${THEME.sidebarBorder}`,padding:collapsed?"12px 8px":"12px",display:"flex",alignItems:"center",gap:10,background:"linear-gradient(180deg,transparent,rgba(255,255,255,0.02))"}}>
        {/* Custom avatar with online dot, sized to match the mockup. */}
        <div style={{position:"relative",width:34,height:34,borderRadius:"50%",background:`linear-gradient(135deg,${THEME.success},#0F7A3D)`,color:"#fff",fontWeight:700,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:`0 2px 8px rgba(22,163,74,0.30)`,fontFamily:FONT}}>
          {userInitials}
          <span style={{position:"absolute",bottom:-1,right:-1,width:10,height:10,borderRadius:"50%",background:THEME.success,border:`2px solid ${THEME.sidebar}`}}/>
        </div>
        {!collapsed&&<div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:12.5,fontWeight:600,color:THEME.sidebarText,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.name}</div>
          <div style={{fontSize:10.5,color:THEME.sidebarDim,fontFamily:FONT_MONO,letterSpacing:"0.04em",textTransform:"uppercase"}}>
            {(user?.role||"").toUpperCase()}{user?.is_super_admin?" · OWNER":""}{country?` · ${country}`:""}
          </div>
        </div>}
        {!collapsed&&<lucide.ChevronDown size={14} style={{color:THEME.sidebarDim}}/>}
      </div>

      {/* ── Collapse toggle ── */}
      <div onClick={()=>setCollapsed(!collapsed)} style={{borderTop:`1px solid ${THEME.sidebarBorder}`,padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:collapsed?"center":"flex-start",gap:8,fontSize:11,color:THEME.sidebarDim,fontFamily:FONT_MONO,letterSpacing:"0.06em",textTransform:"uppercase",cursor:"pointer",flexShrink:0}}
        onMouseEnter={e=>e.currentTarget.style.color=THEME.sidebarMuted} onMouseLeave={e=>e.currentTarget.style.color=THEME.sidebarDim}>
        {collapsed?<lucide.ChevronsRight size={12}/>:<><lucide.ChevronsLeft size={12}/><span>{lang==="fr"?"Réduire":"Collapse sidebar"}</span></>}
      </div>

      {/* ── Resize handle — drag to adjust width, double-click to reset ── */}
      {!collapsed&&(
        <div
          onMouseDown={startSidebarResize}
          onDoubleClick={()=>{setSbWidth(SIDEBAR_DEFAULT);localStorage.setItem("af_sidebar_w",String(SIDEBAR_DEFAULT));}}
          title={lang==="fr"?"Glisser pour redimensionner · double-clic pour réinitialiser":"Drag to resize · double-click to reset"}
          style={{position:"absolute",top:0,right:0,bottom:0,width:5,cursor:"col-resize",zIndex:20,background:resizing?THEME.accentLine:"transparent",transition:"background 0.15s"}}
          onMouseEnter={e=>{if(!resizing)e.currentTarget.style.background=THEME.accentLine;}}
          onMouseLeave={e=>{if(!resizing)e.currentTarget.style.background="transparent";}}
        />
      )}
    </motion.div>
  );
}
function PageHeader({title,subtitle,actions}){return(<div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:28,gap:16,flexWrap:"wrap"}}><div><motion.h2 initial={{opacity:0,x:-10}} animate={{opacity:1,x:0}} style={{fontSize:22,fontWeight:700,color:THEME.text,margin:0}}>{title}</motion.h2>{subtitle&&<p style={{fontSize:13,color:THEME.textDim,margin:"4px 0 0"}}>{subtitle}</p>}</div>{actions&&<div style={{display:"flex",gap:10,alignItems:"center"}}>{actions}</div>}</div>);}

// ─── Sprint 10C Session C1 H5 fix: page-level role guards ───
// Helpers to keep role logic in one place. `isAdmin` already exists on
// AppContext (role==='admin'); these cover the two admin tiers we actually use.
function isAdminOrSuper(user){return user?.role==="admin"||user?.is_super_admin===true;}
function isSuperAdmin(user){return user?.is_super_admin===true;}

// Inline "Access Denied" page. Rendered when a non-privileged user lands
// on a gated page via direct navigation, bookmarked URL, dev-tools event
// dispatch, or any path that bypasses the sidebar filter. Matches the
// existing card/iconography visual language rather than pulling in the
// AccessDeniedModal (which is intended for mid-flow interrupts, not
// full-page refusal).
function AccessDeniedPage({requiredRole}){
  const {lang}=useLang();
  const roleLabel=requiredRole==="super_admin"
    ?(lang==="fr"?"super-administrateur":"super-administrator")
    :(lang==="fr"?"administrateur":"administrator");
  return(
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"60vh",padding:"40px 20px"}}>
      <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} style={{...S.card,maxWidth:480,textAlign:"center",padding:"40px 32px"}}>
        <div style={{width:64,height:64,borderRadius:"50%",background:THEME.dangerDim,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px"}}>
          <lucide.ShieldAlert size={32} color={THEME.danger}/>
        </div>
        <h2 style={{fontSize:20,fontWeight:700,color:THEME.text,margin:"0 0 10px"}}>
          {lang==="fr"?"Accès refusé":"Access Denied"}
        </h2>
        <p style={{fontSize:13,color:THEME.textMuted,margin:"0 0 8px",lineHeight:1.6}}>
          {lang==="fr"
            ?`Cette page est réservée aux comptes de type ${roleLabel}.`
            :`This page is restricted to ${roleLabel} accounts.`}
        </p>
        <p style={{fontSize:12,color:THEME.textDim,margin:0,lineHeight:1.6}}>
          {lang==="fr"
            ?"Si vous pensez qu'il s'agit d'une erreur, contactez l'administrateur de votre organisation."
            :"If you believe this is an error, please contact your organization's administrator."}
        </p>
      </motion.div>
    </div>
  );
}

// ─── METADATA PANEL (inside preview modal) ───
function MetadataPanel({docId,doc}){
  const {toast}=useApp();const {lang}=useLang();
  const [meta,setMeta]=useState({author:"",department:"",keywords:[],doc_class:"",confidentiality:"internal",retention_date:""});
  const [editing,setEditing]=useState(false);const [kw,setKw]=useState("");
  useEffect(()=>{api.get(`/metadata/${docId}`).then(d=>{setMeta({author:d.author||doc?.uploaded_by||"",department:d.department||"",keywords:d.keywords||[],doc_class:d.doc_class||"",confidentiality:d.confidentiality||"internal",retention_date:d.retention_date?d.retention_date.split("T")[0]:""});}).catch(()=>{
    // If metadata fetch fails, still populate author from doc uploader
    setMeta(m=>({...m,author:doc?.uploaded_by||""}));
  });},[docId]);
  async function saveMeta(){try{await api.put(`/metadata/${docId}`,meta);toast(lang==="fr"?"Métadonnées enregistrées":"Metadata saved","success");setEditing(false);}catch(e){toast(e.message,"error");}}
  function addKw(){if(kw.trim()&&!meta.keywords.includes(kw.trim())){setMeta(m=>({...m,keywords:[...m.keywords,kw.trim()]}));setKw("");}}
  function removeKw(k){setMeta(m=>({...m,keywords:m.keywords.filter(x=>x!==k)}));}
  const confColors={public:THEME.success,internal:THEME.info,confidential:THEME.warning,secret:THEME.danger};
  const docClasses=[
    {value:"contract",label:lang==="fr"?"Contrat":"Contract"},
    {value:"report",label:lang==="fr"?"Rapport":"Report"},
    {value:"invoice",label:lang==="fr"?"Facture":"Invoice"},
    {value:"correspondence",label:lang==="fr"?"Correspondance":"Correspondence"},
    {value:"legal",label:lang==="fr"?"Document juridique":"Legal Document"},
    {value:"financial",label:lang==="fr"?"Document financier":"Financial Document"},
    {value:"hr",label:lang==="fr"?"Ressources humaines":"Human Resources"},
    {value:"technical",label:lang==="fr"?"Document technique":"Technical Document"},
    {value:"policy",label:lang==="fr"?"Politique/Procédure":"Policy/Procedure"},
    {value:"other",label:lang==="fr"?"Autre":"Other"},
  ];
  const fieldStyle={padding:"10px 14px",background:THEME.surface,borderRadius:8,border:`1px solid ${THEME.borderSubtle}`};
  const labelStyle={fontSize:10,color:THEME.textDim,textTransform:"uppercase",letterSpacing:0.5,display:"block",marginBottom:5};
  const valueStyle={fontSize:13,color:THEME.text,fontWeight:500};

  return(
    <div>
      {/* Properties header — iManage style */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,paddingBottom:12,borderBottom:`1px solid ${THEME.borderSubtle}`}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}><lucide.Info size={16} color={THEME.accent}/><span style={{fontSize:14,fontWeight:600,color:THEME.text}}>{lang==="fr"?"Propriétés":"Properties"}</span></div>
        <button onClick={()=>editing?saveMeta():setEditing(true)} style={{...S.btn,padding:"6px 14px",fontSize:12,background:editing?THEME.accent:THEME.surfaceHover,color:editing?"#fff":THEME.textMuted,border:`1px solid ${editing?THEME.accent:THEME.border}`}}>{editing?<><lucide.Check size={13}/>{T(lang,"save")}</>:<><lucide.Pencil size={13}/>{lang==="fr"?"Modifier":"Edit"}</>}</button>
      </div>

      {/* Document ID bar */}
      <div style={{...fieldStyle,marginBottom:12,display:"flex",alignItems:"center",gap:12}}>
        <div><span style={labelStyle}>DOCUMENT ID</span><span style={{...valueStyle,fontFamily:FONT_MONO,fontSize:14,color:THEME.accent}}>{formatDocId(doc?.id)}</span></div>
        <div style={{width:1,height:28,background:THEME.border}}/>
        <div><span style={labelStyle}>{T(lang,"version").toUpperCase()}</span><span style={valueStyle}>v{doc?.current_version||1}</span></div>
        <div style={{width:1,height:28,background:THEME.border}}/>
        <div><span style={labelStyle}>{T(lang,"type").toUpperCase()}</span><span style={valueStyle}>{doc?.name?.split(".").pop()?.toUpperCase()||"—"}</span></div>
        <div style={{width:1,height:28,background:THEME.border}}/>
        <div><span style={labelStyle}>{T(lang,"size").toUpperCase()}</span><span style={valueStyle}>{formatBytes(doc?.size)}</span></div>
      </div>

      {/* Metadata grid */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <div style={fieldStyle}><label style={labelStyle}>{T(lang,"author")}</label>{editing?<input value={meta.author} onChange={e=>setMeta(m=>({...m,author:e.target.value}))} style={{...S.input,padding:"4px 8px",fontSize:12,background:"transparent",border:"none"}}/>:<div style={valueStyle}>{meta.author||"—"}</div>}</div>
        <div style={fieldStyle}><label style={labelStyle}>{T(lang,"department")}</label>{editing?<input value={meta.department} onChange={e=>setMeta(m=>({...m,department:e.target.value}))} style={{...S.input,padding:"4px 8px",fontSize:12,background:"transparent",border:"none"}}/>:<div style={valueStyle}>{meta.department||"—"}</div>}</div>
        <div style={fieldStyle}><label style={labelStyle}>{T(lang,"docClass")}</label>{editing?<select value={meta.doc_class} onChange={e=>setMeta(m=>({...m,doc_class:e.target.value}))} style={{...S.input,padding:"4px 8px",fontSize:12,background:"transparent",border:"none"}}><option value="">—</option>{docClasses.map(c=><option key={c.value} value={c.value}>{c.label}</option>)}</select>:<div style={valueStyle}>{docClasses.find(c=>c.value===meta.doc_class)?.label||meta.doc_class||"—"}</div>}</div>
        <div style={fieldStyle}><label style={labelStyle}>{T(lang,"confidentiality")}</label>{editing?<select value={meta.confidentiality} onChange={e=>setMeta(m=>({...m,confidentiality:e.target.value}))} style={{...S.input,padding:"4px 8px",fontSize:12,background:"transparent",border:"none"}}><option value="public">{T(lang,"public")}</option><option value="internal">{T(lang,"internal")}</option><option value="confidential">{T(lang,"confidential")}</option><option value="secret">{T(lang,"secret")}</option></select>:<Badge color={confColors[meta.confidentiality]||THEME.info}>{T(lang,meta.confidentiality)}</Badge>}</div>
        <div style={fieldStyle}><label style={labelStyle}>{T(lang,"retentionDate")}</label>{editing?<input type="date" value={meta.retention_date} onChange={e=>setMeta(m=>({...m,retention_date:e.target.value}))} style={{...S.input,padding:"4px 8px",fontSize:12,background:"transparent",border:"none"}}/>:<div style={valueStyle}>{meta.retention_date||"—"}</div>}</div>
        <div style={fieldStyle}><label style={labelStyle}>{T(lang,"created")}</label><div style={valueStyle}>{formatFullDate(doc?.created_at,lang)}</div></div>
      </div>

      {/* Keywords */}
      <div style={{...fieldStyle,marginTop:10}}>
        <label style={labelStyle}>{T(lang,"keywords")}</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,minHeight:24}}>{meta.keywords.length===0&&!editing?<span style={{fontSize:12,color:THEME.textDim}}>—</span>:meta.keywords.map(k=><span key={k} style={{padding:"3px 10px",borderRadius:20,fontSize:11,background:THEME.accentDim,color:THEME.accent,display:"inline-flex",alignItems:"center",gap:4}}>{k}{editing&&<lucide.X size={10} style={{cursor:"pointer"}} onClick={()=>removeKw(k)}/>}</span>)}</div>
        {editing&&<div style={{display:"flex",gap:6,marginTop:8}}><input value={kw} onChange={e=>setKw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&(e.preventDefault(),addKw())} placeholder={lang==="fr"?"Ajouter un mot-clé...":"Add keyword..."} style={{...S.input,padding:"5px 10px",fontSize:12,flex:1}}/><button onClick={addKw} style={{...S.btn,padding:"5px 10px",fontSize:11,background:THEME.accentDim,color:THEME.accent}}><lucide.Plus size={12}/></button></div>}
      </div>

      {/* Security section — iManage style */}
      <div style={{marginTop:16,paddingTop:12,borderTop:`1px solid ${THEME.borderSubtle}`}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}><lucide.Shield size={14} color={THEME.warning}/><span style={{fontSize:13,fontWeight:600,color:THEME.text}}>{lang==="fr"?"Sécurité":"Security"}</span></div>
        <div style={{...fieldStyle,display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:28,height:28,borderRadius:7,background:confColors[meta.confidentiality]+"18",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><lucide.Lock size={13} color={confColors[meta.confidentiality]}/></div>
          <div style={{flex:1}}><div style={{fontSize:12,fontWeight:600,color:THEME.text}}>{T(lang,meta.confidentiality)}</div><div style={{fontSize:11,color:THEME.textDim}}>{lang==="fr"?"Niveau d'accès du document":"Document access level"}</div></div>
          <div style={{fontSize:11,color:THEME.textMuted,display:"flex",alignItems:"center",gap:4}}><lucide.Users size={12}/>{lang==="fr"?"Accès complet":"Full Access"}</div>
        </div>
      </div>
    </div>
  );
}

// ─── VERSION HISTORY PANEL — iManage-style ───
function VersionPanel({docId,docName}){
  const {toast}=useApp();const {lang}=useLang();
  const [versions,setVersions]=useState([]);const [showUpload,setShowUpload]=useState(false);
  const [changeNotes,setChangeNotes]=useState("");const [uploading,setUploading]=useState(false);const vRef=useRef();
  const [oldVerWarning,setOldVerWarning]=useState(null); // {version_number, action}
  const [previewVer,setPreviewVer]=useState(null); // version object being previewed
  // Sprint 10B-P2: signed URL for the preview iframe/img (multi-use within 5-min TTL)
  const [previewVerUrl,setPreviewVerUrl]=useState(null);
  const [previewVerLoading,setPreviewVerLoading]=useState(false);
  // P7 polish (Finding B diagnostic): track iframe load state so a broken preview
  // doesn't leave a blank white pane. If the iframe hasn't fired onLoad within
  // ~4s the UI falls back to a download-instead link.
  const [previewVerLoaded,setPreviewVerLoaded]=useState(false);
  const [previewVerStalled,setPreviewVerStalled]=useState(false);
  useEffect(()=>{api.get(`/versions/${docId}`).then(r=>setVersions(r.data||[])).catch(()=>{});},[docId]);
  // Sprint 10B-P2: mint a preview-purpose signed URL whenever the user
  // opens a preview. `cancelled` guards against a late response
  // overwriting state after the user has closed or switched previews.
  // 2026-05-14: blob-URL flow (same rationale as DocumentPreviewModal).
  useEffect(()=>{
    if(!previewVer){setPreviewVerUrl(null);setPreviewVerLoading(false);setPreviewVerLoaded(false);setPreviewVerStalled(false);return;}
    let cancelled=false;
    let blobUrlRef=null;
    setPreviewVerUrl(null);setPreviewVerLoading(true);setPreviewVerLoaded(false);setPreviewVerStalled(false);
    (async()=>{
      try{
        const signed=await api.signedUrl("version",docId,{versionNumber:previewVer.version_number,purpose:"preview"});
        if(cancelled||!signed)throw new Error("no-url");
        const r=await fetch(signed,{credentials:"same-origin"});
        if(cancelled)return;
        if(!r.ok){
          const err=new Error(`fetch-${r.status}`); err.status=r.status; throw err;
        }
        const blob=await r.blob();
        if(cancelled)return;
        blobUrlRef=URL.createObjectURL(blob);
        setPreviewVerUrl(blobUrlRef);
        setPreviewVerLoading(false);
      }catch(e){
        if(cancelled)return;
        setPreviewVerLoading(false);
        if(e?.status===404 || String(e?.message).startsWith("fetch-404"))toast(lang==="fr"?"Fichier introuvable":"File not found","warning");
        else if(e?.message!=="no-url")toast(e?.message||(lang==="fr"?"Aperçu indisponible":"Preview unavailable"),"error");
        setPreviewVer(null);
      }
    })();
    return()=>{
      cancelled=true;
      if(blobUrlRef){ try{ URL.revokeObjectURL(blobUrlRef); }catch{} }
    };
  },[previewVer,docId]);
  // P7 polish (Finding B): if iframe doesn't load within 4s, show the download fallback.
  useEffect(()=>{
    if(!previewVerUrl||previewVerLoaded||previewVerStalled)return;
    const t=setTimeout(()=>setPreviewVerStalled(true),4000);
    return()=>clearTimeout(t);
  },[previewVerUrl,previewVerLoaded,previewVerStalled]);
  async function uploadVersion(){
    const file=vRef.current?.files[0];if(!file)return;setUploading(true);
    try{const fd=new FormData();fd.append("file",file);fd.append("change_notes",changeNotes);await api.upload(`/versions/${docId}/upload`,fd);toast(lang==="fr"?"Nouvelle version téléversée":"New version uploaded","success");setShowUpload(false);setChangeNotes("");api.get(`/versions/${docId}`).then(r=>setVersions(r.data||[]));}
    catch(e){toast(e.message,"error");}setUploading(false);
  }
  function getFileExt(name){return(name||docName||"").split(".").pop()?.toUpperCase()||"FILE";}
  function getExtColor(ext){const e=ext?.toLowerCase();if(e==="pdf")return THEME.danger;if(e==="docx"||e==="doc")return THEME.info;if(e==="xlsx"||e==="xls"||e==="csv")return THEME.success;if(e==="pptx")return THEME.accent;return THEME.textDim;}
  function formatFullDateTime(iso,lng){if(!iso)return"—";const d=new Date(iso);if(isNaN(d.getTime()))return"—";return d.toLocaleDateString(lng==="fr"?"fr-FR":"en-US",{month:"short",day:"numeric",year:"numeric"})+", "+d.toLocaleTimeString(lng==="fr"?"fr-FR":"en-US",{hour:"2-digit",minute:"2-digit"});}
  function isPreviewable(name){const e=(name||"").split(".").pop()?.toLowerCase();return["pdf","png","jpg","jpeg","gif","webp","svg"].includes(e);}
  const maxVersion=versions.length>0?Math.max(...versions.map(v=>v.version_number)):1;

  // Sprint 10B-P2: version click mints a signed URL on demand.
  // oldVerWarning only stores {version_number, action} — no URL — so
  // we always mint a fresh token when the user confirms "Continue anyway".
  async function handleVersionClick(v,action){
    if(v.version_number<maxVersion){
      setOldVerWarning({version_number:v.version_number,action});
      return;
    }
    if(action==="preview"){setPreviewVer(v);return;}
    try{
      const url=await api.signedUrl("version",docId,{versionNumber:v.version_number,purpose:"download"});
      if(url)window.open(url,"_blank");
    }catch(e){
      if(e?.status===404)toast(lang==="fr"?"Fichier introuvable":"File not found","warning");
      else toast(e?.message||(lang==="fr"?"Échec du téléchargement":"Download failed"),"error");
    }
  }
  async function handleViewMostRecent(){
    const latest=versions.find(v=>v.version_number===maxVersion);
    setOldVerWarning(null);
    try{
      const url=latest
        ?await api.signedUrl("version",docId,{versionNumber:latest.version_number,purpose:"download"})
        :await api.signedUrl("document",docId,{purpose:"download"});
      if(url)window.open(url,"_blank");
    }catch(e){
      if(e?.status===404)toast(lang==="fr"?"Fichier introuvable":"File not found","warning");
      else toast(e?.message||(lang==="fr"?"Échec du téléchargement":"Download failed"),"error");
    }
  }
  // Sprint 10B-P2: "Continue anyway" on the old-version warning —
  // mints a signed URL on demand (no stale URL in state).
  async function handleContinueAnyway(){
    const w=oldVerWarning;if(!w)return;
    setOldVerWarning(null);
    if(w.action==="preview"){
      const v=versions.find(x=>x.version_number===w.version_number);
      if(v)setPreviewVer(v);
      return;
    }
    try{
      const url=await api.signedUrl("version",docId,{versionNumber:w.version_number,purpose:"download"});
      if(url)window.open(url,"_blank");
    }catch(e){
      if(e?.status===404)toast(lang==="fr"?"Fichier introuvable":"File not found","warning");
      else toast(e?.message||(lang==="fr"?"Échec du téléchargement":"Download failed"),"error");
    }
  }

  return(
    <div style={{marginTop:16}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        <span style={{fontSize:13,fontWeight:600,color:THEME.text,display:"flex",alignItems:"center",gap:6}}><lucide.GitBranch size={14} color={THEME.info}/>{T(lang,"versionHistory")}</span>
        <button onClick={()=>setShowUpload(!showUpload)} style={{...S.btn,padding:"5px 12px",fontSize:11,background:THEME.infoDim,color:THEME.info}}><lucide.Upload size={12}/>{T(lang,"uploadNewVersion")}</button>
      </div>
      <AnimatePresence>{showUpload&&<motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{overflow:"hidden",marginBottom:12}}>
        <div style={{padding:14,background:THEME.surface,borderRadius:8,border:`1px solid ${THEME.border}`,display:"flex",flexDirection:"column",gap:8}}>
          <input ref={vRef} type="file" style={{fontSize:12,color:THEME.textMuted}}/>
          <input value={changeNotes} onChange={e=>setChangeNotes(e.target.value)} placeholder={T(lang,"changeNotes")} style={{...S.input,padding:"6px 10px",fontSize:12}}/>
          <button onClick={uploadVersion} disabled={uploading} style={{...S.btn,padding:"6px 14px",fontSize:12,background:THEME.info,color:"#fff",alignSelf:"flex-start"}}>{uploading?<Spinner size={12}/>:<><lucide.Upload size={12}/>{T(lang,"upload")}</>}</button>
        </div>
      </motion.div>}</AnimatePresence>

      {/* Old version warning — iManage style */}
      <AnimatePresence>{oldVerWarning&&(
        <motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{overflow:"hidden"}}>
          <div style={{padding:"10px 16px",background:THEME.warningDim,border:`1px solid ${THEME.warning}30`,borderRadius:8,marginBottom:12,display:"flex",alignItems:"center",gap:10}}>
            <lucide.AlertTriangle size={16} color={THEME.warning} style={{flexShrink:0}}/>
            <span style={{fontSize:12,color:THEME.warning,flex:1}}>
              {lang==="fr"
                ?"La version que vous consultez n'est pas la plus récente."
                :"The version you are viewing is not the most current."}
            </span>
            <button onClick={handleViewMostRecent} style={{...S.btn,padding:"4px 12px",fontSize:11,background:THEME.accent,color:"#fff",whiteSpace:"nowrap"}}>{lang==="fr"?"Voir la plus récente":"View most recent version"}</button>
            <button onClick={handleContinueAnyway} style={{...S.btn,padding:"4px 12px",fontSize:11,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`,whiteSpace:"nowrap"}}>{lang==="fr"?"Continuer quand même":"Continue anyway"}</button>
            <lucide.X size={14} color={THEME.textDim} style={{cursor:"pointer",flexShrink:0}} onClick={()=>setOldVerWarning(null)}/>
          </div>
        </motion.div>
      )}</AnimatePresence>

      {/* Version preview (inline) */}
      <AnimatePresence>{previewVer&&(
        <motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{overflow:"hidden"}}>
          <div style={{marginBottom:12,borderRadius:10,overflow:"hidden",border:`1px solid ${THEME.border}`,background:THEME.surface}}>
            <div style={{padding:"8px 14px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span style={{fontSize:12,fontWeight:600,color:THEME.textMuted,display:"flex",alignItems:"center",gap:6}}><lucide.Eye size={13}/>{lang==="fr"?"Aperçu":"Preview"} — v{previewVer.version_number}</span>
              <lucide.X size={14} color={THEME.textDim} style={{cursor:"pointer"}} onClick={()=>setPreviewVer(null)}/>
            </div>
            {isPreviewable(previewVer.original_name)?(
              previewVerLoading||!previewVerUrl
                ?<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:350,background:"#fff"}}><Spinner size={20}/></div>
                :previewVer.original_name?.toLowerCase().endsWith(".pdf")
                  ?<div style={{position:"relative"}}>
                    <iframe
                      src={previewVerUrl}
                      style={{width:"100%",height:350,border:"none",background:"#fff",display:previewVerStalled&&!previewVerLoaded?"none":"block"}}
                      title={`v${previewVer.version_number}`}
                      onLoad={()=>{setPreviewVerLoaded(true);console.log("[VersionPreview] iframe loaded for v"+previewVer.version_number);}}/>
                    {previewVerStalled&&!previewVerLoaded&&(
                      <div style={{padding:"32px 20px",textAlign:"center",background:"#fff",minHeight:350,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10}}>
                        <lucide.AlertCircle size={32} color={THEME.warning} strokeWidth={1.2}/>
                        <div style={{fontSize:13,color:THEME.textMuted}}>{lang==="fr"?"Aperçu non disponible":"Preview unavailable"}</div>
                        <div style={{fontSize:11,color:THEME.textDim,maxWidth:320}}>{lang==="fr"?"L'aperçu de cette version ne peut pas être affiché ici. Téléchargez pour ouvrir.":"This version's preview could not be rendered inline. Download to open."}</div>
                        <button
                          onClick={async()=>{
                            try{const url=await api.signedUrl("version",docId,{versionNumber:previewVer.version_number,purpose:"download"});if(url)window.open(url,"_blank");}
                            catch(e){toast(e?.message||(lang==="fr"?"Échec du téléchargement":"Download failed"),"error");}
                          }}
                          style={{...S.btn,padding:"6px 14px",fontSize:12,background:THEME.accent,color:"#fff",marginTop:4}}>
                          <lucide.Download size={12}/> {lang==="fr"?"Télécharger la version":"Download version"}
                        </button>
                      </div>
                    )}
                  </div>
                  :<div style={{textAlign:"center",padding:16}}><img src={previewVerUrl} alt={previewVer.original_name} style={{maxWidth:"100%",maxHeight:300,borderRadius:6}} onLoad={()=>setPreviewVerLoaded(true)} onError={e=>{e.target.style.display="none";setPreviewVerStalled(true);}}/></div>
            ):(
              <div style={{padding:"32px 20px",textAlign:"center"}}>
                <lucide.FileText size={40} color={THEME.textDim} strokeWidth={1}/>
                <div style={{marginTop:10,fontSize:13,color:THEME.textMuted}}>{previewVer.original_name||docName}</div>
                <div style={{marginTop:4,fontSize:11,color:THEME.textDim}}>{lang==="fr"?"Aperçu non disponible pour ce type de fichier. Téléchargez pour ouvrir.":"Preview not available for this file type. Download to open."}</div>
              </div>
            )}
          </div>
        </motion.div>
      )}</AnimatePresence>

      {versions.length===0?<div style={{fontSize:12,color:THEME.textDim,padding:12}}>{lang==="fr"?"Aucune version antérieure":"No previous versions"}</div>
      :versions.map((v,i)=>{
        const ext=getFileExt(v.original_name);const extColor=getExtColor(ext);
        const isCurrent=v.version_number===maxVersion;
        return(
        <div key={v.id} style={{padding:"14px 16px",background:THEME.surface,borderRadius:10,border:`1px solid ${isCurrent?THEME.accent+"40":THEME.borderSubtle}`,marginBottom:8}}>
          {isCurrent&&<div style={{fontSize:9,fontWeight:700,color:THEME.accent,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>{lang==="fr"?"VERSION ACTUELLE":"CURRENT VERSION"}</div>}
          <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,flexShrink:0}}>
              <Avatar name={v.uploaded_by_name||"?"} size={36}/>
              <div style={{padding:"2px 8px",borderRadius:10,background:isCurrent?THEME.accentDim:THEME.infoDim,fontSize:10,fontWeight:700,color:isCurrent?THEME.accent:THEME.info}}>v{v.version_number}</div>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <span style={{fontSize:13,fontWeight:600,color:THEME.text}}>{lang==="fr"?"Version":"Version"} {v.version_number}</span>
                <span style={{padding:"1px 8px",borderRadius:4,fontSize:10,fontWeight:700,color:"#fff",background:extColor,textTransform:"uppercase",letterSpacing:0.5}}>{ext}</span>
              </div>
              <div style={{fontSize:12,color:THEME.text,marginBottom:2}}>{v.original_name||docName||"—"}</div>
              <div style={{fontSize:11,color:THEME.textDim,display:"flex",flexWrap:"wrap",gap:4,alignItems:"center"}}>
                <span>{lang==="fr"?"Par":"By"}: <span style={{color:THEME.textMuted,fontWeight:500}}>{v.uploaded_by_name||"—"}</span></span>
                <span style={{color:THEME.border}}>|</span>
                <span>{lang==="fr"?"Modifié":"Modified"}: {formatFullDateTime(v.created_at,lang)}</span>
                <span style={{color:THEME.border}}>|</span>
                <span>{formatBytes(v.file_size)}</span>
              </div>
              {v.change_notes&&<div style={{fontSize:11,color:THEME.accent,marginTop:4,fontStyle:"italic"}}>"{v.change_notes}"</div>}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0,marginTop:4}}>
              <button onClick={()=>handleVersionClick(v,"preview")} style={{...S.btn,padding:"5px 9px",fontSize:11,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}} title={lang==="fr"?"Aperçu":"Preview"}><lucide.Eye size={13}/></button>
              <button onClick={()=>handleVersionClick(v,"download")} style={{...S.btn,padding:"5px 9px",fontSize:11,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}} title={lang==="fr"?"Télécharger":"Download"}><lucide.Download size={13}/></button>
            </div>
          </div>
        </div>);
      })}
    </div>
  );
}

// ─── COMMENTS PANEL ───
function CommentsPanel({docId}){
  const {toast,user}=useApp();const {lang}=useLang();
  const [comments,setComments]=useState([]);const [newComment,setNewComment]=useState("");const [replyTo,setReplyTo]=useState(null);
  const load=useCallback(()=>{api.get(`/comments/${docId}`).then(r=>setComments(r.data||[])).catch(()=>{});},[docId]);
  useEffect(()=>{load();},[load]);
  async function postComment(){if(!newComment.trim())return;try{await api.post(`/comments/${docId}`,{content:newComment,parent_id:replyTo});setNewComment("");setReplyTo(null);load();toast(lang==="fr"?"Commentaire ajouté":"Comment added","success");}catch(e){toast(e.message,"error");}}
  function renderComment(c,depth=0){return(
    <div key={c.id} style={{marginLeft:depth*20,padding:"8px 0",borderBottom:`1px solid ${THEME.borderSubtle}`}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <Avatar name={c.user_name} size={22}/><span style={{fontSize:12,fontWeight:600,color:THEME.text}}>{c.user_name}</span>
        <span style={{fontSize:11,color:THEME.textDim}}>{formatDate(c.created_at,lang)}</span>
      </div>
      <div style={{fontSize:13,color:THEME.text,marginTop:4,marginLeft:30}}>{c.content}</div>
      <button onClick={()=>{setReplyTo(c.id);}} style={{marginLeft:30,marginTop:4,fontSize:11,color:THEME.accent,background:"none",border:"none",cursor:"pointer",fontFamily:FONT,display:"flex",alignItems:"center",gap:4}}><lucide.Reply size={11}/>{T(lang,"reply")}</button>
      {c.replies?.map(r=>renderComment(r,depth+1))}
    </div>
  );}
  return(
    <div style={{marginTop:16}}>
      <span style={{fontSize:13,fontWeight:600,color:THEME.text,display:"flex",alignItems:"center",gap:6,marginBottom:12}}><lucide.MessageSquare size={14} color={THEME.success}/>{T(lang,"comments")} ({comments.length})</span>
      <div style={{maxHeight:200,overflowY:"auto"}}>{comments.length===0?<div style={{fontSize:12,color:THEME.textDim,padding:8}}>{lang==="fr"?"Aucun commentaire":"No comments"}</div>:comments.map(c=>renderComment(c))}</div>
      {replyTo&&<div style={{fontSize:11,color:THEME.accent,marginTop:6,display:"flex",alignItems:"center",gap:4}}><lucide.CornerDownRight size={11}/>{lang==="fr"?"Réponse au commentaire":"Replying to comment"} <lucide.X size={11} style={{cursor:"pointer"}} onClick={()=>setReplyTo(null)}/></div>}
      <div style={{display:"flex",gap:8,marginTop:8}}>
        <input value={newComment} onChange={e=>setNewComment(e.target.value)} onKeyDown={e=>e.key==="Enter"&&postComment()} placeholder={T(lang,"addComment")} style={{...S.input,padding:"8px 12px",fontSize:12,flex:1}}/>
        <button onClick={postComment} style={{...S.btn,padding:"8px 14px",fontSize:12,background:THEME.success,color:"#fff"}}><lucide.Send size={12}/></button>
      </div>
    </div>
  );
}

// ─── DOCUMENT PREVIEW MODAL — enhanced with metadata, versions, comments tabs ───
function DocumentPreviewModal({doc,onClose,onAction,readOnlyOverride}){
  const {canEdit,isAdmin,toast,user}=useApp();const {lang}=useLang();
  const [moveMode,setMoveMode]=useState(false);const [folders,setFolders]=useState([]);
  const [selectedFolder,setSelectedFolder]=useState(doc?.folder_id||"");const [confirmDelete,setConfirmDelete]=useState(false);
  const [activeTab,setActiveTab]=useState("preview");
  const [showSecurityModal,setShowSecurityModal]=useState(false);
  // Sprint 10C H10-B2 Item 6: when user clicks "Manage at folder level"
  // from the effective view, we store the top-level folder here and
  // render FolderSecurityModal. The effective view closes in the same
  // action so only one modal is visible at a time.
  const [folderSecurityForDoc,setFolderSecurityForDoc]=useState(null);
  // Sprint 6.7: Check if locked by someone else (for disabling actions)
  const [checkoutStatus,setCheckoutStatus]=useState(null);const [forceLoading,setForceLoading]=useState(false);
  // Sprint 10B-P2: signed preview URL (multi-use within 5-min TTL).
  // Minted only when the preview tab is active AND the doc is actually
  // previewable (image/pdf) AND has a file on disk — keeps us from
  // spending tokens for doc types we can't render.
  const [previewUrl,setPreviewUrl]=useState(null);
  const [previewLoading,setPreviewLoading]=useState(false);
  const isLockedByOther=checkoutStatus?.checked_out&&!checkoutStatus?.is_mine;
  const isReadOnly=readOnlyOverride||isLockedByOther;

  useEffect(()=>{if(moveMode)api.get("/folders").then(r=>setFolders(r.data||[])).catch(()=>{});},[moveMode]);
  useEffect(()=>{if(doc?.id)api.get(`/checkouts/status/${doc.id}`).then(setCheckoutStatus).catch(()=>{});},[doc?.id]);

  // Sprint 10B-P2: mint preview URL when the preview tab is open on an
  // image/pdf doc. `cancelled` guards against races when the doc or tab
  // changes mid-mint.
  const docIsImage=doc?.type==="image"||["png","jpg","jpeg","gif","webp","svg"].includes(doc?.name?.split(".").pop()?.toLowerCase());
  const docIsPdf=doc?.type==="pdf"||doc?.name?.toLowerCase().endsWith(".pdf");
  const docIsOffice=(()=>{const ext=doc?.name?.split(".").pop()?.toLowerCase();return ["docx","docm","xlsx","xlsm","csv"].includes(ext);})();
  const shouldPreview=activeTab==="preview"&&!!doc?.file_path&&(docIsImage||docIsPdf||docIsOffice);
  // 2026-05-14: blob-URL flow. Previously this useEffect set previewUrl
  // to the signed URL directly and the iframe loaded it cross-origin.
  // Vercel's catch-all CSP applies frame-ancestors 'none' to every
  // response served from app.klassdoc.com, including the proxied
  // /api/downloads/* responses, so Chrome blocked the iframe with
  // "refused to connect". Fetching the bytes and rendering via a
  // same-origin blob: URL sidesteps all server-response framing
  // headers — the iframe sees a blob (no headers, no origin checks).
  useEffect(()=>{
    if(!shouldPreview||!doc?.id){setPreviewUrl(null);setPreviewLoading(false);return;}
    let cancelled=false;
    let blobUrlRef=null;
    setPreviewUrl(null);setPreviewLoading(true);
    (async()=>{
      try{
        const signed=await api.signedUrl("document",doc.id,{purpose:"preview"});
        if(cancelled||!signed)throw new Error("no-url");
        // For DOCX/XLSX: hit the *-html endpoint variant (same token,
        // different path segment) to get a server-converted HTML
        // preview. For PDFs/images: hit the raw stream endpoint as
        // before. Both responses get rendered via blob URL.
        const fetchUrl = docIsOffice
          ? signed.replace("/api/downloads/document/", "/api/downloads/document-html/")
          : signed;
        const r=await fetch(fetchUrl,{credentials:"same-origin"});
        if(cancelled)return;
        if(!r.ok)throw new Error(`fetch-${r.status}`);
        const blob=await r.blob();
        if(cancelled)return;
        blobUrlRef=URL.createObjectURL(blob);
        setPreviewUrl(blobUrlRef);
        setPreviewLoading(false);
      }catch(e){
        if(cancelled)return;
        setPreviewLoading(false);
        if(e?.message!=="no-url" && !String(e?.message).startsWith("fetch-404") && e?.status!==404 && e?.status!==401){
          console.warn("[DocPreview] preview failed:",e?.message||e);
        }
      }
    })();
    return()=>{
      cancelled=true;
      if(blobUrlRef){ try{ URL.revokeObjectURL(blobUrlRef); }catch{} }
    };
  },[shouldPreview,doc?.id,docIsOffice]);

  async function handleForceRelease(){
    // Sprint 10C Priority 5: pass {force:true} as an explicit signal of intent.
    // The backend derives the actual release reason from row ownership (caller
    // vs. lock owner), so this flag is belt-and-suspenders — it makes the
    // admin intent legible in network traces and lets any future server-side
    // policy (e.g., requiring explicit confirmation) key off it.
    setForceLoading(true);
    try{await api.del(`/checkouts/${doc.id}`,{force:true});setCheckoutStatus({checked_out:false});toast(lang==="fr"?"Réservation forcée libérée":"Checkout force-released","success");}
    catch(e){toast(e.message,"error");}setForceLoading(false);
  }
  if(!doc)return null;
  const isImage=docIsImage;
  const isPdf=docIsPdf;
  async function handleMove(){try{await api.put(`/documents/${doc.id}/move`,{folder_id:selectedFolder||null});toast(T(lang,"docMoved"),"success");setMoveMode(false);onAction?.();}catch{toast(T(lang,"failMove"),"error");}}
  async function handleDelete(){try{await api.del(`/documents/${doc.id}`);toast(T(lang,"docDeleted"),"success");onAction?.();onClose();}catch{toast(T(lang,"failDel"),"error");}}

  const detailTabs=[{id:"preview",label:lang==="fr"?"Aperçu":"Preview",icon:lucide.Eye},{id:"metadata",label:T(lang,"metadata"),icon:lucide.Tags},{id:"versions",label:T(lang,"versions"),icon:lucide.GitBranch},{id:"comments",label:T(lang,"comments"),icon:lucide.MessageSquare}];

  return(
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <motion.div initial={{opacity:0,scale:0.95,y:20}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.95}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:16,width:"100%",maxWidth:740,maxHeight:"90vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
        {/* Header */}
        <div style={{padding:"18px 24px",borderBottom:`1px solid ${THEME.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:12,flex:1,minWidth:0}}>
            <FileTypeBadge doc={doc} size={40} radius={10}/>
            <div style={{minWidth:0}}><div style={{fontSize:15,fontWeight:600,color:THEME.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{doc.name}</div><div style={{fontSize:12,color:THEME.textDim,marginTop:2}}><span style={{fontFamily:FONT_MONO,color:THEME.accent}}>{formatDocId(doc.id)}</span> · {doc.folder||T(lang,"root")} · {formatBytes(doc.size)} · v{doc.current_version||1}{isLockedByOther&&<span style={{marginLeft:8,padding:"1px 7px",borderRadius:10,fontSize:10,fontWeight:600,background:THEME.warningDim,color:THEME.warning}}><lucide.Lock size={9} style={{display:"inline",verticalAlign:"-1px"}}/> {checkoutStatus?.user_name}</span>}</div></div>
          </div><lucide.X size={18} color={THEME.textMuted} style={{cursor:"pointer",flexShrink:0,marginLeft:12}} onClick={onClose}/>
        </div>

        {/* Tab bar */}
        <div style={{display:"flex",gap:0,borderBottom:`1px solid ${THEME.border}`,padding:"0 24px"}}>
          {detailTabs.map(t=>{const isA=activeTab===t.id;return <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{...S.btn,padding:"10px 16px",borderRadius:0,fontSize:12,background:"transparent",color:isA?THEME.accent:THEME.textDim,borderBottom:isA?`2px solid ${THEME.accent}`:"2px solid transparent"}}><t.icon size={13}/>{t.label}</button>;})}
        </div>

        {/* Content */}
        <div style={{flex:1,overflow:"auto",padding:24}}>
          {/* Sprint 6.7: Locked by another user banner */}
          {isLockedByOther&&(
            <div style={{padding:"12px 18px",background:THEME.warningDim,border:`1px solid ${THEME.warning}30`,borderRadius:10,marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:32,height:32,borderRadius:8,background:THEME.warning+"20",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><lucide.Lock size={16} color={THEME.warning}/></div>
              <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:THEME.warning}}>{T(lang,"docLocked")} {checkoutStatus?.user_name}</div><div style={{fontSize:11,color:THEME.textDim,marginTop:2}}>{T(lang,"readOnlyMode")}</div></div>
              {isAdmin&&<button onClick={handleForceRelease} disabled={forceLoading} style={{...S.btn,padding:"6px 12px",fontSize:11,background:THEME.dangerDim,color:THEME.danger,border:`1px solid ${THEME.danger}30`}}>{forceLoading?<Spinner size={12}/>:<><lucide.Unlock size={12}/>{T(lang,"forceRelease")}</>}</button>}
            </div>
          )}
          {activeTab==="preview"&&(<>
            {isImage&&doc.file_path?(<div style={{textAlign:"center"}}>{previewLoading||!previewUrl?<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:300}}><Spinner size={22}/></div>:<img src={previewUrl} alt={doc.name} style={{maxWidth:"100%",maxHeight:400,borderRadius:8,border:`1px solid ${THEME.border}`}} onError={e=>{e.target.style.display="none";}}/>}</div>)
            :isPdf&&doc.file_path?(<div style={{borderRadius:8,overflow:"hidden",border:`1px solid ${THEME.border}`,height:400,background:THEME.surface}}><div style={{padding:"8px 14px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"center",gap:8}}><lucide.FileText size={14} color={THEME.accent}/><span style={{fontSize:12,fontWeight:600,color:THEME.textMuted}}>{T(lang,"pdfPreview")}</span></div>{previewLoading||!previewUrl?<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"calc(100% - 36px)",background:"#fff"}}><Spinner size={22}/></div>:<iframe src={previewUrl} style={{width:"100%",height:"calc(100% - 36px)",border:"none",background:"#fff"}} title={doc.name}/>}</div>)
            :docIsOffice&&doc.file_path?(<div style={{borderRadius:8,overflow:"hidden",border:`1px solid ${THEME.border}`,height:500,background:THEME.surface}}><div style={{padding:"8px 14px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"center",gap:8}}>{doc.name?.toLowerCase().endsWith(".xlsx")||doc.name?.toLowerCase().endsWith(".xlsm")||doc.name?.toLowerCase().endsWith(".csv")?<lucide.Table size={14} color={THEME.success}/>:<lucide.FileType size={14} color={THEME.info}/>}<span style={{fontSize:12,fontWeight:600,color:THEME.textMuted}}>{lang==="fr"?"Aperçu converti":"HTML preview"}</span><span style={{marginLeft:"auto",fontSize:10,color:THEME.textDim,fontStyle:"italic"}}>{lang==="fr"?"Approximation — ouvrez dans Office pour le rendu exact":"Approximation — open in Office for exact rendering"}</span></div>{previewLoading||!previewUrl?<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"calc(100% - 36px)",background:"#fff"}}><Spinner size={22}/></div>:<iframe src={previewUrl} style={{width:"100%",height:"calc(100% - 36px)",border:"none",background:"#fff"}} title={doc.name}/>}</div>)
            :(<div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:60,background:THEME.surface,borderRadius:8,border:`1px solid ${THEME.border}`}}><div style={{textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center"}}><FileTypeBadge doc={doc} size={48} radius={12}/><div style={{marginTop:12,fontSize:14,color:THEME.textMuted}}>{doc.name?.split(".").pop()?.toUpperCase()} {T(lang,"document")}</div><div style={{marginTop:6,fontSize:12,color:THEME.textDim}}>{T(lang,"previewFor")}</div></div></div>)}
            <div style={{marginTop:20,display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
              {[["DOCUMENT ID",formatDocId(doc.id)],[T(lang,"type"),doc.type?.toUpperCase()||"—"],[T(lang,"size"),formatBytes(doc.size)],[T(lang,"folder"),doc.folder||T(lang,"root")],[T(lang,"uploadedBy"),doc.uploaded_by||"—"],[T(lang,"created"),formatFullDate(doc.created_at,lang)],[T(lang,"modified"),formatFullDate(doc.updated_at,lang)],[T(lang,"version"),"v"+(doc.current_version||1)]].map(([l,v])=>(<div key={l} style={{padding:"10px 14px",background:THEME.surface,borderRadius:8,border:`1px solid ${THEME.borderSubtle}`}}><div style={{fontSize:10,color:THEME.textDim,textTransform:"uppercase",letterSpacing:0.3,marginBottom:4}}>{l}</div><div style={{fontSize:13,color:l==="DOCUMENT ID"?THEME.accent:THEME.text,fontWeight:500,fontFamily:l==="DOCUMENT ID"?FONT_MONO:FONT}}>{v}</div></div>))}
            </div>
            <AnimatePresence>{moveMode&&(<motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{marginTop:16,overflow:"hidden"}}><div style={{padding:16,background:THEME.surface,borderRadius:8,border:`1px solid ${THEME.border}`}}><div style={{fontSize:13,fontWeight:600,color:THEME.text,marginBottom:10}}>{T(lang,"moveToFolder")}</div><div style={{display:"flex",gap:10}}><select value={selectedFolder} onChange={e=>setSelectedFolder(e.target.value)} style={{...S.input,flex:1}}><option value="">{T(lang,"rootFolder")}</option>{(()=>{function buildOpts(parentId,depth){return folders.filter(f=>(f.parent_id||null)===(parentId||null)).flatMap(f=>[{id:f.id,label:"\u00A0\u00A0".repeat(depth)+(depth>0?"└ ":"")+f.name},...buildOpts(f.id,depth+1)]);}return buildOpts(null,0).map(f=><option key={f.id} value={f.id}>{f.label}</option>);})()}</select><button style={{...S.btn,background:THEME.accent,color:"#fff"}} onClick={handleMove}>{T(lang,"move")}</button><button style={{...S.btn,background:THEME.surfaceHover,color:THEME.textMuted}} onClick={()=>setMoveMode(false)}>{T(lang,"cancel")}</button></div></div></motion.div>)}</AnimatePresence>
            <AnimatePresence>{confirmDelete&&(<motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{marginTop:16,overflow:"hidden"}}><div style={{padding:16,background:THEME.dangerDim,borderRadius:8,border:`1px solid ${THEME.danger}40`}}><div style={{fontSize:13,color:THEME.danger,fontWeight:600,marginBottom:10}}>{T(lang,"confirmDel")} "{doc.name}" ?</div><div style={{display:"flex",gap:10}}><button style={{...S.btn,background:THEME.danger,color:"#fff"}} onClick={handleDelete}>{T(lang,"delete")}</button><button style={{...S.btn,background:THEME.surfaceHover,color:THEME.textMuted}} onClick={()=>setConfirmDelete(false)}>{T(lang,"cancel")}</button></div></div></motion.div>)}</AnimatePresence>
          </>)}

          {activeTab==="metadata"&&<MetadataPanel docId={doc.id} doc={doc}/>}
          {activeTab==="versions"&&<VersionPanel docId={doc.id} docName={doc.name}/>}
          {activeTab==="comments"&&<CommentsPanel docId={doc.id}/>}
        </div>

        {/* Footer actions */}
        <div style={{padding:"14px 24px",borderTop:`1px solid ${THEME.border}`,display:"flex",gap:10,justifyContent:"flex-end",flexWrap:"wrap"}}>
          <button style={{...S.btn,background:THEME.warningDim||"#2A2520",color:THEME.warning,border:`1px solid ${THEME.warning}30`}} onClick={()=>setShowSecurityModal(true)}><lucide.Shield size={15}/> {lang==="fr"?"Sécurité":"Security"}</button>
          {/* Open in browser tab — for browser-viewable non-Office types
              (PDF, images, text, audio/video). Counterpart to "Open in
              Word/Excel" so reading a file never forces a download into
              the user's Downloads folder. Opens the standalone view-lock
              viewer tab (?viewer_doc=<id> → DocViewerPage), which takes a
              source='view' checkout while the tab is open and releases it
              on close — so an open file shows on Checked Out and a second
              user gets the read-only banner, matching the Office flow. */}
          {(()=>{
            const ext=doc.name?.split(".").pop()?.toLowerCase();
            const viewableExts=["pdf","png","jpg","jpeg","gif","webp","svg","txt","md","log","mp4","webm","mp3","wav","ogg","m4a"];
            if(!viewableExts.includes(ext)||!doc.file_path)return null;
            return(
              <button style={{...S.btn,background:"#1a5276",color:"#5dade2",border:"1px solid #2980b940"}} onClick={()=>{
                window.open(`/?viewer_doc=${doc.id}`,"_blank");
              }}><lucide.ExternalLink size={15}/> {lang==="fr"?"Ouvrir":"Open"}</button>
            );
          })()}
          {/* Sprint 8A: Open in Office button */}
          {canEdit&&(()=>{const ext=doc.name?.split(".").pop()?.toLowerCase();const officeExts=["doc","docx","docm","rtf","xls","xlsx","xlsm","csv","ppt","pptx","pptm"];const isOffice=officeExts.includes(ext);const appNames={doc:"Word",docx:"Word",docm:"Word",rtf:"Word",xls:"Excel",xlsx:"Excel",xlsm:"Excel",csv:"Excel",ppt:"PowerPoint",pptx:"PowerPoint",pptm:"PowerPoint"};const appName=appNames[ext]||"Office";const appIcons={Word:lucide.FileType,Excel:lucide.Table,PowerPoint:lucide.Presentation};const AppIcon=appIcons[appName]||lucide.ExternalLink;return isOffice?(<button style={{...S.btn,background:isReadOnly?THEME.surface:"#1a5276",color:isReadOnly?THEME.textDim:"#5dade2",border:`1px solid ${isReadOnly?"transparent":"#2980b940"}`,opacity:isReadOnly?0.5:1,cursor:isReadOnly?"not-allowed":"pointer"}} disabled={isReadOnly} onClick={async()=>{if(isReadOnly)return;try{const urlData=await api.get(`/documents/${doc.id}/webdav-url`);if(urlData.protocol_url){window.location.href=urlData.protocol_url;toast(T(lang,"webdavOpening"),"info");}else{toast(T(lang,"notOfficeType"),"warning");}}catch(e){toast(e.message||"Failed to get WebDAV URL","error");}}}><AppIcon size={15}/> {lang==="fr"?`Ouvrir dans ${appName}`:`Open in ${appName}`}</button>):null;})()}
          {canEdit&&<button style={{...S.btn,background:isReadOnly?THEME.surface:THEME.surfaceHover,color:isReadOnly?THEME.textDim:THEME.textMuted,border:`1px solid ${THEME.border}`,opacity:isReadOnly?0.5:1,cursor:isReadOnly?"not-allowed":"pointer"}} onClick={()=>{if(!isReadOnly){setMoveMode(!moveMode);setConfirmDelete(false);setActiveTab("preview");}}} disabled={isReadOnly}><lucide.FolderInput size={15}/> {T(lang,"move")}</button>}
          {isAdmin&&<button style={{...S.btn,background:isReadOnly?THEME.surface:THEME.dangerDim,color:isReadOnly?THEME.textDim:THEME.danger,border:`1px solid ${isReadOnly?THEME.border:THEME.danger+"30"}`,opacity:isReadOnly?0.5:1,cursor:isReadOnly?"not-allowed":"pointer"}} onClick={()=>{if(!isReadOnly){setConfirmDelete(!confirmDelete);setMoveMode(false);setActiveTab("preview");}}} disabled={isReadOnly}><lucide.Trash2 size={15}/> {T(lang,"delete")}</button>}
          <button style={{...S.btn,background:THEME.accent,color:"#fff"}} onClick={async()=>{
            if(!doc.file_path){toast(T(lang,"noFile"),"warning");return;}
            try{
              const url=await api.signedUrl("document",doc.id,{purpose:"download"});
              if(url)window.open(url,"_blank");
            }catch(e){
              if(e?.status===404)toast(T(lang,"noFile"),"warning");
              else toast(e?.message||(lang==="fr"?"Échec du téléchargement":"Download failed"),"error");
            }
          }}><lucide.Download size={15}/> {T(lang,"download")}</button>
        </div>
        {/* Security Modals — Sprint 10C H10-B2 Item 6:
            - Documents in folders (folder_id != null): show read-only
              effective view sourced from the top-level folder's ACL.
              User can jump to the FolderSecurityModal from there if
              they have manage rights.
            - Root documents (folder_id == null): keep the Sprint 6.5
              ManageSecurityModal (doc-level ACL still honored for
              root docs, per H10 Design Spec §Root documents).
         */}
        <AnimatePresence>
          {showSecurityModal && doc.folder_id && !folderSecurityForDoc && (
            <DocumentEffectiveSecurityView
              doc={doc}
              onClose={()=>setShowSecurityModal(false)}
              onOpenFolderSecurity={(top)=>{setShowSecurityModal(false);setFolderSecurityForDoc(top);}}
              onSuccess={()=>{toast(lang==="fr"?"Sécurité mise à jour":"Security updated","success");onAction?.();}}
              api={api}
              currentUser={user}
              isAdmin={isAdmin}
              lang={lang}
              THEME={THEME}
            />
          )}
          {showSecurityModal && !doc.folder_id && (
            <ManageSecurityModal
              doc={{...doc, uploaded_by_id: doc.uploaded_by_id || user?.id}}
              myAccess={isAdmin ? "full" : (doc._access_level || "full")}
              onClose={()=>setShowSecurityModal(false)}
              onSuccess={()=>{toast(lang==="fr"?"Permissions mises à jour":"Permissions updated","success");setShowSecurityModal(false);onAction?.();}}
              api={api}
              currentUser={user}
              lang={lang}
              THEME={THEME}
            />
          )}
          {folderSecurityForDoc && (
            <FolderSecurityModal
              folder={folderSecurityForDoc}
              onClose={()=>setFolderSecurityForDoc(null)}
              onSuccess={()=>{toast(lang==="fr"?"Permissions mises à jour":"Permissions updated","success");setFolderSecurityForDoc(null);onAction?.();}}
              api={api}
              currentUser={user}
              lang={lang}
              THEME={THEME}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

// ─── Sprint 11.x redesign — visual helpers used by the new
// DashboardPage + DocumentsPage. Sparklines are decorative SVGs
// for now; can be wired to a real time-series endpoint later.
// ─────────────────────────────────────────────────────────────
function StatSparkLine({color}){
  const gid="sg-"+color.replace("#","");
  return(
    <svg style={{position:"absolute",bottom:0,left:0,right:0,height:38,pointerEvents:"none",opacity:0.85}} viewBox="0 0 200 40" preserveAspectRatio="none">
      <defs><linearGradient id={gid} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={color} stopOpacity="0.35"/><stop offset="1" stopColor={color} stopOpacity="0"/></linearGradient></defs>
      <path d="M0 32 L25 30 L50 28 L75 24 L100 22 L125 18 L150 14 L175 10 L200 8" stroke={color} strokeWidth="1.6" fill="none"/>
      <path d="M0 32 L25 30 L50 28 L75 24 L100 22 L125 18 L150 14 L175 10 L200 8 L200 40 L0 40 Z" fill={`url(#${gid})`}/>
    </svg>
  );
}
function StatSparkDashed({color}){
  return(
    <svg style={{position:"absolute",bottom:0,left:0,right:0,height:38,pointerEvents:"none",opacity:0.85}} viewBox="0 0 200 40" preserveAspectRatio="none">
      <path d="M0 25 L50 25 L100 22 L150 22 L200 20" stroke={color} strokeWidth="1.6" fill="none" strokeDasharray="2 3"/>
    </svg>
  );
}
function StatSparkBars({color}){
  const bars=[[10,28,6],[30,24,10],[50,20,14],[70,22,12],[90,16,18],[110,18,16],[130,12,22],[150,10,24],[170,8,26]];
  return(
    <svg style={{position:"absolute",bottom:0,left:0,right:0,height:38,pointerEvents:"none",opacity:0.85}} viewBox="0 0 200 40" preserveAspectRatio="none">
      {bars.map(([x,y,h],i)=>(<rect key={i} x={x} y={y} width="6" height={h} fill={color} opacity={0.4+i*0.06}/>))}
    </svg>
  );
}

// Shared styles used by the redesigned pages.
const monoLabel={fontFamily:FONT_MONO,fontSize:10.5,color:THEME.textDim,letterSpacing:"0.02em"};
const trendChip=(tone)=>({
  fontFamily:FONT_MONO,fontSize:10.5,padding:"2px 7px",borderRadius:4,
  display:"inline-flex",alignItems:"center",gap:3,letterSpacing:"0.02em",
  color:tone==="up"?THEME.success:tone==="warn"?THEME.accent2:THEME.textDim,
  background:tone==="up"?THEME.successDim:tone==="warn"?THEME.accentDim:"rgba(255,255,255,0.03)",
});

// ─── DASHBOARD — Sprint 11.x redesign (eyebrow header, stat cards
// with sparklines, two-col docs+activity, quick actions grid).
// All API calls, modals, conflict handling preserved from the prior
// design — only the JSX/visual layer was rewritten.
// ─────────────────────────────────────────────────────────────
function DashboardPage(){
  const {toast,canEdit,refreshSignal,triggerRefresh,navigateTo,user}=useApp();const {lang}=useLang();
  const [stats,setStats]=useState({documents:0,folders:0,users:0,storage_gb:0});
  const [recentDocs,setRecentDocs]=useState([]);const [allFolders,setAllFolders]=useState([]);
  // Sprint 11.x: doc lookup maps for enriching audit-log targets that
  // arrive as bare ids ("3", "4"), "document:N", or as raw filenames
  // (uploads). Same broader fetch feeds recentDocs (first 5) plus the
  // activity feed. docsByName lets us resolve target="Versements
  // Hebdomadaire.xlsx" to its id + version.
  const [docsById,setDocsById]=useState({});
  const [docsByName,setDocsByName]=useState({});
  const [previewDoc,setPreviewDoc]=useState(null);const [auditLog,setAuditLog]=useState([]);
  const [showUpPicker,setShowUpPicker]=useState(false);const [selUpFolder,setSelUpFolder]=useState("");
  const [uploading,setUploading]=useState(false);const dashFileRef=useRef();
  const [dashPendingFile,setDashPendingFile]=useState(null);
  // Quick Action: New Folder modal — Sprint 10C H10-B2 uses FolderCreateModal
  const [showNewFolder,setShowNewFolder]=useState(false);
  // Quick Action: New Courrier modal
  const [showNewCourrier,setShowNewCourrier]=useState(false);const [courrierForm,setCourrierForm]=useState({type:"incoming",subject:"",sender:"",recipient:"",department:"",priority:"normal",due_date:"",notes:""});const [creatingCourrier,setCreatingCourrier]=useState(false);
  async function handleCreateCourrier(){
    if(!courrierForm.subject.trim())return;setCreatingCourrier(true);
    try{await api.post("/courrier",courrierForm);toast(lang==="fr"?"Courrier créé avec succès":"Courrier created successfully","success");setCourrierForm({type:"incoming",subject:"",sender:"",recipient:"",department:"",priority:"normal",due_date:"",notes:""});setShowNewCourrier(false);triggerRefresh();}
    catch(e){toast(e.message,"error");}setCreatingCourrier(false);
  }
  // Sprint 6.7
  const [conflictDoc,setConflictDoc]=useState(null);const [conflictStatus,setConflictStatus]=useState(null);
  async function openDocument(doc){
    try{
      const status=await api.get(`/checkouts/status/${doc.id}`);
      if(status.checked_out&&!status.is_mine){setConflictDoc(doc);setConflictStatus(status);return;}
    }catch{}
    // Sprint 11.x DMS-correct behavior: clicking a document opens the
    // preview modal (metadata + actions: download, open-in-Word, version,
    // security). Downloading is an EXPLICIT action inside the modal, not
    // the default click behavior — matches iManage / NetDocuments /
    // OpenText DMS UX where the row click never auto-downloads.
    setPreviewDoc(doc);
  }
  function openReadOnly(doc){setPreviewDoc(doc);setConflictDoc(null);setConflictStatus(null);}

  // Sprint 11.x bugfix — access-filtered counts.
  // /api/dashboard/stats returns ORG-WIDE numbers (4 docs in this org,
  // 0.04 GB stored, etc.). Those should NOT leak to non-admins through
  // the dashboard header subtitle ("4 documents") nor the KPI tiles.
  // The KPI tiles are already admin-only via isAdminOrSuper(user), but
  // the header subtitle used to read stats.documents directly — so an
  // editor with zero accessible docs still saw "4 documents · …".
  //
  // Fix: pull the count + size from /api/documents (which goes through
  // filterListResponse — admin sees org-wide, everyone else sees their
  // accessible slice) and use those numbers in the subtitle.
  const [accessibleDocCount,setAccessibleDocCount]=useState(0);
  const [accessibleDocBytes,setAccessibleDocBytes]=useState(0);

  const loadData=useCallback(()=>{
    api.get("/dashboard/stats").then(setStats).catch(e=>console.error("Stats:",e));
    // Pull a wider doc set so the audit-log feed can resolve targets
    // by id even when only a few rows render on the visible dashboard.
    api.get("/documents?limit=500&sort=updated_at").then(r=>{
      const all=r.data||[];
      setRecentDocs(all.slice(0,5));
      const byId={};const byName={};let bytes=0;
      for(const d of all){byId[d.id]=d;if(d.name)byName[d.name]=d;bytes+=Number(d.size)||0;}
      setDocsById(byId);setDocsByName(byName);
      // r.total is the ACL-filtered count (filterListResponse overwrites
      // it with filtered.length for non-admins; for admins it's the
      // org-wide COUNT(*) from the route's own query).
      setAccessibleDocCount(typeof r?.total==="number"?r.total:all.length);
      setAccessibleDocBytes(bytes);
    }).catch(()=>{});
    api.get("/folders").then(r=>setAllFolders(r.data||[])).catch(()=>{});
    api.get("/audit-log").then(r=>setAuditLog((r.data||[]).slice(0,6))).catch(()=>{});
  },[]);
  useEffect(()=>{loadData();},[refreshSignal,loadData]);

  const rootCount=allFolders.filter(f=>!f.parent_id).length;
  const subCount=allFolders.filter(f=>f.parent_id).length;
  const statCards=[
    {label:T(lang,"docsLabel"),value:stats.documents?.toLocaleString()||"0",icon:lucide.FileText,color:THEME.accent},
    {label:T(lang,"foldersLabel"),value:`${rootCount}`,sub:subCount>0?`+${subCount} ${T(lang,"subfolders")}`:null,icon:lucide.FolderOpen,color:THEME.info},
    {label:T(lang,"teamMembers"),value:stats.users||0,icon:lucide.Users,color:THEME.success},
    {label:T(lang,"storageUsed"),value:(stats.storage_gb||0)+" GB",icon:lucide.HardDrive,color:THEME.warning},
  ];

  function handleDashUpload(files){
    if(!files?.length)return;
    // Sprint 6.5 Wave 3: open properties modal instead of instant upload
    setDashPendingFile(files[0]);
    setShowUpPicker(false);
    if(dashFileRef.current)dashFileRef.current.value="";
  }
  function onDashUploadSuccess(uploaded){
    toast(`"${uploaded?.name||'Document'}" ${T(lang,"filesUp")}`,"success");
    setDashPendingFile(null);
    triggerRefresh();
  }

  return(
    <div>
      {/* ── Page header with greeting + eyebrow ── */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:24,marginBottom:24,flexWrap:"wrap"}}>
        <div>
          <div style={{...monoLabel,letterSpacing:"0.16em",textTransform:"uppercase",marginBottom:6,display:"flex",alignItems:"center",gap:10}}>
            <span style={{width:18,height:1,background:THEME.line3,display:"inline-block"}}/>
            <span>{(()=>{const d=new Date();const dm=d.toLocaleDateString(lang==="fr"?"fr-FR":"en-US",{weekday:"long",month:"long",day:"numeric"});const tm=d.toLocaleTimeString(lang==="fr"?"fr-FR":"en-US",{hour:"2-digit",minute:"2-digit"});return (lang==="fr"?"Espace de travail":"Workspace")+" · "+dm+" · "+tm;})()}</span>
          </div>
          <h1 style={{fontSize:32,fontWeight:600,letterSpacing:"-0.025em",color:THEME.text,margin:"0 0 6px",lineHeight:1.1}}>
            {(()=>{const h=new Date().getHours();return lang==="fr"?(h<12?"Bonjour":h<18?"Bon après-midi":"Bonsoir"):(h<12?"Good morning":h<18?"Good afternoon":"Good evening");})()}, {(user?.name||"").split(" ")[0]} <em style={{fontFamily:FONT_SERIF,fontStyle:"italic",fontWeight:400,color:THEME.accent2}}>—</em> {lang==="fr"?"voici votre archive":"here's your archive"}
          </h1>
          <div style={{color:THEME.textMuted,fontSize:13.5}}>
            {/* ACL-filtered count for non-admins, org-wide for admins.
                rootCount/subCount already come from the access-filtered
                /api/folders endpoint (folders.js:97 listAccessibleTopLevel). */}
            {accessibleDocCount} {lang==="fr"?(accessibleDocCount===1?"document":"documents"):(accessibleDocCount===1?"document":"documents")} · {rootCount} {lang==="fr"?(rootCount===1?"dossier":"dossiers"):(rootCount===1?"folder":"folders")}{subCount>0?(lang==="fr"?" avec "+subCount+" sous-dossier"+(subCount>1?"s":""):" with "+subCount+" subfolder"+(subCount>1?"s":"")):""} · {lang==="fr"?"dernière synchro":"last synced"} {auditLog[0]?.timestamp?formatDate(auditLog[0].timestamp,lang):(lang==="fr"?"à l'instant":"just now")}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          {canEdit&&<button onClick={()=>setShowNewFolder(true)} style={{display:"inline-flex",alignItems:"center",gap:7,height:38,padding:"0 16px",borderRadius:8,fontSize:13,fontWeight:600,border:`1px solid ${THEME.line2}`,background:THEME.surfaceRaised,color:THEME.ink2,cursor:"pointer",fontFamily:FONT}}><lucide.Folder size={14}/>{lang==="fr"?"Nouveau dossier":"New folder"}</button>}
          {canEdit&&<button onClick={()=>dashFileRef.current?.click()} style={{display:"inline-flex",alignItems:"center",gap:7,height:38,padding:"0 16px",borderRadius:8,fontSize:13,fontWeight:600,border:"1px solid rgba(255,255,255,0.12)",background:`linear-gradient(180deg,${THEME.accent2},${THEME.accent})`,color:"#fff",cursor:"pointer",fontFamily:FONT,boxShadow:`0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px ${THEME.accentDim}`}}><lucide.Upload size={14}/>{lang==="fr"?"Téléverser un document":"Upload document"}</button>}
          <input ref={dashFileRef} type="file" multiple hidden onChange={e=>handleDashUpload(e.target.files)}/>
        </div>
      </div>

      {/* ── 4 stat cards with sparklines (admin-only per S10C post-F) ── */}
      {isAdminOrSuper(user)&&(()=>{
        const docQuota=50; const seatsLeft=Math.max(0,5-(stats.users||0)); const storagePlanGB=5;
        const storagePct=Math.min(100,((stats.storage_gb||0)/storagePlanGB)*100);
        const storageMB=((stats.storage_gb||0)*1024).toFixed(1);
        return(
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:22}}>
            {/* Documents */}
            <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:0.05}} style={{position:"relative",background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,padding:"18px 18px 16px",overflow:"hidden"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                <div style={{width:34,height:34,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:THEME.accentDim,border:`1px solid ${THEME.accentLine}`,color:THEME.accent}}><lucide.FileText size={16}/></div>
                <span style={trendChip("up")}>+{recentDocs.length} {lang==="fr"?"cette semaine":"this week"}</span>
              </div>
              <div style={{fontSize:36,fontWeight:600,letterSpacing:"-0.03em",color:THEME.text,lineHeight:1,display:"flex",alignItems:"baseline",gap:6,fontVariantNumeric:"tabular-nums"}}>{stats.documents||0}<span style={{fontSize:13,color:THEME.textMuted,fontWeight:500}}>/ {docQuota} {lang==="fr"?"quota":"quota"}</span></div>
              <div style={{fontSize:12,color:THEME.textMuted,marginTop:6,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <span>{T(lang,"documents")}</span>
                <span style={monoLabel}>{(stats.documents||0)*2} {lang==="fr"?"versions":"versions"}</span>
              </div>
            </motion.div>
            {/* Folders */}
            <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:0.1}} style={{position:"relative",background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,padding:"18px 18px 16px",overflow:"hidden"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                <div style={{width:34,height:34,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:THEME.infoDim,border:`1px solid ${THEME.infoLine}`,color:THEME.info}}><lucide.Folder size={16}/></div>
                <span style={trendChip("flat")}>{lang==="fr"?"stable":"stable"}</span>
              </div>
              <div style={{fontSize:36,fontWeight:600,letterSpacing:"-0.03em",color:THEME.text,lineHeight:1,display:"flex",alignItems:"baseline",gap:6,fontVariantNumeric:"tabular-nums"}}>{rootCount}<span style={{fontSize:13,color:THEME.textMuted,fontWeight:500}}>+ {subCount} {lang==="fr"?"imbriqués":"nested"}</span></div>
              <div style={{fontSize:12,color:THEME.textMuted,marginTop:6,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <span>{T(lang,"folders")}</span>
                <span style={monoLabel}>{lang==="fr"?"profondeur":"depth"} {subCount>0?2:1}</span>
              </div>
            </motion.div>
            {/* Team */}
            <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:0.15}} style={{position:"relative",background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,padding:"18px 18px 16px",overflow:"hidden"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                <div style={{width:34,height:34,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:THEME.successDim,border:`1px solid ${THEME.successLine}`,color:THEME.success}}><lucide.Users size={16}/></div>
                <span style={trendChip("up")}>{stats.users||0} {lang==="fr"?"actifs":"active"}</span>
              </div>
              <div style={{fontSize:36,fontWeight:600,letterSpacing:"-0.03em",color:THEME.text,lineHeight:1,display:"flex",alignItems:"baseline",gap:6,fontVariantNumeric:"tabular-nums"}}>{stats.users||0}<span style={{fontSize:13,color:THEME.textMuted,fontWeight:500}}>{lang==="fr"?"actifs":"active"}</span></div>
              <div style={{fontSize:12,color:THEME.textMuted,marginTop:6,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <span>{T(lang,"teamMembers")}</span>
                <span style={monoLabel}>{seatsLeft} {lang==="fr"?"places libres":"seats left"}</span>
              </div>
            </motion.div>
            {/* Storage */}
            <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:0.2}} style={{position:"relative",background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,padding:"18px 18px 16px",overflow:"hidden"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                <div style={{width:34,height:34,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:THEME.surfaceHover,border:`1px solid ${THEME.line2}`,color:THEME.ink2}}><lucide.Archive size={16}/></div>
                <span style={trendChip("warn")}>{storageMB} MB {lang==="fr"?"utilisés":"used"}</span>
              </div>
              <div style={{fontSize:36,fontWeight:600,letterSpacing:"-0.03em",color:THEME.text,lineHeight:1,display:"flex",alignItems:"baseline",gap:6,fontVariantNumeric:"tabular-nums"}}>{(stats.storage_gb||0).toFixed(2)}<span style={{fontSize:14,color:THEME.textMuted,fontWeight:500}}>GB</span></div>
              <div style={{fontSize:12,color:THEME.textMuted,marginTop:6,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <span>{T(lang,"storageUsed")}</span>
                <span style={monoLabel}>{lang==="fr"?"sur":"of"} {storagePlanGB} GB {lang==="fr"?"plan":"plan"}</span>
              </div>
            </motion.div>
          </div>
        );
      })()}

      {/* ── Recent docs + Recent activity (two columns) ── */}
      <div style={{display:"grid",gridTemplateColumns:"1.05fr 1fr",gap:14,marginBottom:22}}>
        {/* Recent documents */}
        <motion.div initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} transition={{delay:0.3}} style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,overflow:"hidden"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 18px 12px"}}>
            <div style={{fontSize:14,fontWeight:600,color:THEME.text,letterSpacing:"-0.01em"}}>{T(lang,"recentDocs")}<span style={{...monoLabel,marginLeft:8,fontWeight:500,color:THEME.textDim}}>{recentDocs.length}</span></div>
            <button onClick={()=>navigateTo("documents")} style={{...monoLabel,letterSpacing:"0.04em",textTransform:"uppercase",color:THEME.textMuted,display:"inline-flex",alignItems:"center",gap:5,border:"none",background:"transparent",cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.color=THEME.accent2} onMouseLeave={e=>e.currentTarget.style.color=THEME.textMuted}>{lang==="fr"?"Ouvrir l'archive":"Open archive"}<lucide.ArrowRight size={11}/></button>
          </div>
          {recentDocs.length===0&&<div style={{padding:"24px 18px",textAlign:"center",color:THEME.textDim,fontSize:13,borderTop:`1px solid ${THEME.border}`}}>{T(lang,"noDocsYet")}</div>}
          {recentDocs.map((doc)=>{
            return(
              <div key={doc.id} onClick={()=>openDocument(doc)} onContextMenu={e=>{e.preventDefault();setPreviewDoc(doc);}} style={{display:"flex",alignItems:"center",gap:14,padding:"11px 18px",borderTop:`1px solid ${THEME.border}`,cursor:"pointer",transition:"background 0.12s"}} onMouseEnter={e=>e.currentTarget.style.background=THEME.surfaceHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <FileTypeBadge doc={doc} size={36}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:500,color:THEME.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:3,display:"flex",alignItems:"center",gap:6}}>{doc.name}<CheckoutBadge doc={doc} lang={lang}/><SecurityBadge doc={doc} lang={lang}/></div>
                  <div style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.textDim,display:"flex",alignItems:"center",gap:8,letterSpacing:"0.01em"}}>
                    <span style={{color:THEME.textMuted,background:THEME.surfaceHover,padding:"1px 5px",borderRadius:3,border:`1px solid ${THEME.line2}`}}>v{doc.current_version||1} · #{doc.id}</span>
                    <span style={{opacity:0.5}}>·</span><span>{doc.folder||T(lang,"root")}</span>
                    <span style={{opacity:0.5}}>·</span><span>{formatBytes(doc.size)}</span>
                  </div>
                </div>
                <div style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.textDim,textAlign:"right",flexShrink:0}}>{formatDate(doc.updated_at,lang)}</div>
              </div>
            );
          })}
          {/* Drop-the-next-file teaser */}
          <div style={{display:"flex",alignItems:"center",gap:14,padding:"11px 18px",borderTop:`1px solid ${THEME.border}`,opacity:0.55,cursor:canEdit?"pointer":"default"}} onClick={()=>canEdit&&dashFileRef.current?.click()}>
            <div style={{width:40,height:50,borderRadius:5,background:"repeating-linear-gradient(45deg,transparent,transparent 4px,rgba(0,0,0,0.04) 4px,rgba(0,0,0,0.04) 5px),#F4F6F9",border:`1px dashed ${THEME.line2}`,flexShrink:0}}/>
            <div style={{flex:1}}>
              <div style={{fontFamily:FONT_SERIF,fontStyle:"italic",fontSize:13,color:THEME.textMuted}}>{lang==="fr"?"Glissez le prochain fichier pour continuer…":"Drop the next file to continue…"}</div>
              <div style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.textDim,marginTop:3}}>{lang==="fr"?"Une fois téléversé il apparaîtra ici":"Once uploaded it will appear here"}</div>
            </div>
            <span style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.textDim}}>—</span>
          </div>
        </motion.div>

        {/* Recent activity */}
        <motion.div initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} transition={{delay:0.4}} style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,overflow:"hidden"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 18px 12px"}}>
            <div style={{fontSize:14,fontWeight:600,color:THEME.text,letterSpacing:"-0.01em"}}>{T(lang,"recentActivity")}<span style={{...monoLabel,marginLeft:8,fontWeight:500,color:THEME.textDim}}>{auditLog.length}</span></div>
            {isAdminOrSuper(user)&&<button onClick={()=>navigateTo("audit")} style={{...monoLabel,letterSpacing:"0.04em",textTransform:"uppercase",color:THEME.textMuted,display:"inline-flex",alignItems:"center",gap:5,border:"none",background:"transparent",cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.color=THEME.accent2} onMouseLeave={e=>e.currentTarget.style.color=THEME.textMuted}>{T(lang,"audit")}<lucide.ArrowRight size={11}/></button>}
          </div>
          <div style={{padding:"4px 0 8px"}}>
            {auditLog.map((log,i)=>{
              const Icon=getActionIcon(log.action);
              const meta=getActionMeta(log.action,lang);
              const userName=log.user||log.user_name||"—";
              // Sprint 11.x: enrich the target so every row reads with
              // the same #id · v{ver} · filename pattern. Audit log stores
              // .target as one of:
              //   • a filename ("Versements Hebdomadaire.xlsx") — uploads
              //   • a colon-prefixed id ("document:12")          — preview, download
              //   • a bare numeric id ("3", "4")                 — metadata updates
              // We resolve via docsById (id-based) and docsByName (filename-based).
              const rawTarget = log.target || "";
              const colonMatch = rawTarget.match(/^(document|folder|user|courrier|department):(\d+)$/);
              const bareIdMatch = /^\d+$/.test(rawTarget);
              const entity = colonMatch ? colonMatch[1] : (bareIdMatch ? "document" : null);
              const lookupId = colonMatch ? colonMatch[2] : (bareIdMatch ? rawTarget : null);
              const lookedUpById = (entity==="document" && lookupId) ? docsById[lookupId] : null;
              const lookedUpByName = (!lookedUpById && rawTarget && !colonMatch && !bareIdMatch) ? docsByName[rawTarget] : null;
              const lookedUp = lookedUpById || lookedUpByName;
              // targetParts: array of {text, mono?, color?, italic?, chip?} we render inline.
              let targetParts = null;
              if (lookedUp) {
                targetParts = [
                  {text:`#${lookedUp.id}`, mono:true, color:THEME.textDim},
                  {text:`v${lookedUp.current_version||1}`, mono:true, color:THEME.accent2, chip:true},
                  {text:lookedUp.name, color:THEME.text, italic:true},
                ];
              } else if (colonMatch || bareIdMatch) {
                targetParts = [
                  {text:entity, color:THEME.textMuted},
                  {text:`#${lookupId}`, mono:true, color:THEME.text},
                ];
              } else if (rawTarget) {
                targetParts = [{text:rawTarget, color:THEME.text, italic:true}];
              }
              // Absolute timestamp for the second line, no day if it's today.
              const ts = log.timestamp || log.created_at;
              const absTime = ts ? (()=>{
                try{
                  const d=new Date(ts);
                  const today=new Date();
                  const sameDay = d.toDateString()===today.toDateString();
                  return sameDay
                    ? d.toLocaleTimeString(lang==="fr"?"fr-FR":"en-US",{hour:"2-digit",minute:"2-digit"})
                    : d.toLocaleDateString(lang==="fr"?"fr-FR":"en-US",{month:"short",day:"numeric"})+" · "+d.toLocaleTimeString(lang==="fr"?"fr-FR":"en-US",{hour:"2-digit",minute:"2-digit"});
                }catch{ return ""; }
              })() : "";
              return(
                <div key={log.id||i} style={{display:"grid",gridTemplateColumns:"32px 1fr auto",gap:12,alignItems:"flex-start",padding:"10px 18px",position:"relative"}}>
                  <span style={{position:"absolute",left:32,top:i===0?"50%":0,bottom:i===auditLog.length-1?"50%":0,width:1,background:`linear-gradient(180deg,transparent,${THEME.line2} 12%,${THEME.line2} 88%,transparent)`}}/>
                  <div style={{width:24,height:24,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",background:meta.bg,border:`1px solid ${meta.bd}`,color:meta.fg,position:"relative",zIndex:1,marginTop:1}}><Icon size={11}/></div>
                  <div style={{minWidth:0}}>
                    {/* Main line — natural language sentence, action verb tinted to match the dot */}
                    <div style={{fontSize:12.5,color:THEME.ink2,lineHeight:1.5,overflow:"hidden",textOverflow:"ellipsis"}}>
                      <span style={{color:THEME.text,fontWeight:600}}>{userName}</span>
                      <span style={{color:meta.fg,fontWeight:500,marginLeft:5}}>{meta.label}</span>
                    </div>
                    {/* Sub-line — the document target (id chip · version chip · filename in italic) */}
                    {targetParts&&<div style={{marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                      {targetParts.map((p,pi)=>(
                        p.chip
                          ? <span key={pi} style={{fontFamily:FONT_MONO,fontSize:10,fontWeight:600,color:p.color||THEME.accent2,background:THEME.accentDim,border:`1px solid ${THEME.accentLine}`,padding:"1px 5px",borderRadius:3,letterSpacing:"0.02em"}}>{p.text}</span>
                          : <span key={pi} style={{fontSize:p.mono?11:12.5,fontFamily:p.mono?FONT_MONO:FONT,fontWeight:500,fontStyle:p.italic?"italic":"normal",color:p.color||THEME.text,letterSpacing:p.mono?"0.02em":0,overflow:"hidden",textOverflow:"ellipsis"}}>{p.text}</span>
                      ))}
                    </div>}
                    {/* Timestamp sub-line */}
                    {absTime&&<div style={{fontFamily:FONT_MONO,fontSize:10.5,color:THEME.textDim,marginTop:2,letterSpacing:"0.02em"}}>{absTime}</div>}
                  </div>
                  <div style={{fontFamily:FONT_MONO,fontSize:10.5,color:THEME.textDim,whiteSpace:"nowrap",marginTop:3}}>{formatDate(log.timestamp||log.created_at,lang)}</div>
                </div>
              );
            })}
            {auditLog.length===0&&<div style={{padding:"24px 18px",textAlign:"center",color:THEME.textDim,fontSize:13}}>{lang==="fr"?"Aucune activité récente":"No recent activity"}</div>}
          </div>
        </motion.div>
      </div>

      {/* ── Quick actions grid ── */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
        <div style={{fontSize:14,fontWeight:600,color:THEME.text,letterSpacing:"-0.01em"}}>
          {lang==="fr"?"Actions rapides":"Quick actions"} <span style={{fontFamily:FONT_SERIF,fontStyle:"italic",fontWeight:400,color:THEME.textMuted,marginLeft:4}}>· {lang==="fr"?"sautez dedans":"jump in"}</span>
        </div>
        <span style={{...monoLabel,letterSpacing:"0.08em",textTransform:"uppercase"}}>⌘ + 1–6</span>
      </div>
      {(()=>{
        const qa=[
          {icon:lucide.CloudUpload,tone:"amber",label:lang==="fr"?"Téléverser un document":"Upload document",desc:"PDF, DOCX, XLSX…",kbd:"⌘1",action:()=>dashFileRef.current?.click()},
          {icon:lucide.Folder,tone:"blue",label:lang==="fr"?"Nouveau dossier":"New folder",desc:lang==="fr"?"Organiser par année, client…":"Organize by year, client…",kbd:"⌘2",action:()=>setShowNewFolder(true)},
          {icon:lucide.Search,tone:"green",label:lang==="fr"?"Rechercher dans l'archive":"Search archive",desc:lang==="fr"?"Texte intégral + métadonnées":"Full-text + metadata",kbd:"⌘3",action:()=>navigateTo("search")},
          {icon:lucide.Truck,tone:"neutral",label:lang==="fr"?"Nouveau courrier":"New courrier",desc:lang==="fr"?"Entrant ou sortant":"Incoming or outgoing",kbd:"⌘4",action:()=>setShowNewCourrier(true)},
          {icon:lucide.Lock,tone:"neutral",label:lang==="fr"?"Mes réservations":"My checked out",desc:lang==="fr"?"Documents en révision":"Documents in review",kbd:"⌘5",action:()=>navigateTo("checkedout")},
          ...(isAdminOrSuper(user)?[{icon:lucide.BarChart3,tone:"neutral",label:lang==="fr"?"Voir les rapports":"View reports",desc:lang==="fr"?"Activité & stockage":"Activity & storage",kbd:"⌘6",action:()=>navigateTo("reports")}]:[]),
        ];
        const tBg={amber:THEME.accentDim,blue:THEME.infoDim,green:THEME.successDim,neutral:THEME.surfaceHover};
        const tBd={amber:THEME.accentLine,blue:THEME.infoLine,green:THEME.successLine,neutral:THEME.line2};
        const tFg={amber:THEME.accent,blue:THEME.info,green:THEME.success,neutral:THEME.ink2};
        return(
          <div style={{display:"grid",gridTemplateColumns:`repeat(${qa.length},1fr)`,gap:12}}>
            {qa.map((q,i)=>(
              <button key={i} onClick={q.action} style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:10,padding:"16px 14px 14px",cursor:"pointer",transition:"transform 0.12s, border-color 0.12s, background 0.12s",textAlign:"left",display:"flex",flexDirection:"column",gap:10,position:"relative",overflow:"hidden",fontFamily:FONT,color:THEME.text}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=THEME.line3;e.currentTarget.style.transform="translateY(-1px)";e.currentTarget.style.background=THEME.surfaceRaised;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=THEME.border;e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.background=THEME.surface;}}>
                <div style={{width:34,height:34,borderRadius:8,background:tBg[q.tone],border:`1px solid ${tBd[q.tone]}`,display:"flex",alignItems:"center",justifyContent:"center",color:tFg[q.tone]}}><q.icon size={16}/></div>
                <div>
                  <div style={{fontSize:12.5,fontWeight:600,color:THEME.text,letterSpacing:"-0.005em"}}>{q.label}</div>
                  <div style={{fontSize:11,color:THEME.textDim,fontFamily:FONT_MONO,letterSpacing:"0.01em",marginTop:2}}>{q.desc}</div>
                </div>
                <span style={{position:"absolute",top:12,right:12,opacity:0.7,fontFamily:FONT_MONO,fontSize:10,border:`1px solid ${THEME.line2}`,padding:"1px 5px",borderRadius:4,background:THEME.surfaceRaised,color:THEME.textDim}}>{q.kbd}</span>
              </button>
            ))}
          </div>
        );
      })()}
      <AnimatePresence>{previewDoc&&<DocumentPreviewModal doc={previewDoc} onClose={()=>{setPreviewDoc(null);loadData();}} onAction={()=>{setPreviewDoc(null);triggerRefresh();}}/>}</AnimatePresence>
      <AnimatePresence>{conflictDoc&&<CheckoutConflictDialog doc={conflictDoc} checkoutStatus={conflictStatus} lang={lang} onOpenReadOnly={()=>openReadOnly(conflictDoc)} onCancel={()=>{setConflictDoc(null);setConflictStatus(null);}}/>}</AnimatePresence>
      <AnimatePresence>
        {dashPendingFile && (
          <UploadPropertiesModal
            file={dashPendingFile}
            folderId={selUpFolder||null}
            onClose={()=>setDashPendingFile(null)}
            onSuccess={onDashUploadSuccess}
            api={api}
            currentUser={user}
            lang={lang}
            THEME={THEME}
          />
        )}
      </AnimatePresence>
      {/* Quick Action: New Folder Modal (Sprint 10C H10-B2) */}
      <AnimatePresence>
        {showNewFolder && (
          <FolderCreateModal
            open={showNewFolder}
            onClose={()=>setShowNewFolder(false)}
            onSuccess={(created)=>{
              toast(`"${created?.name||T(lang,"folderCreated")}"`,"success");
              setShowNewFolder(false);
              triggerRefresh();
            }}
            allFolders={allFolders}
            defaultParentId={null}
            api={api}
            lang={lang}
            THEME={THEME}
            currentUser={user}
          />
        )}
      </AnimatePresence>
      {/* Quick Action: New Courrier Modal */}
      <AnimatePresence>{showNewCourrier&&(
        <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)setShowNewCourrier(false);}}>
          <motion.div initial={{opacity:0,scale:0.95,y:10}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.95}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:560,padding:28,boxShadow:"0 24px 80px rgba(0,0,0,0.5)",maxHeight:"85vh",overflowY:"auto"}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
              <div style={{width:40,height:40,borderRadius:10,background:THEME.warningDim,display:"flex",alignItems:"center",justifyContent:"center"}}><lucide.Mail size={20} color={THEME.warning}/></div>
              <div><div style={{fontSize:16,fontWeight:600,color:THEME.text}}>{lang==="fr"?"Nouveau courrier":"New Courrier"}</div><div style={{fontSize:12,color:THEME.textDim,marginTop:2}}>{lang==="fr"?"Enregistrer un courrier entrant ou sortant":"Register incoming or outgoing correspondence"}</div></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:5,textTransform:"uppercase"}}>{T(lang,"type")}</label>
                <select value={courrierForm.type} onChange={e=>setCourrierForm(f=>({...f,type:e.target.value}))} style={{...S.input,background:THEME.surface}}>
                  <option value="incoming">{T(lang,"incoming")}</option>
                  <option value="outgoing">{T(lang,"outgoing")}</option>
                </select>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:5,textTransform:"uppercase"}}>{T(lang,"priority")}</label>
                <select value={courrierForm.priority} onChange={e=>setCourrierForm(f=>({...f,priority:e.target.value}))} style={{...S.input,background:THEME.surface}}>
                  <option value="low">{T(lang,"low")}</option>
                  <option value="normal">{T(lang,"normal")}</option>
                  <option value="high">{T(lang,"high")}</option>
                  <option value="urgent">{T(lang,"urgent")}</option>
                </select>
              </div>
            </div>
            <div style={{marginBottom:12}}>
              <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:5,textTransform:"uppercase"}}>{T(lang,"subject")}</label>
              <input value={courrierForm.subject} onChange={e=>setCourrierForm(f=>({...f,subject:e.target.value}))} placeholder={lang==="fr"?"Objet du courrier...":"Subject of the courrier..."} style={{...S.input,background:THEME.surface}} autoFocus/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:5,textTransform:"uppercase"}}>{T(lang,"sender")}</label>
                <input value={courrierForm.sender} onChange={e=>setCourrierForm(f=>({...f,sender:e.target.value}))} style={{...S.input,background:THEME.surface}}/>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:5,textTransform:"uppercase"}}>{T(lang,"recipient")}</label>
                <input value={courrierForm.recipient} onChange={e=>setCourrierForm(f=>({...f,recipient:e.target.value}))} style={{...S.input,background:THEME.surface}}/>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:5,textTransform:"uppercase"}}>{T(lang,"department")}</label>
                <input value={courrierForm.department} onChange={e=>setCourrierForm(f=>({...f,department:e.target.value}))} style={{...S.input,background:THEME.surface}}/>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:5,textTransform:"uppercase"}}>{T(lang,"dueDate")}</label>
                <input type="date" value={courrierForm.due_date} onChange={e=>setCourrierForm(f=>({...f,due_date:e.target.value}))} style={{...S.input,background:THEME.surface}}/>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:5,textTransform:"uppercase"}}>{T(lang,"notes")}</label>
                <input value={courrierForm.notes} onChange={e=>setCourrierForm(f=>({...f,notes:e.target.value}))} placeholder={lang==="fr"?"Notes optionnelles...":"Optional notes..."} style={{...S.input,background:THEME.surface}}/>
              </div>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setShowNewCourrier(false)} style={{...S.btn,padding:"9px 20px",fontSize:13,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}>{T(lang,"cancel")}</button>
              <button onClick={handleCreateCourrier} disabled={creatingCourrier||!courrierForm.subject.trim()} style={{...S.btn,padding:"9px 20px",fontSize:13,background:THEME.warning,color:"#fff",opacity:(!courrierForm.subject.trim()||creatingCourrier)?0.5:1}}>{creatingCourrier?<Spinner size={14}/>:<><lucide.Check size={14}/>{T(lang,"save")}</>}</button>
            </div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>
    </div>
  );
}
function DocumentsPage(){
  const {toast,canEdit,refreshSignal,triggerRefresh,user}=useApp();const {lang}=useLang();
  const [docs,setDocs]=useState([]);const [total,setTotal]=useState(0);
  const [uploading,setUploading]=useState(false);const [dragOver,setDragOver]=useState(false);
  const [viewMode,setViewMode]=useState("list");const [sortBy,setSortBy]=useState("updated_at");
  const [previewDoc,setPreviewDoc]=useState(null);const [dragDocId,setDragDocId]=useState(null);
  const [pendingFile,setPendingFile]=useState(null);
  // Sprint 11.x redesign: client-side filter chip (pdf / docx / xlsx /
  // img / null=all) + row selection map for the "selected N" footer.
  const [filterExt,setFilterExt]=useState(null);
  const [selectedIds,setSelectedIds]=useState({});
  // Sprint 6.7: checkout flow state
  const [conflictDoc,setConflictDoc]=useState(null);const [conflictStatus,setConflictStatus]=useState(null);
  // Sprint 11.1a: Documents page is a "recent activity" snapshot by
  // default — caps at RECENT_LIMIT to keep it scannable as the corpus
  // grows. Older docs are reachable via Search. The "Show all" toggle
  // bumps the limit so users with hundreds of docs can still browse
  // the full inventory when they want to.
  // 5 (not 6) so the cap is visibly active even on small tenants —
  // with 6 docs uploaded, the user sees 5 + a "Show all 6" toggle,
  // confirming the limit is working without needing a 7th doc.
  const RECENT_LIMIT=5;
  const [showAll,setShowAll]=useState(false);
  const fileRef=useRef();
  function loadDocs(){
    const limitParam=showAll?500:RECENT_LIMIT;
    api.get(`/documents?sort=${sortBy}&limit=${limitParam}`).then(r=>{setDocs(r.data||[]);setTotal(r.total||0);}).catch(e=>console.error("Docs:",e));
  }
  useEffect(()=>{loadDocs();},[sortBy,refreshSignal,showAll]);

  // Sprint 6.7: Click document = check checkout status → open file → auto-lock
  async function openDocument(doc){
    try{
      const status=await api.get(`/checkouts/status/${doc.id}`);
      if(status.checked_out&&!status.is_mine){
        setConflictDoc(doc);setConflictStatus(status);return;
      }
    }catch{}
    // Sprint 11.x DMS-correct behavior — see Dashboard.openDocument comment.
    setPreviewDoc(doc);
  }
  function openReadOnly(doc){
    setPreviewDoc(doc);
    setConflictDoc(null);setConflictStatus(null);
  }
  async function handleCheckin(co){
    loadDocs();triggerRefresh();
  }
  function showDetails(co){
    const doc=docs.find(d=>d.id===co.document_id);
    if(doc)setPreviewDoc(doc);
  }

  function handleUpload(files){
    if(!files?.length)return;
    // Sprint 6.5 Wave 1: open properties modal for the first file (multi-file batch in Sprint 7)
    setPendingFile(files[0]);
    if(fileRef.current)fileRef.current.value="";
  }
  function onUploadSuccess(uploaded){
    toast(`"${uploaded?.name||'Document'}" ${T(lang,"filesUp")}`,"success");
    setPendingFile(null);
    triggerRefresh();
  }
  return(
    <div>
      {/* ── Page header: breadcrumb eyebrow + italic-serif title ── */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:24,marginBottom:24,flexWrap:"wrap"}}>
        <div>
          <div style={{...monoLabel,letterSpacing:"0.16em",textTransform:"uppercase",marginBottom:6,display:"flex",alignItems:"center",gap:10}}>
            <span style={{width:18,height:1,background:THEME.line3,display:"inline-block"}}/>
            <span>{lang==="fr"?"Espace de travail":"Workspace"}</span>
            <span style={{color:THEME.muted2}}>›</span>
            <span style={{color:THEME.textMuted}}>{lang==="fr"?"Tous les documents":"All documents"}</span>
          </div>
          <h1 style={{fontSize:32,fontWeight:600,letterSpacing:"-0.025em",color:THEME.text,margin:"0 0 6px",lineHeight:1.1}}>
            {T(lang,"documents")} <em style={{fontFamily:FONT_SERIF,fontStyle:"italic",fontWeight:400,color:THEME.accent2}}>—</em> {total} {lang==="fr"?(total===1?"dans l'archive":"dans l'archive"):(total===1?"in archive":"in archive")}
          </h1>
          <div style={{color:THEME.textMuted,fontSize:13.5}}>{lang==="fr"?"PDF, DOCX, XLSX, images · jusqu'à 50 Mo par fichier · versionné":"PDF, DOCX, XLSX, images · up to 50 MB per file · versioned"}</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          {canEdit&&<button onClick={()=>{}} style={{display:"inline-flex",alignItems:"center",gap:7,height:38,padding:"0 16px",borderRadius:8,fontSize:13,fontWeight:600,border:`1px solid ${THEME.line2}`,background:THEME.surfaceRaised,color:THEME.ink2,cursor:"pointer",fontFamily:FONT,opacity:0.5}} title={lang==="fr"?"Bientôt — créer un dossier depuis ici":"Coming soon — create folder from here"}><lucide.Folder size={14}/>{lang==="fr"?"Nouveau dossier":"New folder"}</button>}
          {canEdit&&<button onClick={()=>fileRef.current?.click()} style={{display:"inline-flex",alignItems:"center",gap:7,height:38,padding:"0 16px",borderRadius:8,fontSize:13,fontWeight:600,border:"1px solid rgba(255,255,255,0.12)",background:`linear-gradient(180deg,${THEME.accent2},${THEME.accent})`,color:"#fff",cursor:"pointer",fontFamily:FONT,boxShadow:`0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px ${THEME.accentDim}`}}><lucide.Upload size={14}/>{lang==="fr"?"Téléverser un document":"Upload document"}</button>}
          <input ref={fileRef} type="file" multiple hidden onChange={e=>handleUpload(e.target.files)}/>
        </div>
      </div>

      {/* ── Dropzone (amber-tinted) ── */}
      {canEdit&&(
        <motion.div
          onDragOver={e=>{e.preventDefault();setDragOver(true);}}
          onDragLeave={()=>setDragOver(false)}
          onDrop={e=>{e.preventDefault();setDragOver(false);handleUpload(e.dataTransfer.files);}}
          onClick={()=>fileRef.current?.click()}
          animate={{borderColor:dragOver?THEME.accent:THEME.line3}}
          style={{
            border:`1.5px dashed ${THEME.line3}`,
            borderRadius:14,
            background:`radial-gradient(60% 100% at 50% 0%, ${THEME.accentDim}, transparent 70%), repeating-linear-gradient(45deg, transparent, transparent 14px, rgba(255,255,255,0.012) 14px, rgba(255,255,255,0.012) 15px), ${THEME.surface}`,
            padding:"34px 20px",textAlign:"center",marginBottom:20,cursor:"pointer",position:"relative"
          }}>
          {uploading?(
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12}}><Spinner size={20}/><span style={{color:THEME.textMuted,fontSize:14}}>{T(lang,"uploading")}</span></div>
          ):(
            <>
              <div style={{width:54,height:54,borderRadius:14,background:THEME.accentDim,border:`1px solid ${THEME.accentLine}`,display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:14,color:THEME.accent2,boxShadow:`0 0 0 6px rgba(224,154,91,0.05)`}}>
                <lucide.CloudUpload size={24}/>
              </div>
              <div style={{fontSize:16,fontWeight:600,color:THEME.text,marginBottom:6,letterSpacing:"-0.01em"}}>{lang==="fr"?"Glissez les fichiers ici":"Drop files here"} <em style={{fontFamily:FONT_SERIF,fontStyle:"italic",fontWeight:400,color:THEME.accent2}}>{lang==="fr"?"ou":"or"}</em> {lang==="fr"?"cliquez pour parcourir":"click to browse"}</div>
              <div style={{fontSize:12,color:THEME.textDim,fontFamily:FONT_MONO,letterSpacing:"0.02em"}}>PDF · DOCX · XLSX · IMAGES — {lang==="fr"?"jusqu'à 50 Mo · versionné automatiquement · OCR au téléversement":"up to 50 MB · auto-versioned · OCR on upload"}</div>
            </>
          )}
        </motion.div>
      )}

      {/* ── Filter chips + sort + view toggle ── */}
      {(()=>{
        const counts = {
          all: docs.length,
          pdf: docs.filter(d=>(d.name||"").toLowerCase().endsWith(".pdf")).length,
          docx: docs.filter(d=>/\.docx?$/i.test(d.name||"")).length,
          xlsx: docs.filter(d=>/\.xlsx?$/i.test(d.name||"")).length,
          img: docs.filter(d=>/\.(png|jpg|jpeg|gif|webp|svg)$/i.test(d.name||"")).length,
        };
        const chips = [
          {k:"all",label:lang==="fr"?"Tous":"All",icon:lucide.Folder,n:counts.all},
          {k:"pdf",label:"PDF",n:counts.pdf},
          {k:"docx",label:"DOCX",n:counts.docx},
          {k:"xlsx",label:"XLSX",n:counts.xlsx},
          {k:"img",label:lang==="fr"?"Images":"Images",n:counts.img},
        ];
        return(
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18,flexWrap:"wrap"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              {chips.map(c=>{
                const on = (filterExt||"all")===c.k;
                return(
                  <button key={c.k} onClick={()=>setFilterExt(c.k==="all"?null:c.k)} style={{display:"inline-flex",alignItems:"center",gap:7,height:32,padding:"0 12px",background:on?THEME.accentDim:THEME.surfaceRaised,border:`1px solid ${on?THEME.accentLine:THEME.line2}`,borderRadius:8,fontSize:12,color:on?THEME.accent2:THEME.ink2,fontWeight:500,cursor:"pointer",fontFamily:FONT}}>
                    {c.icon&&<c.icon size={13} color={on?THEME.accent:THEME.textDim}/>}
                    <span>{c.label}</span>
                    <span style={{fontFamily:FONT_MONO,color:on?THEME.accent:THEME.textDim,fontSize:11}}>{c.n}</span>
                  </button>
                );
              })}
              <button style={{display:"inline-flex",alignItems:"center",gap:7,height:32,padding:"0 12px",background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,borderRadius:8,fontSize:12,color:THEME.ink2,fontWeight:500,cursor:"pointer",fontFamily:FONT,marginLeft:6}}>
                <lucide.SlidersHorizontal size={13} color={THEME.textDim}/>
                <span>{lang==="fr"?"Filtres":"Filters"}</span>
              </button>
            </div>
            <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
              <div style={{display:"inline-flex",alignItems:"center",gap:8,height:32,padding:"0 12px",background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,borderRadius:8,fontSize:12,color:THEME.ink2}}>
                <span style={{...monoLabel,textTransform:"uppercase",letterSpacing:"0.06em"}}>{T(lang,"sortBy")||(lang==="fr"?"Trier":"Sort")}</span>
                <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{background:"transparent",border:"none",fontFamily:FONT,fontSize:12,fontWeight:600,color:THEME.text,cursor:"pointer",outline:"none"}}>
                  <option value="updated_at">{lang==="fr"?"Date d'activité":"Activity date"}</option>
                  <option value="name">{T(lang,"name")}</option>
                  <option value="size">{T(lang,"size")}</option>
                </select>
              </div>
              <div style={{display:"inline-flex",background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,borderRadius:8,overflow:"hidden"}}>
                {[["list",lucide.List],["grid",lucide.LayoutGrid]].map(([m,Icon])=>(
                  <button key={m} onClick={()=>setViewMode(m)} style={{width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",border:"none",cursor:"pointer",background:viewMode===m?THEME.surfaceDeep:"transparent",color:viewMode===m?THEME.accent2:THEME.textDim}}>
                    <Icon size={14}/>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Checked-out bar (Sprint 6.7) — kept ── */}
      <CheckedOutBar lang={lang} onCheckin={handleCheckin} onShowDetails={showDetails} onRefresh={()=>{loadDocs();triggerRefresh();}}/>

      {/* ── Empty state OR grouped list/grid ── */}
      {docs.length===0?(
        <EmptyState icon={lucide.FileText} title={T(lang,"noDocsYet")} subtitle={T(lang,"uploadFirst")}/>
      ):(()=>{
        // Apply local ext filter on top of server sort
        const filtered = docs.filter(d=>{
          if(!filterExt) return true;
          const n=(d.name||"").toLowerCase();
          if(filterExt==="pdf") return n.endsWith(".pdf");
          if(filterExt==="docx") return /\.docx?$/i.test(n);
          if(filterExt==="xlsx") return /\.xlsx?$/i.test(n);
          if(filterExt==="img") return /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(n);
          return true;
        });
        // Group by time-bucket (Today, Yesterday, This week, etc.)
        const groups = {};
        filtered.forEach(doc=>{const g=getTimeGroup(doc.updated_at,lang);(groups[g]=groups[g]||[]).push(doc);});
        const selectedSize = filtered.filter(d=>selectedIds[d.id]).reduce((a,d)=>a+(d.size||0),0);
        const totalSize = filtered.reduce((a,d)=>a+(d.size||0),0);

        if(viewMode==="list"){
          return(
            <>
              {Object.entries(groups).map(([group,gdocs])=>(
                <div key={group}>
                  {/* Date heading band */}
                  <div style={{display:"flex",alignItems:"center",gap:12,margin:"18px 0 10px",fontFamily:FONT_MONO,fontSize:11,letterSpacing:"0.08em",textTransform:"uppercase",color:THEME.textDim}}>
                    <span style={{color:THEME.ink2,background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,padding:"2px 8px",borderRadius:4}}>{group}</span>
                    <span style={{color:THEME.textMuted,fontWeight:500}}>{gdocs.length} {lang==="fr"?"éléments":"items"}</span>
                    <span style={{flex:1,height:1,background:THEME.border}}/>
                  </div>
                  {/* Table */}
                  <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,overflow:"hidden"}}>
                    {/* Head */}
                    <div style={{display:"grid",gridTemplateColumns:"36px 56px 1fr 90px 160px 130px 110px 90px 40px",gap:12,alignItems:"center",padding:"10px 16px",background:THEME.surfaceRaised,borderBottom:`1px solid ${THEME.border}`,fontFamily:FONT_MONO,fontSize:10.5,letterSpacing:"0.08em",textTransform:"uppercase",color:THEME.textDim}}>
                      <span></span><span></span><span>{T(lang,"name")||"Name"}</span><span>{lang==="fr"?"Version":"Version"}</span><span>{lang==="fr"?"Propriétaire":"Owner"}</span><span>{lang==="fr"?"Modifié":"Modified"}</span><span>{lang==="fr"?"Statut":"Status"}</span><span style={{textAlign:"right"}}>{T(lang,"size")||"Size"}</span><span></span>
                    </div>
                    {/* Rows */}
                    {gdocs.map((doc,i)=>{
                      // Sprint 11.x: the docs API returns `checked_out_by_name`
                      // (string when checked out, falsy otherwise) — same field
                      // CheckoutBadge already reads on line 513. An earlier draft
                      // of this redesign looked at `checkout_user_id` /
                      // `is_checked_out`, which don't exist on the response, so
                      // every row showed "Checked in" even when the top
                      // checked-out bar said otherwise.
                      const isCheckedIn=!doc.checked_out_by_name;
                      const checked=!!selectedIds[doc.id];
                      const owner=doc.uploaded_by||doc.owner_name||"—";
                      const ownerInitials=owner.split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase()||"—";
                      return(
                        <div key={doc.id} onClick={()=>openDocument(doc)} onContextMenu={e=>{e.preventDefault();setPreviewDoc(doc);}} style={{display:"grid",gridTemplateColumns:"36px 56px 1fr 90px 160px 130px 110px 90px 40px",gap:12,alignItems:"center",padding:"12px 16px",borderBottom:i<gdocs.length-1?`1px solid ${THEME.border}`:"none",cursor:"pointer",transition:"background 0.12s"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.018)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <button onClick={e=>{e.stopPropagation();setSelectedIds(s=>({...s,[doc.id]:!s[doc.id]}));}} style={{width:16,height:16,borderRadius:4,border:`1.5px solid ${checked?THEME.accent:THEME.line3}`,background:checked?THEME.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",padding:0,justifySelf:"center"}}>
                            {checked&&<lucide.Check size={10} color="#fff" strokeWidth={3}/>}
                          </button>
                          <FileTypeBadge doc={doc} size={36}/>
                          <div style={{minWidth:0}}>
                            <div style={{fontSize:13,color:THEME.text,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6}}>
                              <span>{doc.name}</span>
                              <span style={{fontFamily:FONT_MONO,fontSize:10,color:THEME.textDim,background:THEME.surfaceHover,border:`1px solid ${THEME.line2}`,padding:"1px 5px",borderRadius:3,fontWeight:500}}>#{doc.id}</span>
                              <CheckoutBadge doc={doc} lang={lang}/><SecurityBadge doc={doc} lang={lang}/>
                            </div>
                            <div style={{fontFamily:FONT_MONO,fontSize:10.5,color:THEME.textDim,marginTop:3,letterSpacing:"0.02em"}}>
                              {doc.folder||T(lang,"root")||"Root"}{doc.pages?` · ${doc.pages} ${lang==="fr"?"pages":"pages"}`:""}{doc.ocr_done!==false?` · ${lang==="fr"?"OCR fait":"OCR done"}`:""}
                            </div>
                          </div>
                          <div><span style={{fontFamily:FONT_MONO,fontSize:10.5,fontWeight:600,color:THEME.accent2,background:THEME.accentDim,border:`1px solid ${THEME.accentLine}`,padding:"2px 6px",borderRadius:4}}>v{doc.current_version||1}</span></div>
                          <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                            <span style={{width:22,height:22,borderRadius:"50%",background:`linear-gradient(135deg,${THEME.success},#1f8c64)`,color:"#0a1c14",fontSize:9.5,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontFamily:FONT}}>{ownerInitials}</span>
                            <span style={{fontSize:12,color:THEME.ink2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{owner}</span>
                          </div>
                          <div style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.textDim,lineHeight:1.3}}>
                            <b style={{color:THEME.ink2,fontWeight:500}}>{formatDate(doc.updated_at,lang)}</b>
                          </div>
                          <div>
                            <span style={{display:"inline-flex",alignItems:"center",gap:5,fontFamily:FONT_MONO,fontSize:10.5,textTransform:"uppercase",letterSpacing:"0.06em",color:isCheckedIn?THEME.success:THEME.accent}}>
                              <span style={{width:6,height:6,borderRadius:"50%",background:"currentColor",boxShadow:"0 0 0 3px rgba(255,255,255,0.04)"}}/>
                              {isCheckedIn?(lang==="fr"?"Disponible":"Checked in"):(lang==="fr"?"Réservé":"Checked out")}
                            </span>
                          </div>
                          <div style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.ink2,textAlign:"right",fontVariantNumeric:"tabular-nums"}}>{formatBytes(doc.size)}</div>
                          <button onClick={e=>{e.stopPropagation();setPreviewDoc(doc);}} style={{padding:4,borderRadius:4,border:"none",background:"transparent",color:THEME.textDim,cursor:"pointer",justifySelf:"end"}} onMouseEnter={e=>e.currentTarget.style.color=THEME.text} onMouseLeave={e=>e.currentTarget.style.color=THEME.textDim}><lucide.MoreHorizontal size={16}/></button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {/* Footer count strip — Sprint 11.1a: shows a "Showing X of N"
                  hint when capped to RECENT_LIMIT, with a toggle to view
                  the full inventory. Uses search for older docs hint. */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:14,padding:"0 4px",gap:14,flexWrap:"wrap"}}>
                <div style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.textDim,letterSpacing:"0.04em",textTransform:"uppercase"}}>
                  <b style={{color:THEME.ink2,fontWeight:600}}>{filtered.length}</b> {lang==="fr"?"éléments":"items"} · {lang==="fr"?"sélectionnés":"selected"} <b style={{color:THEME.ink2,fontWeight:600}}>{Object.values(selectedIds).filter(Boolean).length}</b> · <b style={{color:THEME.ink2,fontWeight:600}}>{formatBytes(selectedSize||totalSize)}</b> {lang==="fr"?"total":"total"}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:14}}>
                  {!showAll&&total>RECENT_LIMIT&&(
                    <div style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.textDim,letterSpacing:"0.04em",textTransform:"uppercase"}}>
                      {lang==="fr"?"Affichage des":"Showing"} <b style={{color:THEME.ink2,fontWeight:600}}>{RECENT_LIMIT}</b> {lang==="fr"?"plus récents sur":"most recent of"} <b style={{color:THEME.ink2,fontWeight:600}}>{total}</b>
                    </div>
                  )}
                  {total>RECENT_LIMIT&&(
                    <button onClick={()=>setShowAll(s=>!s)} style={{padding:"6px 12px",borderRadius:6,background:showAll?THEME.accent:"transparent",border:`1px solid ${showAll?THEME.accent:THEME.border}`,color:showAll?"#1a0f04":THEME.ink2,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:FONT,letterSpacing:"0.02em",display:"flex",alignItems:"center",gap:6}}>
                      {showAll
                        ?<><lucide.ListFilter size={12}/>{lang==="fr"?"Récents uniquement":"Show recent only"}</>
                        :<><lucide.List size={12}/>{lang==="fr"?`Voir les ${total} documents`:`Show all ${total} documents`}</>}
                    </button>
                  )}
                </div>
              </div>
            </>
          );
        }
        // Grid view
        return(
          <>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:14}}>
              {filtered.map(doc=>{
                return(
                  <div key={doc.id} onClick={()=>openDocument(doc)} onContextMenu={e=>{e.preventDefault();setPreviewDoc(doc);}} style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,padding:16,display:"flex",flexDirection:"column",gap:14,cursor:"pointer",transition:"border-color 0.15s, transform 0.15s",position:"relative"}} onMouseEnter={e=>{e.currentTarget.style.borderColor=THEME.line3;e.currentTarget.style.transform="translateY(-1px)";}} onMouseLeave={e=>{e.currentTarget.style.borderColor=THEME.border;e.currentTarget.style.transform="translateY(0)";}}>
                    <div style={{height:120,borderRadius:8,background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,position:"relative",overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <FileTypeBadge doc={doc} size={52} radius={12}/>
                    </div>
                    <div>
                      <div style={{fontSize:13,color:THEME.text,fontWeight:500,lineHeight:1.35,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                        <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{doc.name}</span>
                        <CheckoutBadge doc={doc} lang={lang}/><SecurityBadge doc={doc} lang={lang}/>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8,fontFamily:FONT_MONO,fontSize:10.5,color:THEME.textDim,justifyContent:"space-between",marginTop:8}}>
                        <span style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{width:18,height:18,borderRadius:"50%",background:`linear-gradient(135deg,${THEME.success},#1f8c64)`,color:"#0a1c14",fontSize:8.5,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>{(doc.uploaded_by||"—").split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase()||"—"}</span>
                          <span>v{doc.current_version||1} · {formatBytes(doc.size)}</span>
                        </span>
                        <span>{formatDate(doc.updated_at,lang)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Footer count strip (grid) */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:14,padding:"0 4px"}}>
              <div style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.textDim,letterSpacing:"0.04em",textTransform:"uppercase"}}>
                <b style={{color:THEME.ink2,fontWeight:600}}>{filtered.length}</b> {lang==="fr"?"éléments":"items"} · <b style={{color:THEME.ink2,fontWeight:600}}>{formatBytes(totalSize)}</b> {lang==="fr"?"total":"total"}
              </div>
              <div style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.textDim,letterSpacing:"0.04em",textTransform:"uppercase"}}>{lang==="fr"?"Page":"Page"} <b style={{color:THEME.ink2,fontWeight:600}}>1 {lang==="fr"?"sur":"of"} 1</b></div>
            </div>
          </>
        );
      })()}
            <AnimatePresence>{previewDoc&&<DocumentPreviewModal doc={previewDoc} onClose={()=>{setPreviewDoc(null);loadDocs();}} onAction={()=>{setPreviewDoc(null);triggerRefresh();}}/>}</AnimatePresence>
      <AnimatePresence>{conflictDoc&&<CheckoutConflictDialog doc={conflictDoc} checkoutStatus={conflictStatus} lang={lang} onOpenReadOnly={()=>openReadOnly(conflictDoc)} onCancel={()=>{setConflictDoc(null);setConflictStatus(null);}}/>}</AnimatePresence>
      <AnimatePresence>
        {pendingFile && (
          <UploadPropertiesModal
            file={pendingFile}
            folderId={null}
            onClose={()=>setPendingFile(null)}
            onSuccess={onUploadSuccess}
            api={api}
            currentUser={user}
            lang={lang}
            THEME={THEME}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── CHECKED OUT DOCUMENTS PAGE — iManage-style ───
function CheckedOutPage(){
  const {toast,canEdit,triggerRefresh,user}=useApp();const {lang}=useLang();
  const [checkouts,setCheckouts]=useState([]);const [loading,setLoading]=useState(true);
  const [savingId,setSavingId]=useState(null);const fileRefs=useRef({});
  // P7: when user picks "Save as new version…", we stash the checkout here and open the modal
  const [newVersionFor,setNewVersionFor]=useState(null); // checkout row or null

  useEffect(()=>{
    api.get("/checkouts/my").then(r=>{setCheckouts(r.data||[]);setLoading(false);}).catch(()=>setLoading(false));
  },[]);

  // P7: default flipped from /versions/upload → /documents/replace-file for iManage parity
  // with CheckedOutBar. saveMode='new-version' path is driven by the NewVersionModal
  // (which uploads internally; this function handles the common auto-checkin/refresh tail).
  async function saveFile(co,file,saveMode="in-place"){
    if(saveMode==="in-place"&&!file)return;
    setSavingId(co.document_id);
    try{
      if(saveMode==="in-place"){
        const fd=new FormData();fd.append("file",file);
        await api.upload(`/documents/${co.document_id}/replace-file`,fd);
      }
      await api.del(`/checkouts/${co.document_id}`);
      setCheckouts(p=>p.filter(c=>c.document_id!==co.document_id));
      toast(lang==="fr"?"Modifications enregistrées":"Changes saved","success");
      triggerRefresh();
    }catch(e){toast(e.message,"error");}
    setSavingId(null);
  }
  async function handleCheckin(co){
    try{await api.del(`/checkouts/${co.document_id}`);setCheckouts(p=>p.filter(c=>c.document_id!==co.document_id));toast(lang==="fr"?"Document libéré":"Document checked in","success");triggerRefresh();}catch(e){toast(e.message,"error");}
  }

  return(
    <div>
      <PageHeader title={lang==="fr"?"Documents réservés":"Checked Out Documents"} subtitle={lang==="fr"?`${checkouts.length} document(s) réservé(s)`:`${checkouts.length} checked out document(s)`}/>
      {loading?<div style={{textAlign:"center",padding:40}}><Spinner size={24}/></div>
      :checkouts.length===0?(
        <div style={{...S.card,textAlign:"center",padding:"60px 20px"}}>
          <lucide.CheckCircle size={48} color={THEME.success} strokeWidth={1} style={{marginBottom:16}}/>
          <div style={{fontSize:18,fontWeight:600,color:THEME.text,marginBottom:8}}>{lang==="fr"?"Aucune réservation !":"No Matches!"}</div>
          <div style={{fontSize:13,color:THEME.textDim,maxWidth:400,margin:"0 auto"}}>{lang==="fr"?"Vous n'avez aucun document réservé pour le moment. Les documents se réservent automatiquement lorsque vous les ouvrez pour modification.":"No documents found for the current criteria. Documents are automatically checked out when you open them for editing."}</div>
        </div>
      ):(
        <div style={{...S.card,padding:0,overflow:"hidden"}}>
          {checkouts.map((co,i)=>(
            <div key={co.document_id} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 20px",borderBottom:i<checkouts.length-1?`1px solid ${THEME.borderSubtle}`:"none",transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=THEME.surfaceHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{width:16,height:16,borderRadius:3,border:`1.5px solid ${THEME.border}`,flexShrink:0}}/>
              <div style={{width:32,height:32,borderRadius:6,background:THEME.warningDim,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><lucide.Lock size={14} color={THEME.warning}/></div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:THEME.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{co.document_name}</div>
                <div style={{fontSize:11,color:THEME.textDim,marginTop:2}}>{co.folder_name||"Root"} · {formatBytes(co.document_size)} · {lang==="fr"?"Réservé":"Checked out"} {formatDate(co.checked_out_at,lang)}</div>
              </div>
              {/* P7: split-button — primary=in-place, dropdown=new version (parity with CheckedOutBar) */}
              <SaveChangesSplitButton
                lang={lang}
                size="md"
                busy={savingId===co.document_id}
                disabled={!!savingId&&savingId!==co.document_id}
                onSaveInPlace={()=>fileRefs.current[co.document_id]?.click()}
                onSaveAsNewVersion={()=>setNewVersionFor(co)}/>
              <input ref={el=>{fileRefs.current[co.document_id]=el;}} type="file" hidden onChange={e=>{if(e.target.files[0])saveFile(co,e.target.files[0],"in-place");e.target.value="";}}/>
              <button onClick={()=>handleCheckin(co)} disabled={!!savingId} style={{...S.btn,padding:"6px 14px",fontSize:12,background:THEME.surface,color:THEME.textDim,border:`1px solid ${THEME.border}`}}><lucide.Unlock size={12}/> {lang==="fr"?"Libérer":"Check In"}</button>
              <button style={{padding:4,borderRadius:4,border:"none",background:"transparent",color:THEME.textDim,cursor:"pointer",flexShrink:0}}><lucide.MoreVertical size={16}/></button>
            </div>
          ))}
        </div>
      )}
      {/* P7: new-version modal */}
      <AnimatePresence>{newVersionFor&&(
        <NewVersionModal
          doc={{id:newVersionFor.document_id,name:newVersionFor.document_name}}
          lang={lang}
          onClose={()=>setNewVersionFor(null)}
          onUploaded={()=>{const co=newVersionFor;setNewVersionFor(null);saveFile(co,null,"new-version");}}
          onError={e=>toast(e.message||"Upload failed","error")}/>
      )}</AnimatePresence>
    </div>
  );
}

// ─── FOLDERS ───
function FoldersPage(){
  const {toast,canEdit,isAdmin,refreshSignal,triggerRefresh,selectedFolderId,setSelectedFolderId,user}=useApp();const {lang}=useLang();
  const [folders,setFolders]=useState([]);const [activeFolderId,setActiveFolderId]=useState(null);
  const [showCreate,setShowCreate]=useState(false);
  const [folderDocs,setFolderDocs]=useState([]);const [previewDoc,setPreviewDoc]=useState(null);
  // Sprint 10C H10-B2: Manage Security modal state. Holds the TOP-LEVEL
  // folder to edit (for subfolders, we resolve the ancestor first).
  const [securityFolder,setSecurityFolder]=useState(null);
  // Sprint 11.x: admin-only Edit + Delete modals on folder cards.
  // Editing exposes name + color; delete uses a typed-name confirm to
  // prevent fat-finger deletions on top-level folders.
  const [editFolder,setEditFolder]=useState(null); // {id, name, color}
  const [editName,setEditName]=useState("");
  const [editColor,setEditColor]=useState("#D4915E");
  const [savingEdit,setSavingEdit]=useState(false);
  const [deleteFolder,setDeleteFolder]=useState(null); // folder object
  const [deleteConfirm,setDeleteConfirm]=useState("");
  const [deletingFolder,setDeletingFolder]=useState(false);
  const canManageFolders = isAdminOrSuper(user);
  // When sidebar passes a folder ID, use it
  useEffect(()=>{if(selectedFolderId){setActiveFolderId(selectedFolderId);setSelectedFolderId(null);}},[selectedFolderId]);
  // Sprint 6.7
  const [conflictDoc,setConflictDoc]=useState(null);const [conflictStatus,setConflictStatus]=useState(null);
  async function openDocument(doc){
    try{const status=await api.get(`/checkouts/status/${doc.id}`);if(status.checked_out&&!status.is_mine){setConflictDoc(doc);setConflictStatus(status);return;}}catch{}
    // Sprint 11.x DMS-correct behavior: click opens preview modal, never downloads.
    setPreviewDoc(doc);
  }
  function openReadOnly(doc){setPreviewDoc(doc);setConflictDoc(null);setConflictStatus(null);}
  function loadFolders(){api.get("/folders").then(r=>setFolders(r.data||[])).catch(()=>{});}
  useEffect(()=>{loadFolders();},[refreshSignal]);
  useEffect(()=>{if(activeFolderId){api.get(`/documents?folder_id=${activeFolderId}&limit=100`).then(r=>setFolderDocs(r.data||[])).catch(()=>setFolderDocs([]));}else{setFolderDocs([]);}},[activeFolderId,refreshSignal]);

  const rootFolders=folders.filter(f=>!f.parent_id);const activeFolder=folders.find(f=>f.id===activeFolderId);
  const childFolders=activeFolderId?folders.filter(f=>f.parent_id===activeFolderId):[];
  const displayFolders=activeFolderId?childFolders:rootFolders;

  // Sprint 10C H10-B2: resolve top-level ancestor for a folder. For
  // top-level folders returns themselves. For subfolders, hits the
  // backend endpoint to get the authoritative ancestor (the frontend
  // already knows the tree, but we defer to backend for correctness).
  async function openManageSecurity(folder){
    if(!folder.parent_id){
      setSecurityFolder(folder);
      return;
    }
    try{
      const top=await api.get(`/folders/${folder.id}/top-level`);
      setSecurityFolder(top);
    }catch(e){
      toast(e.message||(lang==="fr"?"Impossible de charger le dossier parent":"Failed to load top-level folder"),"error");
    }
  }

  // Sprint 11.x: open edit modal pre-filled with current name + color.
  function openEditFolder(folder){
    setEditFolder(folder);
    setEditName(folder.name||"");
    setEditColor(folder.color||"#D4915E");
  }

  async function saveEditFolder(){
    if(!editFolder||!editName.trim())return;
    setSavingEdit(true);
    try{
      await api.put(`/folders/${editFolder.id}`,{name:editName.trim(),color:editColor});
      toast(lang==="fr"?"Dossier mis à jour":"Folder updated","success");
      setEditFolder(null);
      triggerRefresh();
    }catch(e){
      toast(e.message||(lang==="fr"?"Échec de la mise à jour":"Update failed"),"error");
    }
    setSavingEdit(false);
  }

  async function confirmDeleteFolder(){
    if(!deleteFolder)return;
    // Top-level deletions require exact-name match (defense against
    // fat-finger on a folder full of documents). Subfolders only need
    // a click since they have no independent security boundary.
    if(!deleteFolder.parent_id && deleteConfirm.trim()!==deleteFolder.name){
      toast(lang==="fr"?"Le nom ne correspond pas":"Name doesn't match","error");
      return;
    }
    setDeletingFolder(true);
    try{
      await api.del(`/folders/${deleteFolder.id}`);
      toast(lang==="fr"?`"${deleteFolder.name}" supprimé`:`"${deleteFolder.name}" deleted`,"success");
      if(activeFolderId===deleteFolder.id)setActiveFolderId(deleteFolder.parent_id||null);
      setDeleteFolder(null);
      setDeleteConfirm("");
      triggerRefresh();
    }catch(e){
      toast(e.message||(lang==="fr"?"Échec de la suppression":"Delete failed"),"error");
    }
    setDeletingFolder(false);
  }

  return(
    <div>
      <PageHeader title={T(lang,"folders")} subtitle={activeFolder?`${T(lang,"insideFolder")} ${activeFolder.name}`:`${rootFolders.length} ${T(lang,"rootFolders")}`} actions={canEdit&&<button style={{...S.btn,background:THEME.accent,color:"#fff"}} onClick={()=>setShowCreate(true)}><lucide.FolderPlus size={16}/> {T(lang,"newFolder")}</button>}/>
      {activeFolderId&&(<div style={{display:"flex",alignItems:"center",gap:8,marginBottom:20,fontSize:13}}><span style={{color:THEME.accent,cursor:"pointer"}} onClick={()=>setActiveFolderId(null)}>{T(lang,"allFolders")}</span><lucide.ChevronRight size={14} color={THEME.textDim}/><span style={{color:THEME.text,fontWeight:600}}>{activeFolder?.name}</span></div>)}
      <AnimatePresence>
        {showCreate && (
          <FolderCreateModal
            open={showCreate}
            onClose={()=>setShowCreate(false)}
            onSuccess={(created)=>{
              toast(`${T(lang,"folderCreated")} "${created?.name||""}"`,"success");
              setShowCreate(false);
              triggerRefresh();
            }}
            allFolders={folders}
            defaultParentId={activeFolderId||null}
            hideParentSelect={false}
            api={api}
            lang={lang}
            THEME={THEME}
            currentUser={user}
          />
        )}
      </AnimatePresence>
      {displayFolders.length>0&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:14,marginBottom:activeFolderId?24:0}}>
        {displayFolders.map((folder,i)=>{
          const sub=folders.filter(f=>f.parent_id===folder.id).length;
          const isTopLevel=!folder.parent_id;
          const canManage=isAdmin||folder._access_level==="full";
          return(<motion.div key={folder.id} initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:i*0.04}} onClick={()=>setActiveFolderId(folder.id)} style={{...S.card,padding:20,cursor:"pointer",transition:"all 0.2s",borderLeft:`3px solid ${folder.color||THEME.accent}`,position:"relative"}} onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,0.2)";}} onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="none";}}>
            {/* Action button cluster — admin-only Edit + Delete, plus Shield for anyone with full ACL */}
            <div style={{position:"absolute",top:12,right:12,display:"flex",gap:6}}>
              {canManageFolders && (<>
                <button
                  onClick={e=>{e.stopPropagation();openEditFolder(folder);}}
                  title={lang==="fr"?"Modifier le dossier":"Edit folder"}
                  style={{width:30,height:30,borderRadius:8,border:`1px solid ${THEME.line2}`,background:THEME.surfaceRaised,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",transition:"all 0.15s"}}
                  onMouseEnter={e=>{e.currentTarget.style.background=THEME.surfaceHover;e.currentTarget.style.borderColor=THEME.line3;}}
                  onMouseLeave={e=>{e.currentTarget.style.background=THEME.surfaceRaised;e.currentTarget.style.borderColor=THEME.line2;}}
                >
                  <lucide.Pencil size={13} color={THEME.textMuted}/>
                </button>
                <button
                  onClick={e=>{e.stopPropagation();setDeleteFolder(folder);setDeleteConfirm("");}}
                  title={lang==="fr"?"Supprimer le dossier":"Delete folder"}
                  style={{width:30,height:30,borderRadius:8,border:`1px solid ${THEME.dangerDim}`,background:THEME.dangerDim,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",transition:"all 0.15s"}}
                  onMouseEnter={e=>{e.currentTarget.style.background=THEME.danger+"25";}}
                  onMouseLeave={e=>{e.currentTarget.style.background=THEME.dangerDim;}}
                >
                  <lucide.Trash2 size={13} color={THEME.danger}/>
                </button>
              </>)}
              {canManage && (
                <button
                  onClick={e=>{e.stopPropagation();if(!isTopLevel){toast(lang==="fr"?`Sécurité gérée sur le dossier parent`:`Security managed on top-level folder`,"info");}openManageSecurity(folder);}}
                  title={isTopLevel?(lang==="fr"?"Gérer la sécurité":"Manage Security"):(lang==="fr"?"Gérée au niveau supérieur — cliquer pour ouvrir":"Managed at top level — click to open")}
                  style={{width:30,height:30,borderRadius:8,border:`1px solid ${isTopLevel?THEME.warning+"40":THEME.border}`,background:isTopLevel?THEME.warningDim:THEME.surfaceHover,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",opacity:isTopLevel?1:0.55,transition:"all 0.15s"}}
                  onMouseEnter={e=>{e.currentTarget.style.background=THEME.warning+"25";e.currentTarget.style.borderColor=THEME.warning+"70";}}
                  onMouseLeave={e=>{e.currentTarget.style.background=isTopLevel?THEME.warningDim:THEME.surfaceHover;e.currentTarget.style.borderColor=isTopLevel?THEME.warning+"40":THEME.border;}}
                >
                  <lucide.Shield size={14} color={THEME.warning}/>
                </button>
              )}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14,paddingRight:(canManageFolders?78:0)+(canManage?36:0)}}><lucide.Folder size={22} color={folder.color||THEME.accent} fill={(folder.color||THEME.accent)+"30"}/><span style={{fontSize:15,fontWeight:600,color:THEME.text}}>{folder.name}</span></div>
            <div style={{display:"flex",gap:16,fontSize:12,color:THEME.textDim}}><span>{folder.doc_count||0} {T(lang,"docs")}</span>{sub>0&&<span>{sub} {T(lang,"subfolders")}</span>}</div>
          </motion.div>);
        })}
      </div>}
      {activeFolderId&&(<div>
        {folderDocs.length>0?(<div style={{...S.card,padding:0,overflow:"hidden"}}>
          <div style={{padding:"12px 20px",background:THEME.surface,borderBottom:`1px solid ${THEME.border}`,fontSize:12,fontWeight:600,color:THEME.textMuted,textTransform:"uppercase"}}>{folderDocs.length} {T(lang,"documents")}</div>
          {folderDocs.map((doc,i)=>{return(<div key={doc.id} onClick={()=>openDocument(doc)} onContextMenu={e=>{e.preventDefault();setPreviewDoc(doc);}} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 20px",borderBottom:i<folderDocs.length-1?`1px solid ${THEME.borderSubtle}`:"none",cursor:"pointer",transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=THEME.surfaceHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <FileTypeBadge doc={doc} size={36}/>
            {/* Sprint 11.4 follow-on: iManage-style metadata row.
                Pattern: "v{ver} | #{docId,123} | {AUTHOR} | {date} | {size}".
                Author = uploader's initials (we don't have iManage's user-code
                concept; initials are the closest equivalent). Date is the
                numeric form (compact) rather than "16h ago". Size unchanged.
                Pipe separators match iManage's visual density; we use a
                lighter color + mono font for the metadata line so it reads
                as secondary info under the filename. */}
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:500,color:THEME.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6}}>
                {doc.name}<CheckoutBadge doc={doc} lang={lang}/><SecurityBadge doc={doc} lang={lang}/>
              </div>
              <div style={{fontSize:11,color:THEME.textDim,marginTop:3,fontFamily:FONT_MONO,fontVariantNumeric:"tabular-nums",letterSpacing:"0.01em"}}>
                <span>v{doc.current_version||1}</span>
                <span style={{opacity:0.45,padding:"0 6px"}}>|</span>
                <span>{formatDocId(doc.id)}</span>
                <span style={{opacity:0.45,padding:"0 6px"}}>|</span>
                <span>{authorInitials(doc.uploaded_by)||"—"}</span>
                <span style={{opacity:0.45,padding:"0 6px"}}>|</span>
                <span>{formatNumericDate(doc.updated_at||doc.created_at,lang)}</span>
                <span style={{opacity:0.45,padding:"0 6px"}}>|</span>
                <span>{formatBytes(doc.size)}</span>
              </div>
            </div>
            {/* Author full-name still shown on the right for quick scan —
                the initials in the metadata line are dense; the full name
                here is for the user who wants to know WHO without parsing
                initials. iManage shows both too (just in different places). */}
            <span style={{fontSize:12,color:THEME.textMuted,flexShrink:0}}>{doc.uploaded_by}</span>
          </div>);})}
        </div>):childFolders.length===0&&<EmptyState icon={lucide.FileText} title={T(lang,"noDocsYet")} subtitle={T(lang,"uploadFirst")}/>}
      </div>)}
      {!activeFolderId&&displayFolders.length===0&&<EmptyState icon={lucide.FolderOpen} title={T(lang,"noFolders")} subtitle={T(lang,"createHint")}/>}
      <AnimatePresence>{previewDoc&&<DocumentPreviewModal doc={previewDoc} onClose={()=>{setPreviewDoc(null);triggerRefresh();}} onAction={()=>{setPreviewDoc(null);triggerRefresh();}}/>}</AnimatePresence>
      <AnimatePresence>{conflictDoc&&<CheckoutConflictDialog doc={conflictDoc} checkoutStatus={conflictStatus} lang={lang} onOpenReadOnly={()=>openReadOnly(conflictDoc)} onCancel={()=>{setConflictDoc(null);setConflictStatus(null);}}/>}</AnimatePresence>
      {/* Sprint 10C H10-B2: Folder Security modal */}
      <AnimatePresence>
        {securityFolder && (
          <FolderSecurityModal
            folder={securityFolder}
            onClose={()=>setSecurityFolder(null)}
            onSuccess={()=>{toast(lang==="fr"?"Permissions mises à jour":"Permissions updated","success");setSecurityFolder(null);triggerRefresh();}}
            api={api}
            currentUser={user}
            lang={lang}
            THEME={THEME}
          />
        )}
      </AnimatePresence>

      {/* ── Sprint 11.x — Edit folder modal ── */}
      <AnimatePresence>
        {editFolder && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}
            onClick={e=>{if(e.target===e.currentTarget)setEditFolder(null);}}>
            <motion.div initial={{opacity:0,scale:0.95,y:10}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.95}}
              style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:460,padding:24,boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
                <div style={{width:40,height:40,borderRadius:10,background:THEME.accentDim,border:`1px solid ${THEME.accentLine}`,display:"flex",alignItems:"center",justifyContent:"center"}}><lucide.Pencil size={18} color={THEME.accent}/></div>
                <div><div style={{fontSize:16,fontWeight:600,color:THEME.text}}>{lang==="fr"?"Modifier le dossier":"Edit folder"}</div><div style={{fontSize:12,color:THEME.textDim,marginTop:2}}>#{editFolder.id} · {editFolder.parent_id?(lang==="fr"?"Sous-dossier":"Subfolder"):(lang==="fr"?"Dossier racine":"Top-level folder")}</div></div>
              </div>
              <div style={{marginBottom:14}}>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.4}}>{lang==="fr"?"Nom du dossier":"Folder name"}</label>
                <input value={editName} onChange={e=>setEditName(e.target.value)} autoFocus style={{...S.input,fontSize:13}} onKeyDown={e=>{if(e.key==="Enter")saveEditFolder();}}/>
              </div>
              <div style={{marginBottom:20}}>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.4}}>{lang==="fr"?"Couleur":"Color"}</label>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {["#D4915E","#5E8AD4","#5CB88A","#D46A6A","#D4B85E","#9B6AD4","#7aa7ff","#34c98a"].map(c=>(
                    <button key={c} onClick={()=>setEditColor(c)}
                      style={{width:32,height:32,borderRadius:8,border:`2px solid ${editColor===c?THEME.text:"transparent"}`,background:c,cursor:"pointer",padding:0,boxShadow:editColor===c?`0 0 0 2px ${THEME.surfaceRaised}, 0 0 0 4px ${c}40`:"none"}}
                      title={c}
                    />
                  ))}
                </div>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button onClick={()=>setEditFolder(null)} style={{...S.btn,padding:"9px 20px",fontSize:13,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}>{T(lang,"cancel")}</button>
                <button onClick={saveEditFolder} disabled={savingEdit||!editName.trim()} style={{...S.btn,padding:"9px 20px",fontSize:13,background:THEME.accent,color:"#fff",opacity:(!editName.trim()||savingEdit)?0.5:1,fontWeight:600}}>{savingEdit?<Spinner size={14}/>:<><lucide.Check size={14}/>{lang==="fr"?"Enregistrer":"Save"}</>}</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Sprint 11.x — Delete folder confirm modal ── */}
      <AnimatePresence>
        {deleteFolder && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}
            onClick={e=>{if(e.target===e.currentTarget){setDeleteFolder(null);setDeleteConfirm("");}}}>
            <motion.div initial={{opacity:0,scale:0.95,y:10}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.95}}
              style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.danger}40`,borderRadius:14,width:480,padding:24,boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18}}>
                <div style={{width:40,height:40,borderRadius:10,background:THEME.dangerDim,border:`1px solid ${THEME.danger}40`,display:"flex",alignItems:"center",justifyContent:"center"}}><lucide.Trash2 size={18} color={THEME.danger}/></div>
                <div><div style={{fontSize:16,fontWeight:600,color:THEME.text}}>{lang==="fr"?"Supprimer le dossier":"Delete folder"}</div><div style={{fontSize:12,color:THEME.textDim,marginTop:2,fontFamily:FONT_MONO}}>#{deleteFolder.id} · {deleteFolder.parent_id?(lang==="fr"?"Sous-dossier":"Subfolder"):(lang==="fr"?"Dossier racine":"Top-level folder")}</div></div>
              </div>
              <div style={{padding:"12px 14px",background:THEME.dangerDim,border:`1px solid ${THEME.danger}30`,borderRadius:8,marginBottom:16,fontSize:12.5,color:THEME.ink2,lineHeight:1.5}}>
                {lang==="fr"
                  ? <>Les documents du dossier <b style={{color:THEME.text}}>{deleteFolder.name}</b> seront déplacés à la racine et les sous-dossiers seront détachés. {!deleteFolder.parent_id && <>Les <b style={{color:THEME.danger}}>permissions de sécurité seront supprimées définitivement</b> et ne pourront pas être restaurées.</>}</>
                  : <>Documents in <b style={{color:THEME.text}}>{deleteFolder.name}</b> will be moved to root and subfolders detached. {!deleteFolder.parent_id && <>The <b style={{color:THEME.danger}}>security permissions on this folder will be permanently removed</b> and cannot be restored.</>}</>}
              </div>
              {!deleteFolder.parent_id && (
                <div style={{marginBottom:18}}>
                  <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.4}}>
                    {lang==="fr"?<>Tapez <b style={{color:THEME.danger,fontFamily:FONT_MONO}}>{deleteFolder.name}</b> pour confirmer</>:<>Type <b style={{color:THEME.danger,fontFamily:FONT_MONO}}>{deleteFolder.name}</b> to confirm</>}
                  </label>
                  <input value={deleteConfirm} onChange={e=>setDeleteConfirm(e.target.value)} autoFocus placeholder={deleteFolder.name} style={{...S.input,fontSize:13,fontFamily:FONT_MONO}} onKeyDown={e=>{if(e.key==="Enter")confirmDeleteFolder();}}/>
                </div>
              )}
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button onClick={()=>{setDeleteFolder(null);setDeleteConfirm("");}} style={{...S.btn,padding:"9px 20px",fontSize:13,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}>{T(lang,"cancel")}</button>
                <button onClick={confirmDeleteFolder} disabled={deletingFolder||(!deleteFolder.parent_id && deleteConfirm.trim()!==deleteFolder.name)} style={{...S.btn,padding:"9px 20px",fontSize:13,background:THEME.danger,color:"#fff",opacity:(deletingFolder||(!deleteFolder.parent_id && deleteConfirm.trim()!==deleteFolder.name))?0.5:1,fontWeight:600}}>{deletingFolder?<Spinner size={14}/>:<><lucide.Trash2 size={14}/>{lang==="fr"?"Supprimer définitivement":"Delete permanently"}</>}</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── SEARCH — enhanced with metadata filters ───
function SearchPage(){
  const {lang}=useLang();const {toast,initialSearchQuery,setInitialSearchQuery,triggerRefresh,canEdit}=useApp();
  const [query,setQuery]=useState("");const [results,setResults]=useState([]);
  const [loading,setLoading]=useState(false);const [searched,setSearched]=useState(false);
  const [filters,setFilters]=useState({type:"all",dateRange:"all",department:"",confidentiality:"",author:""});
  const [previewDoc,setPreviewDoc]=useState(null);const [showAdvanced,setShowAdvanced]=useState(false);
  // Sprint 6.7
  const [conflictDoc,setConflictDoc]=useState(null);const [conflictStatus,setConflictStatus]=useState(null);
  async function openDocument(doc){
    try{const status=await api.get(`/checkouts/status/${doc.id}`);if(status.checked_out&&!status.is_mine){setConflictDoc(doc);setConflictStatus(status);return;}}catch{}
    // Sprint 11.x DMS-correct behavior: click opens preview modal, never downloads.
    setPreviewDoc(doc);
  }
  function openReadOnly(doc){setPreviewDoc(doc);setConflictDoc(null);setConflictStatus(null);}
  useEffect(()=>{if(initialSearchQuery){setQuery(initialSearchQuery);doSearchWith(initialSearchQuery);setInitialSearchQuery("");}},[initialSearchQuery]);
  async function doSearchWith(q){
    if(!q?.trim()&&!filters.department&&!filters.author)return;setLoading(true);setSearched(true);
    try{
      // Try advanced search if metadata filters active
      if(filters.department||filters.author||filters.confidentiality){
        const params=new URLSearchParams();
        if(q?.trim())params.set("q",q.trim());
        if(filters.author)params.set("author",filters.author);
        if(filters.department)params.set("department",filters.department);
        if(filters.confidentiality)params.set("confidentiality",filters.confidentiality);
        const res=await api.get(`/metadata/search/advanced?${params.toString()}`);
        setResults(res.data||[]);
      }else{
        const res=await api.get(`/documents/search?q=${encodeURIComponent(q)}&type=${filters.type}&range=${filters.dateRange}`);
        setResults(res.data||[]);
      }
    }catch{setResults([]);}setLoading(false);
  }
  function doSearch(e){e?.preventDefault();doSearchWith(query);}
  return(
    <div>
      <PageHeader title={T(lang,"searchTitle")} subtitle={T(lang,"searchSub")}/>
      <form onSubmit={doSearch} style={{marginBottom:16}}><div style={{display:"flex",alignItems:"center",gap:12,background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:12,padding:"6px 8px 6px 18px"}}><lucide.Search size={18} color={THEME.textDim}/><input value={query} onChange={e=>setQuery(e.target.value)} placeholder={T(lang,"searchPH")} style={{flex:1,border:"none",background:"transparent",color:THEME.text,fontFamily:FONT,fontSize:15,outline:"none",padding:"10px 0"}} autoFocus/><button type="button" onClick={()=>setShowAdvanced(!showAdvanced)} style={{...S.btn,padding:"8px 14px",fontSize:12,background:showAdvanced?THEME.accentDim:"transparent",color:showAdvanced?THEME.accent:THEME.textDim}}><lucide.SlidersHorizontal size={14}/></button><button type="submit" style={{...S.btn,background:THEME.accent,color:"#fff",padding:"10px 24px"}}>{T(lang,"searchBtn")}</button></div></form>
      {/* Basic filters */}
      <div style={{display:"flex",gap:10,marginBottom:showAdvanced?12:24,flexWrap:"wrap"}}>
        <span style={{fontSize:12,color:THEME.textDim,lineHeight:"32px"}}>{T(lang,"typeF")}</span>
        {[["all",T(lang,"all")],["pdf",T(lang,"pdf")],["docx",T(lang,"word")],["image",T(lang,"images")],["xlsx",T(lang,"sheets")]].map(([v,l])=><button key={v} onClick={()=>setFilters(f=>({...f,type:v}))} style={{...S.btn,padding:"6px 14px",fontSize:12,background:filters.type===v?THEME.accentDim:"transparent",color:filters.type===v?THEME.accent:THEME.textDim,border:`1px solid ${filters.type===v?THEME.accent+"40":THEME.border}`}}>{l}</button>)}
        <div style={{width:1,height:28,background:THEME.border,margin:"2px 8px"}}/>
        <span style={{fontSize:12,color:THEME.textDim,lineHeight:"32px"}}>{T(lang,"dateF")}</span>
        {[["all",T(lang,"anyTime")],["7d",T(lang,"pastWeek")],["30d",T(lang,"pastMonth")],["90d",T(lang,"pastQ")]].map(([v,l])=><button key={v} onClick={()=>setFilters(f=>({...f,dateRange:v}))} style={{...S.btn,padding:"6px 14px",fontSize:12,background:filters.dateRange===v?THEME.accentDim:"transparent",color:filters.dateRange===v?THEME.accent:THEME.textDim,border:`1px solid ${filters.dateRange===v?THEME.accent+"40":THEME.border}`}}>{l}</button>)}
      </div>
      {/* Advanced metadata filters */}
      <AnimatePresence>{showAdvanced&&<motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{overflow:"hidden",marginBottom:24}}>
        <div style={{...S.card,display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-end"}}>
          <div style={{flex:1,minWidth:160}}><label style={{display:"block",fontSize:11,color:THEME.textDim,textTransform:"uppercase",marginBottom:4}}>{T(lang,"author")}</label><input value={filters.author} onChange={e=>setFilters(f=>({...f,author:e.target.value}))} style={{...S.input,padding:"6px 10px",fontSize:12}}/></div>
          <div style={{flex:1,minWidth:160}}><label style={{display:"block",fontSize:11,color:THEME.textDim,textTransform:"uppercase",marginBottom:4}}>{T(lang,"department")}</label><input value={filters.department} onChange={e=>setFilters(f=>({...f,department:e.target.value}))} style={{...S.input,padding:"6px 10px",fontSize:12}}/></div>
          <div style={{width:160}}><label style={{display:"block",fontSize:11,color:THEME.textDim,textTransform:"uppercase",marginBottom:4}}>{T(lang,"confidentiality")}</label><select value={filters.confidentiality} onChange={e=>setFilters(f=>({...f,confidentiality:e.target.value}))} style={{...S.input,padding:"6px 10px",fontSize:12}}><option value="">{T(lang,"all")}</option><option value="public">{T(lang,"public")}</option><option value="internal">{T(lang,"internal")}</option><option value="confidential">{T(lang,"confidential")}</option><option value="secret">{T(lang,"secret")}</option></select></div>
        </div>
      </motion.div>}</AnimatePresence>
      {loading?<div style={{textAlign:"center",padding:60}}><Spinner size={28}/></div>
      :searched&&results.length===0?<EmptyState icon={lucide.SearchX} title={T(lang,"noResults")} subtitle={`${T(lang,"noMatch")} "${query}"`}/>
      :results.length>0?(<div style={{...S.card,padding:0,overflow:"hidden"}}><div style={{padding:"12px 20px",borderBottom:`1px solid ${THEME.border}`,fontSize:13,color:THEME.textMuted}}>{results.length} {results.length!==1?T(lang,"results"):T(lang,"result")}</div>{results.map((doc,i)=>{return(<div key={doc.id} onClick={()=>setPreviewDoc(doc)} style={{display:"flex",alignItems:"center",gap:14,padding:"14px 20px",borderBottom:i<results.length-1?`1px solid ${THEME.borderSubtle}`:"none",cursor:"pointer",transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=THEME.surfaceHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}><FileTypeBadge doc={doc} size={36}/><div style={{flex:1,minWidth:0}}><div style={{fontSize:14,fontWeight:500,color:THEME.text}}>{doc.name}</div><div style={{fontSize:12,color:THEME.textDim,marginTop:3}}>{doc.folder||T(lang,"root")} · {formatBytes(doc.size)} · {formatDate(doc.updated_at,lang)}{doc.confidentiality&&doc.confidentiality!=="internal"&&<Badge color={THEME.warning}>{doc.confidentiality}</Badge>}</div></div><button onClick={e=>{e.stopPropagation();openDocument(doc);}} title={lang==="fr"?"Ouvrir dans un nouvel onglet":"Open in new tab"} style={{width:34,height:34,borderRadius:8,border:`1px solid ${THEME.border}`,background:"transparent",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,transition:"all 0.15s"}} onMouseEnter={e=>{e.currentTarget.style.background=THEME.accentDim;e.currentTarget.style.borderColor=THEME.accent+"40";}} onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.borderColor=THEME.border;}}><lucide.ExternalLink size={15} color={THEME.textMuted}/></button></div>);})}</div>)
      :!searched?<div style={{textAlign:"center",padding:"60px 20px"}}><lucide.Search size={48} color={THEME.textDim} strokeWidth={1}/><div style={{marginTop:16,fontSize:15,color:THEME.textMuted}}>{T(lang,"searchArchive")}</div><div style={{marginTop:6,fontSize:13,color:THEME.textDim}}>{T(lang,"fullText")}</div></div>:null}
      <AnimatePresence>{previewDoc&&<DocumentPreviewModal doc={previewDoc} onClose={()=>{setPreviewDoc(null);doSearchWith(query);}} onAction={()=>{setPreviewDoc(null);triggerRefresh();doSearchWith(query);}}/>}</AnimatePresence>
      <AnimatePresence>{conflictDoc&&<CheckoutConflictDialog doc={conflictDoc} checkoutStatus={conflictStatus} lang={lang} onOpenReadOnly={()=>openReadOnly(conflictDoc)} onCancel={()=>{setConflictDoc(null);setConflictStatus(null);}}/>}</AnimatePresence>
    </div>
  );
}



// ─── COURRIER PAGE — Sprint 12 registry redesign v3 (2026-05-14) ───
// 3-pane registry. Left rail: search → 5 filter chips (All/In/Out/Open/
// Urgent) → date-grouped list with attachment-count + snippet → sticky
// keyboard-shortcut footer. Right pane: ref + UNREAD pill + prev/next
// nav, 3-pill row (direction · priority · status), title + meta with
// DUE date, action bar, clickable workflow strip, letter card,
// attachments card grid, inline reply composer at the bottom.
// Keyboard: ↑/↓ navigate, R = reply, E = archive.
//
// v3 (this pass): removed the inline Activity timeline and the Tags
// section. Per-courrier audit events are accessible via the existing
// /audit-log page, so the inline timeline was redundant noise. Tags
// add UI clutter without much value at pilot scale (status + assignee
// + sender + department + search already cover real filtering needs);
// the `tags` JSONB column stays in the schema so a future paying
// customer's request for cross-cutting case grouping is purely a UI
// re-enable, not a destructive migration.
//
// Backend additions still wired here: read_at (timestamptz) +
// POST /courrier/:id/read. The tags column is preserved but unused
// by the UI.
// ─── EMAILS PAGE — filed-email repository (iManage "Emails" tab) ───
// Emails are records filed INTO the DMS (not a live mailbox). They get
// in two ways: (a) "New email" composes/logs one, (b) "Import" uploads
// a .eml which the backend parses (from/to/subject/body/date + its own
// attachments). 2-pane registry: filterable list + read pane with the
// text body and attachment cards. Mirrors CourrierPage's architecture
// but lighter (no workflow stages / priority / assignee).
function EmailsPage(){
  const {toast, canEdit, isAdmin, refreshSignal, triggerRefresh, user} = useApp();
  const {lang} = useLang();

  const [items, setItems] = useState([]);
  const [filterDir, setFilterDir] = useState("");   // "" | incoming | outgoing
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [form, setForm] = useState({direction:"incoming",from_addr:"",to_addr:"",cc_addr:"",subject:"",body:"",folder_id:"",sent_at:""});
  const [previewAtt, setPreviewAtt] = useState(null);   // {url,name,mime,isPdf,isImage,isOffice,isBlob}
  const [showSend, setShowSend] = useState(false);
  const [sendForm, setSendForm] = useState({to:"",cc:"",subject:"",body:"",folder_id:""});
  const [sendFiles, setSendFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const emlInputRef = useRef();
  const attInputRef = useRef();
  const sendFileRef = useRef();

  // Revoke blob URLs on close/switch so we don't leak object-URL memory.
  useEffect(()=>{ return ()=>{ if(previewAtt?.isBlob && previewAtt.url){ try{URL.revokeObjectURL(previewAtt.url);}catch{} } }; },[previewAtt]);

  const FR = lang==="fr";
  const lbl = {display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.3};
  const iconBtn = {width:30,height:30,borderRadius:7,border:`1px solid ${THEME.border}`,background:THEME.surface,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:THEME.textMuted};

  function load(){
    const p = new URLSearchParams();
    if(filterDir) p.set("direction", filterDir);
    if(searchQuery.trim()) p.set("search", searchQuery.trim());
    api.get(`/emails?${p}`).then(r=>{
      const data = r.data||[];
      setItems(data);
      if(data.length && !data.some(e=>e.id===selectedId)) selectFor(data[0]);
      if(!data.length){ setSelectedId(null); setAttachments([]); }
    }).catch(()=>setItems([]));
  }
  useEffect(()=>{
    load();
    api.get("/folders").then(r=>setFolders(r.data||[])).catch(()=>setFolders([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[refreshSignal]);
  useEffect(()=>{ load(); /* eslint-disable-line */ },[filterDir, searchQuery]);

  function selectFor(e){
    if(!e){ setSelectedId(null); setAttachments([]); return; }
    setSelectedId(e.id);
    api.get(`/emails/${e.id}/attachments`).then(r=>setAttachments(r.data||r||[])).catch(()=>setAttachments([]));
    if(!e.read_at){
      api.post(`/emails/${e.id}/read`).then(()=>{
        setItems(prev=>prev.map(it=>it.id===e.id?{...it,read_at:new Date().toISOString()}:it));
      }).catch(()=>{});
    }
  }
  const selected = items.find(e=>e.id===selectedId) || null;

  // Flatten folder tree into indented <option>s (root → leaves).
  function folderOptions(){
    const build=(parentId,depth)=>folders.filter(f=>(f.parent_id||null)===(parentId||null)).flatMap(f=>[{id:f.id,label:"  ".repeat(depth)+(depth>0?"└ ":"")+f.name},...build(f.id,depth+1)]);
    return build(null,0);
  }

  async function createEmail(){
    if(!form.subject.trim()) return;
    try{
      await api.post("/emails", form);
      toast(FR?"Email enregistré":"Email filed","success");
      setShowCompose(false);
      setForm({direction:"incoming",from_addr:"",to_addr:"",cc_addr:"",subject:"",body:"",folder_id:"",sent_at:""});
      triggerRefresh();
    }catch(e){ toast(e?.message||"Error","error"); }
  }

  async function sendEmailNow(){
    if(!sendForm.to.trim() || !sendForm.subject.trim()) return;
    setSending(true);
    try{
      const fd = new FormData();
      fd.append("to", sendForm.to.trim());
      if(sendForm.cc.trim()) fd.append("cc", sendForm.cc.trim());
      fd.append("subject", sendForm.subject.trim());
      fd.append("body", sendForm.body||"");
      if(sendForm.folder_id) fd.append("folder_id", sendForm.folder_id);
      for(const f of sendFiles) fd.append("files", f);
      const r = await api.upload("/emails/send", fd);
      toast(r?.dev ? (FR?"Email journalisé (mode dev — non envoyé)":"Email logged (dev mode — not sent)") : (FR?"Email envoyé":"Email sent"), "success");
      setShowSend(false);
      setSendForm({to:"",cc:"",subject:"",body:"",folder_id:""});
      setSendFiles([]);
      triggerRefresh();
    }catch(e){ toast(e?.message||(FR?"Échec de l'envoi":"Send failed"),"error"); }
    finally{ setSending(false); }
  }

  async function importEml(files){
    if(!files?.length) return;
    setImporting(true);
    let ok=0;
    try{
      for(const file of files){
        const fd=new FormData(); fd.append("file",file);
        try{ await api.upload("/emails/upload", fd); ok++; }catch(e){ toast(`${file.name}: ${e?.message||"failed"}`,"error"); }
      }
      if(ok) toast(FR?`${ok} email(s) importé(s)`:`${ok} email(s) imported`,"success");
      triggerRefresh();
    } finally { setImporting(false); }
  }

  async function uploadAttachment(files){
    if(!files?.length || !selected) return;
    setUploading(true);
    try{
      for(const file of files){ const fd=new FormData(); fd.append("file",file); await api.upload(`/emails/${selected.id}/attachments`, fd); }
      toast(FR?`${files.length} fichier(s) attaché(s)`:`${files.length} file(s) attached`,"success");
      const r=await api.get(`/emails/${selected.id}/attachments`); setAttachments(r?.data||r||[]);
      triggerRefresh();
    }catch(e){ toast(e?.message||"Upload failed","error"); }
    finally{ setUploading(false); }
  }

  async function deleteEmail(){
    if(!selected) return;
    if(!confirm(FR?`Supprimer définitivement cet email et ses pièces jointes ?`:`Permanently delete this email and its attachments?`)) return;
    try{
      await api.del(`/emails/${selected.id}`);
      toast(FR?"Email supprimé":"Email deleted","success");
      setSelectedId(null); setAttachments([]); triggerRefresh();
    }catch(e){ toast(e?.message||"Delete failed","error"); }
  }

  async function deleteAttachment(a){
    if(!selected) return;
    if(!confirm(FR?`Supprimer la pièce jointe "${a.name}" ?`:`Delete attachment "${a.name}"?`)) return;
    try{
      await api.del(`/emails/${selected.id}/attachments/${a.id}`);
      const r=await api.get(`/emails/${selected.id}/attachments`); setAttachments(r?.data||r||[]);
      triggerRefresh();
    }catch(e){ toast(e?.message||"Delete failed","error"); }
  }

  async function refileFolder(folderId){
    if(!selected) return;
    try{
      await api.put(`/emails/${selected.id}`, {folder_id: folderId});
      const fname = folders.find(f=>String(f.id)===String(folderId))?.name || null;
      setItems(prev=>prev.map(it=>it.id===selected.id?{...it,folder_id:folderId,folder_name:fname}:it));
      toast(FR?"Classé":"Filed","success");
    }catch(e){ toast(e?.message||"Error","error"); }
  }

  // Authenticated blob fetch → preview (blob: URL bypasses framing blocks).
  async function previewAttachment(a){
    try{
      const r = await fetch(`${API_BASE}/emails/${selected.id}/attachments/${a.id}/raw`, {headers:{Authorization:`Bearer ${api.token}`}});
      if(!r.ok) throw new Error(`(${r.status})`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const mime = a.mime_type||blob.type||"";
      setPreviewAtt({url,name:a.name,mime,isPdf:mime.includes("pdf"),isImage:mime.startsWith("image/"),isOffice:false,isBlob:true});
    }catch(e){ toast(e?.message||"Preview unavailable","error"); }
  }
  async function downloadAttachment(a){
    try{
      const r = await fetch(`${API_BASE}/emails/${selected.id}/attachments/${a.id}/raw`, {headers:{Authorization:`Bearer ${api.token}`}});
      if(!r.ok) throw new Error(`(${r.status})`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a"); link.href=url; link.download=a.name||"attachment";
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(()=>{try{URL.revokeObjectURL(url);}catch{}},4000);
    }catch(e){ toast(e?.message||"Download failed","error"); }
  }

  const unreadCount = items.filter(e=>!e.read_at).length;

  return (
    <div style={{height:"100%",display:"flex",flexDirection:"column",minHeight:0}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18,gap:16,flexWrap:"wrap"}}>
        <div>
          <h2 style={{fontSize:22,fontWeight:700,color:THEME.text,margin:0,display:"flex",alignItems:"center",gap:10}}>
            <lucide.Mail size={20} color={THEME.accent}/>{FR?"E-mails":"Emails"}
            <span style={{fontSize:14,fontWeight:500,color:THEME.textDim}}>— {items.length} {FR?"classés":"filed"}</span>
          </h2>
          <p style={{fontSize:13,color:THEME.textDim,margin:"4px 0 0"}}>{FR?"Référentiel d'e-mails classés dans vos archives — importez un .eml ou enregistrez manuellement.":"Emails filed into your archive — import a .eml or log one manually."}</p>
        </div>
        {canEdit && <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <button onClick={()=>emlInputRef.current?.click()} disabled={importing} style={{...S.btn,background:THEME.surface,color:THEME.ink2,border:`1px solid ${THEME.border}`,fontSize:13,opacity:importing?0.6:1}}>{importing?<Spinner size={14}/>:<lucide.Upload size={15}/>}{FR?"Importer .eml":"Import .eml"}</button>
          <input ref={emlInputRef} type="file" accept=".eml,.msg,message/rfc822" multiple style={{display:"none"}} onChange={e=>{importEml(e.target.files); e.target.value="";}}/>
          <button onClick={()=>setShowCompose(true)} style={{...S.btn,background:THEME.surface,color:THEME.ink2,border:`1px solid ${THEME.border}`,fontSize:13}}><lucide.FilePlus size={15}/>{FR?"Journaliser":"Log email"}</button>
          <button onClick={()=>setShowSend(true)} style={{...S.btn,background:THEME.accent,color:"#fff",fontSize:13}}><lucide.Send size={15}/>{FR?"Composer":"Compose"}</button>
        </div>}
      </div>

      {/* Filter bar */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        {[{k:"",icon:lucide.Inbox,label:FR?"Tous":"All"},{k:"incoming",icon:lucide.ArrowDownLeft,label:FR?"Reçus":"Incoming"},{k:"outgoing",icon:lucide.ArrowUpRight,label:FR?"Envoyés":"Outgoing"}].map(t=>{
          const on=filterDir===t.k;
          return <button key={t.k||"all"} onClick={()=>setFilterDir(t.k)} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:FONT,border:`1px solid ${on?THEME.accent:THEME.border}`,background:on?THEME.accentDim:THEME.surface,color:on?THEME.accent2:THEME.textMuted}}><t.icon size={13}/>{t.label}</button>;
        })}
        {unreadCount>0 && <span style={{fontSize:11,fontFamily:FONT_MONO,color:THEME.textDim}}>{unreadCount} {FR?"non lus":"unread"}</span>}
        <div style={{flex:1}}/>
        <div style={{position:"relative"}}>
          <lucide.Search size={14} color={THEME.textDim} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)"}}/>
          <input value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder={FR?"Rechercher objet, expéditeur…":"Search subject, sender…"} style={{...S.input,padding:"8px 12px 8px 32px",fontSize:12,width:260}}/>
        </div>
      </div>

      {/* Two-pane */}
      <div style={{flex:1,minHeight:0,display:"grid",gridTemplateColumns:"360px 1fr",gap:14}}>
        {/* List pane */}
        <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,overflow:"auto"}}>
          {items.length===0
            ? <div style={{padding:"48px 20px"}}><EmptyState icon={lucide.MailOpen} title={FR?"Aucun email":"No emails yet"} subtitle={FR?"Importez un .eml ou créez-en un.":"Import a .eml or create one."}/></div>
            : items.map((e,i)=>{
                const who = e.direction==="outgoing" ? (e.to_addr||"—") : (e.from_addr||"—");
                const on = e.id===selectedId;
                const unread = !e.read_at;
                return (
                  <div key={e.id} onClick={()=>selectFor(e)} style={{display:"flex",gap:10,padding:"12px 14px",borderBottom:i<items.length-1?`1px solid ${THEME.borderSubtle}`:"none",cursor:"pointer",background:on?THEME.accentDim:"transparent",borderLeft:`2px solid ${on?THEME.accent:"transparent"}`,transition:"background 0.12s"}} onMouseEnter={ev=>{if(!on)ev.currentTarget.style.background=THEME.surfaceHover;}} onMouseLeave={ev=>{if(!on)ev.currentTarget.style.background="transparent";}}>
                    <div style={{width:30,height:30,borderRadius:8,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",background:e.direction==="outgoing"?THEME.infoDim:THEME.accentDim,color:e.direction==="outgoing"?THEME.info:THEME.accent2}}>
                      {e.direction==="outgoing"?<lucide.ArrowUpRight size={15}/>:<lucide.ArrowDownLeft size={15}/>}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{fontSize:12.5,fontWeight:unread?700:600,color:THEME.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{who}</span>
                        {unread && <span style={{width:7,height:7,borderRadius:"50%",background:THEME.accent,flexShrink:0}}/>}
                        <span style={{fontSize:10,color:THEME.textDim,fontFamily:FONT_MONO,flexShrink:0}}>{formatDate(e.sent_at||e.created_at,lang)}</span>
                      </div>
                      <div style={{fontSize:12,color:THEME.ink2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:2,fontWeight:unread?600:400}}>{e.subject||"(no subject)"}</div>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4,fontSize:10.5,color:THEME.textDim,fontFamily:FONT_MONO}}>
                        {e.folder_name && <span style={{display:"inline-flex",alignItems:"center",gap:3}}><lucide.Folder size={10}/>{e.folder_name}</span>}
                        {Number(e.attachment_count)>0 && <span style={{display:"inline-flex",alignItems:"center",gap:3}}><lucide.Paperclip size={10}/>{e.attachment_count}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>

        {/* Detail pane */}
        <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,overflow:"auto",display:"flex",flexDirection:"column",minHeight:0}}>
          {!selected
            ? <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}><EmptyState icon={lucide.Mail} title={FR?"Sélectionnez un email":"Select an email"} subtitle={FR?"Choisissez un email dans la liste.":"Pick an email from the list."}/></div>
            : (<>
                <div style={{padding:"18px 22px",borderBottom:`1px solid ${THEME.borderSubtle}`}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                    <div style={{fontSize:18,fontWeight:700,color:THEME.text,lineHeight:1.3,flex:1}}>{selected.subject||"(no subject)"}</div>
                    {canEdit && <button onClick={deleteEmail} style={{...S.btn,padding:"7px 14px",fontSize:12,background:THEME.dangerDim,color:THEME.danger,border:`1px solid ${THEME.danger}40`,flexShrink:0}}><lucide.Trash2 size={13}/>{FR?"Supprimer":"Delete"}</button>}
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:"6px 18px",marginTop:12,fontSize:12,color:THEME.textDim}}>
                    <span><span style={{textTransform:"uppercase",letterSpacing:0.6,marginRight:6,fontSize:10}}>{FR?"De":"From"}</span><span style={{color:THEME.text,fontWeight:600}}>{selected.from_addr||"—"}</span></span>
                    <span><span style={{textTransform:"uppercase",letterSpacing:0.6,marginRight:6,fontSize:10}}>{FR?"À":"To"}</span><span style={{color:THEME.text,fontWeight:600}}>{selected.to_addr||"—"}</span></span>
                    {selected.cc_addr && <span><span style={{textTransform:"uppercase",letterSpacing:0.6,marginRight:6,fontSize:10}}>Cc</span><span style={{color:THEME.text,fontWeight:600}}>{selected.cc_addr}</span></span>}
                    <span><span style={{textTransform:"uppercase",letterSpacing:0.6,marginRight:6,fontSize:10}}>{FR?"Date":"Date"}</span><span style={{color:THEME.text,fontWeight:600}}>{formatDate(selected.sent_at||selected.created_at,lang)}</span></span>
                  </div>
                  {canEdit && <div style={{display:"flex",alignItems:"center",gap:8,marginTop:12}}>
                    <span style={{fontSize:10,textTransform:"uppercase",letterSpacing:0.6,color:THEME.textDim,display:"inline-flex",alignItems:"center",gap:5}}><lucide.FolderInput size={12}/>{FR?"Classer dans":"File in"}</span>
                    <select value={selected.folder_id||""} onChange={e=>refileFolder(e.target.value?parseInt(e.target.value,10):"")} style={{...S.input,padding:"5px 10px",fontSize:12,width:240}}>
                      <option value="">{FR?"— Aucun dossier —":"— No folder —"}</option>
                      {folderOptions().map(f=><option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                  </div>}
                </div>

                {/* Body */}
                <div style={{flex:1,minHeight:0,overflow:"auto",padding:"20px 22px"}}>
                  <div style={{...S.card,padding:"18px 20px",fontSize:13.5,lineHeight:1.7,color:THEME.text,whiteSpace:"pre-wrap",wordBreak:"break-word",minHeight:80}}>
                    {selected.body || <span style={{color:THEME.textDim,fontStyle:"italic"}}>{FR?"(Aucun corps de message)":"(No message body)"}</span>}
                  </div>

                  {/* Attachments */}
                  <div style={{marginTop:18}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                      <div style={{fontSize:10,fontWeight:700,letterSpacing:1.2,color:THEME.textDim,textTransform:"uppercase"}}>{FR?"Pièces jointes":"Attachments"} · {attachments.length}</div>
                      {canEdit && <button onClick={()=>attInputRef.current?.click()} disabled={uploading} style={{...S.btn,padding:"4px 10px",fontSize:11,background:THEME.accentDim,color:THEME.accent,border:`1px solid ${THEME.accent}30`}}>{uploading?<Spinner size={11}/>:<lucide.Paperclip size={11}/>}{FR?"Joindre":"Attach"}</button>}
                      <input ref={attInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.docx,.doc,.xlsx,.xls,.csv,.txt" style={{display:"none"}} onChange={e=>{uploadAttachment(e.target.files); e.target.value="";}}/>
                    </div>
                    {attachments.length===0
                      ? <div style={{border:`2px dashed ${THEME.border}`,borderRadius:8,padding:18,textAlign:"center",color:THEME.textDim,fontSize:12}}>{FR?"Aucune pièce jointe":"No attachments"}</div>
                      : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
                          {attachments.map(a=>(
                            <div key={a.id} style={{padding:10,background:THEME.surfaceRaised,border:`1px solid ${THEME.borderSubtle}`,borderRadius:8,display:"flex",alignItems:"center",gap:10}}>
                              <FileTypeBadge doc={{name:a.name,type:a.mime_type}} size={34}/>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:12,fontWeight:600,color:THEME.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.name}</div>
                                <div style={{fontSize:10,color:THEME.textDim,marginTop:2}}>{a.size?formatBytes(a.size):"—"}</div>
                              </div>
                              <button onClick={()=>previewAttachment(a)} title={FR?"Aperçu":"Preview"} style={{border:"none",background:"transparent",cursor:"pointer",padding:4,color:THEME.textDim}}><lucide.Eye size={13}/></button>
                              <button onClick={()=>downloadAttachment(a)} title={FR?"Télécharger":"Download"} style={{border:"none",background:"transparent",cursor:"pointer",padding:4,color:THEME.textDim}}><lucide.Download size={13}/></button>
                              {canEdit && <button onClick={()=>deleteAttachment(a)} title={FR?"Supprimer":"Delete"} style={{border:"none",background:"transparent",cursor:"pointer",padding:4,color:THEME.textDim}} onMouseEnter={ev=>ev.currentTarget.style.color=THEME.danger} onMouseLeave={ev=>ev.currentTarget.style.color=THEME.textDim}><lucide.Trash2 size={13}/></button>}
                            </div>
                          ))}
                        </div>}
                  </div>
                </div>
              </>)}
        </div>
      </div>

      {/* Attachment preview modal */}
      <AnimatePresence>{previewAtt && (
        <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2100,background:"rgba(0,0,0,0.78)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)setPreviewAtt(null);}}>
          <motion.div initial={{opacity:0,scale:0.96}} animate={{opacity:1,scale:1}} exit={{opacity:0,scale:0.96}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:12,width:"min(1100px,96vw)",height:"min(86vh,900px)",display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <div style={{padding:"12px 18px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
              <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}><lucide.Eye size={14} color={THEME.accent}/><span style={{fontSize:13,fontWeight:600,color:THEME.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{previewAtt.name}</span></div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                <a href={previewAtt.url} target="_blank" rel="noopener noreferrer" title={FR?"Nouvel onglet":"Open in new tab"} style={{...iconBtn,textDecoration:"none"}}><lucide.ExternalLink size={13}/></a>
                <button onClick={()=>setPreviewAtt(null)} style={iconBtn}><lucide.X size={13}/></button>
              </div>
            </div>
            <div style={{flex:1,minHeight:0,background:"#1a1a1a",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {previewAtt.isPdf ? <iframe src={previewAtt.url} title={previewAtt.name} style={{width:"100%",height:"100%",border:"none",background:"#fff"}}/>
                : previewAtt.isImage ? <img src={previewAtt.url} alt={previewAtt.name} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
                : <div style={{textAlign:"center",color:THEME.textDim,padding:32}}><lucide.FileQuestion size={48} strokeWidth={1} style={{display:"block",margin:"0 auto 12px"}}/><div style={{fontSize:14,color:"#ccc",marginBottom:8}}>{FR?"Aperçu non disponible pour ce type":"Preview not available for this type"}</div><a href={previewAtt.url} download={previewAtt.name} style={{fontSize:12,color:THEME.accent,textDecoration:"none"}}>{FR?"Télécharger":"Download"} →</a></div>}
            </div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>

      {/* Compose & SEND modal */}
      <AnimatePresence>{showSend && (
        <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.65)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget && !sending)setShowSend(false);}}>
          <motion.div initial={{opacity:0,scale:0.92,y:16}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.92}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:640,maxHeight:"90vh",overflow:"auto"}}>
            <div style={{padding:"18px 24px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:15,fontWeight:600,color:THEME.text,display:"flex",alignItems:"center",gap:8}}><lucide.Send size={16} color={THEME.accent}/>{FR?"Composer un email":"Compose email"}</div>
              <lucide.X size={18} color={THEME.textDim} style={{cursor:sending?"not-allowed":"pointer"}} onClick={()=>!sending&&setShowSend(false)}/>
            </div>
            <div style={{padding:"16px 24px 20px",display:"flex",flexDirection:"column",gap:14}}>
              {/* From / Reply-To info — the deliverable model */}
              <div style={{padding:"10px 12px",background:THEME.accentDim,border:`1px solid ${THEME.accentLine}`,borderRadius:8,fontSize:11.5,color:THEME.ink2,lineHeight:1.6}}>
                <div><span style={{textTransform:"uppercase",letterSpacing:0.6,color:THEME.textDim,fontSize:10,marginRight:6}}>{FR?"De":"From"}</span><b style={{color:THEME.text}}>{user?.name} (via KlassDoc)</b></div>
                <div style={{marginTop:2}}><span style={{textTransform:"uppercase",letterSpacing:0.6,color:THEME.textDim,fontSize:10,marginRight:6}}>Reply-To</span>{user?.email||"—"} <span style={{color:THEME.textDim}}>· {FR?"les réponses vous parviennent":"replies come back to you"}</span></div>
              </div>
              <div><label style={lbl}>{FR?"À":"To"} * <span style={{textTransform:"none",letterSpacing:0,color:THEME.textDim,fontWeight:400}}>({FR?"séparez par des virgules":"comma-separated"})</span></label><input value={sendForm.to} onChange={e=>setSendForm(f=>({...f,to:e.target.value}))} placeholder="client@acme.cd, legal@cabinet.cd" style={S.input}/></div>
              <div><label style={lbl}>Cc</label><input value={sendForm.cc} onChange={e=>setSendForm(f=>({...f,cc:e.target.value}))} style={S.input}/></div>
              <div><label style={lbl}>{FR?"Objet":"Subject"} *</label><input value={sendForm.subject} onChange={e=>setSendForm(f=>({...f,subject:e.target.value}))} style={S.input}/></div>
              <div><label style={lbl}>{FR?"Message":"Message"}</label><textarea value={sendForm.body} onChange={e=>setSendForm(f=>({...f,body:e.target.value}))} rows={8} style={{...S.input,resize:"vertical",fontFamily:FONT}}/></div>
              <div>
                <label style={lbl}>{FR?"Pièces jointes":"Attachments"}</label>
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:sendFiles.length?8:0}}>
                  {sendFiles.map((f,i)=>(
                    <span key={i} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:20,background:THEME.surfaceHover,border:`1px solid ${THEME.line2}`,fontSize:11,color:THEME.ink2}}>
                      <lucide.Paperclip size={11}/>{f.name} <span style={{color:THEME.textDim}}>{formatBytes(f.size)}</span>
                      <lucide.X size={11} style={{cursor:"pointer"}} onClick={()=>setSendFiles(arr=>arr.filter((_,idx)=>idx!==i))}/>
                    </span>
                  ))}
                </div>
                <button type="button" onClick={()=>sendFileRef.current?.click()} style={{...S.btn,padding:"6px 12px",fontSize:12,background:THEME.surface,color:THEME.ink2,border:`1px solid ${THEME.border}`}}><lucide.Paperclip size={13}/>{FR?"Ajouter des fichiers":"Add files"}</button>
                <input ref={sendFileRef} type="file" multiple style={{display:"none"}} onChange={e=>{setSendFiles(arr=>[...arr,...Array.from(e.target.files||[])]); e.target.value="";}}/>
              </div>
              <div><label style={lbl}>{FR?"Classer la copie dans":"File the sent copy in"}</label><select value={sendForm.folder_id} onChange={e=>setSendForm(f=>({...f,folder_id:e.target.value}))} style={S.input}><option value="">{FR?"— Aucun dossier —":"— No folder —"}</option>{folderOptions().map(f=><option key={f.id} value={f.id}>{f.label}</option>)}</select></div>
            </div>
            <div style={{padding:"16px 24px",borderTop:`1px solid ${THEME.borderSubtle}`,display:"flex",justifyContent:"flex-end",gap:10}}>
              <button onClick={()=>setShowSend(false)} disabled={sending} style={{...S.btn,background:"transparent",color:THEME.textMuted,border:`1px solid ${THEME.border}`,fontSize:13,opacity:sending?0.5:1}}>{FR?"Annuler":"Cancel"}</button>
              <button onClick={sendEmailNow} disabled={sending||!sendForm.to.trim()||!sendForm.subject.trim()} style={{...S.btn,background:THEME.accent,color:"#fff",fontSize:13,opacity:(sending||!sendForm.to.trim()||!sendForm.subject.trim())?0.5:1}}>{sending?<Spinner size={14}/>:<lucide.Send size={14}/>}{sending?(FR?"Envoi…":"Sending…"):(FR?"Envoyer":"Send")}</button>
            </div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>

      {/* Compose / log modal */}
      <AnimatePresence>{showCompose && (
        <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.65)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)setShowCompose(false);}}>
          <motion.div initial={{opacity:0,scale:0.92,y:16}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.92}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:620,maxHeight:"88vh",overflow:"auto"}}>
            <div style={{padding:"18px 24px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:15,fontWeight:600,color:THEME.text}}>{FR?"Enregistrer un email":"Log an email"}</div>
              <lucide.X size={18} color={THEME.textDim} style={{cursor:"pointer"}} onClick={()=>setShowCompose(false)}/>
            </div>
            <div style={{padding:"20px 24px",display:"flex",flexDirection:"column",gap:14}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div><label style={lbl}>{FR?"Sens":"Direction"}</label><select value={form.direction} onChange={e=>setForm(f=>({...f,direction:e.target.value}))} style={S.input}><option value="incoming">{FR?"Reçu":"Incoming"}</option><option value="outgoing">{FR?"Envoyé":"Outgoing"}</option></select></div>
                <div><label style={lbl}>{FR?"Date":"Date"}</label><input type="date" value={form.sent_at} onChange={e=>setForm(f=>({...f,sent_at:e.target.value}))} style={S.input}/></div>
                <div><label style={lbl}>{FR?"De":"From"}</label><input value={form.from_addr} onChange={e=>setForm(f=>({...f,from_addr:e.target.value}))} placeholder="client@acme.cd" style={S.input}/></div>
                <div><label style={lbl}>{FR?"À":"To"}</label><input value={form.to_addr} onChange={e=>setForm(f=>({...f,to_addr:e.target.value}))} placeholder="ek@klassdoc.com" style={S.input}/></div>
                <div style={{gridColumn:"span 2"}}><label style={lbl}>Cc</label><input value={form.cc_addr} onChange={e=>setForm(f=>({...f,cc_addr:e.target.value}))} style={S.input}/></div>
                <div style={{gridColumn:"span 2"}}><label style={lbl}>{FR?"Objet":"Subject"} *</label><input value={form.subject} onChange={e=>setForm(f=>({...f,subject:e.target.value}))} style={S.input}/></div>
                <div style={{gridColumn:"span 2"}}><label style={lbl}>{FR?"Dossier":"Folder"}</label><select value={form.folder_id} onChange={e=>setForm(f=>({...f,folder_id:e.target.value}))} style={S.input}><option value="">{FR?"— Aucun dossier —":"— No folder —"}</option>{folderOptions().map(f=><option key={f.id} value={f.id}>{f.label}</option>)}</select></div>
                <div style={{gridColumn:"span 2"}}><label style={lbl}>{FR?"Contenu":"Body"}</label><textarea value={form.body} onChange={e=>setForm(f=>({...f,body:e.target.value}))} rows={6} style={{...S.input,resize:"vertical",fontFamily:FONT}}/></div>
              </div>
            </div>
            <div style={{padding:"16px 24px",borderTop:`1px solid ${THEME.borderSubtle}`,display:"flex",justifyContent:"flex-end",gap:10}}>
              <button onClick={()=>setShowCompose(false)} style={{...S.btn,background:"transparent",color:THEME.textMuted,border:`1px solid ${THEME.border}`,fontSize:13}}>{FR?"Annuler":"Cancel"}</button>
              <button onClick={createEmail} disabled={!form.subject.trim()} style={{...S.btn,background:THEME.accent,color:"#fff",fontSize:13,opacity:form.subject.trim()?1:0.5}}><lucide.Check size={14}/>{FR?"Enregistrer":"Save"}</button>
            </div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>
    </div>
  );
}

function CourrierPage(){
  const {toast, canEdit, refreshSignal, triggerRefresh} = useApp();
  const {lang} = useLang();

  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({type:"", openOnly:false, urgentOnly:false});
  const [sortOrder, setSortOrder] = useState("newest");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [form, setForm] = useState({type:"incoming",subject:"",sender:"",recipient:"",department:"",priority:"normal",notes:"",due_date:"",assigned_to:"",assigned_to_name:""});
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [editForm, setEditForm] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState("");
  const [previewAtt, setPreviewAtt] = useState(null);   // {url, name, mime, isBlob}
  const fileInputRef = useRef();

  // Revoke blob URLs when the modal closes or switches to a new file, so
  // we don't leak object URL memory (Chrome retains them until the tab
  // closes otherwise).
  useEffect(()=>{
    return ()=>{
      if(previewAtt?.isBlob && previewAtt.url){
        try{ URL.revokeObjectURL(previewAtt.url); }catch{}
      }
    };
  },[previewAtt]);

  function load(){
    const params = new URLSearchParams();
    if(filter.type) params.set("type", filter.type);
    if(searchQuery.trim()) params.set("search", searchQuery.trim());
    api.get(`/courrier?${params}`).then(r=>{
      const data = r.data||[];
      setItems(data);
      if(data.length && !data.some(c=>c.id===selectedId)) selectFor(data[0]);
    }).catch(()=>{});
  }
  useEffect(()=>{
    load();
    api.get("/permissions/principals").then(r=>setUsers((r.users||[]).map(u=>({id:u.principal_id,name:u.principal_name,email:u.email})))).catch(()=>{});
    api.get("/departments").then(r=>setDepartments(r.data||[])).catch(()=>setDepartments([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[refreshSignal]);
  useEffect(()=>{ load(); /* eslint-disable-line */ },[filter.type, searchQuery]);

  function selectFor(c){
    if(!c){ setSelectedId(null); setEditForm({}); setAttachments([]); return; }
    setSelectedId(c.id);
    setEditForm({subject:c.subject||"",sender:c.sender||"",recipient:c.recipient||"",department:c.department||"",priority:c.priority||"normal",status:c.status||"received",notes:c.notes||"",assigned_to:c.assigned_to||"",assigned_to_name:c.assigned_to_name||"",due_date:c.due_date?c.due_date.slice(0,10):""});
    api.get(`/courrier/${c.id}/attachments`).then(r=>setAttachments(r.data||r||[])).catch(()=>setAttachments([]));
    if(!c.read_at){
      api.post(`/courrier/${c.id}/read`).then(()=>{
        setItems(prev=>prev.map(it=>it.id===c.id ? {...it, read_at: new Date().toISOString()} : it));
      }).catch(()=>{});
    }
  }

  const selected = items.find(c=>c.id===selectedId) || null;

  async function createCourrier(){
    if(!form.subject.trim()) return;
    try{
      await api.post("/courrier", form);
      toast(lang==="fr"?"Courrier enregistré":"Courrier registered","success");
      setShowCompose(false);
      setForm({type:"incoming",subject:"",sender:"",recipient:"",department:"",priority:"normal",notes:"",due_date:"",assigned_to:"",assigned_to_name:""});
      triggerRefresh();
    } catch(e){ toast(e.message,"error"); }
  }
  async function saveDetail(patch){
    if(!selected) return;
    try{
      await api.put(`/courrier/${selected.id}`, patch);
      setItems(prev=>prev.map(it=>it.id===selected.id ? {...it, ...patch} : it));
    } catch(e){ toast(e.message,"error"); }
  }
  async function setStatus(s){
    if(!selected) return;
    await saveDetail({status:s});
    setEditForm(f=>({...f, status:s}));
    toast(lang==="fr"?"Statut mis à jour":"Status updated","success");
    triggerRefresh();
  }
  async function reassignTo(userId){
    if(!selected) return;
    const u = users.find(u=>String(u.id)===String(userId));
    await saveDetail({assigned_to:userId, assigned_to_name:u?.name||""});
    setEditForm(f=>({...f, assigned_to:userId, assigned_to_name:u?.name||""}));
    toast(lang==="fr"?"Réassigné":"Reassigned","success");
    setReassignOpen(false);
    triggerRefresh();
  }
  function openReplyCompose(initialNote=""){
    if(!selected) return;
    setForm({
      type:"outgoing",
      subject: selected.subject ? (lang==="fr"?"Rép : ":"Re: ")+selected.subject : "",
      sender:"",
      recipient: selected.sender || selected.recipient || "",
      department: selected.department || "",
      priority: selected.priority || "normal",
      notes: initialNote,
      due_date:"",
      assigned_to:"",
      assigned_to_name:"",
    });
    setShowCompose(true);
  }
  async function uploadAttachment(files){
    if(!files?.length || !selected) return;
    setUploading(true);
    try{
      for(const file of files){ const fd=new FormData(); fd.append("file",file); await api.upload(`/courrier/${selected.id}/attachments`, fd); }
      toast(lang==="fr"?`${files.length} fichier(s) attaché(s)`:`${files.length} file(s) attached`,"success");
      api.get(`/courrier/${selected.id}/attachments`).then(r=>setAttachments(r.data||r||[]));
    } catch(e){ toast(e.message,"error"); }
    setUploading(false);
  }
  function submitInlineReply(){
    if(!selected || !replyDraft.trim()) return;
    openReplyCompose(replyDraft);
    setReplyDraft("");
  }

  // Inline style helpers — declared in render so they pick up THEME at evaluation time
  const pillBtn = {...S.btn,padding:"7px 12px",fontSize:12,background:"transparent",color:THEME.textMuted,border:`1px solid ${THEME.border}`};
  const iconBtn = {...S.btn,padding:"5px 7px",fontSize:12,background:"transparent",color:THEME.textMuted,border:`1px solid ${THEME.border}`};
  const popover = {position:"absolute",top:"100%",left:0,marginTop:4,background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:8,padding:6,minWidth:200,maxHeight:280,overflowY:"auto",zIndex:10,boxShadow:"0 8px 24px rgba(0,0,0,0.4)"};
  const popoverItem = {display:"flex",alignItems:"center",gap:8,width:"100%",border:"none",background:"transparent",padding:"6px 8px",cursor:"pointer",borderRadius:6,fontSize:12,color:THEME.text,fontFamily:FONT};
  const lbl = {display:"block",fontSize:11,color:THEME.textDim,textTransform:"uppercase",marginBottom:4};
  const kbd = {fontFamily:FONT_MONO,padding:"1px 5px",background:"rgba(255,255,255,0.05)",border:`1px solid ${THEME.border}`,borderRadius:3,fontSize:9,fontWeight:600,color:THEME.textMuted};

  // Palette
  const statusMeta = {
    received:   {color:THEME.info,    label:T(lang,"received")},
    processing: {color:THEME.warning, label:T(lang,"processing")},
    processed:  {color:THEME.success, label:T(lang,"processed")},
    sent:       {color:THEME.accent,  label:T(lang,"sent")},
    archived:   {color:THEME.textDim, label:T(lang,"archived")},
    draft:      {color:THEME.textDim, label:lang==="fr"?"Brouillon":"Draft"},
  };
  const priorityMeta = {
    urgent: {color:THEME.danger,  label:T(lang,"urgent")},
    high:   {color:THEME.warning, label:T(lang,"high")},
    normal: {color:THEME.info,    label:T(lang,"normal")},
    low:    {color:THEME.textDim, label:T(lang,"low")},
  };
  function StatusPill({status}){
    const m = statusMeta[status]||{color:THEME.textDim,label:status};
    return <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"2px 9px",borderRadius:20,fontSize:10,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,background:m.color+"1A",color:m.color}}><span style={{width:5,height:5,borderRadius:"50%",background:m.color}}/>{m.label}</span>;
  }
  function PriorityPill({priority}){
    const m = priorityMeta[priority]||priorityMeta.normal;
    return <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"2px 9px",borderRadius:20,fontSize:10,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,background:m.color+"1A",color:m.color}}><span style={{width:5,height:5,borderRadius:"50%",background:m.color}}/>{m.label}</span>;
  }
  function DirectionBadge({type, withArrow=true}){
    const isIn = type==="incoming";
    const color = isIn ? THEME.accent : THEME.info;
    return <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"2px 8px",borderRadius:4,fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:0.6,background:color+"20",color}}>{withArrow?(isIn?<lucide.ArrowDown size={9}/>:<lucide.ArrowUp size={9}/>):null}{isIn?"IN":"OUT"}</span>;
  }

  function fmtTime(iso){ if(!iso)return""; const d=new Date(iso); if(isNaN(d.getTime()))return""; return d.toLocaleTimeString(lang==="fr"?"fr-FR":"en-US",{hour:"2-digit",minute:"2-digit"}); }
  function fmtRelative(iso){
    if(!iso)return"—"; const d=new Date(iso); if(isNaN(d.getTime()))return"—";
    const now=new Date();
    if(d.toDateString()===now.toDateString())return (lang==="fr"?"Aujourd'hui · ":"Today · ")+fmtTime(iso);
    const y=new Date(now.getTime()-86400000);
    if(d.toDateString()===y.toDateString())return (lang==="fr"?"Hier · ":"Yesterday · ")+fmtTime(iso);
    return formatFullDate(iso,lang)+" · "+fmtTime(iso);
  }
  function fmtDue(iso){
    if(!iso)return""; const d=new Date(iso); if(isNaN(d.getTime()))return"";
    const now=new Date(); now.setHours(0,0,0,0);
    const due=new Date(d); due.setHours(0,0,0,0);
    const days=Math.round((due-now)/86400000);
    const datePart=d.toLocaleDateString(lang==="fr"?"fr-FR":"en-US",{month:"short",day:"numeric"});
    let rel;
    if(days<0)rel=lang==="fr"?`il y a ${-days} j`:`${-days}d ago`;
    else if(days===0)rel=lang==="fr"?"aujourd'hui":"today";
    else if(days===1)rel=lang==="fr"?"demain":"tomorrow";
    else rel=lang==="fr"?`dans ${days} j`:`in ${days} days`;
    return `${datePart} · ${rel}`;
  }

  // Visible (filter + sort)
  const visible = items
    .filter(c=>{
      if(filter.openOnly && !["received","processing","draft"].includes(c.status))return false;
      if(filter.urgentOnly && c.priority!=="urgent")return false;
      return true;
    })
    .sort((a,b)=>{
      const diff=new Date(b.created_at||0)-new Date(a.created_at||0);
      return sortOrder==="newest"?diff:-diff;
    });
  const activeFilters = (filter.type?1:0)+(filter.openOnly?1:0)+(filter.urgentOnly?1:0);

  function groupByDate(arr){
    const startOfDay=d=>{const x=new Date(d);x.setHours(0,0,0,0);return x.getTime();};
    const today=startOfDay(new Date()), yesterday=today-86400000;
    const groups={today:[],yesterday:[],earlier:[]};
    for(const c of arr){
      const t=startOfDay(c.created_at||0);
      if(t===today)groups.today.push(c);
      else if(t===yesterday)groups.yesterday.push(c);
      else groups.earlier.push(c);
    }
    return groups;
  }
  const groups = groupByDate(visible);

  // Keyboard shortcuts
  useEffect(()=>{
    function isFormField(t){const tag=t?.tagName?.toUpperCase();return tag==="INPUT"||tag==="TEXTAREA"||tag==="SELECT"||t?.isContentEditable;}
    function onKeyDown(e){
      if(showCompose)return;
      if(isFormField(e.target))return;
      const idx=visible.findIndex(c=>c.id===selectedId);
      if(e.key==="ArrowDown"){ e.preventDefault(); if(idx>=0 && idx<visible.length-1)selectFor(visible[idx+1]); else if(idx===-1 && visible.length)selectFor(visible[0]); }
      else if(e.key==="ArrowUp"){ e.preventDefault(); if(idx>0)selectFor(visible[idx-1]); }
      else if((e.key==="r"||e.key==="R") && selected){ e.preventDefault(); openReplyCompose(); }
      else if((e.key==="e"||e.key==="E") && selected && canEdit){ e.preventDefault(); setStatus("archived"); }
    }
    document.addEventListener("keydown", onKeyDown);
    return ()=>document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[visible, selectedId, selected, showCompose, canEdit]);

  const incomingFlow=["received","processing","processed","archived"];
  const outgoingFlow=["draft","sent","archived"];

  const filterChips=[
    {key:"all",label:T(lang,"all"),active:!filter.type && !filter.openOnly && !filter.urgentOnly,onClick:()=>setFilter({type:"",openOnly:false,urgentOnly:false})},
    {key:"in",label:lang==="fr"?"Entrant":"In",active:filter.type==="incoming",icon:<lucide.ArrowDown size={11}/>,onClick:()=>setFilter(f=>({...f,type:f.type==="incoming"?"":"incoming"}))},
    {key:"out",label:lang==="fr"?"Sortant":"Out",active:filter.type==="outgoing",icon:<lucide.ArrowUp size={11}/>,onClick:()=>setFilter(f=>({...f,type:f.type==="outgoing"?"":"outgoing"}))},
    {key:"open",label:lang==="fr"?"Ouvert":"Open",active:filter.openOnly,onClick:()=>setFilter(f=>({...f,openOnly:!f.openOnly}))},
    {key:"urgent",label:lang==="fr"?"Urgent":"Urgent",active:filter.urgentOnly,icon:<lucide.Flag size={11}/>,onClick:()=>setFilter(f=>({...f,urgentOnly:!f.urgentOnly}))},
  ];

  const counterText = lang==="fr" ? `Registre · ${visible.length} sur ${items.length}` : `Registre · ${visible.length} of ${items.length}`;

  return (
    <div>
      <PageHeader title={T(lang,"courrier")} subtitle={counterText} actions={
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>{load();toast(lang==="fr"?"Synchronisé":"Synced","success");}} style={{...S.btn,padding:"7px 14px",fontSize:12,background:THEME.surfaceRaised,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}><lucide.RefreshCw size={13}/>Sync</button>
          {canEdit && <button onClick={()=>{setForm({type:"incoming",subject:"",sender:"",recipient:"",department:"",priority:"normal",notes:"",due_date:"",assigned_to:"",assigned_to_name:""});setShowCompose(true);}} style={{...S.btn,background:THEME.accent,color:"#fff"}}><lucide.Plus size={16}/>{T(lang,"newCourrier")}</button>}
        </div>
      }/>

      <div style={{display:"flex",gap:16,height:"calc(100vh - 180px)",minHeight:420,maxHeight:"calc(100vh - 140px)"}}>
        {/* LEFT RAIL */}
        <div style={{width:320,flexShrink:0,...S.card,padding:0,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <div style={{padding:"12px 14px",borderBottom:`1px solid ${THEME.borderSubtle}`}}>
            <div style={{position:"relative"}}>
              <lucide.Search size={13} color={THEME.textDim} style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)"}}/>
              <input value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder={lang==="fr"?"Rechercher objet, expéditeur, référence…":"Search subject, sender, reference…"} style={{...S.input,paddingLeft:32,fontSize:12,height:34}}/>
            </div>
          </div>
          <div style={{padding:"10px 14px",display:"flex",gap:6,borderBottom:`1px solid ${THEME.borderSubtle}`,flexWrap:"wrap"}}>
            {filterChips.map(c=>(
              <button key={c.key} onClick={c.onClick} style={{...S.btn,padding:"5px 10px",fontSize:11,fontWeight:600,background:c.active?THEME.accentDim:"transparent",color:c.active?THEME.accent:THEME.textDim,border:`1px solid ${c.active?THEME.accent+"40":THEME.border}`,gap:5}}>{c.icon}{c.label}</button>
            ))}
          </div>
          {(activeFilters>0 || sortOrder!=="newest") && (
            <div style={{padding:"7px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1px solid ${THEME.borderSubtle}`,fontSize:11,color:THEME.textDim}}>
              <button onClick={()=>setFilter({type:"",openOnly:false,urgentOnly:false})} style={{border:"none",background:"transparent",cursor:"pointer",color:THEME.textDim,fontSize:11,padding:0,display:"flex",alignItems:"center",gap:5,fontFamily:FONT}}><lucide.Filter size={11}/>{activeFilters>0?(lang==="fr"?`${activeFilters} filtre(s) actif(s)`:`${activeFilters} filter${activeFilters>1?"s":""} applied`):""}</button>
              <button onClick={()=>setSortOrder(o=>o==="newest"?"oldest":"newest")} style={{border:"none",background:"transparent",cursor:"pointer",color:THEME.textDim,fontSize:11,padding:0,display:"flex",alignItems:"center",gap:5,fontFamily:FONT}}><lucide.ArrowDownUp size={11}/>{sortOrder==="newest"?(lang==="fr"?"Plus récent":"Newest first"):(lang==="fr"?"Plus ancien":"Oldest first")}</button>
            </div>
          )}
          <div style={{flex:1,overflowY:"auto"}}>
            {visible.length===0 ? (
              <div style={{padding:40,textAlign:"center",color:THEME.textDim,fontSize:12}}>{lang==="fr"?"Aucun courrier":"No courrier"}</div>
            ) : ([
              {label:lang==="fr"?"Aujourd'hui":"Today",items:groups.today},
              {label:lang==="fr"?"Hier":"Yesterday",items:groups.yesterday},
              {label:lang==="fr"?"Plus ancien":"Earlier",items:groups.earlier},
            ].map(g=>g.items.length===0?null:(
              <div key={g.label}>
                <div style={{padding:"10px 14px 6px",fontSize:10,fontWeight:700,letterSpacing:1.2,color:THEME.textDim,textTransform:"uppercase"}}>{g.label}</div>
                {g.items.map(c=>{
                  const active=c.id===selectedId;
                  const refTail=(c.reference||"").split("-").pop()||"—";
                  const snippet=c.notes?c.notes.replace(/\s+/g," ").slice(0,90)+(c.notes.length>90?"…":""):"";
                  const hasFlag=c.priority==="high"||c.priority==="urgent";
                  return (
                    <button key={c.id} onClick={()=>selectFor(c)} style={{display:"block",width:"100%",textAlign:"left",border:"none",background:active?THEME.surfaceHover:"transparent",borderLeft:`3px solid ${active?THEME.accent:"transparent"}`,padding:"10px 14px",cursor:"pointer",borderBottom:`1px solid ${THEME.borderSubtle}`,fontFamily:FONT}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                        <DirectionBadge type={c.type}/>
                        <span style={{fontSize:10,fontFamily:FONT_MONO,color:THEME.textDim,letterSpacing:0.4}}>{refTail}</span>
                        {hasFlag && <lucide.Flag size={10} color={c.priority==="urgent"?THEME.danger:THEME.warning}/>}
                        {!c.read_at && <span style={{width:6,height:6,borderRadius:"50%",background:THEME.accent}} title={lang==="fr"?"Non lu":"Unread"}/>}
                        <span style={{marginLeft:"auto",fontSize:10,color:THEME.textDim}}>{fmtTime(c.created_at)}</span>
                      </div>
                      <div style={{fontSize:13,fontWeight:!c.read_at?700:600,color:active?THEME.text:(!c.read_at?THEME.text:THEME.textMuted),marginBottom:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.subject||"—"}</div>
                      {snippet && <div style={{fontSize:11,color:THEME.textDim,marginBottom:5,lineHeight:1.45,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{snippet}</div>}
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6}}>
                        <span style={{fontSize:11,color:THEME.textDim,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",flex:1,display:"inline-flex",alignItems:"center",gap:6}}>
                          <span style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{(c.type==="incoming"?c.sender:c.recipient)||"—"}{c.department?` · ${c.department}`:""}</span>
                          {c.attachment_count>0 && <span style={{display:"inline-flex",alignItems:"center",gap:3,padding:"0 5px",borderRadius:8,fontSize:10,color:THEME.textDim,background:THEME.surfaceRaised}}><lucide.Paperclip size={9}/>{c.attachment_count}</span>}
                        </span>
                        <StatusPill status={c.status}/>
                      </div>
                    </button>
                  );
                })}
              </div>
            )))}
          </div>
          <div style={{padding:"8px 14px",borderTop:`1px solid ${THEME.borderSubtle}`,display:"flex",gap:14,fontSize:10,color:THEME.textDim,background:THEME.surfaceRaised}}>
            <span style={{display:"inline-flex",alignItems:"center",gap:4}}><kbd style={kbd}>↑↓</kbd>{lang==="fr"?"Naviguer":"Navigate"}</span>
            <span style={{display:"inline-flex",alignItems:"center",gap:4}}><kbd style={kbd}>R</kbd>{lang==="fr"?"Répondre":"Reply"}</span>
            <span style={{display:"inline-flex",alignItems:"center",gap:4}}><kbd style={kbd}>E</kbd>{lang==="fr"?"Archiver":"Archive"}</span>
          </div>
        </div>

        {/* RIGHT PANE */}
        <div style={{flex:1,minWidth:0,...S.card,padding:0,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          {!selected ? (
            <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:THEME.textDim,fontSize:13}}>{lang==="fr"?"Sélectionnez un courrier":"Select a courrier"}</div>
          ) : (() => {
            const idx = visible.findIndex(c=>c.id===selectedId);
            const prevC = idx>0 ? visible[idx-1] : null;
            const nextC = (idx>=0 && idx<visible.length-1) ? visible[idx+1] : null;
            const flow = selected.type==="outgoing" ? outgoingFlow : incomingFlow;
            const currentStageIdx = flow.indexOf(editForm.status||selected.status);
            return (
              <>
                <div style={{padding:"10px 24px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:11,fontFamily:FONT_MONO,color:THEME.textMuted,letterSpacing:0.3,display:"inline-flex",alignItems:"center",gap:6}}><lucide.Hash size={11} color={THEME.textDim}/>{selected.reference||"—"}</span>
                  {!selected.read_at && <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"2px 8px",borderRadius:4,fontSize:9,fontWeight:700,letterSpacing:0.6,textTransform:"uppercase",background:THEME.accent+"24",color:THEME.accent}}><span style={{width:6,height:6,borderRadius:"50%",background:THEME.accent}}/>{lang==="fr"?"Non lu":"Unread"}</span>}
                  <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:4}}>
                    <button disabled={!prevC} onClick={()=>prevC&&selectFor(prevC)} title={lang==="fr"?"Précédent":"Previous"} style={{...iconBtn,opacity:prevC?1:0.35,cursor:prevC?"pointer":"default"}}><lucide.ChevronLeft size={14}/></button>
                    <button disabled={!nextC} onClick={()=>nextC&&selectFor(nextC)} title={lang==="fr"?"Suivant":"Next"} style={{...iconBtn,opacity:nextC?1:0.35,cursor:nextC?"pointer":"default"}}><lucide.ChevronRight size={14}/></button>
                  </div>
                </div>

                <div style={{padding:"14px 24px 16px",borderBottom:`1px solid ${THEME.borderSubtle}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
                    <DirectionBadge type={selected.type}/>
                    <PriorityPill priority={selected.priority||"normal"}/>
                    <StatusPill status={editForm.status||selected.status}/>
                  </div>
                  <div style={{fontSize:20,fontWeight:700,color:THEME.text,marginBottom:10,lineHeight:1.3}}>{selected.subject||"—"}</div>
                  <div style={{display:"flex",alignItems:"center",gap:18,flexWrap:"wrap",fontSize:11,color:THEME.textDim}}>
                    <span><span style={{textTransform:"uppercase",letterSpacing:0.8,marginRight:6}}>{selected.type==="incoming"?(lang==="fr"?"De":"From"):(lang==="fr"?"À":"To")}</span><span style={{color:THEME.text,fontWeight:600}}>{(selected.type==="incoming"?selected.sender:selected.recipient)||"—"}</span></span>
                    <span style={{display:"flex",alignItems:"center",gap:6}}><span style={{textTransform:"uppercase",letterSpacing:0.8}}>{lang==="fr"?"Assigné":"Assigned"}</span>{selected.assigned_to_name ? <><Avatar name={selected.assigned_to_name} size={20}/><span style={{color:THEME.text,fontWeight:600}}>{selected.assigned_to_name}</span>{selected.department && <span style={{color:THEME.textDim}}>· {selected.department}</span>}</> : <span style={{color:THEME.textDim}}>—</span>}</span>
                    <span><span style={{textTransform:"uppercase",letterSpacing:0.8,marginRight:6}}>{lang==="fr"?"Reçu":"Received"}</span><span style={{color:THEME.text,fontWeight:600}}>{fmtRelative(selected.created_at)}</span></span>
                    {selected.due_date && <span><span style={{textTransform:"uppercase",letterSpacing:0.8,marginRight:6}}>{lang==="fr"?"Échéance":"Due"}</span><span style={{color:THEME.text,fontWeight:600}}>{fmtDue(selected.due_date)}</span></span>}
                  </div>
                </div>

                <div style={{padding:"12px 24px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <button onClick={()=>openReplyCompose()} disabled={!canEdit} style={{...S.btn,background:THEME.accent,color:"#fff",fontSize:12,padding:"7px 14px"}}><lucide.CornerUpLeft size={13}/>{lang==="fr"?"Répondre":"Reply"}</button>
                  <button onClick={()=>setStatus("processed")} disabled={!canEdit||(editForm.status||selected.status)==="processed"} style={pillBtn}><lucide.Check size={13}/>{lang==="fr"?"Marquer traité":"Mark processed"}</button>
                  <div style={{position:"relative"}}>
                    <button onClick={()=>setReassignOpen(o=>!o)} disabled={!canEdit} style={pillBtn}><lucide.UserPlus size={13}/>{lang==="fr"?"Réassigner":"Reassign"}</button>
                    {reassignOpen && <div style={popover}>
                      {users.length===0 ? <div style={{padding:8,fontSize:11,color:THEME.textDim}}>{lang==="fr"?"Aucun utilisateur":"No users"}</div> : users.map(u=>(
                        <button key={u.id} onClick={()=>reassignTo(u.id)} style={popoverItem} onMouseEnter={e=>e.currentTarget.style.background=THEME.surfaceHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <Avatar name={u.name} size={20}/><span>{u.name}</span>
                        </button>
                      ))}
                    </div>}
                  </div>
                  <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
                    <button onClick={()=>setStatus("archived")} disabled={!canEdit||(editForm.status||selected.status)==="archived"} style={pillBtn}><lucide.Archive size={13}/>{lang==="fr"?"Archiver":"Archive"}</button>
                    <button
                      onClick={async()=>{
                        if(!confirm(lang==="fr"?`Supprimer définitivement ce courrier (${selected.reference}) et toutes ses pièces jointes ?`:`Permanently delete this courrier (${selected.reference}) and all its attachments?`))return;
                        try{await api.del(`/courrier/${selected.id}`);toast(lang==="fr"?"Courrier supprimé":"Courrier deleted","success");selectFor(null);triggerRefresh();}
                        catch(e){toast(e?.message||(lang==="fr"?"Suppression impossible":"Delete failed"),"error");}
                      }}
                      disabled={!canEdit}
                      style={{...S.btn,padding:"7px 14px",fontSize:12,background:THEME.dangerDim,color:THEME.danger,border:`1px solid ${THEME.danger}40`,opacity:canEdit?1:0.5}}>
                      <lucide.Trash2 size={13}/>{lang==="fr"?"Supprimer":"Delete"}
                    </button>
                  </div>
                </div>

                <div style={{padding:"12px 24px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"center",gap:0}}>
                  {flow.map((s,i)=>{
                    const meta=statusMeta[s]||{color:THEME.textDim,label:s};
                    const isActive=i<=currentStageIdx;
                    const isCurrent=i===currentStageIdx;
                    return (
                      <div key={s} style={{display:"flex",alignItems:"center",flex:1}}>
                        <button onClick={()=>canEdit&&setStatus(s)} disabled={!canEdit} style={{display:"flex",alignItems:"center",gap:8,border:"none",background:"transparent",cursor:canEdit?"pointer":"default",padding:0,fontFamily:FONT,color:isCurrent?meta.color:isActive?THEME.text:THEME.textDim,fontSize:11,fontWeight:isCurrent?700:500,letterSpacing:0.5,textTransform:"uppercase"}}>
                          <span style={{width:18,height:18,borderRadius:"50%",background:isActive?meta.color+"33":"transparent",border:`1.5px solid ${isActive?meta.color:THEME.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:isActive?meta.color:THEME.textDim}}>{i+1}</span>
                          {meta.label}
                        </button>
                        {i<flow.length-1 && <div style={{flex:1,height:1,background:i<currentStageIdx?statusMeta[flow[i]]?.color:THEME.borderSubtle,margin:"0 10px"}}/>}
                      </div>
                    );
                  })}
                </div>

                <div style={{flex:1,overflowY:"auto",padding:24}}>
                  {/* LETTER CARD */}
                  <div style={{...S.card,padding:0,marginBottom:18,overflow:"hidden"}}>
                    <div style={{padding:"16px 18px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"flex-start",gap:12,background:THEME.surface}}>
                      <Avatar name={(selected.type==="incoming"?selected.sender:selected.recipient)||"?"} size={36}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:14,fontWeight:600,color:THEME.text}}>{(selected.type==="incoming"?selected.sender:selected.recipient)||"—"}</div>
                        <div style={{fontSize:11,color:THEME.textDim,marginTop:2}}>{selected.type==="incoming"?(lang==="fr"?"Expéditeur":"Sender"):(lang==="fr"?"Destinataire":"Recipient")}</div>
                      </div>
                      <div style={{textAlign:"right",fontSize:10,color:THEME.textDim}}>
                        <div style={{textTransform:"uppercase",letterSpacing:1}}>{lang==="fr"?"Référence":"Reference"}</div>
                        <div style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.textMuted,marginTop:2}}>{selected.reference||"—"}</div>
                        <div style={{marginTop:3}}>{fmtRelative(selected.created_at)}</div>
                      </div>
                    </div>
                    <div style={{padding:"20px 22px",fontSize:13,lineHeight:1.7,color:THEME.text,whiteSpace:"pre-wrap"}}>
                      {canEdit ? (
                        <textarea
                          value={editForm.notes||""}
                          onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))}
                          onBlur={()=>{if(editForm.notes!==(selected.notes||""))saveDetail({notes:editForm.notes});}}
                          rows={Math.max(5,Math.min(20,(editForm.notes||"").split("\n").length+1))}
                          placeholder={lang==="fr"?"Ajouter le contenu du courrier…":"Add the courrier body…"}
                          style={{...S.input,resize:"vertical",fontFamily:FONT,fontSize:13,lineHeight:1.7,background:"transparent",border:editForm.notes?"none":`1px dashed ${THEME.border}`,padding:editForm.notes?0:12,width:"100%"}}
                        />
                      ) : (
                        selected.notes || <span style={{color:THEME.textDim,fontStyle:"italic"}}>{lang==="fr"?"Aucun contenu":"No body"}</span>
                      )}
                    </div>
                  </div>

                  {/* ATTACHMENTS */}
                  <div style={{marginBottom:18}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                      <div style={{fontSize:10,fontWeight:700,letterSpacing:1.2,color:THEME.textDim,textTransform:"uppercase"}}>{lang==="fr"?"Pièces jointes":"Attachments"} · {attachments.length}</div>
                      {canEdit && <button onClick={()=>fileInputRef.current?.click()} disabled={uploading} style={{...S.btn,padding:"4px 10px",fontSize:11,background:THEME.accentDim,color:THEME.accent,border:`1px solid ${THEME.accent}30`}}>{uploading?<Spinner size={11}/>:<lucide.Paperclip size={11}/>}{lang==="fr"?"Joindre":"Attach"}</button>}
                      <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.doc,.xls" style={{display:"none"}} onChange={e=>uploadAttachment(e.target.files)}/>
                    </div>
                    {attachments.length===0 ? (
                      <div onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor=THEME.accent;}} onDragLeave={e=>{e.currentTarget.style.borderColor=THEME.border;}} onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor=THEME.border;uploadAttachment(e.dataTransfer.files);}} style={{border:`2px dashed ${THEME.border}`,borderRadius:8,padding:22,textAlign:"center",color:THEME.textDim,fontSize:12}}>
                        <lucide.FileUp size={20} color={THEME.textDim} style={{display:"block",margin:"0 auto 6px"}}/>{lang==="fr"?"Glissez des fichiers ici ou cliquez Joindre":"Drag files here or click Attach"}
                      </div>
                    ) : (
                      <div onDragOver={e=>e.preventDefault()} onDrop={e=>{e.preventDefault();uploadAttachment(e.dataTransfer.files);}} style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10}}>
                        {attachments.map((a,i)=>{
                          const isPdf=a.mime_type?.includes("pdf");
                          const isImage=a.mime_type?.startsWith("image/");
                          const attName=a.name||a.original_name||`Attachment ${i+1}`;
                          const ext=(attName.split(".").pop()||"").toUpperCase().slice(0,4);
                          const iconColor=isPdf?THEME.danger:isImage?THEME.info:THEME.accent;
                          // Preview: fetch the signed URL as a blob, then render
                          // via blob: URL in an iframe/img. This sidesteps every
                          // response-header framing block (X-Frame-Options,
                          // CSP frame-ancestors, COOP/CORP) because blob: URLs
                          // are same-origin to the SPA and explicitly allowed by
                          // the SPA's own `frame-src 'self' blob:` directive.
                          // Standard pattern used by iManage, Dropbox, etc.
                          //
                          // For DOCX/XLSX: hit the *-html endpoint (same token,
                          // different path segment) for server-converted HTML.
                          const isOffice=(()=>{const e=(attName||"").split(".").pop()?.toLowerCase();return ["docx","docm","xlsx","xlsm","csv"].includes(e);})();
                          const onPreview=async()=>{
                            try{
                              const signed=await api.signedUrl("attachment",a.id,{purpose:"preview"});
                              if(!signed)throw new Error("No URL");
                              const fetchUrl=isOffice
                                ? signed.replace("/api/downloads/attachment/","/api/downloads/attachment-html/")
                                : signed;
                              const r=await fetch(fetchUrl,{credentials:"same-origin"});
                              if(!r.ok)throw new Error(`Fetch failed (${r.status})`);
                              const blob=await r.blob();
                              const blobUrl=URL.createObjectURL(blob);
                              setPreviewAtt({url:blobUrl,name:attName,mime:a.mime_type||blob.type||"",isPdf,isImage,isOffice,isBlob:true});
                            }catch(e){
                              toast(e?.message||"Preview unavailable","error");
                            }
                          };
                          const onDownload=async()=>{try{const url=await api.signedUrl("attachment",a.id,{purpose:"download"});if(!url)throw new Error("No URL");const link=document.createElement("a");link.href=url;link.download=attName;document.body.appendChild(link);link.click();document.body.removeChild(link);}catch(e){toast(e?.message||"Download failed","error");}};
                          return (
                            <div key={a.id||i} style={{padding:10,background:THEME.surface,border:`1px solid ${THEME.borderSubtle}`,borderRadius:8,display:"flex",alignItems:"center",gap:10}}>
                              <div style={{width:34,height:38,borderRadius:5,background:iconColor+"18",display:"flex",alignItems:"center",justifyContent:"center",color:iconColor,fontSize:9,fontWeight:700,letterSpacing:0.4,flexShrink:0}}>{ext||(isImage?"IMG":"FILE")}</div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:12,fontWeight:600,color:THEME.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{attName}</div>
                                <div style={{fontSize:10,color:THEME.textDim,marginTop:2}}>{isPdf?"PDF":isImage?(a.mime_type?.split("/")[1]||"image").toUpperCase():"FILE"} · {a.size?formatBytes(a.size):"—"}</div>
                              </div>
                              <button onClick={onPreview} title={lang==="fr"?"Aperçu":"Preview"} style={{border:"none",background:"transparent",cursor:"pointer",padding:4,color:THEME.textDim}}><lucide.Eye size={13}/></button>
                              <button onClick={onDownload} title={lang==="fr"?"Télécharger":"Download"} style={{border:"none",background:"transparent",cursor:"pointer",padding:4,color:THEME.textDim}}><lucide.Download size={13}/></button>
                              {canEdit&&<button
                                onClick={async()=>{
                                  if(!confirm(lang==="fr"?`Supprimer la pièce jointe "${attName}" ?`:`Delete attachment "${attName}"?`))return;
                                  try{
                                    await api.del(`/courrier/${selected.id}/attachments/${a.id}`);
                                    toast(lang==="fr"?"Pièce jointe supprimée":"Attachment deleted","success");
                                    const r=await api.get(`/courrier/${selected.id}/attachments`);
                                    setAttachments(r?.data||r||[]);
                                    triggerRefresh();
                                  }catch(e){toast(e?.message||(lang==="fr"?"Suppression impossible":"Delete failed"),"error");}
                                }}
                                title={lang==="fr"?"Supprimer":"Delete"}
                                style={{border:"none",background:"transparent",cursor:"pointer",padding:4,color:THEME.textDim}}
                                onMouseEnter={e=>e.currentTarget.style.color=THEME.danger}
                                onMouseLeave={e=>e.currentTarget.style.color=THEME.textDim}><lucide.Trash2 size={13}/></button>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                </div>

                {/* Inline reply composer */}
                {canEdit && (
                  <div style={{padding:"10px 18px",borderTop:`1px solid ${THEME.borderSubtle}`,background:THEME.surface,display:"flex",alignItems:"center",gap:10}}>
                    <button onClick={()=>fileInputRef.current?.click()} title={lang==="fr"?"Joindre":"Attach"} style={{border:"none",background:"transparent",cursor:"pointer",color:THEME.textDim,padding:6}}><lucide.Paperclip size={14}/></button>
                    <input
                      value={replyDraft}
                      onChange={e=>setReplyDraft(e.target.value)}
                      onKeyDown={e=>{if(e.key==="Enter" && !e.shiftKey){e.preventDefault();submitInlineReply();}}}
                      placeholder={`${lang==="fr"?"Répondre à":"Reply to"} ${selected.sender||selected.recipient||"—"}…`}
                      style={{...S.input,fontSize:12,flex:1,background:"transparent",border:"none"}}
                    />
                    <button onClick={submitInlineReply} disabled={!replyDraft.trim()} style={{...S.btn,padding:"6px 12px",fontSize:11,background:replyDraft.trim()?THEME.accent:THEME.surfaceRaised,color:replyDraft.trim()?"#fff":THEME.textDim,opacity:replyDraft.trim()?1:0.6}}><lucide.Send size={12}/>{lang==="fr"?"Envoyer":"Send"}</button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* ATTACHMENT PREVIEW MODAL — in-app render so the browser's "always-
          download-PDFs" setting can't override Content-Disposition: inline */}
      <AnimatePresence>{previewAtt && (
        <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2100,background:"rgba(0,0,0,0.78)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)setPreviewAtt(null);}}>
          <motion.div initial={{opacity:0,scale:0.96}} animate={{opacity:1,scale:1}} exit={{opacity:0,scale:0.96}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:12,width:"min(1100px, 96vw)",height:"min(86vh, 900px)",display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <div style={{padding:"12px 18px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
              <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                <lucide.Eye size={14} color={THEME.accent}/>
                <span style={{fontSize:13,fontWeight:600,color:THEME.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{previewAtt.name}</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                <a href={previewAtt.url} target="_blank" rel="noopener noreferrer" title={lang==="fr"?"Ouvrir dans un nouvel onglet":"Open in new tab"} style={{...iconBtn,textDecoration:"none",display:"inline-flex",alignItems:"center",justifyContent:"center"}}><lucide.ExternalLink size={13}/></a>
                <button onClick={()=>setPreviewAtt(null)} title={lang==="fr"?"Fermer":"Close"} style={iconBtn}><lucide.X size={13}/></button>
              </div>
            </div>
            <div style={{flex:1,minHeight:0,background:"#000",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {previewAtt.isPdf || previewAtt.isOffice ? (
                <iframe src={previewAtt.url} title={previewAtt.name} style={{width:"100%",height:"100%",border:"none",background:"#fff"}}/>
              ) : previewAtt.isImage ? (
                <img src={previewAtt.url} alt={previewAtt.name} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
              ) : (
                <div style={{textAlign:"center",color:THEME.textDim,padding:32}}>
                  <lucide.FileQuestion size={48} color={THEME.textDim} strokeWidth={1} style={{display:"block",margin:"0 auto 12px"}}/>
                  <div style={{fontSize:14,color:THEME.textMuted,marginBottom:8}}>{lang==="fr"?"Aperçu non disponible pour ce type de fichier":"Preview not available for this file type"}</div>
                  <a href={previewAtt.url} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:THEME.accent,textDecoration:"none"}}>{lang==="fr"?"Ouvrir dans un nouvel onglet":"Open in new tab"} →</a>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>

      {/* NEW COURRIER MODAL — Sprint 12: department + assigned-to are now dropdowns */}
      <AnimatePresence>{showCompose && (
        <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.65)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)setShowCompose(false);}}>
          <motion.div initial={{opacity:0,scale:0.92,y:16}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.92}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:600,maxHeight:"85vh",overflow:"auto"}}>
            <div style={{padding:"18px 24px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:15,fontWeight:600,color:THEME.text}}>{T(lang,"newCourrier")}</div>
              <lucide.X size={18} color={THEME.textDim} style={{cursor:"pointer"}} onClick={()=>setShowCompose(false)}/>
            </div>
            <div style={{padding:"20px 24px"}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div><label style={lbl}>{T(lang,"type")}</label><select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))} style={S.input}><option value="incoming">{T(lang,"incoming")}</option><option value="outgoing">{T(lang,"outgoing")}</option></select></div>
                <div><label style={lbl}>{T(lang,"priority")}</label><select value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))} style={S.input}><option value="low">{T(lang,"low")}</option><option value="normal">{T(lang,"normal")}</option><option value="high">{T(lang,"high")}</option><option value="urgent">{T(lang,"urgent")}</option></select></div>
                <div style={{gridColumn:"span 2"}}><label style={lbl}>{T(lang,"subject")}</label><input value={form.subject} onChange={e=>setForm(f=>({...f,subject:e.target.value}))} style={S.input}/></div>
                <div><label style={lbl}>{T(lang,"sender")}</label><input value={form.sender} onChange={e=>setForm(f=>({...f,sender:e.target.value}))} style={S.input}/></div>
                <div><label style={lbl}>{T(lang,"recipient")}</label><input value={form.recipient} onChange={e=>setForm(f=>({...f,recipient:e.target.value}))} style={S.input}/></div>
                <div>
                  <label style={lbl}>{T(lang,"department")}</label>
                  <select value={form.department} onChange={e=>setForm(f=>({...f,department:e.target.value}))} style={S.input}>
                    <option value="">{lang==="fr"?"— Sélectionner —":"— Select —"}</option>
                    {departments.map(d=>{const label=lang==="fr"?(d.name_fr||d.name):(d.name_en||d.name);return <option key={d.id} value={d.name}>{label}</option>;})}
                  </select>
                </div>
                <div>
                  <label style={lbl}>{lang==="fr"?"Assigné à":"Assigned to"}</label>
                  <select value={form.assigned_to} onChange={e=>{const u=users.find(u=>String(u.id)===e.target.value);setForm(f=>({...f,assigned_to:e.target.value,assigned_to_name:u?.name||""}));}} style={S.input}>
                    <option value="">{lang==="fr"?"— Personne —":"— Nobody —"}</option>
                    {users.map(u=>(<option key={u.id} value={u.id}>{u.name}</option>))}
                  </select>
                </div>
                <div><label style={lbl}>{T(lang,"dueDate")}</label><input type="date" value={form.due_date} onChange={e=>setForm(f=>({...f,due_date:e.target.value}))} style={S.input}/></div>
                <div style={{gridColumn:"span 2"}}><label style={lbl}>{T(lang,"notes")}</label><textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={3} style={{...S.input,resize:"vertical"}}/></div>
              </div>
              <div style={{display:"flex",gap:10,marginTop:18,justifyContent:"flex-end"}}>
                <button onClick={()=>setShowCompose(false)} style={{...S.btn,padding:"9px 18px",fontSize:13,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}>{T(lang,"cancel")}</button>
                <button onClick={createCourrier} style={{...S.btn,padding:"9px 20px",fontSize:13,background:THEME.accent,color:"#fff"}}><lucide.Check size={14}/>{T(lang,"save")}</button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>
    </div>
  );
}

// ─── REPORTS PAGE — Sprint 9B: AI-Powered Reports ───
// Sprint 10C post-F: Reports are admin-only. The AI summary alone
// leaks folder names, user activity, storage stats, and doc-type
// breakdowns — content that should never land in a non-admin's view.
// Wrapper gate: checks auth BEFORE the heavy component mounts, so no
// /api/reports/* fetches fire for non-admins (they'd 403 now anyway,
// but silence is cleaner than noise).
function ReportsPage(){
  const {user}=useApp();
  if(!isAdminOrSuper(user))return <AccessDeniedPage requiredRole="admin"/>;
  return <ReportsPageImpl/>;
}

function ReportsPageImpl(){
  const {lang}=useLang();const {isAdmin}=useApp();
  const [summary,setSummary]=useState(null);const [docsPerFolder,setDocsPerFolder]=useState([]);
  const [docTypes,setDocTypes]=useState([]);const [activeUsers,setActiveUsers]=useState([]);
  const [uploadActivity,setUploadActivity]=useState([]);
  const [loading,setLoading]=useState(true);const [lastUpdated,setLastUpdated]=useState(null);
  // Sprint 10D1-patch: backend now builds the AI context server-side,
  // so the frontend just sends `purpose` + optional `question`. No more
  // stuffing raw data into prompts from the client.
  const [aiSummary,setAiSummary]=useState("");const [aiInsights,setAiInsights]=useState([]);
  const [aiLoading,setAiLoading]=useState(false);const [aiError,setAiError]=useState(null);
  const [nlQuery,setNlQuery]=useState("");const [nlResult,setNlResult]=useState(null);const [nlLoading,setNlLoading]=useState(false);
  // Tabs keep the page focused instead of one giant scroll.
  const [view,setView]=useState("overview"); // 'overview' | 'storage' | 'users' | 'activity'

  function loadAll(){
    setLoading(true);
    Promise.all([
      api.get("/reports/summary").then(setSummary).catch(()=>setSummary(null)),
      api.get("/reports/docs-per-folder").then(r=>setDocsPerFolder(r.data||[])).catch(()=>{}),
      api.get("/reports/doc-types").then(r=>setDocTypes(r.data||[])).catch(()=>{}),
      api.get("/reports/active-users").then(r=>setActiveUsers(r.data||[])).catch(()=>{}),
      api.get("/reports/upload-activity").then(r=>setUploadActivity(r.data||[])).catch(()=>{}),
    ]).finally(()=>{setLoading(false);setLastUpdated(new Date());});
  }
  useEffect(()=>{loadAll();generateAiInsights();},[]);// eslint-disable-line react-hooks/exhaustive-deps

  async function generateAiInsights(){
    setAiLoading(true);setAiError(null);
    try{
      const res=await api.post("/reports/ai-query",{purpose:"summary",lang});
      setAiSummary(res.summary||"");setAiInsights(Array.isArray(res.insights)?res.insights:[]);
    }catch(e){
      console.error("AI insights error:",e);
      setAiError(e?.message||(lang==="fr"?"Erreur lors de l'analyse AI":"AI analysis error"));
    }
    setAiLoading(false);
  }

  async function handleNlQuery(e){
    e?.preventDefault();if(!nlQuery.trim()||nlLoading)return;
    setNlLoading(true);setNlResult(null);
    try{
      const res=await api.post("/reports/ai-query",{purpose:"question",question:nlQuery,lang});
      setNlResult({answer:res.answer||(lang==="fr"?"Aucune réponse.":"No answer.")});
    }catch(e){
      setNlResult({answer:e?.answer||e?.message||(lang==="fr"?"Erreur lors du traitement de la requête.":"Error processing query.")});
    }
    setNlLoading(false);
  }

  // ── Human-friendly formatters ──
  function fmtSize(bytes){
    if(!bytes||bytes<=0)return "0 B";
    const units=["B","KB","MB","GB","TB"];let i=0,v=Number(bytes);
    while(v>=1024&&i<units.length-1){v/=1024;i++;}
    return `${v.toFixed(v<10?2:v<100?1:0)} ${units[i]}`;
  }
  function fmtPct(n){if(!isFinite(n))return "—";return (n>0?"+":"")+n.toFixed(0)+"%";}

  // ── KPIs with week-over-week delta ──
  const uploadDelta=summary?(()=>{
    const cur=summary.uploads_week||0;const prev=summary.uploads_prev_week||0;
    if(prev===0)return cur>0?100:0;
    return ((cur-prev)/prev)*100;
  })():0;

  const kpis=summary?[
    {label:lang==="fr"?"Documents":"Documents",value:(summary.documents||0).toLocaleString(),sub:fmtSize(summary.total_size),icon:lucide.FileText,color:THEME.accent},
    {label:lang==="fr"?"Dossiers":"Folders",value:summary.folders||0,sub:`${summary.top_level_folders||0} ${lang==="fr"?"racine":"top-level"}`,icon:lucide.FolderOpen,color:THEME.info},
    {label:lang==="fr"?"Utilisateurs actifs":"Active Users",value:summary.users||0,sub:summary.users_total?`${lang==="fr"?"sur":"of"} ${summary.users_total}`:null,icon:lucide.Users,color:THEME.success},
    {label:lang==="fr"?"Cette semaine":"This Week",value:summary.uploads_week||0,sub:null,trend:uploadDelta,icon:lucide.TrendingUp,color:THEME.warning},
    {label:lang==="fr"?"Réservations actives":"Active Checkouts",value:summary.active_checkouts||0,sub:lang==="fr"?"en cours":"in progress",icon:lucide.Lock,color:"#9B6AD4"},
  ]:[];

  const maxDocs=Math.max(...docsPerFolder.map(d=>d.doc_count),1);
  const maxType=Math.max(...docTypes.map(d=>d.count),1);
  const totalDocsForType=docTypes.reduce((s,d)=>s+(d.count||0),0)||1;
  const typeColors=[THEME.accent,THEME.info,THEME.success,THEME.warning,THEME.danger,"#9B6AD4"];
  const suggestedQueries=lang==="fr"?[
    "Quels dossiers ont le plus d'activité cette semaine ?",
    "Qui sont les utilisateurs les moins actifs ?",
    "Quelle est la tendance de stockage sur 30 jours ?",
    "Y a-t-il des réservations bloquées depuis trop longtemps ?",
  ]:[
    "Which folders have the most activity this week?",
    "Who are the least active users?",
    "What's the storage trend over the last 30 days?",
    "Are any checkouts stale?",
  ];

  // ── Client-side CSV export (admin only) ──
  function exportCsv(){
    const lines=[];
    lines.push("# KlassDoc — Reports Export");
    lines.push(`# Generated: ${new Date().toISOString()}`);
    lines.push("");
    lines.push("## Summary");
    lines.push("metric,value");
    if(summary){Object.entries(summary).forEach(([k,v])=>lines.push(`${k},${v}`));}
    lines.push("");
    lines.push("## Documents per folder");
    lines.push("folder,doc_count,total_size_bytes");
    docsPerFolder.forEach(f=>lines.push(`${JSON.stringify(f.name)},${f.doc_count},${f.total_size}`));
    lines.push("");
    lines.push("## Document types");
    lines.push("type,count,total_size_bytes");
    docTypes.forEach(t=>lines.push(`${t.type||"other"},${t.count},${t.total_size}`));
    lines.push("");
    lines.push("## Active users");
    lines.push("name,email,doc_count,total_size_bytes,last_upload");
    activeUsers.forEach(u=>lines.push(`${JSON.stringify(u.name)},${u.email||""},${u.doc_count},${u.total_size},${u.last_upload||""}`));
    const blob=new Blob(["\uFEFF"+lines.join("\n")],{type:"text/csv;charset=utf-8"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");a.href=url;a.download=`klassdoc_reports_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);a.click();a.remove();URL.revokeObjectURL(url);
  }

  const tabs=[
    {id:"overview",label:lang==="fr"?"Vue d'ensemble":"Overview",icon:lucide.LayoutDashboard},
    {id:"storage",label:lang==="fr"?"Stockage":"Storage",icon:lucide.HardDrive},
    {id:"users",label:lang==="fr"?"Utilisateurs":"Users",icon:lucide.Users},
    {id:"activity",label:lang==="fr"?"Activité":"Activity",icon:lucide.TrendingUp},
  ];

  return(
    <div>
      {/* Header + action bar */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,gap:16,flexWrap:"wrap"}}>
        <div>
          <h1 style={{fontSize:24,fontWeight:700,color:THEME.text,margin:0}}>{T(lang,"reports")}</h1>
          <p style={{fontSize:13,color:THEME.textDim,margin:"4px 0 0 0"}}>
            {lang==="fr"?"Analyse de vos documents et de l'activité de votre organisation":"Analytics for your documents and organization activity"}
            {lastUpdated&&<span style={{marginLeft:8,opacity:0.7}}>· {lang==="fr"?"Mis à jour":"Updated"} {lastUpdated.toLocaleTimeString(lang==="fr"?"fr-FR":"en-US",{hour:"2-digit",minute:"2-digit"})}</span>}
          </p>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={loadAll} disabled={loading} style={{...S.btn,padding:"8px 14px",fontSize:12,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`,opacity:loading?0.6:1}}>
            <lucide.RefreshCw size={13} style={loading?{animation:"spin 1s linear infinite"}:{}}/>
            {lang==="fr"?"Actualiser":"Refresh"}
          </button>
          {isAdmin&&<button onClick={exportCsv} style={{...S.btn,padding:"8px 14px",fontSize:12,background:THEME.accentDim,color:THEME.accent,border:`1px solid ${THEME.accent}40`}}>
            <lucide.Download size={13}/>{lang==="fr"?"Exporter CSV":"Export CSV"}
          </button>}
        </div>
      </div>

      {/* ── AI HERO — question up front, auto-summary underneath ── */}
      <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} style={{...S.card,marginBottom:20,background:`linear-gradient(135deg,${THEME.surfaceRaised} 0%,${THEME.surface} 100%)`,border:`1px solid ${THEME.accent}30`,position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-40,right:-40,width:180,height:180,background:`radial-gradient(circle at 50% 50%,${THEME.accent}15 0%,transparent 60%)`,pointerEvents:"none"}}/>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,position:"relative"}}>
          <div style={{width:34,height:34,borderRadius:9,background:`linear-gradient(135deg,${THEME.accent},${THEME.accentHover})`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 4px 12px ${THEME.accent}30`}}>
            <lucide.Sparkles size={17} color="#fff"/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:15,fontWeight:700,color:THEME.text}}>{lang==="fr"?"Assistant AI KlassDoc":"KlassDoc AI Assistant"}</div>
            <div style={{fontSize:11,color:THEME.textDim,marginTop:1}}>{lang==="fr"?"Posez une question sur votre organisation, ou consultez le résumé automatique":"Ask a question about your organization, or see the auto-summary below"}</div>
          </div>
        </div>

        <form onSubmit={handleNlQuery} style={{display:"flex",gap:10,marginBottom:12,position:"relative"}}>
          <input value={nlQuery} onChange={e=>setNlQuery(e.target.value)} placeholder={lang==="fr"?"Ex: Quels dossiers ont le plus d'activité cette semaine ?":"e.g. Which folders have the most activity this week?"} style={{...S.input,flex:1,background:THEME.surface,padding:"12px 16px",fontSize:14}}/>
          <button type="submit" disabled={nlLoading||!nlQuery.trim()} style={{...S.btn,background:`linear-gradient(135deg,${THEME.accent},${THEME.accentHover})`,color:"#fff",padding:"10px 22px",fontSize:13,opacity:(nlLoading||!nlQuery.trim())?0.6:1,boxShadow:`0 4px 12px ${THEME.accent}30`}}>
            {nlLoading?<Spinner size={14}/>:<><lucide.Send size={14}/>{lang==="fr"?"Demander":"Ask"}</>}
          </button>
        </form>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:nlResult?14:0,position:"relative"}}>
          {suggestedQueries.map((q,i)=>(
            <button key={i} onClick={()=>setNlQuery(q)} style={{padding:"5px 11px",borderRadius:20,border:`1px solid ${THEME.border}`,background:THEME.surface,color:THEME.textDim,fontSize:11,cursor:"pointer",fontFamily:FONT,transition:"all 0.15s"}} onMouseEnter={e=>{e.currentTarget.style.borderColor=THEME.accent+"60";e.currentTarget.style.color=THEME.accent;}} onMouseLeave={e=>{e.currentTarget.style.borderColor=THEME.border;e.currentTarget.style.color=THEME.textDim;}}>
              {q}
            </button>
          ))}
        </div>
        <AnimatePresence>{nlResult&&<motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{overflow:"hidden",position:"relative"}}>
          <div style={{padding:16,background:THEME.surface,borderRadius:10,border:`1px solid ${THEME.accent}30`,marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              <lucide.Bot size={15} color={THEME.accent}/>
              <span style={{fontSize:12,fontWeight:700,color:THEME.accent,letterSpacing:0.5}}>{lang==="fr"?"RÉPONSE":"ANSWER"}</span>
            </div>
            <div style={{fontSize:13,color:THEME.text,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{nlResult.answer}</div>
          </div>
        </motion.div>}</AnimatePresence>

        <div style={{borderTop:`1px solid ${THEME.borderSubtle}`,paddingTop:14,position:"relative"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <lucide.Activity size={13} color={THEME.textMuted}/>
            <span style={{fontSize:11,fontWeight:700,color:THEME.textMuted,letterSpacing:0.5,textTransform:"uppercase"}}>{lang==="fr"?"Résumé automatique":"Auto summary"}</span>
            <button onClick={generateAiInsights} disabled={aiLoading} style={{marginLeft:"auto",background:"transparent",border:"none",color:THEME.textDim,fontSize:11,cursor:aiLoading?"wait":"pointer",display:"flex",alignItems:"center",gap:4,padding:4,fontFamily:FONT}} onMouseEnter={e=>!aiLoading&&(e.currentTarget.style.color=THEME.accent)} onMouseLeave={e=>e.currentTarget.style.color=THEME.textDim}>
              <lucide.RefreshCw size={11} style={aiLoading?{animation:"spin 1s linear infinite"}:{}}/>
              {lang==="fr"?"Régénérer":"Regenerate"}
            </button>
          </div>
          {aiLoading?<div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0"}}><Spinner size={14}/><span style={{fontSize:12,color:THEME.textMuted}}>{lang==="fr"?"Analyse en cours...":"Analyzing..."}</span></div>
          :aiError?<div style={{fontSize:12,color:THEME.danger,padding:"10px 0",display:"flex",alignItems:"flex-start",gap:8}}>
            <lucide.AlertCircle size={14} style={{flexShrink:0,marginTop:1}}/>
            <span>{aiError}<span style={{display:"block",fontSize:11,color:THEME.textDim,marginTop:4}}>{lang==="fr"?"Si l'erreur persiste, vérifiez que ANTHROPIC_API_KEY est défini dans .env puis redémarrez le serveur.":"If the error persists, ensure ANTHROPIC_API_KEY is set in .env and restart the server."}</span></span>
          </div>
          :aiSummary?<div>
            <p style={{fontSize:13,color:THEME.text,lineHeight:1.65,margin:"0 0 12px 0"}}>{aiSummary}</p>
            {aiInsights.length>0&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:8}}>
              {aiInsights.map((insight,i)=>(
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"8px 12px",borderRadius:8,background:THEME.surface,border:`1px solid ${THEME.borderSubtle}`}}>
                  <lucide.Zap size={13} color={THEME.accent} style={{marginTop:2,flexShrink:0}}/>
                  <span style={{fontSize:12,color:THEME.textMuted,lineHeight:1.5}}>{insight}</span>
                </div>
              ))}
            </div>}
          </div>
          :<div style={{fontSize:12,color:THEME.textDim,padding:"10px 0"}}>{lang==="fr"?"Aucune analyse disponible.":"No analysis available."}</div>}
        </div>
      </motion.div>

      {/* ── KPI STRIP with trend indicators ── */}
      {summary&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:14,marginBottom:24}}>
        {kpis.map((k,i)=>(<motion.div key={k.label} initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:i*0.05}} style={{...S.card,padding:"16px 18px"}}>
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10}}>
            <div style={{width:38,height:38,borderRadius:10,background:k.color+"18",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <k.icon size={18} color={k.color} strokeWidth={2}/>
            </div>
            {k.trend!=null&&isFinite(k.trend)&&k.trend!==0&&(
              <div style={{display:"flex",alignItems:"center",gap:3,padding:"2px 7px",borderRadius:6,background:k.trend>0?THEME.successDim:THEME.dangerDim,color:k.trend>0?THEME.success:THEME.danger,fontSize:10,fontWeight:700}}>
                {k.trend>0?<lucide.ArrowUpRight size={11}/>:<lucide.ArrowDownRight size={11}/>}
                {fmtPct(k.trend)}
              </div>
            )}
          </div>
          <div style={{fontSize:24,fontWeight:700,color:THEME.text,lineHeight:1}}>{k.value}</div>
          <div style={{fontSize:12,color:THEME.textDim,marginTop:4}}>{k.label}</div>
          {k.sub&&<div style={{fontSize:11,color:THEME.textMuted,marginTop:3}}>{k.sub}</div>}
        </motion.div>))}
      </div>}

      {/* ── VIEW TABS ── */}
      <div style={{display:"flex",gap:4,marginBottom:18,borderBottom:`1px solid ${THEME.border}`}}>
        {tabs.map(t=>{const active=view===t.id;return(
          <button key={t.id} onClick={()=>setView(t.id)} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 18px",background:active?THEME.surfaceRaised:"transparent",border:"none",borderBottom:active?`2px solid ${THEME.accent}`:"2px solid transparent",color:active?THEME.accent:THEME.textDim,fontSize:13,fontWeight:active?600:500,cursor:"pointer",fontFamily:FONT,marginBottom:-1}}>
            <t.icon size={14}/>{t.label}
          </button>
        );})}
      </div>

      {/* ── OVERVIEW ── */}
      {view==="overview"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
          <div style={S.card}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
              <h3 style={{fontSize:14,fontWeight:600,color:THEME.text,margin:0,display:"flex",alignItems:"center",gap:8}}><lucide.FolderOpen size={16} color={THEME.accent}/>{lang==="fr"?"Dossiers les plus actifs":"Most active folders"}</h3>
              <span style={{fontSize:11,color:THEME.textDim}}>Top 8</span>
            </div>
            {docsPerFolder.length===0?<div style={{color:THEME.textDim,fontSize:12,padding:"20px 0",textAlign:"center"}}>{lang==="fr"?"Aucune donnée":"No data"}</div>
            :docsPerFolder.slice(0,8).map((d,i)=>(<div key={d.id||i} style={{marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                <span style={{fontSize:13,color:THEME.text,fontWeight:500}}>{d.name}</span>
                <span style={{fontSize:12,color:THEME.textMuted}}>{d.doc_count} · {fmtSize(d.total_size)}</span>
              </div>
              <div style={{height:6,borderRadius:3,background:THEME.borderSubtle,overflow:"hidden"}}>
                <motion.div initial={{width:0}} animate={{width:`${(d.doc_count/maxDocs)*100}%`}} transition={{delay:i*0.05,duration:0.5,ease:"easeOut"}} style={{height:"100%",background:`linear-gradient(90deg,${THEME.accent},${THEME.accentHover})`}}/>
              </div>
            </div>))}
          </div>

          <div style={S.card}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
              <h3 style={{fontSize:14,fontWeight:600,color:THEME.text,margin:0,display:"flex",alignItems:"center",gap:8}}><lucide.PieChart size={16} color={THEME.info}/>{lang==="fr"?"Types de documents":"Document types"}</h3>
              <span style={{fontSize:11,color:THEME.textDim}}>{totalDocsForType} {lang==="fr"?"au total":"total"}</span>
            </div>
            {docTypes.length===0?<div style={{color:THEME.textDim,fontSize:12,padding:"20px 0",textAlign:"center"}}>{lang==="fr"?"Aucune donnée":"No data"}</div>
            :docTypes.map((d,i)=>{const pct=((d.count/totalDocsForType)*100).toFixed(1);return(
              <div key={d.type||"other"} style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                <div style={{width:12,height:12,borderRadius:3,background:typeColors[i%typeColors.length],flexShrink:0}}/>
                <span style={{fontSize:13,color:THEME.text,fontWeight:500,flex:1}}>{(d.type||"other").toUpperCase()}</span>
                <span style={{fontSize:11,color:THEME.textDim,width:60,textAlign:"right"}}>{fmtSize(d.total_size)}</span>
                <div style={{width:90,height:6,borderRadius:3,background:THEME.borderSubtle,overflow:"hidden"}}>
                  <motion.div initial={{width:0}} animate={{width:`${(d.count/maxType)*100}%`}} transition={{delay:i*0.06,duration:0.5}} style={{height:"100%",background:typeColors[i%typeColors.length]}}/>
                </div>
                <span style={{fontSize:12,color:THEME.text,fontWeight:600,width:60,textAlign:"right"}}>{d.count} <span style={{color:THEME.textDim,fontWeight:400,fontSize:10}}>({pct}%)</span></span>
              </div>
            );})}
          </div>

          <div style={{...S.card,gridColumn:"1 / -1"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <h3 style={{fontSize:14,fontWeight:600,color:THEME.text,margin:0,display:"flex",alignItems:"center",gap:8}}><lucide.TrendingUp size={16} color={THEME.warning}/>{lang==="fr"?"Activité de téléversement":"Upload activity"}</h3>
              <span style={{fontSize:11,color:THEME.textDim}}>{lang==="fr"?"30 derniers jours":"Last 30 days"}</span>
            </div>
            {uploadActivity.length===0?<div style={{color:THEME.textDim,fontSize:12,padding:"40px 0",textAlign:"center"}}>{lang==="fr"?"Aucune donnée — téléversez un premier document pour voir l'activité.":"No data yet — upload a document to see activity."}</div>
            :(()=>{const data=uploadActivity.slice(-30);const maxU=Math.max(...data.map(d=>d.uploads),1);return(
              <div style={{display:"flex",alignItems:"flex-end",gap:3,height:140,paddingTop:8}}>
                {data.map((d,i)=>{const h=Math.max(6,(d.uploads/maxU)*110);return(
                  <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4,minWidth:0}} title={`${new Date(d.date).toLocaleDateString(lang==="fr"?"fr-FR":"en-US")}: ${d.uploads}`}>
                    <span style={{fontSize:9,color:THEME.textMuted}}>{d.uploads||""}</span>
                    <motion.div initial={{height:0}} animate={{height:h}} transition={{delay:i*0.02,duration:0.4}} style={{width:"100%",borderRadius:"3px 3px 0 0",background:`linear-gradient(180deg,${THEME.warning},${THEME.warning}AA)`,minHeight:4}}/>
                    {i%5===0&&<span style={{fontSize:8,color:THEME.textDim,whiteSpace:"nowrap"}}>{new Date(d.date).toLocaleDateString(lang==="fr"?"fr-FR":"en-US",{month:"short",day:"numeric"})}</span>}
                  </div>
                );})}
              </div>
            );})()}
          </div>
        </div>
      )}

      {/* ── STORAGE ── */}
      {view==="storage"&&(
        <div style={S.card}>
          <h3 style={{fontSize:14,fontWeight:600,color:THEME.text,marginBottom:16,display:"flex",alignItems:"center",gap:8}}><lucide.HardDrive size={16} color={THEME.accent}/>{lang==="fr"?"Répartition du stockage par dossier":"Storage distribution by folder"}</h3>
          {docsPerFolder.length===0?<div style={{color:THEME.textDim,fontSize:12,padding:"20px 0",textAlign:"center"}}>{lang==="fr"?"Aucune donnée":"No data"}</div>
          :(()=>{const maxSize=Math.max(...docsPerFolder.map(d=>Number(d.total_size)||0),1);return docsPerFolder.slice(0,20).map((d,i)=>(
            <div key={d.id||i} style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                <span style={{fontSize:13,color:THEME.text}}>{d.name}<span style={{color:THEME.textMuted,fontSize:11,marginLeft:6}}>· {d.doc_count} docs</span></span>
                <span style={{fontSize:12,color:THEME.accent,fontWeight:600}}>{fmtSize(d.total_size)}</span>
              </div>
              <div style={{height:5,borderRadius:3,background:THEME.borderSubtle}}>
                <motion.div initial={{width:0}} animate={{width:`${(Number(d.total_size)/maxSize)*100}%`}} transition={{delay:i*0.04}} style={{height:"100%",borderRadius:3,background:THEME.accent}}/>
              </div>
            </div>
          ));})()}
        </div>
      )}

      {/* ── USERS ── */}
      {view==="users"&&(
        <div style={S.card}>
          <h3 style={{fontSize:14,fontWeight:600,color:THEME.text,marginBottom:16,display:"flex",alignItems:"center",gap:8}}><lucide.Users size={16} color={THEME.success}/>{lang==="fr"?"Utilisateurs les plus actifs":"Most active users"}</h3>
          {activeUsers.length===0?<div style={{color:THEME.textDim,fontSize:12,padding:"20px 0",textAlign:"center"}}>{lang==="fr"?"Aucune donnée":"No data"}</div>
          :<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:10}}>
            {activeUsers.map(u=>(
              <div key={u.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:10,background:THEME.surface,border:`1px solid ${THEME.borderSubtle}`}}>
                <Avatar name={u.name} size={36}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:THEME.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.name}</div>
                  <div style={{fontSize:11,color:THEME.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.email}{u.role&&<span style={{marginLeft:6,padding:"1px 6px",borderRadius:4,background:THEME.accentDim,color:THEME.accent,fontSize:9,textTransform:"uppercase",letterSpacing:0.5}}>{u.role}</span>}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:16,fontWeight:700,color:THEME.accent}}>{u.doc_count||0}</div>
                  <div style={{fontSize:10,color:THEME.textDim}}>{fmtSize(u.total_size)}</div>
                </div>
              </div>
            ))}
          </div>}
        </div>
      )}

      {/* ── ACTIVITY ── */}
      {view==="activity"&&(
        <div style={S.card}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
            <h3 style={{fontSize:14,fontWeight:600,color:THEME.text,margin:0,display:"flex",alignItems:"center",gap:8}}><lucide.TrendingUp size={16} color={THEME.warning}/>{lang==="fr"?"Activité complète sur 90 jours":"Full 90-day activity"}</h3>
          </div>
          {uploadActivity.length===0?<div style={{color:THEME.textDim,fontSize:12,padding:"40px 0",textAlign:"center"}}>{lang==="fr"?"Aucune donnée":"No data"}</div>
          :(()=>{const maxU=Math.max(...uploadActivity.map(d=>d.uploads),1);return(
            <div style={{display:"flex",alignItems:"flex-end",gap:2,height:180,paddingTop:8}}>
              {uploadActivity.map((d,i)=>{const h=Math.max(4,(d.uploads/maxU)*160);return(
                <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",minWidth:0}} title={`${new Date(d.date).toLocaleDateString(lang==="fr"?"fr-FR":"en-US")}: ${d.uploads}`}>
                  <motion.div initial={{height:0}} animate={{height:h}} transition={{delay:i*0.01,duration:0.4}} style={{width:"100%",borderRadius:"2px 2px 0 0",background:`linear-gradient(180deg,${THEME.warning},${THEME.warning}AA)`,minHeight:3}}/>
                </div>
              );})}
            </div>
          );})()}
        </div>
      )}
    </div>
  );
}

// ─── USERS ───
// Sprint 10C Session C1 H5 fix: gated to admin OR super_admin. Non-admin
// users previously could browse the user list (mild PII) though they couldn't
// mutate it. Now they're refused entirely at the page level.
function UsersPage(){
  const {toast,isAdmin,refreshSignal,triggerRefresh,user}=useApp();const {lang}=useLang();
  const [users,setUsers]=useState([]);const [showInvite,setShowInvite]=useState(false);
  // Sprint 11.x: invite-flow polish — first + last name surface real
  // names in the user list, job_title + department feed the new
  // top-right user-pill subtitle and the team-list department column.
  const [inviteFirstName,setInviteFirstName]=useState("");
  const [inviteLastName,setInviteLastName]=useState("");
  const [inviteJobTitle,setInviteJobTitle]=useState("");
  const [inviteDepartmentId,setInviteDepartmentId]=useState("");
  const [departments,setDepartments]=useState([]);
  const [inviteEmail,setInviteEmail]=useState("");const [inviteRole,setInviteRole]=useState("viewer");
  const [inviting,setInviting]=useState(false);
  // Result of a successful invite — shows the generated temp password
  // so the admin can hand it over if the Resend email lands in spam.
  const [inviteResult,setInviteResult]=useState(null); // {user, initial_password, email_sent, email_error}
  const [editUserId,setEditUserId]=useState(null);
  const [editPerms,setEditPerms]=useState({add:false,save:false,modify:false,delete:false,view:true});
  // Sprint 11.1a: admins can edit profile fields (title/department/role/
  // status) for existing users, seeded from the row on startEdit().
  const [editProfile,setEditProfile]=useState({job_title:"",department_id:"",role:"viewer",status:"active"});
  const [confirmRemoveId,setConfirmRemoveId]=useState(null);

  const allowed=isAdminOrSuper(user);

  function loadUsers(){api.get("/users").then(r=>setUsers(r.data||[])).catch(()=>{});}
  useEffect(()=>{if(allowed){loadUsers();api.get("/departments").then(r=>setDepartments(r.data||[])).catch(()=>{});}},[refreshSignal,allowed]);

  if(!allowed)return <AccessDeniedPage requiredRole="admin"/>;

  // Sprint 11.x: invite now POSTs first_name + last_name + lang so the
  // welcome email arrives in the recipient's preferred language and the
  // user-list shows real names. The response includes the generated
  // temp password — we hold it in inviteResult to show in a one-shot
  // result panel as a fallback if email fails.
  async function sendInvite(){
    if(!inviteEmail.trim())return;
    setInviting(true);
    try{
      const res=await api.post("/users",{
        email:inviteEmail.trim().toLowerCase(),
        first_name:inviteFirstName.trim(),
        last_name:inviteLastName.trim(),
        job_title:inviteJobTitle.trim()||null,
        department_id:inviteDepartmentId||null,
        role:inviteRole,
        lang,
      });
      const reinvitedMsg = res?.reinvited
        ? (lang==="fr"?"Invitation renvoyée — nouveau mot de passe":"Invitation resent — new password")
        : (lang==="fr"?"Compte créé — e-mail non envoyé":"Account created — email not sent");
      toast(res?.email_sent?(`${res?.reinvited?(lang==="fr"?"Invitation renvoyée à":"Invitation resent to"):T(lang,"inviteSent")} ${inviteEmail}`):reinvitedMsg, res?.email_sent?"success":"warning");
      setInviteResult(res);
      setShowInvite(false);
      setInviteFirstName("");setInviteLastName("");setInviteJobTitle("");setInviteDepartmentId("");setInviteEmail("");setInviteRole("viewer");
      triggerRefresh();
    }catch(err){
      toast(err?.message||"Failed","error");
    }
    setInviting(false);
  }
  function startEdit(u){
    setEditUserId(u.id);
    setEditPerms(u.permissions||{add:false,save:false,modify:false,delete:false,view:true});
    setEditProfile({
      job_title:u.job_title||"",
      department_id:u.department_id?String(u.department_id):"",
      role:u.role||"viewer",
      status:u.status||"active",
    });
    setConfirmRemoveId(null);
  }
  // Save profile (role/status/title/department) AND permissions in one
  // click. Profile goes through PUT /users/:id, permissions through
  // PUT /users/:id/permissions. We do profile first so a failure there
  // leaves permissions untouched (transactional-ish; both endpoints are
  // idempotent).
  async function savePerms(id){
    try{
      await api.put(`/users/${id}`,{
        role:editProfile.role,
        status:editProfile.status,
        job_title:editProfile.job_title.trim()||null,
        department_id:editProfile.department_id||null,
      });
      await api.put(`/users/${id}/permissions`,{permissions:editPerms});
      toast(T(lang,"permsSaved"),"success");
      setEditUserId(null);
      triggerRefresh();
    }catch(err){toast(err.message,"error");}
  }
  async function removeUser(id){try{await api.del(`/users/${id}`);toast(T(lang,"userRemoved"),"success");setConfirmRemoveId(null);setEditUserId(null);triggerRefresh();}catch(err){toast(err.message,"error");}}

  const rc={admin:THEME.danger,editor:THEME.accent,viewer:THEME.info,pending:THEME.textDim};
  const pLabels=[{k:"add",l:T(lang,"addDocs"),i:lucide.Plus},{k:"save",l:T(lang,"saveDocs"),i:lucide.Save},{k:"modify",l:T(lang,"modifyDocs"),i:lucide.Pencil},{k:"delete",l:T(lang,"deleteDocs"),i:lucide.Trash2},{k:"view",l:T(lang,"viewOnly"),i:lucide.Eye}];

  return(
    <div>
      <PageHeader title={T(lang,"userMgmt")} subtitle={`${users.length} ${T(lang,"membersCount")}`} actions={isAdmin&&<button style={{...S.btn,background:THEME.accent,color:"#fff"}} onClick={()=>setShowInvite(true)}><lucide.UserPlus size={16}/> {T(lang,"inviteUser")}</button>}/>
      <AnimatePresence>{showInvite&&(
        <motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{overflow:"hidden",marginBottom:20}}>
          <div style={{...S.card,padding:20}}>
            <div style={{fontSize:14,fontWeight:600,color:THEME.text,marginBottom:14,letterSpacing:"-0.01em"}}>{lang==="fr"?"Inviter un nouveau membre":"Invite a new member"}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.4}}>{lang==="fr"?"Prénom":"First name"}</label>
                <input value={inviteFirstName} onChange={e=>setInviteFirstName(e.target.value)} placeholder={lang==="fr"?"Marie":"Marie"} style={S.input}/>
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.4}}>{lang==="fr"?"Nom":"Last name"}</label>
                <input value={inviteLastName} onChange={e=>setInviteLastName(e.target.value)} placeholder={lang==="fr"?"Mukendi":"Mukendi"} style={S.input}/>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12,marginBottom:12}}>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.4}}>{T(lang,"emailAddr")}</label>
                <input value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="colleague@company.com" style={S.input} onKeyDown={e=>e.key==="Enter"&&sendInvite()}/>
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.4}}>{T(lang,"role")}</label>
                <select value={inviteRole} onChange={e=>setInviteRole(e.target.value)} style={{...S.input,cursor:"pointer"}}>
                  <option value="viewer">{T(lang,"viewer")}</option>
                  <option value="editor">{T(lang,"editor")}</option>
                  <option value="admin">{T(lang,"admin")}</option>
                </select>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12,marginBottom:16}}>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.4}}>{lang==="fr"?"Fonction (optionnel)":"Job title (optional)"}</label>
                <input value={inviteJobTitle} onChange={e=>setInviteJobTitle(e.target.value)} placeholder={lang==="fr"?"ex. Analyste financier, Responsable RH":"e.g. Financial Analyst, HR Manager"} style={S.input}/>
                <div style={{fontSize:11,color:THEME.textDim,marginTop:4}}>{lang==="fr"?"Affiché sous le nom dans l'en-tête":"Shown under the name in the header"}</div>
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.4}}>{lang==="fr"?"Département (optionnel)":"Department (optional)"}</label>
                <select value={inviteDepartmentId} onChange={e=>setInviteDepartmentId(e.target.value)} style={{...S.input,cursor:"pointer"}}>
                  <option value="">{lang==="fr"?"— Aucun —":"— None —"}</option>
                  {departments.map(d=>(<option key={d.id} value={d.id}>{lang==="fr"?(d.name_fr||d.name):(d.name_en||d.name)}</option>))}
                </select>
              </div>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setShowInvite(false)} style={{...S.btn,background:THEME.surfaceHover,color:THEME.textMuted,height:42,padding:"0 18px"}}>{T(lang,"cancel")}</button>
              <button onClick={sendInvite} disabled={inviting||!inviteEmail.trim()} style={{...S.btn,background:THEME.accent,color:"#fff",height:42,padding:"0 18px",opacity:(inviting||!inviteEmail.trim())?0.5:1,fontWeight:600}}>{inviting?<Spinner size={14}/>:<><lucide.Send size={14}/>{T(lang,"sendInvite")}</>}</button>
            </div>
          </div>
        </motion.div>
      )}</AnimatePresence>

      {/* Sprint 11.x — One-shot invite result modal. Shows the temp
          password so the admin can copy it manually if email lands in
          spam. Closes via "I've shared the password" — once dismissed,
          the password is no longer retrievable from the UI. */}
      <AnimatePresence>
        {inviteResult && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}
            onClick={e=>{if(e.target===e.currentTarget)setInviteResult(null);}}>
            <motion.div initial={{opacity:0,scale:0.95,y:10}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.95}}
              style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:520,padding:28,boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18}}>
                <div style={{width:42,height:42,borderRadius:11,background:inviteResult.email_sent?THEME.successDim:THEME.warningDim,border:`1px solid ${inviteResult.email_sent?THEME.successLine:THEME.warning+"40"}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {inviteResult.email_sent?<lucide.MailCheck size={20} color={THEME.success}/>:<lucide.AlertTriangle size={20} color={THEME.warning}/>}
                </div>
                <div>
                  <div style={{fontSize:16,fontWeight:600,color:THEME.text}}>{lang==="fr"?"Compte créé":"Account created"}</div>
                  <div style={{fontSize:12,color:THEME.textDim,marginTop:2}}>{inviteResult.name||inviteResult.email}</div>
                </div>
              </div>
              {inviteResult.email_sent
                ? <div style={{padding:"12px 14px",background:THEME.successDim,border:`1px solid ${THEME.successLine}`,borderRadius:8,marginBottom:16,fontSize:12.5,color:THEME.ink2,lineHeight:1.5}}>
                    {lang==="fr"
                      ? <>L'e-mail d'invitation a été envoyé à <b style={{color:THEME.text}}>{inviteResult.email}</b>. Le mot de passe temporaire ci-dessous est inclus dans l'e-mail — gardez-le comme secours si l'utilisateur ne le reçoit pas.</>
                      : <>Welcome email sent to <b style={{color:THEME.text}}>{inviteResult.email}</b>. The temporary password below is included in the email — keep it as a backup in case the user doesn't receive it.</>}
                  </div>
                : <div style={{padding:"12px 14px",background:THEME.warningDim,border:`1px solid ${THEME.warning}40`,borderRadius:8,marginBottom:16,fontSize:12.5,color:THEME.ink2,lineHeight:1.5}}>
                    {lang==="fr"
                      ? <><b style={{color:THEME.warning}}>L'e-mail n'a pas pu être envoyé.</b> Partagez le mot de passe temporaire ci-dessous manuellement (SMS, WhatsApp, ou en personne). Vous pouvez le copier en cliquant.</>
                      : <><b style={{color:THEME.warning}}>The welcome email could not be sent.</b> Share the temporary password below manually (SMS, WhatsApp, in person). Click to copy.</>}
                  </div>}
              <div style={{padding:"14px 16px",background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:10,marginBottom:18}}>
                <div style={{fontSize:10.5,fontWeight:600,color:THEME.textDim,textTransform:"uppercase",letterSpacing:0.8,marginBottom:5,fontFamily:FONT_MONO}}>{lang==="fr"?"Mot de passe temporaire":"Temporary password"}</div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <code style={{flex:1,fontFamily:FONT_MONO,fontSize:16,fontWeight:600,color:THEME.accent2,padding:"8px 12px",background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,borderRadius:6,letterSpacing:"0.04em",userSelect:"all"}}>{inviteResult.initial_password}</code>
                  <button onClick={()=>{navigator.clipboard?.writeText(inviteResult.initial_password);toast(lang==="fr"?"Copié":"Copied","success");}} style={{...S.btn,padding:"9px 14px",fontSize:12,background:THEME.surfaceRaised,color:THEME.ink2,border:`1px solid ${THEME.line2}`,height:38}}><lucide.Copy size={14}/></button>
                </div>
                <div style={{fontSize:11,color:THEME.textDim,marginTop:8,lineHeight:1.5}}>{lang==="fr"?"L'utilisateur devra changer ce mot de passe à sa première connexion.":"The user will be required to change this password on first sign-in."}</div>
              </div>
              <div style={{display:"flex",justifyContent:"flex-end"}}>
                <button onClick={()=>setInviteResult(null)} style={{...S.btn,background:THEME.accent,color:"#fff",padding:"10px 22px",fontSize:13,fontWeight:600}}><lucide.Check size={14}/>{lang==="fr"?"J'ai partagé le mot de passe":"I've shared the password"}</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div style={{...S.card,padding:0,overflow:"hidden"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 180px 130px 130px 90px 100px 120px 50px",gap:12,padding:"12px 20px",background:THEME.surface,borderBottom:`1px solid ${THEME.border}`,fontSize:11,fontWeight:600,color:THEME.textDim,textTransform:"uppercase"}}><span>{T(lang,"user")}</span><span>{T(lang,"email")}</span><span>{lang==="fr"?"Poste":"Title"}</span><span>{lang==="fr"?"Département":"Department"}</span><span>{T(lang,"role")}</span><span>{T(lang,"status")}</span><span>{T(lang,"lastActive")}</span><span></span></div>
        {users.map((u,i)=>(<div key={u.id}>
          <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*0.04}} style={{display:"grid",gridTemplateColumns:"1fr 180px 130px 130px 90px 100px 120px 50px",gap:12,padding:"14px 20px",alignItems:"center",borderBottom:editUserId===u.id?"none":`1px solid ${THEME.borderSubtle}`,transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=THEME.surfaceHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <div style={{display:"flex",alignItems:"center",gap:10}}><Avatar name={u.name} size={32}/><span style={{fontSize:13,fontWeight:500,color:THEME.text}}>{u.name}</span></div>
            <span style={{fontSize:12,color:THEME.textMuted,fontFamily:FONT_MONO}}>{u.email}</span>
            <span style={{fontSize:12,color:u.job_title?THEME.textMuted:THEME.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.job_title||"—"}</span>
            <span style={{fontSize:12,color:u.department_name?THEME.textMuted:THEME.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.department_name||"—"}</span>
            <Badge color={rc[u.role]||THEME.textDim}>{u.role}</Badge>
            <div style={{display:"flex",alignItems:"center",gap:6}}><div style={{width:7,height:7,borderRadius:"50%",background:u.status==="active"?THEME.success:u.status==="pending"?THEME.warning:THEME.textDim}}/><span style={{fontSize:12,color:THEME.textMuted}}>{u.status?u.status.charAt(0).toUpperCase()+u.status.slice(1):u.status}</span></div>
            <span style={{fontSize:12,color:THEME.textDim}}>{formatDate(u.last_active,lang)}</span>
            <div>{isAdmin&&<button onClick={()=>editUserId===u.id?setEditUserId(null):startEdit(u)} style={{...S.btn,padding:"4px 8px",background:editUserId===u.id?THEME.accentDim:"transparent",color:editUserId===u.id?THEME.accent:THEME.textDim,border:"none"}}><lucide.Settings size={14}/></button>}</div>
          </motion.div>
          <AnimatePresence>{editUserId===u.id&&(<motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{overflow:"hidden"}}><div style={{padding:"16px 20px",background:THEME.surface,borderBottom:`1px solid ${THEME.borderSubtle}`}}>
            {/* Sprint 11.1a: profile fields — Title / Department / Role / Status */}
            <div style={{fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:12,textTransform:"uppercase"}}>{lang==="fr"?"Profil":"Profile"} — {u.name}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:18}}>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.4}}>{lang==="fr"?"Poste":"Title"}</label>
                <input value={editProfile.job_title} onChange={e=>setEditProfile(prev=>({...prev,job_title:e.target.value}))} placeholder={lang==="fr"?"ex. Analyste financier":"e.g. Financial Analyst"} style={S.input}/>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.4}}>{lang==="fr"?"Département":"Department"}</label>
                <select value={editProfile.department_id} onChange={e=>setEditProfile(prev=>({...prev,department_id:e.target.value}))} style={{...S.input,cursor:"pointer"}}>
                  <option value="">{lang==="fr"?"— Aucun —":"— None —"}</option>
                  {departments.map(d=>(<option key={d.id} value={d.id}>{lang==="fr"?(d.name_fr||d.name):(d.name_en||d.name)}</option>))}
                </select>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.4}}>{T(lang,"role")}</label>
                <select value={editProfile.role} onChange={e=>setEditProfile(prev=>({...prev,role:e.target.value}))} disabled={u.id===user?.id} style={{...S.input,cursor:u.id===user?.id?"not-allowed":"pointer",opacity:u.id===user?.id?0.55:1}}>
                  <option value="viewer">{T(lang,"viewer")}</option>
                  <option value="editor">{T(lang,"editor")}</option>
                  <option value="admin">{T(lang,"admin")}</option>
                </select>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.4}}>{T(lang,"status")}</label>
                <select value={editProfile.status} onChange={e=>setEditProfile(prev=>({...prev,status:e.target.value}))} disabled={u.id===user?.id} style={{...S.input,cursor:u.id===user?.id?"not-allowed":"pointer",opacity:u.id===user?.id?0.55:1}}>
                  <option value="active">{lang==="fr"?"Actif":"Active"}</option>
                  <option value="pending">{lang==="fr"?"En attente":"Pending"}</option>
                </select>
              </div>
            </div>
            <div style={{fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:12,textTransform:"uppercase"}}>{T(lang,"permissions")} — {u.name}</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14}}>{pLabels.map(p=>{const on=editPerms[p.k];const PI=p.i;return<button key={p.k} onClick={()=>setEditPerms(prev=>({...prev,[p.k]:!prev[p.k]}))} style={{...S.btn,padding:"7px 14px",fontSize:12,background:on?THEME.successDim:THEME.surfaceHover,color:on?THEME.success:THEME.textDim,border:`1px solid ${on?THEME.success+"40":THEME.border}`}}><PI size={13}/>{p.l}</button>;})}</div>
            {/* Sprint 8A: WebDAV toggle */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,padding:"10px 14px",background:THEME.surface,borderRadius:8,border:`1px solid ${THEME.borderSubtle}`}}>
              <lucide.Globe size={14} color={u.webdav_enabled!==false?THEME.info:THEME.textDim}/>
              <span style={{fontSize:12,color:THEME.text,flex:1}}>{lang==="fr"?"Accès WebDAV (édition Office)":"WebDAV Access (Office editing)"}</span>
              <button onClick={async()=>{try{await api.put(`/users/${u.id}`,{webdav_enabled:u.webdav_enabled===false?true:false});toast(lang==="fr"?"Accès WebDAV mis à jour":"WebDAV access updated","success");triggerRefresh();}catch(e){toast(e.message,"error");}}} style={{...S.btn,padding:"5px 14px",fontSize:11,background:u.webdav_enabled!==false?THEME.successDim:THEME.surfaceHover,color:u.webdav_enabled!==false?THEME.success:THEME.textDim,border:`1px solid ${u.webdav_enabled!==false?THEME.success+"40":THEME.border}`}}>{u.webdav_enabled!==false?(lang==="fr"?"Activé":"Enabled"):(lang==="fr"?"Désactivé":"Disabled")}</button>
            </div>
            <div style={{display:"flex",gap:10}}><button style={{...S.btn,background:THEME.accent,color:"#fff",padding:"8px 18px",fontSize:12}} onClick={()=>savePerms(u.id)}><lucide.Check size={14}/> {T(lang,"savePerms")}</button><button style={{...S.btn,background:THEME.dangerDim,color:THEME.danger,padding:"8px 18px",fontSize:12,border:`1px solid ${THEME.danger}30`}} onClick={()=>setConfirmRemoveId(confirmRemoveId===u.id?null:u.id)}><lucide.UserMinus size={14}/> {T(lang,"removeUser")}</button></div>
            <AnimatePresence>{confirmRemoveId===u.id&&(<motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{marginTop:12,overflow:"hidden"}}><div style={{padding:12,background:THEME.dangerDim,borderRadius:8,border:`1px solid ${THEME.danger}30`}}><div style={{fontSize:12,color:THEME.danger,fontWeight:600,marginBottom:8}}>{T(lang,"confirmRemove")}</div><div style={{display:"flex",gap:8}}><button style={{...S.btn,background:THEME.danger,color:"#fff",padding:"6px 14px",fontSize:12}} onClick={()=>removeUser(u.id)}>{T(lang,"yesRemove")}</button><button style={{...S.btn,background:THEME.surfaceHover,color:THEME.textMuted,padding:"6px 14px",fontSize:12}} onClick={()=>setConfirmRemoveId(null)}>{T(lang,"cancel")}</button></div></div></motion.div>)}</AnimatePresence>
          </div></motion.div>)}</AnimatePresence>
        </div>))}
      </div>
    </div>
  );
}

// ─── DEPARTMENTS — admin CRUD (Sprint 11.1) ───
// Admins manage the per-org Departments table that the upload-properties
// dropdown and the document-metadata Department field both read from.
// Disabled departments are soft-deleted (is_active=false) — existing
// documents keep their stored department name. Org-scoped via tenantGuard
// on every endpoint; the admin gate matches UsersPage (admin OR super).
function DepartmentsPage(){
  const {toast,isAdmin,refreshSignal,triggerRefresh,user}=useApp();const {lang}=useLang();
  const [rows,setRows]=useState([]);
  const [showAdd,setShowAdd]=useState(false);
  const [form,setForm]=useState({name:"",name_fr:"",name_en:"",description:""});
  const [busy,setBusy]=useState(false);
  const [editId,setEditId]=useState(null);
  const [editForm,setEditForm]=useState({name:"",name_fr:"",name_en:"",description:""});
  const [confirmDisableId,setConfirmDisableId]=useState(null);
  const [includeInactive,setIncludeInactive]=useState(false);

  const allowed=isAdminOrSuper(user);

  function load(){
    // Backend GET /api/departments only returns is_active=true. To surface
    // disabled departments for re-enable, we'd need a query param the
    // backend doesn't expose yet — for now the active toggle is decorative
    // and the list always shows the active set. Disabled rows simply vanish
    // until a future backend addition lets us list inactive ones too.
    api.get("/departments").then(r=>setRows(r.data||[])).catch(()=>{});
  }
  useEffect(()=>{if(allowed)load();},[refreshSignal,allowed]);
  if(!allowed)return <AccessDeniedPage requiredRole="admin"/>;

  async function add(){
    if(!form.name.trim()){toast(T(lang,"departmentName")+" — required","error");return;}
    setBusy(true);
    try{
      await api.post("/departments",{
        name:form.name.trim(),
        name_fr:form.name_fr.trim()||form.name.trim(),
        name_en:form.name_en.trim()||form.name.trim(),
        description:form.description.trim()||null,
      });
      toast(lang==="fr"?"Département ajouté":"Department added","success");
      setShowAdd(false);
      setForm({name:"",name_fr:"",name_en:"",description:""});
      triggerRefresh();
    }catch(err){toast(err?.message||"Add failed","error");}
    setBusy(false);
  }

  function startEdit(d){
    setEditId(d.id);
    setEditForm({name:d.name||"",name_fr:d.name_fr||"",name_en:d.name_en||"",description:d.description||""});
    setConfirmDisableId(null);
  }

  async function saveEdit(id){
    try{
      await api.put(`/departments/${id}`,{
        name:editForm.name.trim()||undefined,
        name_fr:editForm.name_fr.trim()||undefined,
        name_en:editForm.name_en.trim()||undefined,
        description:editForm.description.trim()||null,
      });
      toast(lang==="fr"?"Département mis à jour":"Department updated","success");
      setEditId(null);
      triggerRefresh();
    }catch(err){toast(err?.message||"Save failed","error");}
  }

  async function disable(id){
    try{
      await api.del(`/departments/${id}`);
      toast(lang==="fr"?"Département désactivé":"Department disabled","success");
      setConfirmDisableId(null);
      setEditId(null);
      triggerRefresh();
    }catch(err){toast(err?.message||"Disable failed","error");}
  }

  const inputStyle={...S.input,padding:"8px 12px",fontSize:13};
  const labelStyle={display:"block",fontSize:11,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase"};

  return(
    <div>
      <PageHeader
        title={T(lang,"departments")}
        subtitle={`${rows.length} ${lang==="fr"?"actifs":"active"}`}
        actions={isAdmin&&<button style={{...S.btn,background:THEME.accent,color:"#fff"}} onClick={()=>setShowAdd(true)}><lucide.Plus size={16}/> {T(lang,"addDepartment")}</button>}
      />

      <AnimatePresence>{showAdd&&(
        <motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{overflow:"hidden",marginBottom:20}}>
          <div style={{...S.card,display:"grid",gridTemplateColumns:"2fr 1fr 1fr 2fr auto auto",gap:12,alignItems:"flex-end"}}>
            <div><label style={labelStyle}>{T(lang,"departmentName")} *</label><input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Finance" style={inputStyle} autoFocus/></div>
            <div><label style={labelStyle}>FR</label><input value={form.name_fr} onChange={e=>setForm(f=>({...f,name_fr:e.target.value}))} placeholder={lang==="fr"?"Finance":"Finance"} style={inputStyle}/></div>
            <div><label style={labelStyle}>EN</label><input value={form.name_en} onChange={e=>setForm(f=>({...f,name_en:e.target.value}))} placeholder="Finance" style={inputStyle}/></div>
            <div><label style={labelStyle}>{T(lang,"departmentDescription")}</label><input value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} style={inputStyle}/></div>
            <button disabled={busy} style={{...S.btn,background:THEME.accent,color:"#fff",height:38,opacity:busy?0.6:1}} onClick={add}><lucide.Check size={14}/> {lang==="fr"?"Ajouter":"Add"}</button>
            <button style={{...S.btn,background:THEME.surfaceHover,color:THEME.textMuted,height:38}} onClick={()=>setShowAdd(false)}>{T(lang,"cancel")}</button>
          </div>
        </motion.div>
      )}</AnimatePresence>

      {rows.length===0?(
        <div style={{...S.card,padding:"40px 20px",textAlign:"center"}}>
          <div style={{width:48,height:48,borderRadius:"50%",background:THEME.surfaceHover,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}><lucide.Building2 size={20} color={THEME.textDim}/></div>
          <div style={{fontSize:13,color:THEME.textMuted,marginBottom:14}}>{T(lang,"noDepartments")}</div>
          {isAdmin&&<button style={{...S.btn,background:THEME.accent,color:"#fff"}} onClick={()=>setShowAdd(true)}><lucide.Plus size={16}/> {T(lang,"addDepartment")}</button>}
        </div>
      ):(
        <div style={{...S.card,padding:0,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 140px 140px 2fr 80px",gap:12,padding:"12px 20px",background:THEME.surface,borderBottom:`1px solid ${THEME.border}`,fontSize:11,fontWeight:600,color:THEME.textDim,textTransform:"uppercase"}}>
            <span>{T(lang,"departmentName")}</span>
            <span>FR</span>
            <span>EN</span>
            <span>{T(lang,"departmentDescription")}</span>
            <span></span>
          </div>
          {rows.map((d,i)=>(<div key={d.id}>
            <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*0.03}} style={{display:"grid",gridTemplateColumns:"1fr 140px 140px 2fr 80px",gap:12,padding:"14px 20px",alignItems:"center",borderBottom:editId===d.id?"none":`1px solid ${THEME.borderSubtle}`,transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=THEME.surfaceHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{display:"flex",alignItems:"center",gap:10}}><div style={{width:28,height:28,borderRadius:6,background:THEME.surfaceHover,display:"flex",alignItems:"center",justifyContent:"center"}}><lucide.Building2 size={14} color={THEME.accent}/></div><span style={{fontSize:13,fontWeight:500,color:THEME.text}}>{d.name}</span></div>
              <span style={{fontSize:12,color:THEME.textMuted}}>{d.name_fr||"—"}</span>
              <span style={{fontSize:12,color:THEME.textMuted}}>{d.name_en||"—"}</span>
              <span style={{fontSize:12,color:THEME.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.description||"—"}</span>
              <div>{isAdmin&&<button onClick={()=>editId===d.id?setEditId(null):startEdit(d)} style={{...S.btn,padding:"4px 8px",background:editId===d.id?THEME.accentDim:"transparent",color:editId===d.id?THEME.accent:THEME.textDim,border:"none"}}><lucide.Settings size={14}/></button>}</div>
            </motion.div>
            <AnimatePresence>{editId===d.id&&(
              <motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{overflow:"hidden"}}>
                <div style={{padding:"16px 20px",background:THEME.surface,borderBottom:`1px solid ${THEME.borderSubtle}`}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 2fr",gap:12,marginBottom:14}}>
                    <div><label style={labelStyle}>{T(lang,"departmentName")}</label><input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} style={inputStyle}/></div>
                    <div><label style={labelStyle}>FR</label><input value={editForm.name_fr} onChange={e=>setEditForm(f=>({...f,name_fr:e.target.value}))} style={inputStyle}/></div>
                    <div><label style={labelStyle}>EN</label><input value={editForm.name_en} onChange={e=>setEditForm(f=>({...f,name_en:e.target.value}))} style={inputStyle}/></div>
                    <div><label style={labelStyle}>{T(lang,"departmentDescription")}</label><input value={editForm.description} onChange={e=>setEditForm(f=>({...f,description:e.target.value}))} style={inputStyle}/></div>
                  </div>
                  <div style={{display:"flex",gap:10}}>
                    <button style={{...S.btn,background:THEME.accent,color:"#fff",padding:"8px 18px",fontSize:12}} onClick={()=>saveEdit(d.id)}><lucide.Check size={14}/> {lang==="fr"?"Enregistrer":"Save"}</button>
                    <button style={{...S.btn,background:THEME.dangerDim,color:THEME.danger,padding:"8px 18px",fontSize:12,border:`1px solid ${THEME.danger}30`}} onClick={()=>setConfirmDisableId(confirmDisableId===d.id?null:d.id)}><lucide.Trash2 size={14}/> {T(lang,"disableDepartment")}</button>
                  </div>
                  <AnimatePresence>{confirmDisableId===d.id&&(
                    <motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{marginTop:12,overflow:"hidden"}}>
                      <div style={{padding:12,background:THEME.dangerDim,borderRadius:8,border:`1px solid ${THEME.danger}30`}}>
                        <div style={{fontSize:12,color:THEME.danger,fontWeight:600,marginBottom:8}}>{T(lang,"confirmDisableDept")}</div>
                        <div style={{display:"flex",gap:8}}>
                          <button style={{...S.btn,background:THEME.danger,color:"#fff",padding:"6px 14px",fontSize:12}} onClick={()=>disable(d.id)}>{lang==="fr"?"Oui, désactiver":"Yes, disable"}</button>
                          <button style={{...S.btn,background:THEME.surfaceHover,color:THEME.textMuted,padding:"6px 14px",fontSize:12}} onClick={()=>setConfirmDisableId(null)}>{T(lang,"cancel")}</button>
                        </div>
                      </div>
                    </motion.div>
                  )}</AnimatePresence>
                </div>
              </motion.div>
            )}</AnimatePresence>
          </div>))}
        </div>
      )}
    </div>
  );
}

// ─── AUDIT LOG — Enhanced (Sprint 5 Backlog) ───
// Server-side CSV export, pagination, date-range + search filters
// Sprint 10C Session C1 H5 fix: gated to admin OR super_admin. Audit trails
// can expose who viewed/modified which documents and when — closed to
// non-admins per the H5 full-lockdown posture.
function AuditLogPage(){
  // Sprint 11.x audit log redesign — editorial layout matching the
  // mockup. Two-column grid (stream + side panels), 4 stat cards at
  // top, 24-hour heatmap, filter toolbar with search + date range +
  // category chips, day-grouped event stream with INLINE expandable
  // detail (replaces the previous right-side drawer modal).
  const {lang}=useLang();const {toast,refreshSignal,isAdmin,user}=useApp();
  const [logs,setLogs]=useState([]);const [total,setTotal]=useState(0);
  const [filter,setFilter]=useState("all");const [search,setSearch]=useState("");
  const [dateFrom,setDateFrom]=useState("");const [dateTo,setDateTo]=useState("");
  const [page,setPage]=useState(0);const PAGE_SIZE=25;
  const [exporting,setExporting]=useState(false);
  // Sprint 11.x: rows expand inline rather than opening a side drawer.
  const [expandedRowId,setExpandedRowId]=useState(null);
  // Sprint 11.x: doc lookup maps so audit-log target=bare-id,
  // "document:N", or raw filename all resolve to "#N · v{ver} ·
  // filename" instead of a naked ID/string. Fetched once on mount.
  const [docsById,setDocsById]=useState({});
  const [docsByName,setDocsByName]=useState({});
  useEffect(()=>{
    if(!isAdminOrSuper(user))return;
    api.get("/documents?limit=200&sort=updated_at").then(r=>{
      const byId={};const byName={};
      for(const d of (r?.data||[])){byId[d.id]=d;if(d.name)byName[d.name]=d;}
      setDocsById(byId);setDocsByName(byName);
    }).catch(()=>{});
  },[refreshSignal,user]);

  const allowed=isAdminOrSuper(user);

  const loadLogs=useCallback(()=>{
    if(!allowed)return;
    const params=new URLSearchParams();
    params.set("limit",PAGE_SIZE);params.set("offset",page*PAGE_SIZE);
    if(filter&&filter!=="all")params.set("action",filter);
    if(search.trim())params.set("q",search.trim());
    if(dateFrom)params.set("from",dateFrom);
    if(dateTo)params.set("to",dateTo+"T23:59:59");
    api.get(`/audit-log?${params.toString()}`).then(r=>{setLogs(r.data||[]);setTotal(r.total||r.data?.length||0);}).catch(()=>{});
  },[page,filter,search,dateFrom,dateTo,refreshSignal,allowed]);

  useEffect(()=>{loadLogs();},[loadLogs]);

  if(!allowed)return <AccessDeniedPage requiredRole="admin"/>;

  const totalPages=Math.max(1,Math.ceil(total/PAGE_SIZE));

  // ───── CSV export — server-side preferred, client-side fallback ─────
  function buildAndDownloadCsv(rows){
    const h=["ID","Action","User","Target","Timestamp","IP"];
    const r=rows.map(l=>[l.id,l.action,l.user||l.user_name||"",l.target||"",l.timestamp||l.created_at||"",l.ip||l.ip_address||""]);
    const csv=[h,...r].map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const b=new Blob(["\uFEFF"+csv],{type:"text/csv"});const u=URL.createObjectURL(b);
    const a=document.createElement("a");a.href=u;a.download=`klassdoc_audit_${new Date().toISOString().slice(0,10)}.csv`;a.click();URL.revokeObjectURL(u);
  }
  async function exportCsv(){
    if(exporting)return;setExporting(true);
    try{
      const params=new URLSearchParams();
      if(filter&&filter!=="all")params.set("action",filter);
      if(search.trim())params.set("q",search.trim());
      if(dateFrom)params.set("from",dateFrom);
      if(dateTo)params.set("to",dateTo+"T23:59:59");
      const response=await fetch(`/api/audit-log/export?${params.toString()}`,{
        headers:{"Authorization":`Bearer ${api.token}`}
      });
      if(!response.ok){
        toast(lang==="fr"?"Export serveur indisponible — export de la page courante":"Server export unavailable — exporting current page","warning");
        buildAndDownloadCsv(logs);setExporting(false);return;
      }
      const blob=await response.blob();
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");
      a.href=url;a.download=`klassdoc_audit_${new Date().toISOString().slice(0,10)}.csv`;a.click();URL.revokeObjectURL(url);
      toast(lang==="fr"?"Exporté avec succès":"Exported successfully","success");
    }catch(e){
      console.error("CSV export error:",e);
      toast(lang==="fr"?"Erreur d'export — export de la page courante":"Export error — exporting current page","warning");
      buildAndDownloadCsv(logs);
    }
    setExporting(false);
  }

  // ───── Client-side aggregations from the visible page ─────
  // (Real totals would come from server-aggregate endpoints in a later
  // sprint; for now we summarize what's loaded on the current page.)
  const actorMap=new Map();
  const ipMap=new Map();
  const categoryMap=new Map();
  for(const l of logs){
    const name=l.user||l.user_name||"—";
    actorMap.set(name,(actorMap.get(name)||0)+1);
    const ip=l.ip||l.ip_address||"—";
    if(ip!=="—")ipMap.set(ip,(ipMap.get(ip)||0)+1);
    const entity=(l.action||"").split(".")[0]||"other";
    categoryMap.set(entity,(categoryMap.get(entity)||0)+1);
  }
  const actors=[...actorMap.entries()].sort((a,b)=>b[1]-a[1]).slice(0,5);
  const topIps=[...ipMap.entries()].sort((a,b)=>b[1]-a[1]).slice(0,4);
  const categories=[...categoryMap.entries()].sort((a,b)=>b[1]-a[1]);
  const topAction=categories[0];

  // 24-hour event distribution from current page.
  const hours=new Array(24).fill(0);
  let maxHr=0;
  for(const l of logs){
    const t=new Date(l.timestamp||l.created_at);
    if(!isFinite(t))continue;
    const diffH=(Date.now()-t.getTime())/3600000;
    if(diffH<24){ hours[t.getHours()]+=1; if(hours[t.getHours()]>maxHr)maxHr=hours[t.getHours()]; }
  }
  const last24hCount=hours.reduce((a,b)=>a+b,0);

  // Category chip catalog. The first 4 mirror the mockup; the rest derive
  // from the data so customers see their actual action mix.
  const baseChips=[
    {k:"all",label:lang==="fr"?"Tous les événements":"All events",dot:THEME.muted2,activeDot:THEME.accent},
    {k:"download",label:lang==="fr"?"Aperçu":"Preview",dot:THEME.info,filter:"download.preview"},
    {k:"download.download",label:lang==="fr"?"Téléchargement":"Download",dot:THEME.accent,filter:"download.download"},
    {k:"document.check",label:lang==="fr"?"Check in / out":"Check in / out",dot:THEME.success,filter:"checkout"},
    {k:"document.replace",label:lang==="fr"?"Nouvelle version":"New version",dot:"#b07aff",filter:"replace"},
    {k:"folder",label:lang==="fr"?"Dossier":"Folder",dot:THEME.textDim,filter:"folder"},
    {k:"department",label:lang==="fr"?"Département":"Department",dot:THEME.textDim,filter:"department"},
  ];
  function chipCount(chip){
    if(chip.k==="all")return total||logs.length;
    return logs.filter(l=>(l.action||"").startsWith(chip.filter||chip.k)).length;
  }

  // Group rows by day for the date bands.
  function dayKey(ts){
    if(!ts)return "other";
    const d=new Date(ts);
    return d.toISOString().slice(0,10);
  }
  function prettyDay(key){
    if(key==="other")return lang==="fr"?"Autre":"Other";
    const d=new Date(key);
    const t=new Date();
    const sameDay=(a,b)=>a.toDateString()===b.toDateString();
    if(sameDay(d,t))return lang==="fr"?"Aujourd'hui":"Today";
    const y=new Date(t);y.setDate(y.getDate()-1);
    if(sameDay(d,y))return lang==="fr"?"Hier":"Yesterday";
    return d.toLocaleDateString(lang==="fr"?"fr-FR":"en-US",{weekday:"long"});
  }
  function fullDate(key){
    if(key==="other")return "";
    const d=new Date(key);
    return d.toLocaleDateString(lang==="fr"?"fr-FR":"en-US",{month:"long",day:"numeric",year:"numeric"});
  }
  const grouped={};
  for(const l of logs){
    const k=dayKey(l.timestamp||l.created_at);
    (grouped[k]=grouped[k]||[]).push(l);
  }
  const dayKeys=Object.keys(grouped).sort((a,b)=>b.localeCompare(a));

  // ───── Render ─────
  return(
    <div>
      {/* ── Page header ── */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:24,marginBottom:24,flexWrap:"wrap"}}>
        <div>
          <div style={{...monoLabel,letterSpacing:"0.16em",textTransform:"uppercase",marginBottom:6,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <span style={{width:18,height:1,background:THEME.line3,display:"inline-block"}}/>
            <span>Admin</span>
            <span style={{color:THEME.muted2}}>›</span>
            <span style={{color:THEME.textMuted}}>{lang==="fr"?"Journal d'audit":"Audit log"}</span>
            <span style={{color:THEME.muted2}}>·</span>
            <span style={{color:THEME.textDim}}>{(()=>{const d=new Date();return d.toLocaleDateString(lang==="fr"?"fr-FR":"en-US",{weekday:"short",month:"short",day:"numeric"})+" · "+d.toLocaleTimeString(lang==="fr"?"fr-FR":"en-US",{hour:"2-digit",minute:"2-digit"});})()}</span>
          </div>
          <h1 style={{fontSize:32,fontWeight:600,letterSpacing:"-0.025em",color:THEME.text,margin:"0 0 6px",lineHeight:1.1}}>
            {T(lang,"auditLog")} <em style={{fontFamily:FONT_SERIF,fontStyle:"italic",fontWeight:400,color:THEME.accent2}}>—</em> {total} {lang==="fr"?"événements enregistrés":"events recorded"}
          </h1>
          <div style={{color:THEME.textMuted,fontSize:13.5}}>
            {lang==="fr"
              ?<>Historique immuable de toute lecture, écriture et modification sur les documents, dossiers et départements. <span style={{color:THEME.ink2}}>Rétention&nbsp;: 365 jours</span> · dernier événement {logs[0]?(formatDate(logs[0].timestamp||logs[0].created_at,lang)):(lang==="fr"?"—":"—")}</>
              :<>Immutable history of every read, write &amp; mutation across documents, folders and departments. <span style={{color:THEME.ink2}}>Retention: 365 days</span> · last event {logs[0]?(formatDate(logs[0].timestamp||logs[0].created_at,lang)):"—"}</>}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          <button onClick={()=>loadLogs()} style={{display:"inline-flex",alignItems:"center",gap:7,height:38,padding:"0 16px",borderRadius:8,fontSize:13,fontWeight:600,border:`1px solid ${THEME.line2}`,background:THEME.surfaceRaised,color:THEME.ink2,cursor:"pointer",fontFamily:FONT}}><lucide.RefreshCw size={14}/>{lang==="fr"?"Rafraîchir":"Refresh"}</button>
          <button onClick={isAdmin?exportCsv:()=>buildAndDownloadCsv(logs)} disabled={exporting} style={{display:"inline-flex",alignItems:"center",gap:7,height:38,padding:"0 16px",borderRadius:8,fontSize:13,fontWeight:600,border:"1px solid rgba(255,255,255,0.12)",background:`linear-gradient(180deg,${THEME.accent2},${THEME.accent})`,color:"#fff",cursor:"pointer",fontFamily:FONT,boxShadow:`0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px ${THEME.accentDim}`,opacity:exporting?0.6:1}}>{exporting?<Spinner size={14}/>:<lucide.Download size={14}/>}{lang==="fr"?"Exporter CSV":"Export CSV"} <span style={{fontFamily:FONT_MONO,fontWeight:500,opacity:0.7,fontSize:11,marginLeft:2}}>({lang==="fr"?"tout":"all"})</span></button>
        </div>
      </div>

      {/* ── 4 stat cards (no sparklines per Sprint 11.x dashboard precedent) ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:22}}>
        {/* Total events */}
        <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,padding:"18px 18px 16px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
            <div style={{width:34,height:34,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:THEME.accentDim,border:`1px solid ${THEME.accentLine}`,color:THEME.accent}}><lucide.Activity size={16}/></div>
            <span style={trendChip("up")}>+{last24hCount} {lang==="fr"?"aujourd'hui":"today"}</span>
          </div>
          <div style={{fontSize:36,fontWeight:600,letterSpacing:"-0.03em",color:THEME.text,lineHeight:1,display:"flex",alignItems:"baseline",gap:6,fontVariantNumeric:"tabular-nums"}}>{total}<span style={{fontSize:13,color:THEME.textMuted,fontWeight:500}}>{lang==="fr"?"événements":"events"}</span></div>
          <div style={{fontSize:12,color:THEME.textMuted,marginTop:6,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <span>{lang==="fr"?"Total enregistré":"Total recorded"}</span>
            <span style={monoLabel}>{lang==="fr"?"page":"page"} {page+1}/{totalPages}</span>
          </div>
        </div>
        {/* Active actors */}
        <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,padding:"18px 18px 16px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
            <div style={{width:34,height:34,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:THEME.successDim,border:`1px solid ${THEME.successLine}`,color:THEME.success}}><lucide.Users size={16}/></div>
            <span style={trendChip("flat")}>·</span>
          </div>
          <div style={{fontSize:36,fontWeight:600,letterSpacing:"-0.03em",color:THEME.text,lineHeight:1,display:"flex",alignItems:"baseline",gap:6,fontVariantNumeric:"tabular-nums"}}>{actors.length}<span style={{fontSize:13,color:THEME.textMuted,fontWeight:500}}>{actors.length===1?(lang==="fr"?"utilisateur":"user"):(lang==="fr"?"utilisateurs":"users")}</span></div>
          <div style={{fontSize:12,color:THEME.textMuted,marginTop:6,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <span>{lang==="fr"?"Acteurs actifs":"Active actors"}</span>
            <span style={monoLabel}>{actors[0]?actors[0][0].split(" ").slice(0,2).join(" "):"—"}</span>
          </div>
        </div>
        {/* IPs */}
        <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,padding:"18px 18px 16px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
            <div style={{width:34,height:34,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:THEME.infoDim,border:`1px solid ${THEME.infoLine}`,color:THEME.info}}><lucide.MapPin size={16}/></div>
            <span style={trendChip("warn")}>{ipMap.size>1?`${ipMap.size} ${lang==="fr"?"réseaux":"networks"}`:lang==="fr"?"primaire":"primary"}</span>
          </div>
          <div style={{fontSize:36,fontWeight:600,letterSpacing:"-0.03em",color:THEME.text,lineHeight:1,display:"flex",alignItems:"baseline",gap:6,fontVariantNumeric:"tabular-nums"}}>{ipMap.size}<span style={{fontSize:13,color:THEME.textMuted,fontWeight:500}}>{lang==="fr"?"IPs vues":"IPs seen"}</span></div>
          <div style={{fontSize:12,color:THEME.textMuted,marginTop:6,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <span>{lang==="fr"?"Adresses sources":"Source addresses"}</span>
            <span style={monoLabel}>{topIps[0]?topIps[0][0]:"—"}</span>
          </div>
        </div>
        {/* Top action */}
        <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,padding:"18px 18px 16px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
            <div style={{width:34,height:34,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:THEME.surfaceHover,border:`1px solid ${THEME.line2}`,color:THEME.ink2}}><lucide.Eye size={16}/></div>
            <span style={trendChip("flat")}>{lang==="fr"?"plus fréquent":"most frequent"}</span>
          </div>
          <div style={{fontSize:36,fontWeight:600,letterSpacing:"-0.03em",color:THEME.text,lineHeight:1,display:"flex",alignItems:"baseline",gap:6,fontVariantNumeric:"tabular-nums"}}>{topAction?topAction[1]:0}<span style={{fontSize:13,color:THEME.textMuted,fontWeight:500}}>{topAction?topAction[0]:"—"}</span></div>
          <div style={{fontSize:12,color:THEME.textMuted,marginTop:6,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <span>{lang==="fr"?"Action principale":"Top action"}</span>
          </div>
        </div>
      </div>

      {/* ── 24-hour heatmap ── */}
      <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,padding:"16px 18px 14px",marginBottom:18}}>
        <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:14,gap:18}}>
          <div style={{fontSize:13,fontWeight:600,color:THEME.text,letterSpacing:"-0.01em",display:"flex",alignItems:"center",gap:10}}>
            <span>{lang==="fr"?"Événements par heure":"Events by hour"}</span>
            <span style={{...monoLabel,fontWeight:500,borderLeft:`1px solid ${THEME.line2}`,paddingLeft:10,marginLeft:2,textTransform:"uppercase",letterSpacing:"0.04em"}}>{lang==="fr"?"dernières 24 h":"last 24 h"} · {last24hCount} {lang==="fr"?"événements":"events"}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12,...monoLabel,letterSpacing:"0.06em",textTransform:"uppercase",fontSize:10}}>
            <span>{lang==="fr"?"Moins":"Less"}</span>
            <span style={{display:"inline-flex",gap:2,alignItems:"flex-end"}}>
              <span style={{display:"block",width:6,height:5,background:THEME.accent,opacity:0.2,borderRadius:1}}/>
              <span style={{display:"block",width:6,height:8,background:THEME.accent,opacity:0.4,borderRadius:1}}/>
              <span style={{display:"block",width:6,height:11,background:THEME.accent,opacity:0.65,borderRadius:1}}/>
              <span style={{display:"block",width:6,height:14,background:THEME.accent,opacity:0.9,borderRadius:1}}/>
            </span>
            <span>{lang==="fr"?"Plus":"More"}</span>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(24,1fr)",gap:3,alignItems:"end",height:64}}>
          {hours.map((c,h)=>{
            const lit=c>0;
            const pct=maxHr>0?Math.max(6,(c/maxHr)*96):6;
            return(
              <div key={h} title={lit?`${c} ${lang==="fr"?"évén":"event"}${c!==1?"s":""} · ${String(h).padStart(2,"0")}h`:""}
                style={{position:"relative",background:lit?`linear-gradient(180deg,${THEME.accent},rgba(224,154,91,0.4))`:THEME.surfaceHover,borderTop:`1px solid ${lit?THEME.accentLine:THEME.line2}`,borderRadius:"3px 3px 0 0",height:pct+"%",minHeight:4}}/>
            );
          })}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(24,1fr)",gap:3,...monoLabel,fontSize:9.5,letterSpacing:"0.04em",marginTop:6}}>
          {Array.from({length:24}).map((_,i)=>(<span key={i} style={{textAlign:"center",color:[0,6,12,18].includes(i)?THEME.textMuted:THEME.textDim}}>{[0,6,12,18].includes(i)?String(i).padStart(2,"0"):""}</span>))}
        </div>
      </div>

      {/* ── Main grid: stream (left) + side panels (right) ── */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 320px",gap:18,alignItems:"flex-start"}}>

        {/* ─── LEFT: filter toolbar + date bands + audit stream ─── */}
        <div style={{minWidth:0}}>

          {/* Filter toolbar */}
          <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,padding:"14px 16px",marginBottom:14,display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              {/* Search */}
              <div style={{flex:1,minWidth:240,height:36,background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,borderRadius:8,display:"flex",alignItems:"center",gap:9,padding:"0 12px"}}>
                <lucide.Search size={14} color={THEME.textDim}/>
                <input value={search} onChange={e=>{setSearch(e.target.value);setPage(0);}} placeholder={lang==="fr"?"Rechercher par utilisateur, action, document, IP…   essai: action:document.checkin user:EK":"Search by user, action, document, IP…   try: action:document.checkin user:EK"} style={{flex:1,border:0,outline:0,background:"transparent",color:THEME.text,fontSize:13,fontFamily:FONT}}/>
                {search&&<button onClick={()=>{setSearch("");setPage(0);}} style={{fontFamily:FONT_MONO,fontSize:10,border:`1px solid ${THEME.line2}`,padding:"1px 5px",borderRadius:4,background:THEME.surfaceHover,color:THEME.textDim,fontWeight:500,cursor:"pointer"}}>esc</button>}
              </div>
              {/* Date range */}
              <div style={{display:"flex",alignItems:"center",gap:6,height:36,padding:"0 10px",background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,borderRadius:8,fontFamily:FONT_MONO,fontSize:11.5,color:THEME.ink2}}>
                <lucide.Calendar size={13} color={THEME.textDim}/>
                <span style={{color:THEME.textDim,textTransform:"uppercase",letterSpacing:"0.06em",fontSize:10}}>{lang==="fr"?"Du":"From"}</span>
                <input type="date" value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setPage(0);}} style={{background:"transparent",border:0,outline:"none",color:THEME.text,fontWeight:600,fontFamily:FONT_MONO,fontSize:11.5,width:110}}/>
                <span style={{color:THEME.muted2}}>→</span>
                <span style={{color:THEME.textDim,textTransform:"uppercase",letterSpacing:"0.06em",fontSize:10}}>{lang==="fr"?"Au":"To"}</span>
                <input type="date" value={dateTo} onChange={e=>{setDateTo(e.target.value);setPage(0);}} style={{background:"transparent",border:0,outline:"none",color:THEME.text,fontWeight:600,fontFamily:FONT_MONO,fontSize:11.5,width:110}}/>
              </div>
              {(search||dateFrom||dateTo||filter!=="all")&&<button onClick={()=>{setSearch("");setDateFrom("");setDateTo("");setFilter("all");setPage(0);}} style={{display:"inline-flex",alignItems:"center",gap:6,height:36,padding:"0 12px",borderRadius:8,border:`1px solid ${THEME.line2}`,background:THEME.surfaceRaised,color:THEME.ink2,fontSize:12,fontFamily:FONT,cursor:"pointer"}}><lucide.X size={13}/>{lang==="fr"?"Effacer":"Clear"}</button>}
            </div>
            {/* Category chips */}
            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",borderTop:`1px dashed ${THEME.line2}`,paddingTop:12}}>
              <span style={{fontFamily:FONT_MONO,fontSize:10,color:THEME.textDim,letterSpacing:"0.08em",textTransform:"uppercase",marginRight:6}}>{lang==="fr"?"Catégorie":"Category"}</span>
              {baseChips.map(chip=>{
                const on=(chip.k==="all"&&filter==="all")||(filter===chip.filter)||(filter===chip.k);
                const n=chipCount(chip);
                return(
                  <button key={chip.k} onClick={()=>{setFilter(chip.k==="all"?"all":(chip.filter||chip.k));setPage(0);}}
                    style={{display:"inline-flex",alignItems:"center",gap:7,height:28,padding:"0 11px",background:on?THEME.accentDim:THEME.surfaceRaised,border:`1px solid ${on?THEME.accentLine:THEME.line2}`,borderRadius:14,fontSize:12,color:on?THEME.accent2:THEME.ink2,fontWeight:500,cursor:"pointer",fontFamily:FONT,transition:"all 0.12s"}}>
                    <span style={{width:7,height:7,borderRadius:"50%",background:on?THEME.accent:chip.dot,flexShrink:0}}/>
                    <span>{chip.label}</span>
                    <span style={{fontFamily:FONT_MONO,fontSize:10.5,color:on?THEME.accent:THEME.textDim}}>{n}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Audit stream — grouped by day */}
          {logs.length===0&&(
            <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,padding:"40px 20px",textAlign:"center",color:THEME.textDim,fontSize:13}}>
              {lang==="fr"?"Aucun événement à afficher":"No events to display"}
            </div>
          )}
          {dayKeys.map(dk=>{
            const dayRows=grouped[dk];
            const firstT=dayRows[dayRows.length-1]?.timestamp||dayRows[dayRows.length-1]?.created_at;
            const lastT=dayRows[0]?.timestamp||dayRows[0]?.created_at;
            const f=firstT?new Date(firstT).toLocaleTimeString(lang==="fr"?"fr-FR":"en-US",{hour:"2-digit",minute:"2-digit"}):"";
            const t=lastT?new Date(lastT).toLocaleTimeString(lang==="fr"?"fr-FR":"en-US",{hour:"2-digit",minute:"2-digit"}):"";
            return(
              <div key={dk}>
                {/* Date band */}
                <div style={{display:"flex",alignItems:"center",gap:14,margin:"22px 0 10px",padding:"0 4px",fontFamily:FONT_MONO}}>
                  <span style={{display:"inline-flex",alignItems:"center",gap:9,padding:"5px 12px",background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,borderRadius:14,fontSize:11,color:THEME.text,letterSpacing:"0.04em",textTransform:"uppercase",fontWeight:600}}>
                    <b style={{color:THEME.accent2,fontWeight:600}}>{prettyDay(dk)}</b>
                    <span style={{color:THEME.textDim,fontWeight:500}}>{fullDate(dk)}</span>
                  </span>
                  <span style={{fontSize:10.5,color:THEME.textDim,letterSpacing:"0.06em",textTransform:"uppercase"}}>{dayRows.length} {lang==="fr"?"événements":"events"}{f&&t?` · ${f} → ${t}`:""}</span>
                  <span style={{flex:1,height:1,background:`linear-gradient(90deg,${THEME.border},transparent)`}}/>
                </div>
                {/* Stream card */}
                <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,overflow:"hidden"}}>
                  {dayRows.map((log,i)=>{
                    const meta=getActionMeta(log.action,lang);
                    const Icon=getActionIcon(log.action);
                    const ip=log.ip||log.ip_address||"";
                    const target=log.target||"";
                    const ts=log.timestamp||log.created_at;
                    const time=ts?new Date(ts):null;
                    const stamp=time?time.toLocaleTimeString(lang==="fr"?"fr-FR":"en-US",{hour:"2-digit",minute:"2-digit",second:"2-digit"}):"";
                    const isOpen=expandedRowId===(log.id||`d${dk}i${i}`);
                    const rowKey=log.id||`d${dk}i${i}`;
                    const userName=log.user||log.user_name||"—";
                    const userInitials=userName.split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase()||"—";
                    // Sprint 11.x — resolve target to (id, version, filename)
                    // for every shape the server emits: bare id ("3"),
                    // "document:N" (preview/download), or raw filename (uploads).
                    const colonMatch=target.match(/^(document|folder|user|courrier|department):(\d+)$/);
                    const bareIdMatch=/^\d+$/.test(target);
                    const entity=colonMatch?colonMatch[1]:(bareIdMatch?"document":null);
                    const lookupId=colonMatch?colonMatch[2]:(bareIdMatch?target:null);
                    const lookedUpById=(entity==="document"&&lookupId)?docsById[lookupId]:null;
                    const lookedUpByName=(!lookedUpById&&target&&!colonMatch&&!bareIdMatch)?docsByName[target]:null;
                    const lookedUp=lookedUpById||lookedUpByName;
                    return(
                      <div key={rowKey}>
                        {/* Row */}
                        <div onClick={()=>setExpandedRowId(isOpen?null:rowKey)} style={{display:"grid",gridTemplateColumns:"54px 1fr auto",gap:14,alignItems:"flex-start",padding:"14px 18px",position:"relative",cursor:"pointer",borderBottom:i<dayRows.length-1||isOpen?`1px solid ${THEME.border}`:"none",background:isOpen?"rgba(224,154,91,0.025)":"transparent",transition:"background 0.12s"}} onMouseEnter={e=>{if(!isOpen)e.currentTarget.style.background="rgba(255,255,255,0.018)";}} onMouseLeave={e=>{if(!isOpen)e.currentTarget.style.background="transparent";}}>
                          {/* Timeline rail */}
                          <span style={{position:"absolute",left:43,top:i===0?24:0,bottom:i===dayRows.length-1?`calc(100% - 24px)`:0,width:1,background:THEME.line2}}/>
                          {/* Action dot */}
                          <div style={{width:32,height:32,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:meta.bg,border:`1px solid ${meta.bd}`,color:meta.fg,position:"relative",zIndex:1,boxShadow:`0 0 0 4px ${THEME.surface}`,flexShrink:0}}>
                            <Icon size={14}/>
                          </div>
                          {/* Body */}
                          <div style={{minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                              <span style={{display:"inline-flex",alignItems:"center",gap:7}}>
                                <span style={{width:20,height:20,borderRadius:"50%",background:`linear-gradient(135deg,${THEME.success},#1f8c64)`,color:"#0a1c14",fontSize:9,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontFamily:FONT}}>{userInitials}</span>
                                <span style={{fontSize:13,fontWeight:600,color:THEME.text,letterSpacing:"-0.005em"}}>{userName}</span>
                              </span>
                              <span style={{color:THEME.muted2,fontSize:11}}>·</span>
                              <span style={{display:"inline-flex",alignItems:"center",gap:5,fontFamily:FONT_MONO,fontSize:11,fontWeight:500,padding:"2px 7px",borderRadius:4,letterSpacing:"0.01em",background:meta.bg,color:meta.fg,border:`1px solid ${meta.bd}`}}>{log.action}</span>
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:10,fontSize:12,color:THEME.textMuted,flexWrap:"wrap"}}>
                              {target&&(lookedUp
                                ? <span style={{display:"inline-flex",alignItems:"center",gap:6,fontFamily:FONT_MONO,fontSize:11.5,color:THEME.ink2,background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,padding:"2px 8px",borderRadius:4,letterSpacing:"0.01em",maxWidth:"100%",overflow:"hidden"}}>
                                    <lucide.FileText size={11} color={THEME.textDim} style={{flexShrink:0}}/>
                                    <b style={{color:THEME.textDim,fontWeight:500,flexShrink:0}}>#{lookedUp.id}</b>
                                    <span style={{fontFamily:FONT_MONO,fontSize:10,fontWeight:600,color:THEME.accent2,background:THEME.accentDim,border:`1px solid ${THEME.accentLine}`,padding:"0 4px",borderRadius:3,flexShrink:0}}>v{lookedUp.current_version||1}</span>
                                    <span style={{fontFamily:FONT,fontStyle:"italic",color:THEME.text,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{lookedUp.name}</span>
                                  </span>
                                : <span style={{display:"inline-flex",alignItems:"center",gap:6,fontFamily:FONT_MONO,fontSize:11.5,color:THEME.ink2,background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,padding:"2px 8px",borderRadius:4,letterSpacing:"0.01em"}}>
                                    <lucide.FileText size={11} color={THEME.textDim}/>
                                    <b style={{fontFamily:(entity&&lookupId)?FONT_MONO:FONT,fontStyle:(entity&&lookupId)?"normal":"italic",color:THEME.text,fontWeight:500}}>{(entity&&lookupId)?`${entity} #${lookupId}`:target}</b>
                                  </span>)}
                              {ip&&<span style={{display:"inline-flex",alignItems:"center",gap:6,fontFamily:FONT_MONO,fontSize:11,color:THEME.textDim,padding:"1px 0",letterSpacing:"0.02em"}}><lucide.MapPin size={10}/>{ip}</span>}
                            </div>
                          </div>
                          {/* Right: time + chevron */}
                          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6,flexShrink:0,textAlign:"right"}}>
                            <div style={{fontFamily:FONT_MONO,fontSize:11.5,color:THEME.ink2,fontWeight:600}}>
                              {formatDate(ts,lang)}
                            </div>
                            <div style={{fontFamily:FONT_MONO,fontSize:10,color:THEME.textDim,letterSpacing:"0.04em"}}>{stamp}</div>
                            <lucide.ChevronRight size={13} color={isOpen?THEME.accent:THEME.textDim} style={{transform:isOpen?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.15s",marginTop:2}}/>
                          </div>
                        </div>
                        {/* Expanded detail */}
                        {isOpen&&(()=>{
                          let detailObj=null;
                          try{detailObj=typeof log.details==="string"?JSON.parse(log.details):log.details;}catch{}
                          const payload={
                            action:log.action,
                            actor:{id:log.user_id||null,name:userName,email:log.user_email||null},
                            target:target||null,
                            context:{ip:ip||null,user_agent:log.user_agent||null,session_id:log.session_id||null,request_id:log.request_id||null},
                            ...(detailObj&&typeof detailObj==="object"?{details:detailObj}:{}),
                          };
                          return(
                            <div style={{background:`linear-gradient(180deg,rgba(224,154,91,0.03),transparent 60%)`,borderBottom:i<dayRows.length-1?`1px solid ${THEME.border}`:"none",padding:"6px 18px 18px 86px",position:"relative"}}>
                              <span style={{position:"absolute",left:43,top:0,bottom:34,width:1,background:`linear-gradient(180deg,${THEME.accentLine},transparent)`}}/>
                              <div style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:14,marginBottom:14}}>
                                <div style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:8,padding:"10px 12px"}}>
                                  <div style={{fontFamily:FONT_MONO,fontSize:9.5,letterSpacing:"0.1em",textTransform:"uppercase",color:THEME.textDim,marginBottom:4}}>{lang==="fr"?"ID":"Event ID"}</div>
                                  <div style={{fontFamily:FONT_MONO,fontSize:11.5,color:THEME.text,wordBreak:"break-all"}}>#{log.id||"—"}</div>
                                </div>
                                <div style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:8,padding:"10px 12px"}}>
                                  <div style={{fontFamily:FONT_MONO,fontSize:9.5,letterSpacing:"0.1em",textTransform:"uppercase",color:THEME.textDim,marginBottom:4}}>{lang==="fr"?"Acteur":"Actor"}</div>
                                  <div style={{fontSize:12.5,color:THEME.text,fontWeight:500}}>{userName}</div>
                                </div>
                                <div style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:8,padding:"10px 12px"}}>
                                  <div style={{fontFamily:FONT_MONO,fontSize:9.5,letterSpacing:"0.1em",textTransform:"uppercase",color:THEME.textDim,marginBottom:4}}>IP</div>
                                  <div style={{fontFamily:FONT_MONO,fontSize:11.5,color:THEME.text}}>{ip||"—"}</div>
                                </div>
                                <div style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:8,padding:"10px 12px"}}>
                                  <div style={{fontFamily:FONT_MONO,fontSize:9.5,letterSpacing:"0.1em",textTransform:"uppercase",color:THEME.textDim,marginBottom:4}}>{lang==="fr"?"Résultat":"Result"}</div>
                                  <div style={{fontSize:12.5,color:THEME.text,fontWeight:500}}><span style={{color:THEME.success}}>● {lang==="fr"?"succès":"success"}</span></div>
                                </div>
                              </div>
                              <pre style={{margin:0,background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:8,padding:"12px 14px",fontFamily:FONT_MONO,fontSize:11.5,color:THEME.ink2,lineHeight:1.7,overflow:"auto",maxHeight:280}}>{JSON.stringify(payload,null,2)}</pre>
                              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:12,gap:10,flexWrap:"wrap"}}>
                                <div style={{fontFamily:FONT_MONO,fontSize:10.5,color:THEME.textDim,letterSpacing:"0.04em"}}>{lang==="fr"?"Événement immuable, signé à l'écriture":"Immutable event, signed at write time"}</div>
                                <div style={{display:"flex",alignItems:"center",gap:8}}>
                                  <button onClick={()=>{navigator.clipboard?.writeText(JSON.stringify(payload,null,2));toast(lang==="fr"?"JSON copié":"JSON copied","success");}} style={{display:"inline-flex",alignItems:"center",gap:6,height:28,padding:"0 11px",borderRadius:6,fontSize:11.5,fontWeight:500,background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,color:THEME.ink2,cursor:"pointer",fontFamily:FONT}}><lucide.Copy size={12}/>{lang==="fr"?"Copier JSON":"Copy JSON"}</button>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {totalPages>1&&(
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:18,padding:"12px 4px"}}>
              <div style={{fontFamily:FONT_MONO,fontSize:11.5,color:THEME.textDim}}>
                {lang==="fr"?"Affichage":"Showing"} <b style={{color:THEME.ink2,fontWeight:600}}>{page*PAGE_SIZE+1}–{Math.min((page+1)*PAGE_SIZE,total)}</b> {lang==="fr"?"sur":"of"} <b style={{color:THEME.ink2,fontWeight:600}}>{total}</b> {lang==="fr"?"événements":"events"}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <button disabled={page===0} onClick={()=>setPage(p=>p-1)} style={{height:30,minWidth:30,padding:"0 10px",background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,borderRadius:6,fontFamily:FONT_MONO,fontSize:11.5,fontWeight:600,color:THEME.ink2,display:"inline-flex",alignItems:"center",justifyContent:"center",cursor:page===0?"not-allowed":"pointer",opacity:page===0?0.4:1}}><lucide.ChevronLeft size={11}/></button>
                {(()=>{
                  const max=Math.min(totalPages,5);
                  const start=Math.max(0,Math.min(totalPages-max,page-2));
                  return Array.from({length:max}).map((_,k)=>{
                    const p=start+k;
                    const on=p===page;
                    return(<button key={p} onClick={()=>setPage(p)} style={{height:30,minWidth:30,padding:"0 10px",background:on?THEME.accentDim:THEME.surfaceRaised,border:`1px solid ${on?THEME.accentLine:THEME.line2}`,borderRadius:6,fontFamily:FONT_MONO,fontSize:11.5,fontWeight:600,color:on?THEME.accent2:THEME.ink2,cursor:"pointer"}}>{p+1}</button>);
                  });
                })()}
                <button disabled={page>=totalPages-1} onClick={()=>setPage(p=>p+1)} style={{height:30,minWidth:30,padding:"0 10px",background:THEME.surfaceRaised,border:`1px solid ${THEME.line2}`,borderRadius:6,fontFamily:FONT_MONO,fontSize:11.5,fontWeight:600,color:THEME.ink2,display:"inline-flex",alignItems:"center",justifyContent:"center",cursor:page>=totalPages-1?"not-allowed":"pointer",opacity:page>=totalPages-1?0.4:1}}><lucide.ChevronRight size={11}/></button>
              </div>
            </div>
          )}
        </div>

        {/* ─── RIGHT: side panel cards ─── */}
        <aside style={{position:"sticky",top:0,display:"flex",flexDirection:"column",gap:14}}>

          {/* Action breakdown */}
          <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,overflow:"hidden"}}>
            <div style={{padding:"13px 16px 10px",borderBottom:`1px solid ${THEME.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:13,fontWeight:600,color:THEME.text,letterSpacing:"-0.01em"}}>{lang==="fr"?"Répartition":"Action breakdown"}</div>
              <span style={{...monoLabel,letterSpacing:"0.06em",textTransform:"uppercase"}}>{lang==="fr"?"page":"page"}</span>
            </div>
            <div style={{padding:"6px 16px 14px"}}>
              {categories.length===0?<div style={{padding:"12px 0",fontSize:12,color:THEME.textDim,textAlign:"center"}}>{lang==="fr"?"Aucune donnée":"No data"}</div>:
                categories.slice(0,6).map(([cat,n])=>{
                  const meta=getActionMeta(cat+".create",lang); // get a representative tone
                  const pct=Math.round((n/logs.length)*100);
                  return(
                    <div key={cat} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0"}}>
                      <span style={{width:8,height:8,borderRadius:2,background:meta.fg,flexShrink:0}}/>
                      <span style={{fontSize:12,color:THEME.ink2,textTransform:"capitalize"}}>{cat}</span>
                      <span style={{flex:1,height:4,background:THEME.surfaceHover,borderRadius:2,overflow:"hidden",minWidth:30}}>
                        <span style={{display:"block",height:"100%",width:pct+"%",background:meta.fg,borderRadius:2}}/>
                      </span>
                      <span style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.ink2,fontVariantNumeric:"tabular-nums",minWidth:24,textAlign:"right"}}>{n}</span>
                      <span style={{fontFamily:FONT_MONO,fontSize:10,color:THEME.textDim,minWidth:30,textAlign:"right"}}>{pct}%</span>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Top actors */}
          <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,overflow:"hidden"}}>
            <div style={{padding:"13px 16px 10px",borderBottom:`1px solid ${THEME.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:13,fontWeight:600,color:THEME.text,letterSpacing:"-0.01em"}}>{lang==="fr"?"Acteurs":"Top actors"}</div>
              <span style={{...monoLabel,letterSpacing:"0.06em",textTransform:"uppercase"}}>{lang==="fr"?"page":"page"}</span>
            </div>
            <div style={{padding:"6px 16px 14px"}}>
              {actors.length===0&&<div style={{padding:"12px 0",fontSize:12,color:THEME.textDim,textAlign:"center"}}>{lang==="fr"?"Aucun acteur":"No actors"}</div>}
              {actors.map(([name,n],idx)=>{
                const initials=name.split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase()||"—";
                return(
                  <div key={name} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:idx<actors.length-1?`1px dashed ${THEME.border}`:"none"}}>
                    <span style={{width:26,height:26,borderRadius:"50%",background:`linear-gradient(135deg,${THEME.success},#1f8c64)`,color:"#0a1c14",fontSize:10,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontFamily:FONT}}>{initials}</span>
                    <span style={{flex:1,minWidth:0,fontSize:12.5,color:THEME.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</span>
                    <span style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.accent2,fontWeight:600}}>{n}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Source IPs */}
          {topIps.length>0&&(
            <div style={{background:THEME.surface,border:`1px solid ${THEME.border}`,borderRadius:14,overflow:"hidden"}}>
              <div style={{padding:"13px 16px 10px",borderBottom:`1px solid ${THEME.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{fontSize:13,fontWeight:600,color:THEME.text,letterSpacing:"-0.01em"}}>{lang==="fr"?"IPs sources":"Source IPs"}</div>
                <span style={{...monoLabel,letterSpacing:"0.06em",textTransform:"uppercase"}}>{lang==="fr"?"unique":"unique"}</span>
              </div>
              <div style={{padding:"6px 16px 14px"}}>
                {topIps.map(([ip,n],idx)=>(
                  <div key={ip} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:idx<topIps.length-1?`1px dashed ${THEME.border}`:"none"}}>
                    <span style={{width:26,height:26,borderRadius:6,background:THEME.surfaceHover,color:THEME.info,fontFamily:FONT_MONO,fontSize:9,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,border:`1px solid ${THEME.line2}`}}>IP</span>
                    <span style={{flex:1,minWidth:0,fontFamily:FONT_MONO,fontSize:11.5,fontWeight:500,color:THEME.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ip}</span>
                    <span style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.accent2,fontWeight:600}}>{n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Retention notice */}
          <div style={{background:`linear-gradient(180deg,${THEME.accentDim},transparent)`,border:`1px solid ${THEME.accentLine}`,borderRadius:14,padding:"14px 16px",display:"flex",gap:12}}>
            <div style={{width:32,height:32,borderRadius:8,background:THEME.accentDim,border:`1px solid ${THEME.accentLine}`,color:THEME.accent,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><lucide.Shield size={14}/></div>
            <div>
              <div style={{fontSize:12,color:THEME.ink2,lineHeight:1.5}}>{lang==="fr"?<>Les événements d'audit sont en <b style={{color:THEME.accent2,fontWeight:600}}>ajout uniquement</b> et signés à l'écriture. Ils ne peuvent pas être modifiés ou supprimés depuis l'interface.</>:<>Audit events are <b style={{color:THEME.accent2,fontWeight:600}}>append-only</b> and signed at write time. They cannot be edited or deleted from the UI.</>}</div>
              <div style={{...monoLabel,marginTop:6,letterSpacing:"0.04em",textTransform:"uppercase"}}>{lang==="fr"?"Rétention 365 j · chaîne SHA-256":"Retention 365 d · SHA-256 chain"}</div>
            </div>
          </div>

        </aside>
      </div>
    </div>
  );
}

// ─── SUBSCRIPTION PAGE — Hardened (Sprint 5 Backlog) ───
// Sprint 10C Session C1 H5 fix: gated to admin OR super_admin. Non-admin
// users get AccessDeniedPage. The early return is placed BEFORE the
// useEffect that fires /subscriptions/* API calls so non-admin JWTs don't
// generate noisy 403s against the (newly-locked-down) billing endpoints.
function SubscriptionPage(){
  const {toast,refreshSignal,triggerRefresh,user}=useApp();const {lang}=useLang();
  const [plans,setPlans]=useState([]);const [current,setCurrent]=useState(null);const [usage,setUsage]=useState(null);
  const [changingPlan,setChangingPlan]=useState(null);const [paymentModal,setPaymentModal]=useState(null);

  const allowed=isAdminOrSuper(user);

  useEffect(()=>{
    if(!allowed)return;
    api.get("/subscriptions/plans").then(r=>setPlans(r.data||[])).catch(()=>setPlans([]));
    api.get("/subscriptions/my").then(r=>{if(r&&typeof r==="object")setCurrent(r);}).catch(()=>setCurrent(null));
    api.get("/subscriptions/usage").then(r=>{if(r&&typeof r==="object")setUsage(r);}).catch(()=>setUsage(null));
  },[refreshSignal,allowed]);

  if(!allowed)return <AccessDeniedPage requiredRole="admin"/>;

  const planColors={free:THEME.textMuted,basic:THEME.info,premium:THEME.accent,enterprise:THEME.success};
  const planIcons={free:lucide.Gift,basic:lucide.Zap,premium:lucide.Crown,enterprise:lucide.Building2};

  async function handleChangePlan(plan){
    setChangingPlan(plan.id);
    try{
      await api.put("/subscriptions/change-plan",{plan_id:plan.id});
      toast(lang==="fr"?`Plan changé vers ${plan.display_name}`:`Plan changed to ${plan.display_name}`,"success");
      triggerRefresh();
    }catch(err){
      toast(err.message||"Failed to change plan","error");
    }
    setChangingPlan(null);
  }

  const paymentMethods=[
    {id:"mobile_money",name:"Mobile Money",desc:"M-Pesa, Airtel Money, Orange Money",icon:lucide.Smartphone,color:THEME.success,
     info:lang==="fr"
       ?"Envoyez votre paiement via Mobile Money au numéro indiqué, puis contactez-nous avec la référence de transaction."
       :"Send your payment via Mobile Money to the number shown, then contact us with the transaction reference.",
     details:[{label:lang==="fr"?"Numéro":"Number",value:"+243 812 345 678"},{label:lang==="fr"?"Nom":"Name",value:"KlassDoc / Seven Co Success"},{label:lang==="fr"?"Référence":"Reference",value:current?.org_name||"KlassDoc"}]},
    {id:"card",name:lang==="fr"?"Carte bancaire":"Credit Card",desc:"Visa, Mastercard (Stripe)",icon:lucide.CreditCard,color:THEME.info,
     info:lang==="fr"
       ?"Le paiement par carte sera bientôt disponible via Stripe. Contactez-nous pour un lien de paiement."
       :"Card payment via Stripe coming soon. Contact us for a payment link.",
     details:[{label:"Email",value:"billing@klassdoc.com"},{label:"WhatsApp",value:"+243 812 345 678"}]},
    {id:"bank_transfer",name:lang==="fr"?"Virement bancaire":"Bank Transfer",desc:lang==="fr"?"Facture manuelle":"Manual invoice",icon:lucide.Building,color:THEME.warning,
     info:lang==="fr"
       ?"Effectuez un virement bancaire aux coordonnées ci-dessous et envoyez-nous la preuve de paiement."
       :"Make a bank transfer to the details below and send us proof of payment.",
     details:[{label:lang==="fr"?"Banque":"Bank",value:"Rawbank"},{label:lang==="fr"?"Compte":"Account",value:"00011-01234567890-42"},{label:lang==="fr"?"Référence":"Reference",value:current?.org_name||"KlassDoc"}]},
  ];

  return(
    <div>
      <PageHeader title={lang==="fr"?"Abonnement":"Subscription"} subtitle={lang==="fr"?"Gérez votre plan et facturation":"Manage your plan and billing"}/>

      {/* No subscription info banner */}
      {!current&&plans.length>0&&(
        <div style={{...S.card,marginBottom:24,borderLeft:`3px solid ${THEME.info}`,display:"flex",alignItems:"center",gap:14}}>
          <lucide.Info size={20} color={THEME.info}/>
          <span style={{fontSize:13,color:THEME.textMuted}}>{lang==="fr"?"Sélectionnez un plan pour commencer":"Select a plan to get started"}</span>
        </div>
      )}

      {/* Trial countdown banner */}
      {current?.is_trial&&current?.trial_days_left!=null&&(
        <motion.div initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}} style={{marginBottom:20,padding:"12px 20px",borderRadius:10,background:current.trial_days_left<=3?THEME.dangerDim:THEME.warningDim,border:`1px solid ${current.trial_days_left<=3?THEME.danger:THEME.warning}30`,display:"flex",alignItems:"center",gap:12}}>
          <lucide.Clock size={18} color={current.trial_days_left<=3?THEME.danger:THEME.warning}/>
          <span style={{fontSize:13,color:THEME.text}}>
            {lang==="fr"
              ?`Période d'essai : ${current.trial_days_left} jour${current.trial_days_left!==1?"s":""} restant${current.trial_days_left!==1?"s":""}`
              :`Trial period: ${current.trial_days_left} day${current.trial_days_left!==1?"s":""} remaining`}
          </span>
        </motion.div>
      )}

      {current&&(<motion.div initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} style={{...S.card,marginBottom:24,borderLeft:`3px solid ${planColors[current.plan_name]||THEME.accent}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:16}}>
          <div><div style={{fontSize:12,color:THEME.textDim,textTransform:"uppercase",marginBottom:6}}>{lang==="fr"?"Plan actuel":"Current Plan"}</div><div style={{fontSize:22,fontWeight:700,color:THEME.text}}>{current.plan_display||current.plan_name}</div><div style={{fontSize:13,color:THEME.textMuted,marginTop:4}}>{current.org_name||current.name}</div></div>
          <div style={{textAlign:"right"}}><div style={{fontSize:28,fontWeight:700,color:THEME.accent}}>${current.price_monthly||0}<span style={{fontSize:14,fontWeight:400,color:THEME.textDim}}>/mo</span></div></div>
        </div>
        {usage&&(<div style={{marginTop:20,display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
          {[{label:lang==="fr"?"Utilisateurs":"Users",used:usage.users,max:current.max_users,icon:lucide.Users},{label:"Documents",used:usage.documents,max:current.max_documents,icon:lucide.FileText},{label:lang==="fr"?"Stockage":"Storage",used:Math.round(usage.storage_bytes/(1024*1024*1024)*100)/100,max:current.max_storage_gb,unit:"GB",icon:lucide.HardDrive}].map(m=>{const pct=m.max>0?Math.min(100,Math.round(m.used/m.max*100)):0;const isUnlimited=m.max<0;const isWarning=pct>=80;const isDanger=pct>=95;return(
            <div key={m.label} style={{padding:16,background:THEME.surface,borderRadius:10,border:`1px solid ${isDanger?THEME.danger+"40":isWarning?THEME.warning+"40":THEME.borderSubtle}`}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}><m.icon size={14} color={THEME.textMuted}/><span style={{fontSize:12,color:THEME.textMuted}}>{m.label}</span></div>
              <div style={{fontSize:18,fontWeight:700,color:THEME.text}}>{m.used}{m.unit?" "+m.unit:""} <span style={{fontSize:12,fontWeight:400,color:THEME.textDim}}>/ {isUnlimited?"∞":m.max+(m.unit?" "+m.unit:"")}</span></div>
              {!isUnlimited&&<div style={{marginTop:8,height:4,borderRadius:2,background:THEME.border}}><div style={{height:"100%",borderRadius:2,background:isDanger?THEME.danger:isWarning?THEME.warning:THEME.success,width:pct+"%",transition:"width 0.5s"}}/></div>}
              {isWarning&&!isUnlimited&&<div style={{marginTop:6,fontSize:11,color:isDanger?THEME.danger:THEME.warning}}>{isDanger?(lang==="fr"?"Limite presque atteinte !":"Limit almost reached!"):(lang==="fr"?"Utilisation élevée":"High usage")}</div>}
            </div>);})}
        </div>)}
      </motion.div>)}

      <h3 style={{fontSize:16,fontWeight:600,color:THEME.text,marginBottom:16}}>{lang==="fr"?"Plans disponibles":"Available Plans"}</h3>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:16}}>
        {plans.map((plan,i)=>{const isCurrent=current?.plan_name===plan.name;const color=planColors[plan.name]||THEME.accent;const PIcon=planIcons[plan.name]||lucide.Package;const features=plan.features||{};return(
          <motion.div key={plan.id} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:i*0.08}} style={{...S.card,borderTop:`3px solid ${color}`,position:"relative",opacity:isCurrent?1:0.85}} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>{if(!isCurrent)e.currentTarget.style.opacity="0.85";}}>
            {isCurrent&&<div style={{position:"absolute",top:12,right:12,fontSize:10,fontWeight:700,padding:"3px 10px",borderRadius:20,background:color+"20",color,textTransform:"uppercase"}}>{lang==="fr"?"Actuel":"Current"}</div>}
            <PIcon size={24} color={color} style={{marginBottom:12}}/><div style={{fontSize:18,fontWeight:700,color:THEME.text}}>{plan.display_name}</div>
            <div style={{fontSize:28,fontWeight:700,color,marginTop:8}}>${plan.price_monthly}<span style={{fontSize:13,fontWeight:400,color:THEME.textDim}}>/mo</span></div>
            <div style={{fontSize:12,color:THEME.textDim,marginTop:2}}>${plan.price_yearly}/yr</div>
            <div style={{marginTop:16,display:"flex",flexDirection:"column",gap:8}}>
              {[{t:plan.max_users<0?"Unlimited users":`${plan.max_users} users`,on:true},{t:plan.max_storage_gb<0?"Unlimited storage":`${plan.max_storage_gb} GB storage`,on:true},{t:plan.max_documents<0?"Unlimited docs":`${plan.max_documents.toLocaleString()} documents`,on:true},{t:"Audit Log",on:features.audit_log},{t:"CSV Export",on:features.csv_export},{t:"API Access",on:features.api_access},{t:"Self-hosted",on:features.self_hosted},{t:"Priority Support",on:features.priority_support}].map((f,fi)=>(<div key={fi} style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:f.on?THEME.text:THEME.textDim}}>{f.on?<lucide.Check size={13} color={color}/>:<lucide.X size={13} color={THEME.textDim}/>}{f.t}</div>))}
            </div>
            {!isCurrent&&<button disabled={changingPlan===plan.id} style={{...S.btn,width:"100%",justifyContent:"center",marginTop:16,background:color+"18",color,border:`1px solid ${color}40`,opacity:changingPlan===plan.id?0.6:1}} onClick={()=>handleChangePlan(plan)}>{changingPlan===plan.id?<Spinner size={14}/>:null}{lang==="fr"?"Choisir":"Choose"}</button>}
          </motion.div>);})}
      </div>

      <h3 style={{fontSize:16,fontWeight:600,color:THEME.text,marginTop:32,marginBottom:16}}>{lang==="fr"?"Moyens de paiement":"Payment Methods"}</h3>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14}}>
        {paymentMethods.map((pm,i)=>(<motion.div key={pm.id} initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:0.3+i*0.08}} onClick={()=>setPaymentModal(pm)} style={{...S.card,display:"flex",alignItems:"center",gap:14,cursor:"pointer",transition:"all 0.2s"}} onMouseEnter={e=>{e.currentTarget.style.borderColor=pm.color+"50";e.currentTarget.style.transform="translateY(-2px)";}} onMouseLeave={e=>{e.currentTarget.style.borderColor=THEME.border;e.currentTarget.style.transform="translateY(0)";}}>
          <div style={{width:42,height:42,borderRadius:10,background:pm.color+"14",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><pm.icon size={20} color={pm.color}/></div>
          <div style={{flex:1}}><div style={{fontSize:14,fontWeight:600,color:THEME.text}}>{pm.name}</div><div style={{fontSize:12,color:THEME.textDim,marginTop:2}}>{pm.desc}</div></div>
          <lucide.ChevronRight size={16} color={THEME.textDim}/>
        </motion.div>))}
      </div>

      {/* Payment method detail modal */}
      <AnimatePresence>{paymentModal&&(
        <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)setPaymentModal(null);}}>
          <motion.div initial={{opacity:0,scale:0.92,y:16}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.92}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:480,overflow:"hidden",boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
            <div style={{padding:"22px 24px 16px",display:"flex",alignItems:"center",gap:14,borderBottom:`1px solid ${THEME.borderSubtle}`}}>
              <div style={{width:44,height:44,borderRadius:10,background:paymentModal.color+"14",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><paymentModal.icon size={22} color={paymentModal.color}/></div>
              <div style={{flex:1}}><div style={{fontSize:16,fontWeight:600,color:THEME.text}}>{paymentModal.name}</div><div style={{fontSize:12,color:THEME.textDim}}>{paymentModal.desc}</div></div>
              <lucide.X size={18} color={THEME.textDim} style={{cursor:"pointer"}} onClick={()=>setPaymentModal(null)}/>
            </div>
            <div style={{padding:"18px 24px"}}>
              <p style={{fontSize:13,color:THEME.textMuted,lineHeight:1.6,marginBottom:18}}>{paymentModal.info}</p>
              <div style={{background:THEME.surface,borderRadius:10,padding:16,border:`1px solid ${THEME.borderSubtle}`}}>
                {paymentModal.details.map((d,di)=>(
                  <div key={di} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:di<paymentModal.details.length-1?`1px solid ${THEME.borderSubtle}`:"none"}}>
                    <span style={{fontSize:12,color:THEME.textDim}}>{d.label}</span>
                    <span style={{fontSize:13,fontWeight:600,color:THEME.text,fontFamily:FONT_MONO}}>{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{padding:"12px 24px 20px",display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>{navigator.clipboard.writeText(paymentModal.details.map(d=>`${d.label}: ${d.value}`).join("\n"));toast(lang==="fr"?"Copié !":"Copied!","success");}} style={{...S.btn,padding:"9px 18px",fontSize:13,background:paymentModal.color+"18",color:paymentModal.color,border:`1px solid ${paymentModal.color}40`}}><lucide.Copy size={14}/> {lang==="fr"?"Copier les détails":"Copy Details"}</button>
              <button onClick={()=>setPaymentModal(null)} style={{...S.btn,padding:"9px 18px",fontSize:13,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}>{lang==="fr"?"Fermer":"Close"}</button>
            </div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>
    </div>
  );
}

// ─── ADMIN PORTAL — Enhanced with Plan Editor (Sprint 5 Backlog) ───
// Sprint 10C Session C1 H5 fix: page-level guard to match the sidebar's
// superAdminOnly flag. Previously the sidebar filter was the only gate;
// any navigateTo("admin") call (including the "Control Center" dropdown
// and any future event-bus route) would render this page for anyone.
// Hits cross-org endpoints (/subscriptions/admin/*) so defense-in-depth
// matters here more than anywhere else on the SPA.
function AdminPortalPage(){
  const {toast,refreshSignal,triggerRefresh,user}=useApp();const {lang}=useLang();
  const [stats,setStats]=useState(null);const [orgs,setOrgs]=useState([]);const [payments,setPayments]=useState([]);
  const [plans,setPlans]=useState([]);const [tab,setTab]=useState("overview");
  const [showPayment,setShowPayment]=useState(false);const [payForm,setPayForm]=useState({organization_id:"",amount:"",payment_method:"bank_transfer",description:""});
  // Plan editor state
  const [editingPlan,setEditingPlan]=useState(null);const [planForm,setPlanForm]=useState({});
  const [showNewPlan,setShowNewPlan]=useState(false);
  const [newPlanForm,setNewPlanForm]=useState({name:"",display_name:"",price_monthly:"",price_yearly:"",max_users:"10",max_storage_gb:"5",max_documents:"500"});
  // Sprint 10C post-F — tenant provisioning + offboarding
  const [showNewOrg,setShowNewOrg]=useState(false);
  const [newOrgForm,setNewOrgForm]=useState({name:"",country:"DR Congo",plan_id:"",admin_name:"",admin_email:"",status:"trial",trial_days:30});
  const [creatingOrg,setCreatingOrg]=useState(false);
  const [newOrgResult,setNewOrgResult]=useState(null); // {organization, admin_user, initial_password}
  const [deleteOrgTarget,setDeleteOrgTarget]=useState(null); // {id, name}
  const [deleteConfirmName,setDeleteConfirmName]=useState("");
  const [deletingOrg,setDeletingOrg]=useState(false);

  const allowed=isSuperAdmin(user);

  useEffect(()=>{
    if(!allowed)return;
    api.get("/subscriptions/admin/stats").then(setStats).catch(()=>{});
    api.get("/subscriptions/admin/organizations").then(r=>setOrgs(r.data||[])).catch(()=>{});
    api.get("/subscriptions/admin/payments").then(r=>setPayments(r.data||[])).catch(()=>{});
    api.get("/subscriptions/plans").then(r=>setPlans(r.data||[])).catch(()=>{});
  },[refreshSignal,allowed]);

  if(!allowed)return <AccessDeniedPage requiredRole="super_admin"/>;

  async function updateOrg(orgId,updates){try{await api.put(`/subscriptions/admin/organizations/${orgId}`,updates);toast(lang==="fr"?"Organisation mise à jour":"Organization updated","success");triggerRefresh();}catch(err){toast(err.message,"error");}}

  // Sprint 10C post-F: tenant provisioning. Success path opens a
  // one-shot result modal showing the initial password — super-admin
  // must save it before closing. Then clear the form and refresh.
  async function createOrg(){
    if(!newOrgForm.name.trim()){toast(lang==="fr"?"Nom requis":"Name required","error");return;}
    if(!newOrgForm.plan_id){toast(lang==="fr"?"Plan requis":"Plan required","error");return;}
    if(!newOrgForm.admin_email||!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newOrgForm.admin_email)){toast(lang==="fr"?"E-mail administrateur invalide":"Invalid admin email","error");return;}
    setCreatingOrg(true);
    try{
      const res=await api.post("/subscriptions/admin/organizations",{
        name:newOrgForm.name.trim(),
        country:newOrgForm.country||null,
        plan_id:parseInt(newOrgForm.plan_id,10),
        admin_name:newOrgForm.admin_name||null,
        admin_email:newOrgForm.admin_email.trim().toLowerCase(),
        status:newOrgForm.status||"trial",
        trial_days:parseInt(newOrgForm.trial_days,10)||30,
        lang, // Sprint 11.1a — owner receives activation email in their preferred language
      });
      setNewOrgResult(res);
      setShowNewOrg(false);
      setNewOrgForm({name:"",country:"DR Congo",plan_id:"",admin_name:"",admin_email:"",status:"trial",trial_days:30});
      triggerRefresh();
    }catch(err){
      // Sprint 11.1a: surface the full backend message so the super-admin
      // sees the actionable hint ("delete the org first") instead of a
      // generic "Create failed". Toast supports long copy; we keep the
      // modal open so the form values aren't lost.
      const msg=err?.message||"Create failed";
      toast(msg,"error");
    }
    setCreatingOrg(false);
  }

  // Sprint 11.1a: re-send activation email for a pending org owner.
  // Low-risk one-click (just regenerates the token + sends email),
  // gated by super-admin role on the backend. Toast result.
  async function resendActivation(org){
    try{
      const res=await api.post(`/subscriptions/admin/organizations/${org.id}/resend-activation`,{lang});
      const msg=res?.message||(lang==="fr"?"E-mail envoyé":"Email sent");
      toast(msg,res?.email_sent?"success":"warning");
    }catch(err){
      toast(err?.message||(lang==="fr"?"Échec du renvoi":"Resend failed"),"error");
    }
  }

  // Destructive — requires the super-admin to type the org name exactly.
  async function deleteOrg(){
    if(!deleteOrgTarget)return;
    if(deleteConfirmName!==deleteOrgTarget.name){
      toast(lang==="fr"?"Le nom saisi ne correspond pas":"Typed name doesn't match","error");
      return;
    }
    setDeletingOrg(true);
    try{
      await api.del(`/subscriptions/admin/organizations/${deleteOrgTarget.id}`,{confirm_name:deleteOrgTarget.name});
      toast(lang==="fr"?`Organisation "${deleteOrgTarget.name}" supprimée`:`Organization "${deleteOrgTarget.name}" deleted`,"success");
      setDeleteOrgTarget(null);
      setDeleteConfirmName("");
      triggerRefresh();
    }catch(err){toast(err?.message||"Delete failed","error");}
    setDeletingOrg(false);
  }
  async function recordPayment(){
    if(!payForm.organization_id){toast(lang==="fr"?"Sélectionnez une organisation":"Select an organization","error");return;}
    if(!payForm.amount||parseFloat(payForm.amount)<=0){toast(lang==="fr"?"Montant invalide":"Invalid amount","error");return;}
    try{await api.post("/subscriptions/admin/payments",{...payForm,amount:parseFloat(payForm.amount)});toast(lang==="fr"?"Paiement enregistré":"Payment recorded","success");setShowPayment(false);setPayForm({organization_id:"",amount:"",payment_method:"bank_transfer",description:""});triggerRefresh();}catch(err){toast(err.message,"error");}
  }

  function startEditPlan(plan){setEditingPlan(plan.id);setPlanForm({display_name:plan.display_name,price_monthly:plan.price_monthly,price_yearly:plan.price_yearly,max_users:plan.max_users,max_storage_gb:plan.max_storage_gb,max_documents:plan.max_documents});}
  async function savePlan(planId){
    try{await api.put(`/subscriptions/admin/plans/${planId}`,{...planForm,price_monthly:parseFloat(planForm.price_monthly),price_yearly:parseFloat(planForm.price_yearly),max_users:parseInt(planForm.max_users),max_storage_gb:parseFloat(planForm.max_storage_gb),max_documents:parseInt(planForm.max_documents)});toast(lang==="fr"?"Plan mis à jour":"Plan updated","success");setEditingPlan(null);triggerRefresh();}catch(err){toast(err.message,"error");}
  }
  async function createPlan(){
    if(!newPlanForm.name||!newPlanForm.display_name){toast(lang==="fr"?"Nom requis":"Name required","error");return;}
    try{await api.post("/subscriptions/admin/plans",{...newPlanForm,price_monthly:parseFloat(newPlanForm.price_monthly)||0,price_yearly:parseFloat(newPlanForm.price_yearly)||0,max_users:parseInt(newPlanForm.max_users)||10,max_storage_gb:parseFloat(newPlanForm.max_storage_gb)||5,max_documents:parseInt(newPlanForm.max_documents)||500});toast(lang==="fr"?"Plan créé":"Plan created","success");setShowNewPlan(false);setNewPlanForm({name:"",display_name:"",price_monthly:"",price_yearly:"",max_users:"10",max_storage_gb:"5",max_documents:"500"});triggerRefresh();}catch(err){toast(err.message,"error");}
  }

  const tabs=[{id:"overview",label:lang==="fr"?"Vue d'ensemble":"Overview",icon:lucide.BarChart3},{id:"orgs",label:lang==="fr"?"Organisations":"Organizations",icon:lucide.Building2},{id:"plans",label:"Plans",icon:lucide.Package},{id:"payments",label:lang==="fr"?"Paiements":"Payments",icon:lucide.CreditCard}];

  const LBL={display:"block",fontSize:11,fontWeight:600,color:THEME.textDim,textTransform:"uppercase",marginBottom:4,letterSpacing:"0.03em"};

  return(
    <div>
      <PageHeader title={lang==="fr"?"Portail Administrateur":"Admin Portal"} subtitle={lang==="fr"?"Gérer les abonnés et accès":"Manage subscribers and access"}/>
      <TabBar tabs={tabs} active={tab} onChange={setTab}/>

      {tab==="overview"&&stats&&(()=>{
        // Derived metrics for a more useful owner overview
        const trialCount=(orgs||[]).filter(o=>o.status==="trial").length;
        const suspendedCount=(orgs||[]).filter(o=>o.status==="suspended").length;
        const cancelledCount=(orgs||[]).filter(o=>o.status==="cancelled").length;
        const totalStorage=(orgs||[]).reduce((s,o)=>s+Number(o.storage_used||0),0);
        const totalDocs=(orgs||[]).reduce((s,o)=>s+Number(o.doc_count||0),0);
        const recentPayments=(payments||[]).slice(0,5);
        const monthStart=new Date();monthStart.setDate(1);monthStart.setHours(0,0,0,0);
        const mtdRevenue=(payments||[]).filter(p=>p.payment_status==="completed"&&new Date(p.created_at)>=monthStart).reduce((s,p)=>s+Number(p.amount||0),0);
        return(<div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:14,marginBottom:18}}>
            {[
              {label:lang==="fr"?"Organisations":"Organizations",value:stats.total_orgs,sub:`${stats.active_orgs} ${lang==="fr"?"actives":"active"}`,icon:lucide.Building2,color:THEME.info},
              {label:lang==="fr"?"En essai":"Trial",value:trialCount,sub:lang==="fr"?"en évaluation":"evaluating",icon:lucide.Clock,color:THEME.warning},
              {label:lang==="fr"?"Utilisateurs":"Users",value:stats.total_users,sub:lang==="fr"?"sur la plateforme":"across platform",icon:lucide.Users,color:THEME.accent},
              {label:lang==="fr"?"Documents":"Documents",value:totalDocs.toLocaleString(),sub:formatBytes(totalStorage),icon:lucide.FileText,color:"#9B6AD4"},
              {label:lang==="fr"?"Revenus totaux":"Total Revenue",value:`$${Number(stats.total_revenue||0).toLocaleString()}`,sub:`$${mtdRevenue.toFixed(2)} ${lang==="fr"?"ce mois":"this month"}`,icon:lucide.DollarSign,color:THEME.success},
            ].map((s,i)=>(
              <motion.div key={s.label} initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} transition={{delay:i*0.05}} style={{...S.card,padding:"14px 16px"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                  <div style={{width:32,height:32,borderRadius:8,background:s.color+"18",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><s.icon size={15} color={s.color}/></div>
                  <div style={{fontSize:11,color:THEME.textDim,textTransform:"uppercase",letterSpacing:0.3,fontWeight:600}}>{s.label}</div>
                </div>
                <div style={{fontSize:22,fontWeight:700,color:THEME.text,lineHeight:1}}>{s.value}</div>
                {s.sub&&<div style={{fontSize:11,color:THEME.textMuted,marginTop:4}}>{s.sub}</div>}
              </motion.div>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            {/* Status mix */}
            <div style={S.card}>
              <h3 style={{fontSize:14,fontWeight:600,color:THEME.text,marginBottom:14,display:"flex",alignItems:"center",gap:8}}><lucide.PieChart size={15} color={THEME.accent}/>{lang==="fr"?"État des tenants":"Tenant status"}</h3>
              {[
                {label:lang==="fr"?"Actifs":"Active",value:stats.active_orgs,color:THEME.success},
                {label:lang==="fr"?"En essai":"Trial",value:trialCount,color:THEME.warning},
                {label:lang==="fr"?"Suspendus":"Suspended",value:suspendedCount,color:THEME.danger},
                {label:lang==="fr"?"Annulés":"Cancelled",value:cancelledCount,color:THEME.textDim},
              ].map(row=>{const pct=stats.total_orgs>0?(row.value/stats.total_orgs)*100:0;return(
                <div key={row.label} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                    <span style={{color:THEME.text,fontWeight:500}}>{row.label}</span>
                    <span style={{color:THEME.textMuted}}>{row.value} ({pct.toFixed(0)}%)</span>
                  </div>
                  <div style={{height:5,borderRadius:3,background:THEME.borderSubtle,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:row.color,borderRadius:3,transition:"width 0.4s"}}/>
                  </div>
                </div>
              );})}
            </div>
            {/* Plans breakdown */}
            <div style={S.card}>
              <h3 style={{fontSize:14,fontWeight:600,color:THEME.text,marginBottom:14,display:"flex",alignItems:"center",gap:8}}><lucide.Package size={15} color={THEME.info}/>{lang==="fr"?"Répartition par plan":"Plans breakdown"}</h3>
              {(stats.plans_breakdown||[]).map(p=>{const pct=stats.total_orgs>0?(p.count/stats.total_orgs)*100:0;return(
                <div key={p.display_name} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                    <span style={{color:THEME.text,fontWeight:500}}>{p.display_name}</span>
                    <span style={{color:THEME.textMuted}}>{p.count} ({pct.toFixed(0)}%)</span>
                  </div>
                  <div style={{height:5,borderRadius:3,background:THEME.borderSubtle,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:THEME.info,borderRadius:3}}/>
                  </div>
                </div>
              );})}
            </div>
            {/* Recent payments */}
            <div style={{...S.card,gridColumn:"1 / -1"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                <h3 style={{fontSize:14,fontWeight:600,color:THEME.text,margin:0,display:"flex",alignItems:"center",gap:8}}><lucide.DollarSign size={15} color={THEME.success}/>{lang==="fr"?"Paiements récents":"Recent payments"}</h3>
                <button onClick={()=>setTab("payments")} style={{background:"transparent",border:"none",color:THEME.accent,fontSize:12,cursor:"pointer",fontFamily:FONT}}>{lang==="fr"?"Voir tout":"See all"}<lucide.ChevronRight size={13} style={{verticalAlign:-2,marginLeft:2}}/></button>
              </div>
              {recentPayments.length===0?<div style={{fontSize:12,color:THEME.textDim,padding:"12px 0",textAlign:"center"}}>{lang==="fr"?"Aucun paiement enregistré":"No payments recorded yet"}</div>
              :recentPayments.map((p,i)=>(
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:i<recentPayments.length-1?`1px solid ${THEME.borderSubtle}`:"none"}}>
                  <div style={{width:30,height:30,borderRadius:7,background:THEME.successDim,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><lucide.DollarSign size={13} color={THEME.success}/></div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:THEME.text}}>{p.org_name}</div>
                    <div style={{fontSize:11,color:THEME.textDim}}>{p.payment_method} · {p.description||"—"}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:14,fontWeight:700,color:THEME.success}}>${Number(p.amount||0).toLocaleString()}</div>
                    <div style={{fontSize:10,color:THEME.textDim}}>{formatDate(p.created_at,lang)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>);
      })()}

      {tab==="orgs"&&(<div>
        <div style={{marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:13,color:THEME.textMuted}}>{orgs.length} {lang==="fr"?"organisations":"organizations"}</span>
          <button onClick={()=>setShowNewOrg(true)} style={{...S.btn,background:THEME.accent,color:"#fff"}}><lucide.Plus size={16}/>{lang==="fr"?"Nouveau tenant":"Add Organization"}</button>
        </div>
        <div style={{...S.card,padding:0,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 130px 110px 70px 110px 110px 90px",gap:12,padding:"12px 20px",background:THEME.surface,borderBottom:`1px solid ${THEME.border}`,fontSize:11,fontWeight:600,color:THEME.textDim,textTransform:"uppercase"}}>
            <span>Organization</span><span>Plan</span><span>{lang==="fr"?"Utilisateurs":"Users"}</span><span>Docs</span><span>Status</span><span>Storage</span><span></span>
          </div>
          {orgs.length===0?<div style={{padding:40,textAlign:"center",color:THEME.textDim,fontSize:13}}>{lang==="fr"?"Aucune organisation — cliquez sur \"Nouveau tenant\" pour créer la première.":"No organizations — click \"Add Organization\" to create the first one."}</div>
          :orgs.map((org,i)=>{
            const plan=plans.find(p=>p.id===org.plan_id);
            const userLimit=plan&&plan.max_users>0?plan.max_users:null;
            const userPct=userLimit?(org.user_count/userLimit)*100:0;
            const userWarn=userPct>=90;
            return(
              <div key={org.id} style={{display:"grid",gridTemplateColumns:"1fr 130px 110px 70px 110px 110px 90px",gap:12,padding:"14px 20px",alignItems:"center",borderBottom:i<orgs.length-1?`1px solid ${THEME.borderSubtle}`:"none"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:THEME.text}}>{org.name}</div>
                  <div style={{fontSize:11,color:THEME.textDim}}>{org.country||"—"}</div>
                </div>
                <select value={org.plan_id||""} onChange={e=>updateOrg(org.id,{plan_id:parseInt(e.target.value)})} style={{...S.input,padding:"5px 8px",fontSize:12}}>{plans.map(p=><option key={p.id} value={p.id}>{p.display_name}</option>)}</select>
                <div>
                  <div style={{fontSize:13,color:userWarn?THEME.warning:THEME.text,fontWeight:userWarn?600:500}}>{org.user_count}{userLimit?` / ${userLimit}`:""}</div>
                  {userLimit&&<div style={{height:3,borderRadius:2,background:THEME.borderSubtle,marginTop:3,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.min(100,userPct)}%`,background:userWarn?THEME.warning:THEME.accent}}/></div>}
                </div>
                <span style={{fontSize:13,color:THEME.text}}>{org.doc_count}</span>
                <select value={org.status} onChange={e=>updateOrg(org.id,{status:e.target.value})} style={{...S.input,padding:"5px 8px",fontSize:12,color:org.status==="suspended"?THEME.danger:org.status==="trial"?THEME.warning:org.status==="cancelled"?THEME.textDim:org.status==="test"?THEME.info:THEME.success}}><option value="active">Active</option><option value="trial">Trial</option><option value="test">{lang==="fr"?"Test (interne)":"Test (internal)"}</option><option value="suspended">Suspended</option><option value="cancelled">Cancelled</option></select>
                <span style={{fontSize:11,color:THEME.textDim}}>{formatBytes(Number(org.storage_used||0))}</span>
                <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                  <button onClick={()=>resendActivation(org)} title={lang==="fr"?"Renvoyer l'e-mail d'activation":"Resend activation email"} style={{background:"transparent",border:`1px solid ${THEME.info}30`,borderRadius:6,padding:"5px 8px",cursor:"pointer",color:THEME.info,display:"flex",alignItems:"center",justifyContent:"center"}}><lucide.MailPlus size={13}/></button>
                  <button onClick={()=>{setDeleteOrgTarget({id:org.id,name:org.name});setDeleteConfirmName("");}} title={lang==="fr"?"Supprimer":"Delete"} style={{background:"transparent",border:`1px solid ${THEME.danger}30`,borderRadius:6,padding:"5px 8px",cursor:"pointer",color:THEME.danger,display:"flex",alignItems:"center",justifyContent:"center"}}><lucide.Trash2 size={13}/></button>
                </div>
              </div>
            );
          })}
        </div>

        {/* New tenant modal */}
        <AnimatePresence>{showNewOrg&&(
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget&&!creatingOrg)setShowNewOrg(false);}}>
            <motion.div initial={{opacity:0,scale:0.95,y:10}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.95}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:"100%",maxWidth:560,padding:0,boxShadow:"0 24px 80px rgba(0,0,0,0.5)",overflow:"hidden"}}>
              <div style={{padding:"18px 24px",borderBottom:`1px solid ${THEME.border}`,display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:40,height:40,borderRadius:10,background:THEME.accentDim,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><lucide.Building2 size={20} color={THEME.accent}/></div>
                <div style={{flex:1}}>
                  <div style={{fontSize:15,fontWeight:700,color:THEME.text}}>{lang==="fr"?"Nouveau tenant":"Add Organization"}</div>
                  <div style={{fontSize:11,color:THEME.textDim,marginTop:2}}>{lang==="fr"?"Provisionnez une organisation et son administrateur initial":"Provision an organization and its initial admin"}</div>
                </div>
                <button onClick={()=>!creatingOrg&&setShowNewOrg(false)} disabled={creatingOrg} style={{background:"transparent",border:"none",cursor:creatingOrg?"not-allowed":"pointer",padding:4}}><lucide.X size={18} color={THEME.textMuted}/></button>
              </div>
              <div style={{padding:"20px 24px"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                  <div><label style={LBL}>{lang==="fr"?"Nom de l'organisation":"Organization name"}*</label><input value={newOrgForm.name} onChange={e=>setNewOrgForm(f=>({...f,name:e.target.value}))} placeholder={lang==="fr"?"Cabinet XYZ":"Acme Law Firm"} style={{...S.input,fontSize:13}}/></div>
                  <div><label style={LBL}>{lang==="fr"?"Pays":"Country"}</label>
                    <select value={newOrgForm.country} onChange={e=>setNewOrgForm(f=>({...f,country:e.target.value}))} style={{...S.input,fontSize:13,cursor:"pointer"}}>
                      <optgroup label={lang==="fr"?"Fréquents":"Popular"}>
                        {COUNTRIES_POPULAR.map(c=><option key={"pop-"+c} value={c}>{c}</option>)}
                      </optgroup>
                      <optgroup label={lang==="fr"?"Tous les pays":"All countries"}>
                        {COUNTRIES_ALL.map(c=><option key={"all-"+c} value={c}>{c}</option>)}
                      </optgroup>
                    </select>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                  <div><label style={LBL}>Plan*</label><select value={newOrgForm.plan_id} onChange={e=>setNewOrgForm(f=>({...f,plan_id:e.target.value}))} style={{...S.input,fontSize:13}}><option value="">{lang==="fr"?"Choisir un plan...":"Choose a plan..."}</option>{plans.map(p=><option key={p.id} value={p.id}>{p.display_name} — ${p.price_monthly}/mo · {p.max_users>0?p.max_users+" users":"unlimited"}</option>)}</select></div>
                  <div><label style={LBL}>{lang==="fr"?"État initial":"Initial status"}</label><select value={newOrgForm.status} onChange={e=>setNewOrgForm(f=>({...f,status:e.target.value}))} style={{...S.input,fontSize:13}}><option value="trial">Trial</option><option value="active">Active</option><option value="test">{lang==="fr"?"Test (interne)":"Test (internal)"}</option></select></div>
                </div>
                {(newOrgForm.status==="trial"||newOrgForm.status==="test")&&(
                  <div style={{marginBottom:12}}><label style={LBL}>{newOrgForm.status==="test"?(lang==="fr"?"Durée d'évaluation (jours)":"Evaluation period (days)"):(lang==="fr"?"Durée de l'essai (jours)":"Trial days")}</label><input type="number" value={newOrgForm.trial_days} onChange={e=>setNewOrgForm(f=>({...f,trial_days:e.target.value}))} style={{...S.input,fontSize:13,width:140}}/></div>
                )}
                <div style={{height:1,background:THEME.borderSubtle,margin:"14px 0"}}/>
                <div style={{fontSize:11,fontWeight:600,color:THEME.textMuted,textTransform:"uppercase",letterSpacing:0.3,marginBottom:10}}>{lang==="fr"?"Administrateur initial":"Initial admin user"}</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
                  <div><label style={LBL}>{lang==="fr"?"Nom":"Name"}</label><input value={newOrgForm.admin_name} onChange={e=>setNewOrgForm(f=>({...f,admin_name:e.target.value}))} placeholder={lang==="fr"?"Optionnel":"Optional"} style={{...S.input,fontSize:13}}/></div>
                  <div><label style={LBL}>{lang==="fr"?"E-mail":"Email"}*</label><input type="email" value={newOrgForm.admin_email} onChange={e=>setNewOrgForm(f=>({...f,admin_email:e.target.value}))} placeholder="admin@cabinet-xyz.com" style={{...S.input,fontSize:13}}/></div>
                </div>
                <div style={{padding:10,background:THEME.infoDim,border:`1px solid ${THEME.info}40`,borderRadius:8,fontSize:11,color:THEME.textDim,lineHeight:1.55,display:"flex",gap:8}}>
                  <lucide.Mail size={13} color={THEME.info} style={{flexShrink:0,marginTop:2}}/>
                  <span>{lang==="fr"?"Un e-mail d'activation sera envoyé à l'administrateur. Il cliquera sur le lien pour choisir son mot de passe et accédera directement à son espace de travail. Aucun mot de passe n'est partagé par KlassDoc — c'est le propriétaire qui choisit le sien.":"An activation email will be sent to the admin. They'll click the link, choose their own password, and be taken straight to their workspace. KlassDoc never sends a password — the owner picks their own."}</span>
                </div>
              </div>
              <div style={{padding:"14px 24px",borderTop:`1px solid ${THEME.border}`,display:"flex",justifyContent:"flex-end",gap:10,background:THEME.surface}}>
                <button onClick={()=>setShowNewOrg(false)} disabled={creatingOrg} style={{padding:"9px 18px",borderRadius:8,background:"transparent",border:`1px solid ${THEME.border}`,color:THEME.textMuted,fontSize:13,cursor:creatingOrg?"not-allowed":"pointer",fontFamily:FONT}}>{lang==="fr"?"Annuler":"Cancel"}</button>
                <button onClick={createOrg} disabled={creatingOrg||!newOrgForm.name.trim()||!newOrgForm.plan_id||!newOrgForm.admin_email} style={{padding:"9px 20px",borderRadius:8,background:THEME.accent,color:"#fff",border:"none",fontSize:13,fontWeight:700,cursor:(creatingOrg||!newOrgForm.name.trim()||!newOrgForm.plan_id||!newOrgForm.admin_email)?"not-allowed":"pointer",fontFamily:FONT,opacity:(creatingOrg||!newOrgForm.name.trim()||!newOrgForm.plan_id||!newOrgForm.admin_email)?0.6:1,display:"flex",alignItems:"center",gap:8}}>
                  {creatingOrg?<Spinner size={14}/>:<><lucide.Building2 size={14}/>{lang==="fr"?"Créer":"Create"}</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}</AnimatePresence>

        {/* Sprint 11.1a result modal — activation email is the path now,
            not a plaintext password. Owner clicks the link in their inbox
            and sets their own password (Doanix parity). */}
        <AnimatePresence>{newOrgResult&&(
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.8)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)setNewOrgResult(null);}}>
            <motion.div initial={{scale:0.95}} animate={{scale:1}} style={{background:THEME.surfaceRaised,border:`1px solid ${(newOrgResult.email_sent?THEME.success:THEME.warning)}40`,borderRadius:14,width:"100%",maxWidth:520,padding:24,boxShadow:"0 24px 80px rgba(0,0,0,0.6)"}}>
              <div style={{textAlign:"center",marginBottom:18}}>
                <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:48,height:48,borderRadius:12,background:newOrgResult.email_sent?THEME.successDim:THEME.warningDim,marginBottom:12}}>
                  {newOrgResult.email_sent
                    ? <lucide.MailCheck size={24} color={THEME.success}/>
                    : <lucide.AlertTriangle size={24} color={THEME.warning}/>}
                </div>
                <h3 style={{fontSize:16,fontWeight:700,color:THEME.text,margin:"0 0 6px 0"}}>
                  {newOrgResult.reinvited
                    ? (lang==="fr"?"Lien d'activation renvoyé":"Activation link re-sent")
                    : (lang==="fr"?"Organisation créée":"Organization created")}
                </h3>
                <p style={{fontSize:12,color:THEME.textDim,margin:0,lineHeight:1.5}}>{newOrgResult.organization?.name} · {newOrgResult.admin_user?.email}</p>
                {newOrgResult.reinvited&&(
                  <p style={{fontSize:11,color:THEME.warning,margin:"6px 0 0 0",lineHeight:1.5}}>
                    {lang==="fr"
                      ?"Ce compte existait déjà en attente d'activation — l'organisation existante a été réutilisée."
                      :"This account was already pending activation — the existing organization was reused."}
                  </p>
                )}
              </div>
              {newOrgResult.email_sent
                ? <div style={{padding:14,background:THEME.successDim,border:`1px solid ${THEME.success}40`,borderRadius:10,marginBottom:16,fontSize:13,color:THEME.ink2,lineHeight:1.55}}>
                    {newOrgResult.reinvited
                      ? (lang==="fr"
                          ? <>Un nouvel e-mail d'activation a été envoyé à <strong style={{color:THEME.text}}>{newOrgResult.admin_user?.email}</strong>. L'ancien lien (s'il n'a pas encore été utilisé) est désormais invalide.</>
                          : <>A fresh activation email has been sent to <strong style={{color:THEME.text}}>{newOrgResult.admin_user?.email}</strong>. The previous link (if still unused) is now invalidated.</>)
                      : (lang==="fr"
                          ? <>Un e-mail d'activation a été envoyé à <strong style={{color:THEME.text}}>{newOrgResult.admin_user?.email}</strong>. Le nouveau propriétaire cliquera sur le lien, choisira son mot de passe, et accédera directement à son espace de travail.</>
                          : <>An activation email has been sent to <strong style={{color:THEME.text}}>{newOrgResult.admin_user?.email}</strong>. The new owner will click the link, choose their password, and go straight to their workspace.</>)}
                    <div style={{fontSize:11,color:THEME.textDim,marginTop:10,paddingTop:10,borderTop:`1px solid ${THEME.success}25`}}>
                      {lang==="fr"?"Le lien est personnel, à usage unique, et expire dans 24 heures.":"The link is personal, single-use, and expires in 24 hours."}
                    </div>
                  </div>
                : <div style={{padding:14,background:THEME.warningDim,border:`1px solid ${THEME.warning}50`,borderRadius:10,marginBottom:16}}>
                    <div style={{fontSize:11,color:THEME.warning,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8,display:"flex",alignItems:"center",gap:6}}><lucide.AlertTriangle size={13}/>{lang==="fr"?"E-mail non envoyé":"Email not sent"}</div>
                    <div style={{fontSize:12.5,color:THEME.ink2,lineHeight:1.55}}>
                      {lang==="fr"
                        ? <>L'organisation a été créée, mais l'e-mail d'activation n'a pas pu être livré. Demandez à votre infra de vérifier la configuration Resend.</>
                        : <>The organization was created, but the activation email could not be delivered. Ask your infra to check the Resend configuration.</>}
                    </div>
                    {newOrgResult.activation_token && (
                      <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${THEME.warning}30`}}>
                        <div style={{fontSize:10,color:THEME.textDim,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4,fontWeight:600}}>{lang==="fr"?"Lien d'activation (dev)":"Activation link (dev)"}</div>
                        <div style={{fontFamily:FONT_MONO,fontSize:11,color:THEME.text,userSelect:"all",padding:"6px 8px",background:THEME.surface,borderRadius:5,wordBreak:"break-all"}}>{window.location.origin}/?activate_token={newOrgResult.activation_token}</div>
                      </div>
                    )}
                  </div>}
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button onClick={()=>setNewOrgResult(null)} style={{padding:"9px 18px",borderRadius:8,background:THEME.accent,color:"#fff",border:"none",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:FONT}}>{lang==="fr"?"Fermer":"Close"}</button>
              </div>
            </motion.div>
          </motion.div>
        )}</AnimatePresence>

        {/* Destructive delete confirmation — name-match interstitial */}
        <AnimatePresence>{deleteOrgTarget&&(
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.8)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget&&!deletingOrg){setDeleteOrgTarget(null);setDeleteConfirmName("");}}}>
            <motion.div initial={{scale:0.95}} animate={{scale:1}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.danger}40`,borderRadius:14,width:"100%",maxWidth:460,padding:24,boxShadow:"0 24px 80px rgba(0,0,0,0.6)"}}>
              <div style={{textAlign:"center",marginBottom:16}}>
                <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:48,height:48,borderRadius:12,background:THEME.dangerDim,marginBottom:12}}><lucide.AlertTriangle size={24} color={THEME.danger}/></div>
                <h3 style={{fontSize:16,fontWeight:700,color:THEME.text,margin:"0 0 6px 0"}}>{lang==="fr"?"Supprimer définitivement ?":"Delete permanently?"}</h3>
                <p style={{fontSize:12,color:THEME.textDim,margin:0,lineHeight:1.55}}>
                  {lang==="fr"?"Tous les documents, dossiers, utilisateurs, paiements et journaux d'audit de ":"All documents, folders, users, payments, and audit logs for "}
                  <strong style={{color:THEME.text}}>{deleteOrgTarget.name}</strong>
                  {lang==="fr"?" seront supprimés. Action irréversible.":" will be deleted. Cannot be undone."}
                </p>
              </div>
              <div style={{marginBottom:16}}>
                <label style={{...LBL,marginBottom:6}}>{lang==="fr"?"Tapez le nom de l'organisation pour confirmer :":"Type the organization name to confirm:"}</label>
                <input value={deleteConfirmName} onChange={e=>setDeleteConfirmName(e.target.value)} placeholder={deleteOrgTarget.name} autoFocus style={{...S.input,fontSize:13,fontFamily:FONT_MONO,background:THEME.surface}}/>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button onClick={()=>{setDeleteOrgTarget(null);setDeleteConfirmName("");}} disabled={deletingOrg} style={{padding:"9px 18px",borderRadius:8,background:"transparent",border:`1px solid ${THEME.border}`,color:THEME.textMuted,fontSize:13,cursor:deletingOrg?"not-allowed":"pointer",fontFamily:FONT}}>{lang==="fr"?"Annuler":"Cancel"}</button>
                <button onClick={deleteOrg} disabled={deletingOrg||deleteConfirmName!==deleteOrgTarget.name} style={{padding:"9px 20px",borderRadius:8,background:THEME.danger,color:"#fff",border:"none",fontSize:13,fontWeight:700,cursor:(deletingOrg||deleteConfirmName!==deleteOrgTarget.name)?"not-allowed":"pointer",fontFamily:FONT,opacity:(deletingOrg||deleteConfirmName!==deleteOrgTarget.name)?0.5:1,display:"flex",alignItems:"center",gap:8}}>
                  {deletingOrg?<Spinner size={14}/>:<><lucide.Trash2 size={14}/>{lang==="fr"?"Supprimer définitivement":"Delete permanently"}</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}</AnimatePresence>
      </div>)}

      {/* ── PLANS TAB — Edit pricing, limits, create new plans ── */}
      {tab==="plans"&&(<div>
        <div style={{marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:13,color:THEME.textMuted}}>{plans.length} {lang==="fr"?"plans configurés":"plans configured"}</span>
          <button onClick={()=>setShowNewPlan(!showNewPlan)} style={{...S.btn,background:THEME.accent,color:"#fff"}}><lucide.Plus size={16}/> {lang==="fr"?"Nouveau plan":"New Plan"}</button>
        </div>
        {/* New plan form */}
        <AnimatePresence>{showNewPlan&&(<motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{overflow:"hidden",marginBottom:20}}>
          <div style={{...S.card,borderLeft:`3px solid ${THEME.success}`}}>
            <div style={{fontSize:14,fontWeight:600,color:THEME.text,marginBottom:16}}>{lang==="fr"?"Créer un nouveau plan":"Create New Plan"}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div><label style={LBL}>{lang==="fr"?"Identifiant":"Slug"}</label><input value={newPlanForm.name} onChange={e=>setNewPlanForm(f=>({...f,name:e.target.value.toLowerCase().replace(/\s/g,"_")}))} placeholder="e.g. starter" style={{...S.input,fontSize:13}}/></div>
              <div><label style={LBL}>{lang==="fr"?"Nom affiché":"Display Name"}</label><input value={newPlanForm.display_name} onChange={e=>setNewPlanForm(f=>({...f,display_name:e.target.value}))} placeholder="e.g. Starter" style={{...S.input,fontSize:13}}/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:12,marginBottom:16}}>
              <div><label style={LBL}>{lang==="fr"?"Prix/mois ($)":"Price/mo ($)"}</label><input value={newPlanForm.price_monthly} onChange={e=>setNewPlanForm(f=>({...f,price_monthly:e.target.value}))} type="number" step="0.01" style={{...S.input,fontSize:13}}/></div>
              <div><label style={LBL}>{lang==="fr"?"Prix/an ($)":"Price/yr ($)"}</label><input value={newPlanForm.price_yearly} onChange={e=>setNewPlanForm(f=>({...f,price_yearly:e.target.value}))} type="number" step="0.01" style={{...S.input,fontSize:13}}/></div>
              <div><label style={LBL}>{lang==="fr"?"Max utilisateurs":"Max Users"}</label><input value={newPlanForm.max_users} onChange={e=>setNewPlanForm(f=>({...f,max_users:e.target.value}))} type="number" style={{...S.input,fontSize:13}}/><div style={{fontSize:10,color:THEME.textDim,marginTop:2}}>-1 = {lang==="fr"?"illimité":"unlimited"}</div></div>
              <div><label style={LBL}>{lang==="fr"?"Stockage (GB)":"Storage (GB)"}</label><input value={newPlanForm.max_storage_gb} onChange={e=>setNewPlanForm(f=>({...f,max_storage_gb:e.target.value}))} type="number" style={{...S.input,fontSize:13}}/></div>
              <div><label style={LBL}>Max Docs</label><input value={newPlanForm.max_documents} onChange={e=>setNewPlanForm(f=>({...f,max_documents:e.target.value}))} type="number" style={{...S.input,fontSize:13}}/></div>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setShowNewPlan(false)} style={{...S.btn,padding:"8px 18px",fontSize:13,background:THEME.surfaceHover,color:THEME.textDim,border:`1px solid ${THEME.border}`}}>{lang==="fr"?"Annuler":"Cancel"}</button>
              <button onClick={createPlan} style={{...S.btn,padding:"8px 18px",fontSize:13,background:THEME.success,color:"#fff"}}><lucide.Check size={14}/> {lang==="fr"?"Créer":"Create"}</button>
            </div>
          </div>
        </motion.div>)}</AnimatePresence>
        {/* Existing plans — inline edit */}
        <div style={{display:"grid",gap:14}}>
          {plans.map(plan=>{const isEditing=editingPlan===plan.id;return(
            <motion.div key={plan.id} layout style={{...S.card,borderLeft:`3px solid ${THEME.accent}`}}>
              {!isEditing?(
                <div style={{display:"flex",alignItems:"center",gap:16}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:15,fontWeight:600,color:THEME.text}}>{plan.display_name}</div>
                    <div style={{fontSize:12,color:THEME.textDim,marginTop:2}}>{plan.name}</div>
                  </div>
                  <div style={{textAlign:"center",minWidth:90}}>
                    <div style={{fontSize:18,fontWeight:700,color:THEME.accent}}>${plan.price_monthly}</div>
                    <div style={{fontSize:10,color:THEME.textDim}}>/mo · ${plan.price_yearly}/yr</div>
                  </div>
                  <div style={{display:"flex",gap:16,fontSize:12,color:THEME.textMuted}}>
                    <span>{plan.max_users<0?"∞":plan.max_users} {lang==="fr"?"util.":"users"}</span>
                    <span>{plan.max_storage_gb<0?"∞":plan.max_storage_gb} GB</span>
                    <span>{plan.max_documents<0?"∞":plan.max_documents.toLocaleString()} docs</span>
                  </div>
                  <button onClick={()=>startEditPlan(plan)} style={{...S.btn,padding:"6px 14px",fontSize:12,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}><lucide.Pencil size={13}/> {lang==="fr"?"Modifier":"Edit"}</button>
                </div>
              ):(
                <div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                    <div><label style={LBL}>{lang==="fr"?"Nom affiché":"Display Name"}</label><input value={planForm.display_name} onChange={e=>setPlanForm(f=>({...f,display_name:e.target.value}))} style={{...S.input,fontSize:13}}/></div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                      <div><label style={LBL}>{lang==="fr"?"Prix/mois ($)":"Price/mo ($)"}</label><input value={planForm.price_monthly} onChange={e=>setPlanForm(f=>({...f,price_monthly:e.target.value}))} type="number" step="0.01" style={{...S.input,fontSize:13}}/></div>
                      <div><label style={LBL}>{lang==="fr"?"Prix/an ($)":"Price/yr ($)"}</label><input value={planForm.price_yearly} onChange={e=>setPlanForm(f=>({...f,price_yearly:e.target.value}))} type="number" step="0.01" style={{...S.input,fontSize:13}}/></div>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
                    <div><label style={LBL}>{lang==="fr"?"Max utilisateurs":"Max Users"}</label><input value={planForm.max_users} onChange={e=>setPlanForm(f=>({...f,max_users:e.target.value}))} type="number" style={{...S.input,fontSize:13}}/><div style={{fontSize:10,color:THEME.textDim,marginTop:2}}>-1 = {lang==="fr"?"illimité":"unlimited"}</div></div>
                    <div><label style={LBL}>{lang==="fr"?"Stockage (GB)":"Storage (GB)"}</label><input value={planForm.max_storage_gb} onChange={e=>setPlanForm(f=>({...f,max_storage_gb:e.target.value}))} type="number" style={{...S.input,fontSize:13}}/></div>
                    <div><label style={LBL}>Max Docs</label><input value={planForm.max_documents} onChange={e=>setPlanForm(f=>({...f,max_documents:e.target.value}))} type="number" style={{...S.input,fontSize:13}}/></div>
                  </div>
                  <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                    <button onClick={()=>setEditingPlan(null)} style={{...S.btn,padding:"7px 16px",fontSize:12,background:THEME.surfaceHover,color:THEME.textDim,border:`1px solid ${THEME.border}`}}>{lang==="fr"?"Annuler":"Cancel"}</button>
                    <button onClick={()=>savePlan(plan.id)} style={{...S.btn,padding:"7px 16px",fontSize:12,background:THEME.accent,color:"#fff"}}><lucide.Check size={13}/> {lang==="fr"?"Enregistrer":"Save"}</button>
                  </div>
                </div>
              )}
            </motion.div>);})}
        </div>
      </div>)}

      {tab==="payments"&&(<div>
        <div style={{marginBottom:16}}><button onClick={()=>setShowPayment(!showPayment)} style={{...S.btn,background:THEME.accent,color:"#fff"}}><lucide.Plus size={16}/>{lang==="fr"?"Enregistrer un paiement":"Record Payment"}</button></div>
        <AnimatePresence>{showPayment&&(<motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} style={{overflow:"hidden",marginBottom:20}}>
          <div style={{...S.card,display:"flex",gap:12,alignItems:"flex-end",flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:200}}><label style={LBL}>Organization</label><select value={payForm.organization_id} onChange={e=>setPayForm(f=>({...f,organization_id:e.target.value}))} style={S.input}><option value="">Select...</option>{orgs.map(o=><option key={o.id} value={o.id}>{o.name}</option>)}</select></div>
            <div style={{width:120}}><label style={LBL}>{lang==="fr"?"Montant ($)":"Amount ($)"}</label><input value={payForm.amount} onChange={e=>setPayForm(f=>({...f,amount:e.target.value}))} style={S.input} type="number" step="0.01" min="0.01"/></div>
            <div style={{width:160}}><label style={LBL}>{lang==="fr"?"Méthode":"Method"}</label><select value={payForm.payment_method} onChange={e=>setPayForm(f=>({...f,payment_method:e.target.value}))} style={S.input}><option value="bank_transfer">{lang==="fr"?"Virement":"Bank Transfer"}</option><option value="mobile_money">Mobile Money</option><option value="card">{lang==="fr"?"Carte":"Card"}</option><option value="cash">{lang==="fr"?"Espèces":"Cash"}</option></select></div>
            <div style={{flex:1,minWidth:160}}><label style={LBL}>Description</label><input value={payForm.description} onChange={e=>setPayForm(f=>({...f,description:e.target.value}))} style={S.input} placeholder={lang==="fr"?"Détails du paiement...":"Payment details..."}/></div>
            <button onClick={recordPayment} style={{...S.btn,background:THEME.accent,color:"#fff",height:42}}><lucide.Check size={14}/>{lang==="fr"?"Enregistrer":"Record"}</button>
          </div>
        </motion.div>)}</AnimatePresence>
        <div style={{...S.card,padding:0,overflow:"hidden"}}>
          {payments.length===0?<div style={{padding:40,textAlign:"center",color:THEME.textDim}}>{lang==="fr"?"Aucun paiement":"No payments yet"}</div>
          :payments.map((p,i)=>(<div key={p.id} style={{display:"flex",alignItems:"center",gap:14,padding:"14px 20px",borderBottom:i<payments.length-1?`1px solid ${THEME.borderSubtle}`:"none"}}>
            <div style={{width:34,height:34,borderRadius:8,background:p.payment_status==="completed"?THEME.successDim:THEME.warningDim,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><lucide.DollarSign size={15} color={p.payment_status==="completed"?THEME.success:THEME.warning}/></div>
            <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:THEME.text}}>{p.org_name}</div><div style={{fontSize:12,color:THEME.textDim}}>{p.payment_method} · {p.description||"—"}</div></div>
            <div style={{textAlign:"right",marginRight:12}}><div style={{fontSize:15,fontWeight:700,color:THEME.success}}>${p.amount}</div><div style={{fontSize:11,color:THEME.textDim}}>{formatDate(p.created_at,lang)}</div></div>
            <div style={{display:"flex",gap:6,flexShrink:0}}>
              <button onClick={()=>{const newAmt=prompt(lang==="fr"?"Nouveau montant ($):":"New amount ($):",p.amount);if(newAmt&&!isNaN(parseFloat(newAmt))&&parseFloat(newAmt)>0){api.put(`/subscriptions/admin/payments/${p.id}`,{amount:parseFloat(newAmt)}).then(()=>{toast(lang==="fr"?"Paiement mis à jour":"Payment updated","success");triggerRefresh();}).catch(e=>toast(e.message,"error"));}}} style={{...S.btn,padding:"5px 10px",fontSize:11,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}} title={lang==="fr"?"Modifier":"Edit"}><lucide.Pencil size={12}/></button>
              <button onClick={()=>{if(confirm(lang==="fr"?`Supprimer ce paiement de $${p.amount} ?`:`Delete this $${p.amount} payment?`)){api.del(`/subscriptions/admin/payments/${p.id}`).then(()=>{toast(lang==="fr"?"Paiement supprimé":"Payment deleted","success");triggerRefresh();}).catch(e=>toast(e.message,"error"));}}} style={{...S.btn,padding:"5px 10px",fontSize:11,background:THEME.dangerDim,color:THEME.danger,border:`1px solid ${THEME.danger}30`}} title={lang==="fr"?"Supprimer":"Delete"}><lucide.Trash2 size={12}/></button>
            </div>
          </div>))}
        </div>
      </div>)}
    </div>
  );
}

// ─── APP SHELL ───
// ─── IDLE TIMEOUT GUARD — Sprint 12.x (2026-05-14) ───
// Mirrors the Doanix spec: 10 minutes of no user activity triggers a
// 5-minute warning dialog; if no activity during the warning, the user
// is signed out and tokens are cleared. Cross-tab synchronization via
// localStorage events means activity in any open tab keeps every tab
// signed in (and a logout in any tab signs out every tab). The
// visibilitychange handler covers mobile resume — when a phone wakes
// from screen-lock or the user comes back to a backgrounded tab, we
// reconcile against absolute wall-clock time rather than trusting the
// suspended setTimeout to have fired correctly.
//
// No backend change. Sign-out routes through the same path as the
// 401-detected session-expired flow (clear tokens + emit event + let
// AppShell switch to LoginPage), so the user lands cleanly on /login.
const IDLE_TIMEOUT_MS    = 10 * 60 * 1000;   // 10 min before warning
const WARNING_DURATION_MS = 5 * 60 * 1000;   // 5 min of warning before forced logout
const ACTIVITY_THROTTLE_MS = 1000;            // collapse mousemove storms
const ACTIVITY_WRITE_MS = 5000;               // throttle cross-tab broadcasts
const LS_LAST_ACTIVITY = "kd_last_activity";
const LS_LOGGED_OUT    = "kd_logged_out";

function IdleTimeoutGuard({onLogout}){
  const {lang}=useLang();
  const [warning,setWarning]=useState({open:false,seconds:0});
  // Stable refs so the long-lived useEffect doesn't see stale closures
  // when AppShell re-renders or the parent's onLogout identity flips.
  const onLogoutRef=useRef(onLogout); useEffect(()=>{onLogoutRef.current=onLogout;},[onLogout]);
  const warningOpenRef=useRef(false); warningOpenRef.current=warning.open;
  const noteActivityRef=useRef(null);

  useEffect(()=>{
    let lastActivity=Date.now();
    let lastWrite=0;
    let lastEventFire=0;
    let idleTimer=null;
    let warningInterval=null;

    function clearTimers(){
      if(idleTimer){clearTimeout(idleTimer);idleTimer=null;}
      if(warningInterval){clearInterval(warningInterval);warningInterval=null;}
    }
    function scheduleIdle(){
      clearTimers();
      idleTimer=setTimeout(()=>triggerWarning(),IDLE_TIMEOUT_MS);
    }
    function triggerWarning(initialSeconds){
      const seconds=initialSeconds??Math.floor(WARNING_DURATION_MS/1000);
      setWarning({open:true,seconds});
      warningInterval=setInterval(()=>{
        setWarning(prev=>{
          if(!prev.open)return prev;
          if(prev.seconds<=1){doLogout();return {open:false,seconds:0};}
          return {open:true,seconds:prev.seconds-1};
        });
      },1000);
    }
    function doLogout(){
      clearTimers();
      setWarning({open:false,seconds:0});
      // Broadcast to other tabs FIRST so they unmount before this tab's
      // onLogout runs and possibly hits a logout endpoint.
      try{localStorage.setItem(LS_LOGGED_OUT,String(Date.now()));}catch{}
      onLogoutRef.current?.();
    }
    function noteActivity(){
      const now=Date.now();
      lastActivity=now;
      if(now-lastWrite>ACTIVITY_WRITE_MS){
        lastWrite=now;
        try{localStorage.setItem(LS_LAST_ACTIVITY,String(now));}catch{}
      }
      if(warningOpenRef.current){
        clearTimers();
        setWarning({open:false,seconds:0});
      }
      scheduleIdle();
    }
    noteActivityRef.current=noteActivity;

    function onActivityEvent(){
      const now=Date.now();
      if(now-lastEventFire<ACTIVITY_THROTTLE_MS)return;
      lastEventFire=now;
      noteActivity();
    }
    const events=["mousemove","keydown","click","scroll","touchstart","touchmove","wheel"];
    events.forEach(e=>window.addEventListener(e,onActivityEvent,{passive:true}));

    function onStorage(e){
      if(e.key===LS_LAST_ACTIVITY && e.newValue){
        const ts=parseInt(e.newValue,10);
        if(Number.isFinite(ts) && ts>lastActivity){
          lastActivity=ts;
          if(warningOpenRef.current){clearTimers();setWarning({open:false,seconds:0});}
          scheduleIdle();
        }
      } else if(e.key===LS_LOGGED_OUT){
        // Another tab forced logout — follow along without writing back.
        clearTimers();
        setWarning({open:false,seconds:0});
        onLogoutRef.current?.();
      }
    }
    window.addEventListener("storage",onStorage);

    // Mobile resume + tab-foreground reconciliation. setTimeout in
    // backgrounded tabs is throttled by browsers (Chrome: 1 sec min,
    // Safari may pause entirely) so we can't trust the idle timer
    // alone to have fired across long pauses. On every visibility
    // flip to visible, reconcile against absolute wall-clock.
    function onVisibility(){
      if(document.visibilityState!=="visible")return;
      const sinceActive=Date.now()-lastActivity;
      if(sinceActive >= IDLE_TIMEOUT_MS+WARNING_DURATION_MS){
        doLogout();
      } else if(sinceActive >= IDLE_TIMEOUT_MS){
        const remaining=Math.max(1,Math.ceil((IDLE_TIMEOUT_MS+WARNING_DURATION_MS-sinceActive)/1000));
        clearTimers();
        triggerWarning(remaining);
      } else {
        scheduleIdle();
      }
    }
    document.addEventListener("visibilitychange",onVisibility);

    scheduleIdle();

    return ()=>{
      clearTimers();
      events.forEach(e=>window.removeEventListener(e,onActivityEvent));
      window.removeEventListener("storage",onStorage);
      document.removeEventListener("visibilitychange",onVisibility);
    };
  // Run once for the lifetime of AppShell.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  function stayActive(){
    noteActivityRef.current?.();
  }
  function logoutNow(){
    try{localStorage.setItem(LS_LOGGED_OUT,String(Date.now()));}catch{}
    onLogoutRef.current?.();
  }

  if(!warning.open) return null;

  const mm=String(Math.floor(warning.seconds/60)).padStart(2,"0");
  const ss=String(warning.seconds%60).padStart(2,"0");

  return (
    <div style={{position:"fixed",inset:0,zIndex:5000,background:"rgba(0,0,0,0.78)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,padding:"30px 32px",maxWidth:460,textAlign:"center",boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
        <div style={{width:56,height:56,borderRadius:"50%",background:THEME.warning+"1A",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
          <lucide.Clock size={26} color={THEME.warning}/>
        </div>
        <div style={{fontSize:18,fontWeight:700,color:THEME.text,marginBottom:10}}>
          {lang==="fr"?"Êtes-vous toujours là ?":"Are you still there?"}
        </div>
        <div style={{fontSize:13,color:THEME.textDim,lineHeight:1.6,marginBottom:8}}>
          {lang==="fr"
            ?"Vous serez déconnecté(e) automatiquement pour cause d'inactivité dans :"
            :"You will be signed out automatically due to inactivity in:"}
        </div>
        <div style={{fontSize:32,fontWeight:700,color:THEME.warning,fontFamily:FONT_MONO,letterSpacing:1,marginBottom:22}}>
          {mm}:{ss}
        </div>
        <div style={{display:"flex",gap:10,justifyContent:"center"}}>
          <button onClick={logoutNow} style={{...S.btn,padding:"10px 18px",fontSize:13,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}>
            <lucide.LogOut size={14}/>
            {lang==="fr"?"Se déconnecter":"Sign out"}
          </button>
          <button onClick={stayActive} style={{...S.btn,padding:"10px 20px",fontSize:13,background:THEME.accent,color:"#fff"}}>
            <lucide.Check size={14}/>
            {lang==="fr"?"Rester connecté(e)":"Stay signed in"}
          </button>
        </div>
        <div style={{marginTop:14,fontSize:11,color:THEME.textDim}}>
          {lang==="fr"
            ?"Toute activité (souris, clavier, défilement) annule également ce délai."
            :"Any activity (mouse, keyboard, scroll) also dismisses this warning."}
        </div>
      </div>
    </div>
  );
}

function AppShell({user,onLogout}){
  const [page,setPage]=useState(()=>localStorage.getItem("kd_default_tab")||"dashboard");const [toastData,setToastData]=useState(null);
  const [lang,setLang]=useState(()=>localStorage.getItem("af_lang")||"fr");
  const [refreshSignal,setRefreshSignal]=useState(0);const [initialSearchQuery,setInitialSearchQuery]=useState("");
  const [qsOpen,setQsOpen]=useState(false);const [qsQuery,setQsQuery]=useState("");const qsRef=useRef();
  const [userMenu,setUserMenu]=useState(false);const userMenuRef=useRef();
  const [selectedFolderId,setSelectedFolderId]=useState(null);
  const [showAbout,setShowAbout]=useState(false);const [showUserSettings,setShowUserSettings]=useState(false);
  const [defaultTab,setDefaultTab]=useState(()=>localStorage.getItem("kd_default_tab")||"dashboard");
  const [pwCurrent,setPwCurrent]=useState("");const [pwNew,setPwNew]=useState("");const [pwConfirm,setPwConfirm]=useState("");const [pwSaving,setPwSaving]=useState(false);const [pwShowNew,setPwShowNew]=useState(false);

  useEffect(()=>{localStorage.setItem("af_lang",lang);},[lang]);
  useEffect(()=>{const kd=e=>{if((e.metaKey||e.ctrlKey)&&e.key==="k"){e.preventDefault();setQsOpen(p=>!p);}if(e.key==="Escape"){setQsOpen(false);setUserMenu(false);}};window.addEventListener("keydown",kd);return()=>window.removeEventListener("keydown",kd);},[]);
  useEffect(()=>{if(qsOpen)setTimeout(()=>qsRef.current?.focus(),100);},[qsOpen]);
  useEffect(()=>{function close(e){if(userMenuRef.current&&!userMenuRef.current.contains(e.target))setUserMenu(false);}document.addEventListener("mousedown",close);return()=>document.removeEventListener("mousedown",close);},[]);

  function toast(msg,type="info"){setToastData({message:msg,type,id:Date.now()});setTimeout(()=>setToastData(null),4000);}
  function triggerRefresh(){setRefreshSignal(p=>p+1);}
  function handleQs(e){e.preventDefault();if(qsQuery.trim()){setInitialSearchQuery(qsQuery.trim());setPage("search");setQsOpen(false);setQsQuery("");}}

  // ─── Sprint 10C / Priority 8.1 — Central 401 handler bridge ───
  // ApiClient lives outside React. When it detects an unrecoverable 401
  // (refresh failed, or non-expired invalid token), it emits "session-expired".
  // We toast once in the current language, then log the user out so React
  // transitions to LoginPage cleanly — no window.reload, no /login href.
  // The de-dupe flag lives in ApiClient (_sessionExpiredFired) so burst 401s
  // from parallel in-flight requests only fire this handler once.
  // Ref lets the effect read current lang without re-subscribing on lang toggle.
  const langRef=useRef(lang);
  useEffect(()=>{langRef.current=lang;},[lang]);
  useEffect(()=>{
    const unsub=api.onAuthEvent(evt=>{
      if(evt==="session-expired"){
        toast(T(langRef.current,"sessionExpired"),"error");
        onLogout();
      }
    });
    return unsub;
  // Intentionally stable: we don't want to re-subscribe on toast/onLogout identity changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const pages={dashboard:DashboardPage,documents:DocumentsPage,folders:FoldersPage,search:SearchPage,users:UsersPage,departments:DepartmentsPage,audit:AuditLogPage,subscription:SubscriptionPage,admin:AdminPortalPage,courrier:CourrierPage,emails:EmailsPage,reports:ReportsPage,checkedout:CheckedOutPage};
  const ActivePage=pages[page]||DashboardPage;

  return(
    <LangContext.Provider value={{lang,setLang}}>
      <AppContext.Provider value={{user,toast,canEdit:user?.role==="admin"||user?.role==="editor",isAdmin:user?.role==="admin",refreshSignal,triggerRefresh,initialSearchQuery,setInitialSearchQuery,navigateTo:setPage,selectedFolderId,setSelectedFolderId}}>
        <IdleTimeoutGuard onLogout={onLogout}/>
        <div style={{display:"flex",height:"100vh",background:THEME.bg,fontFamily:FONT,color:THEME.text}}>
          <Sidebar active={page} onNav={setPage} user={user} onLogout={onLogout}/>
          <div style={{flex:1,overflow:"auto"}}>
            {/* ── iManage-style Top Bar — aligned borders with sidebar ── */}
            <div style={{position:"sticky",top:0,zIndex:50,background:THEME.bg+"E6",backdropFilter:"blur(12px)",borderBottom:`1px solid ${THEME.borderSubtle}`,padding:"10px 32px",display:"flex",alignItems:"center",gap:8}}>
              {/* Search: scope dropdown + wide bar + search icon — fills all available space */}
              <div style={{flex:1,display:"flex",alignItems:"center",gap:0,minWidth:0}}>
                <div style={{padding:"7px 12px",borderRadius:"8px 0 0 8px",background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRight:"none",display:"flex",alignItems:"center",gap:6,cursor:"pointer",height:36,fontSize:12,fontWeight:600,color:THEME.text,whiteSpace:"nowrap"}} onClick={()=>setQsOpen(true)}><lucide.FileText size={13} color={THEME.accent}/>{T(lang,"documents")}<lucide.ChevronDown size={11} color={THEME.textDim}/></div>
                <div style={{flex:1,padding:"7px 14px",background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderLeft:"none",borderRight:"none",display:"flex",alignItems:"center",gap:8,cursor:"pointer",height:36,minWidth:0}} onClick={()=>setQsOpen(true)}>
                  <span style={{fontSize:13,color:THEME.textDim,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{T(lang,"quickSearch")}</span>
                  <span style={{fontSize:10,fontFamily:FONT_MONO,color:THEME.textDim,padding:"2px 6px",background:THEME.surface,borderRadius:4,border:`1px solid ${THEME.border}`,flexShrink:0}}>⌘K</span>
                </div>
                <div style={{padding:"7px 10px",borderRadius:"0 8px 8px 0",background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderLeft:"none",display:"flex",alignItems:"center",height:36,cursor:"pointer",flexShrink:0}} onClick={()=>setQsOpen(true)}><lucide.Search size={14} color={THEME.textDim}/></div>
              </div>
              {/* Right cluster: tightly packed — lang, bell, user */}
              <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                <LangToggle lang={lang} setLang={setLang}/>
                <div style={{position:"relative",cursor:"pointer"}}><lucide.Bell size={18} color={THEME.textMuted}/><div style={{position:"absolute",top:-2,right:-2,width:7,height:7,borderRadius:"50%",background:THEME.accent,border:`2px solid ${THEME.bg}`}}/></div>
                {/* iManage user avatar + dropdown */}
                <div style={{position:"relative"}} ref={userMenuRef}>
                  <div onClick={()=>setUserMenu(!userMenu)} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"4px 8px",borderRadius:8,transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=THEME.surfaceHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <Avatar name={user?.name} size={32}/>
                    {/* Sprint 11.x: two-line identity pill — name on top,
                        job title underneath. If no job_title is set, falls
                        back to department name; if neither, shows just the
                        role. Mirrors the Doanix top-right pattern. */}
                    <div style={{display:"flex",flexDirection:"column",lineHeight:1.15,maxWidth:200,overflow:"hidden"}}>
                      <span style={{fontSize:12.5,color:THEME.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.name}</span>
                      <span style={{fontSize:10.5,color:THEME.textMuted,fontFamily:FONT_MONO,letterSpacing:"0.02em",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {user?.job_title || user?.department_name || (user?.role||"").toUpperCase()}
                      </span>
                    </div>
                    <lucide.ChevronDown size={11} color={THEME.textDim}/>
                  </div>
                  <AnimatePresence>{userMenu&&(
                    <motion.div initial={{opacity:0,y:-6,scale:0.96}} animate={{opacity:1,y:0,scale:1}} exit={{opacity:0,y:-6,scale:0.96}} style={{position:"absolute",right:0,top:"100%",marginTop:6,width:220,background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:10,boxShadow:"0 12px 40px rgba(0,0,0,0.4)",overflow:"hidden",zIndex:200}}>
                      <div style={{padding:"12px 16px",borderBottom:`1px solid ${THEME.borderSubtle}`,display:"flex",alignItems:"center",gap:10}}>
                        <Avatar name={user?.name} size={30}/><div><div style={{fontSize:12,fontWeight:600,color:THEME.text}}>{user?.name}</div><div style={{fontSize:10,color:THEME.textDim}}>{user?.email||user?.role}</div></div>
                      </div>
                      {/* Sprint 10C Session C1 H5 fix: Control Center (Admin Portal)
                          was a second entry point that bypassed the sidebar's
                          superAdminOnly gate. Filter it here to match. */}
                      {[
                        {icon:lucide.Info,label:lang==="fr"?"À propos":"About",action:()=>{setUserMenu(false);setShowAbout(true);}},
                        {icon:lucide.MessageCircle,label:lang==="fr"?"Retour":"Feedback",action:()=>{setUserMenu(false);window.open("mailto:contact@klassdoc.com?subject=KlassDoc Feedback","_blank");}},
                        {icon:lucide.Trash2,label:lang==="fr"?"Corbeille":"Trash",action:()=>{setUserMenu(false);toast(lang==="fr"?"Corbeille — bientôt disponible":"Trash — coming soon","info");}},
                        {icon:lucide.Settings,label:lang==="fr"?"Paramètres":"User Settings",action:()=>{setUserMenu(false);setShowUserSettings(true);}},
                        ...(user?.is_super_admin===true?[{icon:lucide.Gauge,label:lang==="fr"?"Centre de contrôle":"Control Center",action:()=>{setUserMenu(false);setPage("admin");}}]:[]),
                      ].map((item,i)=>(
                        <button key={i} onClick={item.action} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 16px",border:"none",background:"transparent",color:THEME.textMuted,fontSize:12,fontFamily:FONT,cursor:"pointer",transition:"background 0.1s"}} onMouseEnter={e=>e.currentTarget.style.background=THEME.surfaceHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}><item.icon size={14}/>{item.label}</button>
                      ))}
                      <div style={{borderTop:`1px solid ${THEME.borderSubtle}`}}>
                        <button onClick={()=>{setUserMenu(false);onLogout();}} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 16px",border:"none",background:"transparent",color:THEME.danger,fontSize:12,fontWeight:600,fontFamily:FONT,cursor:"pointer",transition:"background 0.1s"}} onMouseEnter={e=>e.currentTarget.style.background=THEME.dangerDim} onMouseLeave={e=>e.currentTarget.style.background="transparent"}><lucide.LogOut size={14}/>{T(lang,"logout")}</button>
                      </div>
                    </motion.div>
                  )}</AnimatePresence>
                </div>
              </div>
            </div>
            <div style={{padding:"28px 32px"}}><div key={page}><ActivePage/></div></div>
          </div>
          <AnimatePresence>{qsOpen&&(<motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(4px)",display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:120}} onClick={e=>{if(e.target===e.currentTarget)setQsOpen(false);}}>
            <motion.div initial={{opacity:0,y:-20,scale:0.95}} animate={{opacity:1,y:0,scale:1}} exit={{opacity:0,y:-20,scale:0.95}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:520,overflow:"hidden",boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
              <form onSubmit={handleQs} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 18px"}}><lucide.Search size={18} color={THEME.textDim}/><input ref={qsRef} value={qsQuery} onChange={e=>setQsQuery(e.target.value)} placeholder={T(lang,"searchPH")} style={{flex:1,border:"none",background:"transparent",color:THEME.text,fontFamily:FONT,fontSize:15,outline:"none"}}/><span style={{fontSize:11,color:THEME.textDim,fontFamily:FONT_MONO}}>ESC</span></form>
              <div style={{padding:"8px 18px 14px",borderTop:`1px solid ${THEME.borderSubtle}`,fontSize:12,color:THEME.textDim}}>{T(lang,"pressEnter")}</div>
            </motion.div>
          </motion.div>)}</AnimatePresence>
          {/* About Modal */}
          <AnimatePresence>{showAbout&&(
            <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)setShowAbout(false);}}>
              <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} exit={{opacity:0,scale:0.95}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:400,padding:0,boxShadow:"0 24px 80px rgba(0,0,0,0.5)",overflow:"hidden"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px",borderBottom:`1px solid ${THEME.borderSubtle}`}}>
                  <span style={{fontSize:15,fontWeight:600,color:THEME.text}}>About</span>
                  <lucide.X size={16} color={THEME.textDim} style={{cursor:"pointer"}} onClick={()=>setShowAbout(false)}/>
                </div>
                <div style={{padding:"28px 20px",textAlign:"center"}}>
                  <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
                    <svg width="160" height="56" viewBox="0 0 280 72" xmlns="http://www.w3.org/2000/svg"><g transform="translate(4,2)"><rect x="8" y="0" width="32" height="40" rx="4" fill="#D4915E" opacity="0.25"/><rect x="2" y="6" width="32" height="40" rx="4" fill="#D4915E"/><line x1="10" y1="20" x2="26" y2="20" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity="0.6"/><line x1="10" y1="26" x2="22" y2="26" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity="0.6"/><line x1="10" y1="32" x2="18" y2="32" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity="0.6"/><circle cx="30" cy="12" r="10" fill="#5CB88A"/><polyline points="25,12 28,15 35,8" stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></g><text x="56" y="36" fontFamily="'DM Sans','Segoe UI',system-ui,sans-serif" fontSize="32" fontWeight="700" letterSpacing="-0.5"><tspan fill="#D4915E">Klass</tspan><tspan fill="#E8ECF1">Doc</tspan></text><text x="57" y="50" fontFamily="'DM Sans','Segoe UI',system-ui,sans-serif" fontSize="9.5" fill="#7B8AA0" letterSpacing="2.5" fontWeight="500">DOCUMENT MANAGEMENT</text><text x="57" y="66" fontFamily="'DM Sans','Segoe UI',system-ui,sans-serif" fontSize="8.5" fill="#4A5568" letterSpacing="0.5">by <tspan fontWeight="600" fill="#7B8AA0">SEVEN CO SUCCESS</tspan></text></svg>
                  </div>
                  <div style={{height:1,background:THEME.borderSubtle,margin:"16px 0"}}/>
                  <div style={{fontSize:13,color:THEME.textMuted,lineHeight:1.8,textAlign:"left"}}>
                    <div><span style={{color:THEME.textDim}}>Version:</span> <span style={{color:THEME.text}}>9.0.0</span></div>
                    <div><span style={{color:THEME.textDim}}>Server:</span> <span style={{color:THEME.text}}>KlassDoc API v9.0</span></div>
                    <div><span style={{color:THEME.textDim}}>Status:</span> <span style={{color:THEME.success}}>Online</span></div>
                  </div>
                  <div style={{marginTop:16,fontSize:11,color:THEME.textDim}}>© 2026 Seven Co Success. All rights reserved.</div>
                </div>
              </motion.div>
            </motion.div>
          )}</AnimatePresence>
          {/* User Settings Modal */}
          <AnimatePresence>{showUserSettings&&(
            <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)setShowUserSettings(false);}}>
              <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} exit={{opacity:0,scale:0.95}} style={{background:THEME.surfaceRaised,border:`1px solid ${THEME.border}`,borderRadius:14,width:480,padding:0,boxShadow:"0 24px 80px rgba(0,0,0,0.5)",overflow:"hidden"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px",borderBottom:`1px solid ${THEME.borderSubtle}`}}>
                  <span style={{fontSize:15,fontWeight:600,color:THEME.text}}>{lang==="fr"?"Paramètres utilisateur":"User Settings"}</span>
                  <lucide.X size={16} color={THEME.textDim} style={{cursor:"pointer"}} onClick={()=>setShowUserSettings(false)}/>
                </div>
                <div style={{padding:"24px 20px"}}>
                  <div style={{marginBottom:20}}>
                    <label style={{display:"block",fontSize:13,fontWeight:600,color:THEME.text,marginBottom:8}}>{lang==="fr"?"Langue":"Language"}</label>
                    <select value={lang} onChange={e=>setLang(e.target.value)} style={{...S.input,background:THEME.surface}}>
                      <option value="en">English</option>
                      <option value="fr">Français</option>
                    </select>
                  </div>
                  <div style={{marginBottom:20}}>
                    <label style={{display:"block",fontSize:13,fontWeight:600,color:THEME.text,marginBottom:4}}>{lang==="fr"?"Onglet par défaut":"Default Tab"}</label>
                    <div style={{fontSize:12,color:THEME.textDim,marginBottom:8}}>{lang==="fr"?"Sélectionnez l'onglet qui s'affichera à votre connexion.":"Select which tab will load when you come back to KlassDoc."}</div>
                    <select value={defaultTab} onChange={e=>{setDefaultTab(e.target.value);localStorage.setItem("kd_default_tab",e.target.value);}} style={{...S.input,background:THEME.surface}}>
                      <option value="dashboard">{lang==="fr"?"Tableau de bord":"Dashboard"}</option>
                      <option value="documents">Documents</option>
                      <option value="folders">{lang==="fr"?"Dossiers":"Folders"}</option>
                      <option value="search">{lang==="fr"?"Recherche":"Search"}</option>
                      <option value="courrier">Courrier</option>
                      {/* Sprint 10C post-F: Reports is admin-only — hide option for non-admins */}
                      {isAdminOrSuper(user)&&<option value="reports">{lang==="fr"?"Rapports":"Reports"}</option>}
                    </select>
                  </div>
                  <div style={{marginBottom:20}}>
                    <div style={{height:1,background:THEME.borderSubtle,marginBottom:20}}/>
                    <label style={{fontSize:13,fontWeight:600,color:THEME.text,marginBottom:12,display:"flex",alignItems:"center",gap:6}}><lucide.Lock size={14}/>{lang==="fr"?"Changer le mot de passe":"Change password"}</label>
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      <input type="password" placeholder={lang==="fr"?"Mot de passe actuel":"Current password"} value={pwCurrent} onChange={e=>setPwCurrent(e.target.value)} style={{...S.input,background:THEME.surface,fontSize:13}}/>
                      <div style={{position:"relative"}}><input type={pwShowNew?"text":"password"} placeholder={lang==="fr"?"Nouveau mot de passe (8+ car., maj., min., chiffre)":"New password (8+ chars, upper, lower, number)"} value={pwNew} onChange={e=>setPwNew(e.target.value)} style={{...S.input,background:THEME.surface,fontSize:13,paddingRight:36}}/><button type="button" onClick={()=>setPwShowNew(!pwShowNew)} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",border:"none",background:"transparent",cursor:"pointer",padding:2}}>{pwShowNew?<lucide.EyeOff size={14} color={THEME.textDim}/>:<lucide.Eye size={14} color={THEME.textDim}/>}</button></div>
                      <input type="password" placeholder={lang==="fr"?"Confirmer le nouveau mot de passe":"Confirm new password"} value={pwConfirm} onChange={e=>setPwConfirm(e.target.value)} style={{...S.input,background:THEME.surface,fontSize:13}}/>
                    </div>
                    {pwNew&&pwConfirm&&pwNew!==pwConfirm&&<div style={{fontSize:11,color:THEME.danger,marginTop:4}}>{lang==="fr"?"Les mots de passe ne correspondent pas":"Passwords don't match"}</div>}
                    <button onClick={async()=>{if(!pwCurrent||!pwNew)return toast(lang==="fr"?"Remplissez tous les champs":"Fill all fields","warning");if(pwNew!==pwConfirm)return toast(lang==="fr"?"Les mots de passe ne correspondent pas":"Passwords don't match","error");setPwSaving(true);try{await api.post("/auth/change-password",{currentPassword:pwCurrent,newPassword:pwNew});toast(lang==="fr"?"Mot de passe mis à jour":"Password updated","success");setPwCurrent("");setPwNew("");setPwConfirm("");}catch(e){toast(e.message,"error");}setPwSaving(false);}} disabled={pwSaving||!pwCurrent||!pwNew||pwNew!==pwConfirm} style={{...S.btn,marginTop:10,padding:"8px 16px",fontSize:12,background:THEME.accent,color:"#fff",opacity:pwSaving||!pwCurrent||!pwNew||pwNew!==pwConfirm?0.5:1}}>{pwSaving?<Spinner size={12}/>:<lucide.Check size={12}/>}{lang==="fr"?"Mettre à jour":"Update password"}</button>
                  </div>
                  {/* Sprint 10C D2 — MFA / 2FA management */}
                  <MfaSettingsSection api={api} lang={lang} THEME={THEME} toast={toast}/>
                  <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
                    <button onClick={()=>setShowUserSettings(false)} style={{...S.btn,padding:"9px 20px",fontSize:13,background:THEME.accent,color:"#fff"}}>{lang==="fr"?"Fermer":"Close"}</button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}</AnimatePresence>
          <AnimatePresence>{toastData&&<Toast message={toastData.message} type={toastData.type} onClose={()=>setToastData(null)}/>}</AnimatePresence>
        </div>
      </AppContext.Provider>
    </LangContext.Provider>
  );
}

// ─── PASSWORD RESET LANDING PAGE (Sprint 10C Session D1) ───
// Shown when the URL carries ?reset_token=xxx (i.e., the user clicked a
// reset-email link). Decoupled from LoginPage because:
//   1. It's always a full-screen takeover, including for already-logged-
//      in users who clicked the link from another tab.
//   2. It has three distinct states (verifying / valid / invalid) that
//      would bloat LoginPage's state machine.
//   3. On success it clears the token from the URL so a browser-back
//      doesn't re-trigger the flow.
function ResetPasswordPage({token,onDone}){
  const [lang,setLang]=useState(()=>localStorage.getItem("af_lang")||"fr");
  useEffect(()=>{localStorage.setItem("af_lang",lang);},[lang]);

  const [phase,setPhase]=useState("verifying"); // 'verifying' | 'valid' | 'invalid' | 'done'
  const [accountEmail,setAccountEmail]=useState(null); // masked, server-provided
  const [pwd,setPwd]=useState("");
  const [pwdConfirm,setPwdConfirm]=useState("");
  const [showPw,setShowPw]=useState(false);
  const [submitting,setSubmitting]=useState(false);
  const [error,setError]=useState(null);

  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      try{
        const res=await api.post("/auth/password-reset/verify",{token});
        if(cancelled)return;
        if(res?.valid){setAccountEmail(res.email||null);setPhase("valid");}
        else{setPhase("invalid");}
      }catch{
        if(!cancelled)setPhase("invalid");
      }
    })();
    return()=>{cancelled=true;};
  },[token]);

  async function handleSubmit(e){
    e.preventDefault();
    if(submitting)return;
    if(pwd!==pwdConfirm){setError(T(lang,"passwordsDontMatch"));return;}
    setError(null);setSubmitting(true);
    try{
      await api.post("/auth/password-reset/complete",{token,newPassword:pwd});
      setPhase("done");
    }catch(err){
      const msg=err?.message||T(lang,"error");
      // Backend returns structured policy errors; surface any returned 'errors' array.
      if(Array.isArray(err?.errors)&&err.errors.length>0){
        setError(err.errors.join(" · "));
      }else{
        setError(msg);
      }
    }finally{
      setSubmitting(false);
    }
  }

  function goToLogin(){
    // Strip the ?reset_token param so the login page doesn't re-route here on reload.
    try{
      const url=new URL(window.location.href);
      url.searchParams.delete("reset_token");
      window.history.replaceState({},"",url.pathname+url.hash);
    }catch{}
    onDone();
  }

  return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:THEME.bg,fontFamily:FONT,backgroundImage:`radial-gradient(ellipse 80% 60% at 50% 0%,${THEME.accentGlow} 0%,transparent 60%)`}}>
      <div style={{position:"fixed",top:20,right:20}}><LangToggle lang={lang} setLang={setLang}/></div>
      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} style={{width:420,padding:40}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:22,fontWeight:700,letterSpacing:-0.5,marginBottom:8}}>
            <span style={{color:THEME.accent}}>Klass</span><span style={{color:THEME.text}}>Doc</span>
          </div>
          <div style={{fontSize:10,color:THEME.textMuted,letterSpacing:2.5,fontWeight:500}}>DOCUMENT MANAGEMENT</div>
        </div>

        {phase==="verifying"&&(
          <div style={{textAlign:"center",padding:"40px 20px"}}>
            <Spinner size={28}/>
            <div style={{fontSize:13,color:THEME.textDim,marginTop:14}}>{T(lang,"verifyingLink")}</div>
          </div>
        )}

        {phase==="invalid"&&(
          <div style={{textAlign:"center"}}>
            <div style={{width:56,height:56,borderRadius:14,background:THEME.dangerDim,display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:16}}>
              <lucide.AlertCircle size={28} color={THEME.danger}/>
            </div>
            <h2 style={{fontSize:17,fontWeight:700,color:THEME.text,margin:"0 0 10px 0"}}>{T(lang,"linkInvalid")}</h2>
            <p style={{fontSize:13,color:THEME.textDim,margin:"0 0 24px 0",lineHeight:1.55}}>{T(lang,"linkExpired")}</p>
            <button onClick={goToLogin} style={{...S.btn,width:"100%",justifyContent:"center",background:THEME.accent,color:"#fff",padding:"12px 20px",fontSize:14}}>
              {T(lang,"backToLogin")}
            </button>
          </div>
        )}

        {phase==="valid"&&(
          <form onSubmit={handleSubmit}>
            <h2 style={{fontSize:17,fontWeight:700,color:THEME.text,margin:"0 0 6px 0"}}>{T(lang,"resetPassword")}</h2>
            {accountEmail&&(
              <p style={{fontSize:12,color:THEME.textDim,margin:"0 0 20px 0"}}>
                {T(lang,"resetForAccount")}<strong style={{color:THEME.text}}>{accountEmail}</strong>
              </p>
            )}
            <div style={{marginBottom:14}}>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase"}}>{T(lang,"newPassword")}</label>
              <div style={{position:"relative"}}>
                <input type={showPw?"text":"password"} value={pwd} onChange={e=>setPwd(e.target.value)} style={{...S.input,background:THEME.surfaceRaised,paddingRight:40}} autoFocus onFocus={e=>e.target.style.borderColor=THEME.accent} onBlur={e=>e.target.style.borderColor=THEME.border}/>
                <button type="button" onClick={()=>setShowPw(!showPw)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",border:"none",background:"transparent",cursor:"pointer",padding:4}}>
                  {showPw?<lucide.EyeOff size={16} color={THEME.textDim}/>:<lucide.Eye size={16} color={THEME.textDim}/>}
                </button>
              </div>
            </div>
            <div style={{marginBottom:12}}>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase"}}>{T(lang,"confirmPassword")}</label>
              <input type={showPw?"text":"password"} value={pwdConfirm} onChange={e=>setPwdConfirm(e.target.value)} style={{...S.input,background:THEME.surfaceRaised}} onFocus={e=>e.target.style.borderColor=THEME.accent} onBlur={e=>e.target.style.borderColor=THEME.border}/>
              {pwd&&pwdConfirm&&pwd!==pwdConfirm&&(
                <div style={{fontSize:11,color:THEME.danger,marginTop:4}}>{T(lang,"passwordsDontMatch")}</div>
              )}
            </div>
            <p style={{fontSize:11,color:THEME.textDim,margin:"0 0 18px 0",lineHeight:1.5}}>{T(lang,"passwordRequirements")}</p>
            {error&&(
              <div style={{marginBottom:14,padding:"10px 14px",borderRadius:8,background:THEME.dangerDim,color:THEME.danger,fontSize:13}}>
                {error}
              </div>
            )}
            <button type="submit" disabled={submitting||!pwd||pwd!==pwdConfirm} style={{...S.btn,width:"100%",justifyContent:"center",background:`linear-gradient(135deg,${THEME.accent},${THEME.accentHover})`,color:"#fff",padding:"12px 20px",fontSize:15,opacity:(submitting||!pwd||pwd!==pwdConfirm)?0.6:1,boxShadow:`0 4px 16px ${THEME.accent}30`}}>
              {submitting?<Spinner size={18}/>:<><lucide.ShieldCheck size={16}/>{T(lang,"resetPassword")}</>}
            </button>
          </form>
        )}

        {phase==="done"&&(
          <div style={{textAlign:"center"}}>
            <div style={{width:56,height:56,borderRadius:14,background:THEME.successDim,display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:16}}>
              <lucide.CheckCircle size={28} color={THEME.success}/>
            </div>
            <h2 style={{fontSize:17,fontWeight:700,color:THEME.text,margin:"0 0 10px 0"}}>{T(lang,"passwordUpdated")}</h2>
            <button onClick={goToLogin} style={{...S.btn,width:"100%",justifyContent:"center",background:THEME.accent,color:"#fff",padding:"12px 20px",fontSize:14,marginTop:12}}>
              {T(lang,"backToLogin")}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

// ─── ACTIVATION (FIRST-PASSWORD) LANDING PAGE (Sprint 11.1a) ───
// Shown when the URL carries ?activate_token=xxx (i.e., the new org
// owner clicked the welcome email link). Distinct from ResetPasswordPage
// because:
//   1. On success it auto-logs-in via the JWT pair returned by
//      /auth/activate/complete (Doanix parity — owner lands in workspace,
//      not back on /login).
//   2. Copy emphasizes "set" not "reset" so first-time owners aren't
//      confused into thinking something went wrong.
//   3. Distinct audit-log action server-side ('user.activated').
function ActivatePasswordPage({token,onActivated,onDone}){
  const [lang,setLang]=useState(()=>localStorage.getItem("af_lang")||"fr");
  useEffect(()=>{localStorage.setItem("af_lang",lang);},[lang]);

  const [phase,setPhase]=useState("verifying"); // 'verifying' | 'valid' | 'invalid' | 'done'
  const [accountEmail,setAccountEmail]=useState(null);
  const [accountName,setAccountName]=useState(null);
  const [pwd,setPwd]=useState("");
  const [pwdConfirm,setPwdConfirm]=useState("");
  const [showPw,setShowPw]=useState(false);
  const [submitting,setSubmitting]=useState(false);
  const [error,setError]=useState(null);

  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      try{
        const res=await api.post("/auth/activate/verify",{token});
        if(cancelled)return;
        if(res?.valid){
          setAccountEmail(res.email||null);
          setAccountName(res.name||null);
          setPhase("valid");
        }else{
          setPhase("invalid");
        }
      }catch{
        if(!cancelled)setPhase("invalid");
      }
    })();
    return()=>{cancelled=true;};
  },[token]);

  async function handleSubmit(e){
    e.preventDefault();
    if(submitting)return;
    if(pwd!==pwdConfirm){setError(T(lang,"passwordsDontMatch"));return;}
    setError(null);setSubmitting(true);
    try{
      const res=await api.post("/auth/activate/complete",{token,password:pwd});
      // Auto-login: install access + refresh tokens from the response.
      if(res?.token){
        api.setToken(res.token);
        if(res.refreshToken)localStorage.setItem("kd_refresh_token",res.refreshToken);
        // Strip the activate_token param BEFORE handing control to the
        // AppShell so a refresh doesn't bounce back here.
        try{
          const url=new URL(window.location.href);
          url.searchParams.delete("activate_token");
          window.history.replaceState({},"",url.pathname+url.hash);
        }catch{}
        // Brief "done" screen for ~800 ms so the transition isn't jarring,
        // then hand the user object up to KlassDoc root which renders AppShell.
        setPhase("done");
        setTimeout(()=>onActivated&&onActivated(res.user),800);
      }else{
        setPhase("done");
      }
    }catch(err){
      const msg=err?.message||T(lang,"error");
      if(Array.isArray(err?.errors)&&err.errors.length>0){
        setError(err.errors.join(" · "));
      }else{
        setError(msg);
      }
    }finally{
      setSubmitting(false);
    }
  }

  function goToLogin(){
    try{
      const url=new URL(window.location.href);
      url.searchParams.delete("activate_token");
      window.history.replaceState({},"",url.pathname+url.hash);
    }catch{}
    onDone&&onDone();
  }

  return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:THEME.bg,fontFamily:FONT,backgroundImage:`radial-gradient(ellipse 80% 60% at 50% 0%,${THEME.accentGlow} 0%,transparent 60%)`}}>
      <div style={{position:"fixed",top:20,right:20}}><LangToggle lang={lang} setLang={setLang}/></div>
      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} style={{width:440,padding:40}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:22,fontWeight:700,letterSpacing:-0.5,marginBottom:8}}>
            <span style={{color:THEME.accent}}>Klass</span><span style={{color:THEME.text}}>Doc</span>
          </div>
          <div style={{fontSize:10,color:THEME.textMuted,letterSpacing:2.5,fontWeight:500}}>DOCUMENT MANAGEMENT</div>
        </div>

        {phase==="verifying"&&(
          <div style={{textAlign:"center",padding:"40px 20px"}}>
            <Spinner size={28}/>
            <div style={{fontSize:13,color:THEME.textDim,marginTop:14}}>{lang==="fr"?"Vérification du lien…":"Verifying link…"}</div>
          </div>
        )}

        {phase==="invalid"&&(
          <div style={{textAlign:"center"}}>
            <div style={{width:56,height:56,borderRadius:14,background:THEME.dangerDim,display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:16}}>
              <lucide.AlertCircle size={28} color={THEME.danger}/>
            </div>
            <h2 style={{fontSize:17,fontWeight:700,color:THEME.text,margin:"0 0 10px 0"}}>{lang==="fr"?"Lien invalide ou expiré":"Invalid or expired link"}</h2>
            <p style={{fontSize:13,color:THEME.textDim,margin:"0 0 24px 0",lineHeight:1.55}}>
              {lang==="fr"
                ?"Ce lien d'activation a déjà été utilisé ou a expiré. Demandez à votre super-administrateur de régénérer une invitation."
                :"This activation link has already been used or has expired. Ask your super-administrator to regenerate an invitation."}
            </p>
            <button onClick={goToLogin} style={{...S.btn,width:"100%",justifyContent:"center",background:THEME.accent,color:"#fff",padding:"12px 20px",fontSize:14}}>
              {lang==="fr"?"Retour à la connexion":"Back to sign-in"}
            </button>
          </div>
        )}

        {phase==="valid"&&(
          <form onSubmit={handleSubmit}>
            <h2 style={{fontSize:19,fontWeight:700,color:THEME.text,margin:"0 0 8px 0"}}>
              {lang==="fr"?`Bienvenue${accountName?", "+accountName:""}`:`Welcome${accountName?", "+accountName:""}`}
            </h2>
            <p style={{fontSize:13,color:THEME.textDim,margin:"0 0 22px 0",lineHeight:1.55}}>
              {lang==="fr"?"Choisissez votre mot de passe pour activer ":"Choose your password to activate "}
              {accountEmail&&<strong style={{color:THEME.text}}>{accountEmail}</strong>}
              {lang==="fr"?". Vous serez ensuite redirigé vers votre espace de travail.":". You'll then be taken to your workspace."}
            </p>
            <div style={{marginBottom:14}}>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase"}}>{lang==="fr"?"Mot de passe":"Password"}</label>
              <div style={{position:"relative"}}>
                <input type={showPw?"text":"password"} value={pwd} onChange={e=>setPwd(e.target.value)} style={{...S.input,background:THEME.surfaceRaised,paddingRight:40}} autoFocus onFocus={e=>e.target.style.borderColor=THEME.accent} onBlur={e=>e.target.style.borderColor=THEME.border}/>
                <button type="button" onClick={()=>setShowPw(!showPw)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",border:"none",background:"transparent",cursor:"pointer",padding:4}}>
                  {showPw?<lucide.EyeOff size={16} color={THEME.textDim}/>:<lucide.Eye size={16} color={THEME.textDim}/>}
                </button>
              </div>
            </div>
            <div style={{marginBottom:12}}>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:THEME.textMuted,marginBottom:6,textTransform:"uppercase"}}>{lang==="fr"?"Confirmer le mot de passe":"Confirm password"}</label>
              <input type={showPw?"text":"password"} value={pwdConfirm} onChange={e=>setPwdConfirm(e.target.value)} style={{...S.input,background:THEME.surfaceRaised}} onFocus={e=>e.target.style.borderColor=THEME.accent} onBlur={e=>e.target.style.borderColor=THEME.border}/>
              {pwd&&pwdConfirm&&pwd!==pwdConfirm&&(
                <div style={{fontSize:11,color:THEME.danger,marginTop:4}}>{T(lang,"passwordsDontMatch")}</div>
              )}
            </div>
            <p style={{fontSize:11,color:THEME.textDim,margin:"0 0 18px 0",lineHeight:1.5}}>
              {lang==="fr"
                ?"Minimum 8 caractères, avec au moins une majuscule, une minuscule et un chiffre."
                :"At least 8 characters, including one uppercase, one lowercase, and one number."}
            </p>
            {error&&(
              <div style={{marginBottom:14,padding:"10px 14px",borderRadius:8,background:THEME.dangerDim,color:THEME.danger,fontSize:13}}>
                {error}
              </div>
            )}
            <button type="submit" disabled={submitting||!pwd||pwd!==pwdConfirm} style={{...S.btn,width:"100%",justifyContent:"center",background:`linear-gradient(135deg,${THEME.accent},${THEME.accentHover})`,color:"#fff",padding:"12px 20px",fontSize:15,opacity:(submitting||!pwd||pwd!==pwdConfirm)?0.6:1,boxShadow:`0 4px 16px ${THEME.accent}30`}}>
              {submitting?<Spinner size={18}/>:<><lucide.ShieldCheck size={16}/>{lang==="fr"?"Activer mon compte":"Activate my account"}</>}
            </button>
          </form>
        )}

        {phase==="done"&&(
          <div style={{textAlign:"center"}}>
            <div style={{width:56,height:56,borderRadius:14,background:THEME.successDim,display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:16}}>
              <lucide.CheckCircle size={28} color={THEME.success}/>
            </div>
            <h2 style={{fontSize:17,fontWeight:700,color:THEME.text,margin:"0 0 6px 0"}}>{lang==="fr"?"Compte activé":"Account activated"}</h2>
            <p style={{fontSize:13,color:THEME.textDim,margin:"0 0 20px 0"}}>
              {lang==="fr"?"Redirection vers votre espace de travail…":"Taking you to your workspace…"}
            </p>
            <Spinner size={22}/>
          </div>
        )}
      </motion.div>
    </div>
  );
}

// ─── DOC VIEWER TAB — view-lock wrapper (2026-06-11) ───
// Rendered when the URL carries ?viewer_doc=<id> (the preview modal's
// "Open" button opens this in a NEW TAB). It wraps the browser-native
// render of a PDF/image/text/media file with the checkout lifecycle:
// opening a file LOCKS it; closing the tab RELEASES it.
//   - acquire: POST /checkouts/:id {source:'view'} on mount.
//       acquired:true  → we own the lock. Heartbeat every 60s slides the
//                        3-min TTL; on pagehide a keepalive DELETE gives
//                        instant release; the TTL is the safety net for
//                        crashed tabs / lost release requests.
//       acquired:false → a lock by US pre-existed (Office edit session,
//                        manual checkout, or another viewer tab) — view
//                        without touching it and NEVER release on close,
//                        or we'd drop the user's own edit lock.
//       409            → someone ELSE holds it — view read-only with a
//                        banner naming the holder (same semantic as the
//                        Office conflict dialog).
//   - render: preview-purpose signed URL fetched as a blob → iframe
//     (blob: URLs bypass framing-header blocks, same as every other
//     in-app preview).
function DocViewerPage({docId}){
  const FR=(localStorage.getItem("af_lang")||"fr")==="fr";
  const [doc,setDoc]=useState(null);
  const [blobUrl,setBlobUrl]=useState(null);
  const [lock,setLock]=useState({state:"acquiring",message:null});
  const [err,setErr]=useState(null);
  const acquiredRef=useRef(false);

  useEffect(()=>{
    let cancelled=false,hb=null,objUrl=null;
    const release=()=>{
      if(!acquiredRef.current)return;
      acquiredRef.current=false;
      try{
        // keepalive lets the request survive tab close (sendBeacon can't
        // carry the Authorization header; keepalive fetch can).
        fetch(`${API_BASE}/checkouts/${docId}`,{method:"DELETE",headers:{Authorization:`Bearer ${api.token}`},keepalive:true});
      }catch{}
    };
    (async()=>{
      try{
        const meta=await api.get(`/documents/${docId}`);
        if(cancelled)return;
        setDoc(meta);
        try{document.title=`${meta.name} — KlassDoc`;}catch{}
        try{
          const r=await api.post(`/checkouts/${docId}`,{source:"view"});
          if(cancelled)return;
          if(r?.acquired){
            acquiredRef.current=true;
            setLock({state:"mine",message:null});
            // Heartbeat keeps the 3-min TTL sliding while the tab lives.
            // If a beat comes back 404 the lock LAPSED (e.g. the laptop
            // slept past the TTL): immediately stop claiming ownership —
            // critical, or an admin's tab-close release could force-drop
            // someone ELSE's lock — then silently re-acquire. Free →
            // ours again, chip stays "Locked by you". Taken → chip flips
            // to the read-only banner so the user isn't misled. And if
            // the other user later releases, a following beat re-acquires
            // automatically and the chip returns to "Locked by you".
            hb=setInterval(async()=>{
              try{
                await api.put(`/checkouts/${docId}/heartbeat`);
              }catch(e){
                if(e?.status!==404)return; // transient/network — retry next beat
                acquiredRef.current=false;
                try{
                  const r2=await api.post(`/checkouts/${docId}`,{source:"view"});
                  if(cancelled)return;
                  if(r2?.acquired){
                    acquiredRef.current=true;
                    setLock({state:"mine",message:null});
                  }else{
                    setLock({state:"inherited",message:null});
                  }
                }catch(e2){
                  if(cancelled)return;
                  if(e2?.status===409)setLock({state:"readonly",message:e2?.message||null});
                  // other failures: stay pessimistic (not owner); next beat retries
                }
              }
            },60000);
          }else{
            setLock({state:"inherited",message:null});
          }
        }catch(e){
          if(!cancelled)setLock({state:"readonly",message:e?.message||null});
        }
        const url=await api.signedUrl("document",docId,{purpose:"preview"});
        const resp=await fetch(url,{credentials:"same-origin"});
        if(!resp.ok)throw new Error(`Fetch failed (${resp.status})`);
        const blob=await resp.blob();
        if(cancelled)return;
        objUrl=URL.createObjectURL(blob);
        setBlobUrl(objUrl);
      }catch(e){
        if(!cancelled)setErr(e?.message||(FR?"Impossible d'ouvrir le document":"Could not open the document"));
      }
    })();
    window.addEventListener("pagehide",release);
    return()=>{
      cancelled=true;
      if(hb)clearInterval(hb);
      window.removeEventListener("pagehide",release);
      release();
      if(objUrl){try{URL.revokeObjectURL(objUrl);}catch{}}
    };
  },[docId]); // eslint-disable-line

  const lockChip=(()=>{
    if(lock.state==="mine")return{bg:THEME.successDim,color:THEME.success,icon:lucide.Lock,label:FR?"Verrouillé par vous — fermez cet onglet pour libérer":"Locked by you — close this tab to release"};
    if(lock.state==="inherited")return{bg:THEME.accentDim,color:THEME.accent2,icon:lucide.Lock,label:FR?"Déjà verrouillé par vous (autre session)":"Already locked by you (other session)"};
    if(lock.state==="readonly")return{bg:THEME.warningDim,color:THEME.warning,icon:lucide.Eye,label:lock.message||(FR?"Verrouillé par un autre utilisateur — lecture seule":"Locked by another user — read-only")};
    return{bg:THEME.surfaceHover,color:THEME.textMuted,icon:lucide.Loader2,label:FR?"Verrouillage…":"Locking…"};
  })();

  return(
    <div style={{height:"100vh",display:"flex",flexDirection:"column",background:THEME.bg,fontFamily:FONT}}>
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",background:THEME.surface,borderBottom:`1px solid ${THEME.border}`,flexShrink:0}}>
        {doc?<FileTypeBadge doc={doc} size={30} radius={7}/>:<Spinner size={20}/>}
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13.5,fontWeight:600,color:THEME.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{doc?.name||"…"}</div>
        </div>
        <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 12px",borderRadius:20,fontSize:11.5,fontWeight:600,background:lockChip.bg,color:lockChip.color,whiteSpace:"nowrap"}}>
          <lockChip.icon size={12}/>{lockChip.label}
        </span>
        <button onClick={()=>window.close()} style={{...S.btn,padding:"6px 14px",fontSize:12,background:THEME.surfaceHover,color:THEME.textMuted,border:`1px solid ${THEME.border}`}}>
          <lucide.X size={13}/>{FR?"Fermer":"Close"}
        </button>
      </div>
      <div style={{flex:1,minHeight:0,background:"#525659"}}>
        {err
          ?<div style={{height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{textAlign:"center",color:"#ccc"}}><lucide.FileWarning size={42} strokeWidth={1} style={{display:"block",margin:"0 auto 10px"}}/><div style={{fontSize:14}}>{err}</div></div></div>
          :!blobUrl
            ?<div style={{height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}><Spinner size={28}/></div>
            :<iframe src={blobUrl} title={doc?.name||"document"} style={{width:"100%",height:"100%",border:"none",background:"#fff"}}/>}
      </div>
    </div>
  );
}

// ─── ROOT ───
export default function KlassDoc(){
  const [user,setUser]=useState(null);const [checking,setChecking]=useState(true);
  // Sprint 10C Session D1: detect ?reset_token param on mount so the
  // password-reset landing page intercepts before any auth logic runs.
  // Stored in state so clearing the URL after success doesn't re-render
  // the reset page (we keep serving it until onDone() flips it off).
  const [resetToken,setResetToken]=useState(()=>{
    try{return new URL(window.location.href).searchParams.get("reset_token")||null;}
    catch{return null;}
  });
  // Sprint 11.1a: same pattern for ?activate_token=xxx (new-org owner
  // onboarding). Intercepts before auth so an already-logged-in user who
  // clicks an activation link from another tab still sees the activation
  // flow (they might be activating a different account).
  const [activateToken,setActivateToken]=useState(()=>{
    try{return new URL(window.location.href).searchParams.get("activate_token")||null;}
    catch{return null;}
  });
  // 2026-06-11: ?viewer_doc=<id> renders the standalone view-lock viewer
  // tab (see DocViewerPage). Checked AFTER auth so an unauthenticated
  // click lands on the login page first, then proceeds into the viewer.
  const [viewerDocId]=useState(()=>{
    try{return new URL(window.location.href).searchParams.get("viewer_doc")||null;}
    catch{return null;}
  });
  useEffect(()=>{
    if(api.token){api.get("/auth/me").then(setUser).catch(()=>api.setToken(null)).finally(()=>setChecking(false));}
    else{setChecking(false);}
  },[]);
  function logout(){api.setToken(null);setUser(null);}
  if(activateToken)return <ActivatePasswordPage token={activateToken} onActivated={u=>{setUser(u);setActivateToken(null);}} onDone={()=>setActivateToken(null)}/>;
  if(resetToken)return <ResetPasswordPage token={resetToken} onDone={()=>setResetToken(null)}/>;
  if(checking)return(<div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:THEME.bg,fontFamily:FONT}}><Spinner size={32}/></div>);
  if(!user)return <LoginPage onLogin={setUser}/>;
  if(viewerDocId)return <DocViewerPage docId={viewerDocId}/>;
  return <AppShell user={user} onLogout={logout}/>;
}
